import { Injectable, NgModule } from '@angular/core';
import { DataApiService, BaseService } from '@xpo-ltl/data-api';
import { ConfigManagerService, CONFIG_MANAGER_SERVICE_CONFIGURATION, ConfigManagerModule } from '@xpo-ltl/config-manager';
import { PayloadType } from '@xpo-ltl/sdk-common';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@xpo-ltl/data-api';
import * as ɵngcc2 from '@xpo-ltl/config-manager';
class CityOperationsApiService extends BaseService {
    /**
     * @param {?} dataApiService
     * @param {?} configManager
     */
    constructor(dataApiService, configManager) {
        super();
        this.dataApiService = dataApiService;
        this.configManager = configManager;
    }
    /**
     * Health check URL. Responds with success message if the service is running.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    healthCheck(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/health-check'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List of resources.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    options(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/options'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Details of user invoking the API.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    loggedInUser(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/appusers/logged-in-user'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the operating schedule assigned to a customer. It includes general operation, pickup and delivery hours.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid customer account number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the operating schedules for the requested customerAcctId are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getCustomerOperationsSchedules(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/{customerAcctId}/schedules'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the next available date and stop windows for a customer schedule.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid customerAcctId and destinationSicCd must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the next date for when the customer is available is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getNextAvailableDateForCustomerSchedule(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/{customerAcctId}/schedules/destinations/{destinationSicCd}/next-available-date'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This service returns the customer operations profile and optionally any notes or schedules for that profile using a valid customer number.
     * <br/>​
     * <br/>Pre-conditions:
     * <br/>1. A valid customer account number is provided.
     * <br/>2. An optional list of valid profile detail codes is provided to include additional profile notes and schedules for that customer in the response.
     * <br/>​
     * <br/>Post-conditions:
     * <br/>1. If successful, the customer profile, and if requested, any notes and schedules are returned.
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getOperationsCustomerProfile(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/{customerAcctId}/profile'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists the notes and instructions for customer profiles from table SCO_CUSTOMER_NOTE.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. At least one valid customer account number is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then customer profile notes and instructions for all the requested customerAcctIds are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listCustomerProfileNotes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/profile-notes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation is called from a batch job to retrieve the list of daily pickup stops with the account information which is later used to create a pickup request from each of them.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid operationTypeCd and weekDayCd are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then daily pickup stops and account information that matched the filter criteria are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listDailyPickupStopCustomerPreferences(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-stops/preferences'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation lists all customer schedules for the requested days, if provided, and the operation type code where the schedule end date is greater than the current date.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid operationTypeCd is provided.
     * <br/>2. The schedule end date must be greater than the current date.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If one or more week day codes were provided, then the customer schedules for the requested week days are listed.
     * <br/>2. If no week day codes were provided, then customer schedules for all week days are listed.
     * <br/>3. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOperationsSchedulesForCustomers(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/operations/{operationTypeCd}/schedules'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation updates a customer's operations profile, including any notes and schedules.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid customer account ID is provided. The customer associated with it must exist in the Customer system.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the customer's Profile, Notes, and Schedule information is updated, by action code, either by creating, updating, or deleting any information that is from the request.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateOperationsCustomerProfile(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/{customerAcctId}/profile'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation assigns the next shipment exception sequence number for a specified exception date and location responsible for the exception.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid terminal location SIC responsible for the exception is provided.
     * <br/>2. A valid exception date is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the exception sequence number is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    assignNextShipmentExceptionSequenceNbr(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{terminalSicCd}/exception-dates/{exceptionDate}/next-exception-sequence-number'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation completes the delivery activities for a stop from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and trip node sequence number must be provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the trip stop delivery activities are completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeDeliveryActivitiesFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/delivery-activities'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a delivery stop from the Mobile SCO device.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deliveryStop and that TripNode stop exists.
     * <br/>3. A list of delivery activity information for all the deliveries to be completed on that stop.
     * <br/>4. A list of ActivityEquipment is provided, which contains the activity and equipment related to the delivery activity.  The data must be valid along with both the activity and equipment.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the delivery stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeDeliveryStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/delivery-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes an equipment stop from a device.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided and the trip stop exists.
     * <br/>3. A permitted trip node activity code.
     * <br/>4. The SIC code, drop location, and route prefix are provided and valid.
     * <br/>5. The trip stop is provided and the stop exists.
     * <br/>6. A list of DropStopEquipment is provided, which contains the activity and equipment related to that activity.  The data must be valid along with both the activity and equipment.  They must be in the correct status.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then all the equipment-related activities on the request are processed for that stop on that trip.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeEquipmentStopFromDevice(request, pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/equipment-stops'), {
            pathParams: pathParams,
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a hook-loaded stop from the Mobile SCO device.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deviceTripStop and that stop exists.
     * <br/>3. A list of DeviceTripStopActivity is provided with the deviceTripStop. The list contains pickup activities and equipment related to the hook-loaded stop.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the hook-loaded stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeHookLoadedStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/hook-loaded-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a pickup stop from the Mobile SCO device.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deviceTripStop and that stop exists.
     * <br/>3. A list of DeviceTripStopActivity is provided with the deviceTripStop. The list contains activities and equipment related to the pickup stop.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the pickup stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completePickupStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/pickup-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes a drop stop and activity for a specific customer stop.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the stop and drop activity are completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completePnDDropStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/drop-activity/completions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes or returns a PnD trip, and updates the mobile device end timestamp.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID and mobile device end timestamp are provided.
     * <br/>2. The trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip information will be updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completePnDTripFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/completions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes the spot load activities for a stop from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and trip node sequence number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip stop spot load activities are completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeSpotLoadActivitiesFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/spot-load-activities'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a spot-loaded stop from the Mobile SCO device.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the spotLoadedStop and that TripNode stop exists.
     * <br/>3. A list of delivery activity information for all the deliveries to be completed on that stop.
     * <br/>4. A list of ActivityEquipment is provided, which contains the activity and equipment related to the spot-loaded activity.  The data must be valid along with both the activity and equipment.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the spot-loaded stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeSpotLoadedStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/spot-loaded-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes the trip stop from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and trip node sequence number must be provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the trip stop is completed. The operation can be partially completed as well.
     * <br/>2. Otherwise, an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeTripStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/completions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation creates a DSR message log from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a DSR message log is created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createDsrMessageLogFromDevice(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/message-logs'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a drop stop and activity for a specific customer stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then either a new stop and drop activity are created or an existing compatible stop is created for that customer with the drop activity.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDDropStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/drop-activity'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a trip activity such as enter gate, leave gate, start lunch, end lunch, other activity start, other activity end, and text message from the driver.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID, SCO event type codes, trip activity timestamp, and DSR employee ID are provided.
     * <br/>2. The trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip information will be created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDTripActivityFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/activities'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a trip event from a mobile device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. None
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a trip event is created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createTripEventFromDevice(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trip-events'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves trip details and indicators for a driver at a service center.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center and employeeId are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then details for a trip are retrieved as well as trip status indicators.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getAutoDispatchTripForDsrFromDevice(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/service-centers/{sicCd}/drivers/{employeeId}/auto-dispatch-trips'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the estimated sequence and timings of arrival and departure at all the stops of a trip.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then expectations for a trip are retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getTripExpectation(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/expectation'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the trip stop activity on a trip stop from a mobile device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID, trip node sequence number, trip node activity sequence number, and trip node activity code are provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the trip node activity detail is retrieved.
     * <br/>2. Otherwise, an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getTripStopActivityFromDevice(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/activities/{tripNodeActivitySequenceNbr}/{tripNodeActivityCd}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the trip stop on a trip from a mobile device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and trip node sequence number are provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the trip node detail is retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getTripStopFromDevice(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation creates both a log message and an event log from a text message sent by the driver from his primary mobile device for a trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip should exist.
     * <br/>2. Either the trip instance ID or the DSR employee ID must be provided along with the event text message and event timestamp.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, an event log is created. A message log is also created and associated with the event log.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    recordPnDTripMessageFromDevice(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/messages'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation splits trip stop activities into a new stop from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and original trip node sequence number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip stop activities are split into the new stop.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    splitTripStopActivitiesToNewStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/original-stops/{originalTripNodeSequenceNbr}/split-stop-activities'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the line items of a pickup or hook load activity. It accepts the new list of activity line items and performs add, update, delete or move as needed.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID, trip node sequence number, and trip node activity sequence number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the activity line items are updated.
     * <br/>2. Otherwise, an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateActivityLineItemsFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/activities/{tripNodeActivitySequenceNbr}/line-items'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates and unassigns hook loads from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and trip node sequence number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip stop hook load activities are updated and unassigned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateAndUnassignHookLoadsFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/hook-loads'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates or completes a hook load activity for a stop, based on the value of updateAsCompleteInd, from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID, trip node sequence number, trip node activity sequence number, and update as complete indicator must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip stop hook load activity is updated or completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateHookLoadActivityFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/activities/{tripNodeActivitySequenceNbr}/hook-load'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation allows the DSR to update a customer's operations profile from the mobile SCO device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid customer account ID is provided. The customer associated with it must exist in the Customer system.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the customer's profile is updated, by action code, either by creating, updating, or deleting any information that is from the request.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateOperationsCustomerProfileFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/customers/{customerAcctId}/profile'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates or completes a pickup activity for a stop, based on the value of updateAsCompleteInd, from a device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID, trip node sequence number, trip node activity sequence number, and update as complete indicator must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip stop pickup activity is updated or completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePickupActivityFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/activities/{tripNodeActivitySequenceNbr}/pickup'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates a drop stop and activity for a specific customer stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the stop and drop activity are updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDDropStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/stops/drop-activity'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the details for an equipment-related hook stop and is only consumed from mobile SCO.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. The SIC code and route prefix are provided and valid.
     * <br/>3. The hook trip stop is provided and the stop exists.
     * <br/>4. If a list of DropStopEquipment is provided it contains the activity and equipment related to that activity.  The data must be valid along with both the activity and equipment; both must be in the correct status.
     * <br/>5. An override warning indicator is provided to override any warning messages that occur during processing of this update.  A value of true will override any and all warnings.
     * <br/>6. The equipment change detail is provided and is valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then all the equipment-related activities on the request are processed for that stop on that trip.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDEquipmentHookStopFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/equipment-hook-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the odometer reading along with the arrival and departure timestamps for a trip stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID and trip sequence number is provided.
     * <br/>2. The trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip information will be updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDStopActualArrivalDepartureFromDevice(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/sequences/{tripNodeSequenceNbr}/stops/actual-arrivals-and-departures'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation calculates the bulk shipment total volume (cubic feet) and pup volume percentage. The calculation is done in following order:
     * <br/>1. Based on actual dimensions if all shipment dimensions (piece, length, height, and width) are provided.
     * <br/>2. Based on the shipper dimension profile provided and list of shipment cubes information.
     * <br/>3. Based on service center default.
     * <br/>4. Based on company default.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. All input parameters are optional since several combinations can be used to perform the calculation.  Whichever parameters are provided must be valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the calculated bulk shipment cubes are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    bulkCalculateShipmentCube(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/shipment-cubes/bulk-creation'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation calculates the shipment total volume (cubic feet) and pup volume percentage. The calculation is done in the order shown below:
     * <br/>1. Based on actual dimensions if all shipment dimensions (piece, length, height, width) are provided.
     * <br/>2. Based on the shipper dimension profile provided, the total pieces, weight, and destination.
     * <br/>3. Based on service center default.
     * <br/>4. Based on company default.
     * <br/>
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. All input parameters are optional since serveral combinations can be used to perform the calculation.  Whichever parameters are provided must be valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the calculated cube is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    calculateShipmentCube(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/shipment-cubes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation builds an optimizer request for submission to an optimization vendor.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the vendor optimization request is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    buildPnDOptimizerRequest(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/requests'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation will determine the dwell time for the customer stop of a trip based on the input provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A list of valid TripInstIds and seqNbrs along with the associated data is provided
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a list of dwell times by tripInstId and seqNbr will be returned
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    calculateCustomerDwellTimes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/stops/dwell-times'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation returns the collection of PnD Metrics associated with the specified routes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The values provided in the request must be valid.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then all metrics associated with the specified parameters are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    calculatePnDOptimizerMetrics(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizers/metrics'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new run optimizer entry in SCO_PDO_CONTROL.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid terminal SIC, plan date, and status code must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a new entry is stored in SCO_PDO_CONTROL and a RUN_NBR is generated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves all the commodity and dimension details from SHM tables and creates records in the SCO_PDO_RQST_COMMODITY and SCO_PDO_RQST_DIMENSION tables.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid runNbr must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, it stores the Commodity and Dimension details in SCO_PDO_RQST_COMMODITY and SCO_PDO_RQST_DIMENSION tables.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerCommodityDimensions(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/commodity-dimensions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates optimizer drivers, associated with an existing run number, in SCO_PDO_RQST_DRIVER.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid runNbr, sicCd, and planDate must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, optimizer drivers are created in the SCO_PDO_RQST_DRIVER table and the records are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerDrivers(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/drivers'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates the equipment details and returns the SCO_PDO_RQST_EQUIPMENT record.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number and a valid sic code must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the equipment record is created in the SCO_PDO_RQST_EQUIPMENT table and the record is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerEquipment(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/equipment'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a record in SCO_PDO_RSPNS_HDR with the fail reason for the corresponding RUN_NBR and RUN_SEQ_NBR.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid runNbr, runSequenceNbr, and failReason must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a new entry is created in SCO_PDO_RSPNS_HDR and a RUN_NBR is generated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerFailureResponse(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/failure-responses'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates pickup requests for optimization based upon the given planDate and returns the SCO_PDO_RQST_PKUP_REQUEST records.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number, terminal SIC, and plan date.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the pickup request records are created in the SCO_PDO_RQST_PKUP_REQUEST table and the records are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerPickupRequests(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/pickup-requests'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates optimizer shipment records in SCO_PDO_RQST_SHIPMENT for eligible routed shipments.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number, terminal SIC, and plan date.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the records are created in the SCO_PDO_RQST_SHIPMENT table.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerRoutedShipments(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/routed-shipments'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates PnD routes from the routes in the optimizer results.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided.
     * <br/>2. A valid planDate is provided.
     * <br/>3. An optional list of optimizer route details is provided. Each route detail contains an optimizer route and associated array of route stop details. Each route stop detail contains a stop and associated activities.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the routes, stops, and activities are created in PnD.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerRoutes(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{terminalSicCd}/plan-dates/{planDate}/optimizer-routes'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates optimizer shipment records in SCO_PDO_RQST_SHIPMENT.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An array of OptimizerShipment which contains valid values.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the records are created in the SCO_PDO_RQST_SHIPMENT table and they are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDOptimizerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/shipments'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation checks whether or not there was a successful previous PnD optimizer run.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided.
     * <br/>2. A valid planDate is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, returns true if any previous PnD optimizer run was successful; returns false if not.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    determinePnDOptimizerSuccessfulRun(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{terminalSicCd}/plan-dates/{planDate}/optimizer-successful-runs'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation executes an optimizer request with an optimization vendor and creates routes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number and a vendor-specific optimization request are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the vendor optimization request is executed and routes, stops, and activities are created in PnD.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    executePnDOptimizerRequest(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/requests'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves an optimizer's status.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid SIC or a valid runNbr is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the status of an optimizer will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDOptimizerStatus(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/status'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns an optimizer summary from SCO_PDO_RQST_SHIPMENT, SCO_PDO_RQST_DRIVER, and/or SCO_PDO_RQST_EQUIPMENT based on the optomizerSummaryTypeCd.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. An existing run number must be provided.
     * <br/>2. A optimizerSummaryTypeCd of either SHIPMENT, DRIVER, and/or EQUIPMENT is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a summary is returned from SCO_PDO_RQST_SHIPMENT, SCO_PDO_RQST_DRIVER, and/or SCO_PDO_RQST_EQUIPMENT.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDOptimizerSummary(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/summary'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * *** Only used by the Optimizer app ***
     * <br/>
     * <br/>This operation lists the enroute shipments with same addresses stored in the staging table SCO_PDO_ENRT_SAME_ADDR_SHPMT.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a list of enroute shipments with same addresses will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listOptimizerSameAddressShipments(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/same-address-shipments'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns all equipment for a given SIC.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of equipment associated with the SIC is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDEquipment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/equipment'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of Optimizer commodities and their associated dimensions.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of commodities and their dimensions is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerCommodityDimensions(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/commodities-dimensions'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * *** Only used by the Optimizer app ***
     * <br/>
     * <br/>This operation lists all the drivers stored in the staging table SCO_PDO_RQST_DRIVER that are to be 'reviewed by the planner'/ 'selected by the planner' to send to optimization.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a list of details for drivers will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerDrivers(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/drivers'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of equipment from the SCO_PDO_RQST_EQUIPMENT table based on the run number provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of equipment is retrieved from SCO_PDO_RQST_EQUIPMENT.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerEquipment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/equipment'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of optimizer parameters from table SCO_PDO_PARAMETER_SETTING.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid SIC is provided.
     * <br/>2. A valid OptimizerParameterTypeCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a list of optimizer parameters will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerParameters(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/parameters'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns the collection of all stops associated with a specific optimizer route.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid route instance ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then all pickup stops meeting the criteria are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerPickupStops(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-routes/{routeInstId}/pickup-stops'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists metrics and routing exceptions associated with an Optimizer run.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number and sequence number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a set of metrics and a list of routing exceptions will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerResults(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/sequence/{runSequenceNbr}/results'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation is called by the Ensemble process which retrieves the routes from PDO staging tables in order to create Edge PnD routes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of routes and their details will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerRoutes(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/sequence/{runSequenceNbr}/routes'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a list of Optimizer routes and associated stops and shipments for the given list of route instance IDs.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An optional list of route instance IDs is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of routes and their details will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerRoutesByRouteInstIds(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-routes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * *** Only used by the Optimizer app ***
     * <br/>
     * <br/>This operation lists all the shipments stored in the staging table SCO_PDO_RQST_SHIPMENT that are to be 'reviewed by the planner'/'selected by the planner' to send to optimization.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a list of details for optimizer shipments will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDOptimizerShipments(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/shipments'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operations lists the pickup positioning points for a service center and plan date.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center SIC is provided.
     * <br/>2. A valid planning date is provided.
     * <br/>3. If provided, zoneInd is valid.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, pickup points are returned for the service center, the service center plus its zones, or only the zones of the service center depending on the value of zoneInd.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDPositioningPoints(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-center/{sicCd}/plan-dates/{planDate}/positioning-points'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation starts the PnD Optimizer using a run number.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the run number is valid, then the PnD Optimizer will be started.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startPnDOptimizer(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation updates selected drivers based on the run number and sequence number for use in the Optimizer in SCO_PDO_RQST_DRIVER.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number, valid DSR sequence number, driver employee ID, and include indicator must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the optimizer equipment entry in the table SCO_PDO_RQST_DRIVER is updated with the data from the request.  This service returns a null response.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDOptimizerDrivers(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/driver'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates updates selected trailer equipment based on the run number and sequence number for use in the Optimizer in SCO_PDO_RQST_EQUIPMENT.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number, valid sequence number, equipment ID, and include indicator must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the optimizer equipment entry in the table SCO_PDO_RQST_EQUIPMENT is updated with the data from the request.  This service returns a null response.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDOptimizerEquipment(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/equipment'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates a PnD route from a route in the optimizer results.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid optimizer route details are provided.  The route detail contains an optimizer route and associated array of route stop details.  Each route stop detail contains a stop and associated activities.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the route, stops, and activities are updated in PnD.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDOptimizerRoute(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/routes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     *
     * <br/>			This operation updates the pupOnlyInd, liftgateInd and proEtaTmst fields of the shipments in SCO_PDO_RQST_SHIPMENT that were specified in the request and that are associated to the specified runNbr.
     * <br/>
     * <br/>			Pre-condition:
     * <br/>			1. A list of PdoRequestShipment is specified with shipmentInstId, liftgateInd, pupOnlyInd and proEtaDateTime fields.
     * <br/>
     * <br/>			Post-conditions:
     * <br/>			1. If successful, the optimizer shipments entries in the table SCO_PDO_RQST_SHIPMENT are updated with the data from the request.  This service returns a null response.
     * <br/>			2. Otherwise an appropriate error message is returned.
     * <br/>
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDOptimizerShipments(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/shipments'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the status associated with an existing run optimizer entry in SCO_PDO_CONTROL.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. An existing run number and a valid new status code must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the status of the optimizer run is updated in the SCO_PDO_CONTROL table and a response is returned with the run number.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDOptimizerStatus(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/statuses'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the optimization route results in SCO_PDO_RSPNS_ROUTE and inserts exception details in SCO_PDO_EXCEPTION.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid route results and exception details must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the optimization route results are updated in the SCO_PDO_RSPNS_ROUTE table, and exception details are inserted in SCO_PDO_EXCEPTION. This service returns a null response.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    upsertPnDRouteResults(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/route-results'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation creates route records in SCO_PDO_RSPNS_HDR.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid OptimizerResult must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, a new record is created in SCO_PDO_RSPNS_HDR.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createRoutesFromOptimizer(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/optimizer-runs/{runNbr}/sequence/{runSequenceNbr}/routes'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation assigns one or more hook equipment stops to a trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid dock trip instance ID must be provided.
     * <br/>2. One or more valid hook instance ID are provided and the associated hook requests exist in the system.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the hook equipment stops are added to the trip.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    assignHookEquipmentToTrip(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/hook-equipment'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation assigns one or more pickup stops to a dock drop route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid dock drop route instance ID must be provided.
     * <br/>2. One or more valid pickup request instance ID are provided and the associated pickup requests exist in the system.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the pickup stops are added to the dock drop route.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    assignPickupsToDockDropRoute(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/{routeInstId}/pickups'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation assigns one or more pickup stops to an existing trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip exists and the trip ID is provided.
     * <br/>2. A list of one or more pickup request instance IDs is provided and those pickup requests exist in the system
     * <br/>3. A pickup request status is provided whose value is either 'ASGN' or 'PREA'.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the pickup stops are either added as new stops or merged with existing stops to the trip based on whether or not the shipper is already on an existing stop.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    assignPickupsToPnDTrip(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/pickups'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service autocompletes a delivery stop when the cartage carrier sends a delivery status message.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deliveryStop and that TripNode stop exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the delivery stop is autocompleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    autocompleteDeliveryStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/delivery-stops/auto-completions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes an equipment stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided and the trip stop exists.
     * <br/>3. A permitted trip node activity code.
     * <br/>4. The trip stop is provided and the stop exists.
     * <br/>5. A list of DropStopEquipment is provided, which contains the activity and equipment related to that activity.  The data must be valid along with both the activity and equipment.  They must be in the correct status.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the equipment stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeEquipmentStop(request, pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/equipment-stops'), {
            pathParams: pathParams,
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a delivery stop.  It is used by the PnD application.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deliveryStop and that TripNode stop exists.
     * <br/>3. A list of delivery activity information for all the deliveries to be completed on that stop.
     * <br/>4. A list of StopActivityEquipment is provided, which contains the activity and equipment related to the delivery activity.  The data must be valid along with both the activity and equipment.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the equipment stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeDeliveryStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/delivery-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a dock pickup.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Valid dock visit details are provided.
     * <br/>2. A list of dock activity information for all the shipments involved in the dock pickup.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the dock pickup is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeDockPickup(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/dock-pickups/completions'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a hook-loaded stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deviceTripStop and that stop exists.
     * <br/>3. A list of DeviceTripStopActivity is provided with the deviceTripStop. The list contains pickup activities and equipment related to the hook-loaded stop.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the hook-loaded stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeHookLoadedStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/hook-loaded-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a pickup stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the deviceTripStop and that stop exists.
     * <br/>3. A list of DeviceTripStopActivity is provided with the deviceTripStop. The list contains activities and equipment related to the pickup stop.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the pickup stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completePickupStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/pickup-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes a drop stop and activity for a specific customer stop.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the stop and drop activity are completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completePnDDropStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/drop-activity/completions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This service completes a spot-loaded stop.  It is used by the PnD application.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid trip node sequence number is provided with the spotLoadedStop and that TripNode stop exists.
     * <br/>3. A list of delivery activity information for all the deliveries to be completed on that stop.
     * <br/>4. A list of ActivityEquipment is provided, which contains the activity and equipment related to the spot-loaded activity.  The data must be valid along with both the activity and equipment.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the spot-loaded stop is completed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeSpotLoadedStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/spotloaded-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation completes or returns a trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID, trip complete timestamp, and indicator if invoked from handheld are provided.
     * <br/>2. The trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip information will be updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    completeTrip(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/completions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation converts selected activities for a stop from a specific activity type to another based on the request.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A source trip node activity code is on the request along with a target trip node activity code.
     * <br/>3. A valid trip node sequence number is required and the associated TripNode stop must exist.
     * <br/>4. A valid list of trip node activity sequence numbers for the activities selected by the user to be converted is supplied.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then all the activities are converted to the target activity type.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    convertStopActivities(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/activity-conversions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation converts a list of regular customer-based stops to cartage stops.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. At least one valid trip node sequence number is required and the associated TripNode stop must exist.
     * <br/>3. A valid CMS carrier and location ID for it are provided and the associated carrier and carrier location exist.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the stops on the request are converted to cartage stops.  If the trip is dispatched, then events are sent to the driver to notify them
     * <br/>of the stop change and the OFDV movement in Shipment is deleted.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    convertToCartageStop(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/stops/cartage-conversions'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation inserts the delivery receipt DMS scanned document information into the Exadata Tables for the audit process.
     * <br/>			​
     * <br/>Pre-condition:
     * <br/>1. A valid deliverySpecialService is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, record would be inserted into Exadata Tables
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createDeliveryShipmentForAudit(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/audit'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a dock drop pickup request along with any shipment information when provided.
     * <br/>
     * <br/>
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid pickup location, pickup date, pickup ready and close times, and pickup type are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a pickup request record is created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createDockDropPickupRequest(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/dock-drops'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a drop stop with drop location and activity for the list of customer stops provided.  This service is only for use in Edge; it is not to be used by Mobile SCO.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid sicCd and trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then either a new stop and drop activity are created or an existing compatible stop is created for that customer with the drop activity.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createDropStops(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/trips/{tripInstId}/stops/drop-activity'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates DSR check-in documents for a trip.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd along with at least one tripInstId must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the documents are created and the list of associated tripInstIds and document data are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createDsrCheckInDocuments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/dsr-check-in-documents'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a City Operations event based on type and sub-type codes along with the details on the request.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid OpsEvent is fully populated on the request to meet the needs for the business case represented by the type and sub-type codes.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the event is created and the event instance ID is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createOpsEvent(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ops-events'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates one dispatch group and associates it with one dispatch group region.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Valid dispatch group information is on the request: SIC, name, and description.
     * <br/>2. An optional region with a region ID and that region exists.  If this region ID is on the request serves as a forceful override and it will be associated with the dispatch group on the request regardless of what actual dispatch group with which that region may currently be associated.
     * <br/>3. A valid list of latitude-longitude points are on the request and correctly represent the polygonal shape of the region.  This list is required and must always be on the request.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then one dispatch group is created and the region is associated with it.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDDispatchGroup(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/dispatch-groups'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates Trip (Required), Trip Drivers, Trip Nodes, Routes and Equipment (optional)
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid Trip, Trip Drivers, Trip Nodes, Routes and Equipment
     * <br/>2. SIC code and trip date are required.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip information will be created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDTrip(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation delete a drop location using a drop location instance id.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid drop location instance ID is provided and the drop location exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the drop location is deleted.
     * <br/>2. Otherwise a list of error messages is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteDropLocation(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drop-locations/{dropLocationInstId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation deletes a dispatch group and sets the associations in trips and dispatch areas to null.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid group ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the dispatch group is deleted and disassociated from trips and dispatch areas.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deletePnDDispatchGroup(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/dispatch-groups/{groupId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation dispatches the trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a trip is dispatched.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    dispatchPnDTrip(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/dispatches'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation returns the driver's actual location based on the latest information from the handheld device.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid employeeId is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the driver's actual location is returned in the response.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getActualDriverLocation(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drivers/{employeeId}/actual-location'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a list of trips and their associated stops that a given cartage carrier location is used on.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Either a valid carrier location ID or CIS customer number is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of trips with trip nodes where that carrier location or CIS customer location for it are used are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getCarrierLocationTrips(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/cartage-locations'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the delivery shipment special services for audit review.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid shipmentInstId is provided.
     * <br/>			​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated record is retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getDeliveryShipmentForAudit(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/audit'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the delivery shipment special services for audit review for the requested proNbr.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid proNbr is provided.
     * <br/>            ​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated delivery shipments for audit are retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getDeliveryShipmentsForAuditByPro(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/{proNbr}/audit'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the drop location details.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1.  A valid drop location instanceId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then details for a drop location are retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getDropLocation(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drop-locations/{dropLocationInstanceId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Gets all the preferences for automated printing of PnD manifest, FBDS, DSR check-in and Out-bound Break-out Reports. The attribute includeZoneIndicator is an optional indicator to include zones.
     * <br/>
     * <br/>Precondition:
     * <br/>1. A valid SIC code and an optional indicator includeZoneIndicator is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Auto print preferences for the given SIC of parent SIC(if exists)are returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getServiceCenterPrintPreferences(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/print-preferences'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns stop information for dock drop and dock pickup routes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one valid route instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then all the route dock stops are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listDockRouteStops(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/dock-routes/stops'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation list all drop locations for a sic code (required) with a zone code parameter (optional).  It is possible in the system for some drop locations to be stored for a zone SIC code, so consumers need to send the correct zone code parameter value to yield the correct list of locations.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1.  A valid SIC code is provided.
     * <br/>2.  An optional ZoneIndicatorCd is provided with the desired value; if one is not provided the service assumes a value of 'SIC Only', which will select drop locations without respect to zones.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of drop locations related to the SIC (and any zones is requested by the ZoneIndicatorCd value) are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listDropLocations(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/drop-locations'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation list hook equipment stops based on statusCds query for a sic code (required) with a zone code parameter (optional).
     * <br/>
     * <br/>Pre-condition:
     * <br/>1.  A valid SIC code is provided.
     * <br/>2.  An optional ZoneIndicatorCd is provided with the desired value; if one is not provided the service assumes a value of 'SIC Only', which will select unassigned hook equipment stops without respect to zones.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of unassigned hook equipment stops related to the SIC (and any zones is requested by the ZoneIndicatorCd value) are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listHookEquipmentStops(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/hook-equipment-stops'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Using standard Elastic XRT search functionality, this operation lists the concentration of latitude-longitude points representing actual delivery locations and a count of the number of deliveries at that point from a specific point in time; this is the location's delivery density.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid XRT search header is provided.
     * <br/>2. A valid XRT search filter is provided with the hostDestSicCd.values populated with a sic code list containing the host destination SIC code plus any zone SICs.
     * <br/>3. A valid density baseline date is provided as a string and in the format of yyyy/mm/dd.  This date is used to determine the scope of the density, which will be seven days from the baseline date, but is exclusive of this date.  If the density baseline date is not provided, then the current system date will be used instead.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of the delivery density is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDDeliveryDensity(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/dispatch-groups/density'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation lists dispatch groups for a list of SICs.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid list of SICs is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of dispatch groups is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDDispatchGroups(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/dispatch-groups'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a summarized list of trips and their attributes for the dispatch trip board.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input provided is valid, the requested trips and its attributes are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDDispatchTrips(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/dispatch-trips'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns all equipment for a given SIC and plan date.  It retrieves information from the SMART 3rd party API.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center and plan date are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of drivers associated with the SIC and plan date is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDDrivers(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/pnd-drivers'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists equipment for a SIC that has been spotted or dropped.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of all equipment that has been spotted or dropped, at a customer site or drop location, for a SIC is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSpotOrDropEquipment(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/equipment/spot-or-drop'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists all the drivers who are on a trip in Dispatch or Returning status.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd and tripDate must be provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the requested summary for the drivers is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listTripDrivers(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/trip-drivers'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists trip node activities.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one valid search criterion is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of Trip Node Activities that matched the filter criteria are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listTripNodeActivities(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trip-node-activities'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation reassigns stops from one trip to another.  This only allows the reassignment of equipment and pickup stops.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid old trip exists and the trip instance ID is provided.
     * <br/>2. A valid new trip exists and the trip instance ID is provided.
     * <br/>3. A pickup request status is provided whose value is either 'Pre-assigned' or 'Dispatched'.
     * <br/>4. A set of valid Stop IDs corresponding to Trip Nodes.
     * <br/>5. A flag indicating whether it overrides a soft error or not; 'Y' or 'N' respectively.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the pickup stops are swapped between the old and new trip provided.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    reassignPnDPickupStops(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/reassigned-pickup-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation reschedules or cancels the list of pickup requests. If it was the only pickup request on the stop, then this unassigns the stop from the trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid list of pickup request details must be provided.
     * <br/>2. Either the reschedule indicator is set to true or the value of the pickup request status code provided is Cancelled.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the requested pickup requests are rescheduled or cancelled.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    rescheduleOrCancelPickups(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/rescheduled-or-cancelled'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation starts the on demand driver location to get the driver's current location.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid employeeId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then nothing is done.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startDriverLocation(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drivers/{employeeId}/on-demand-driver-location'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation unassigns the list of pickup requests from a stop.  If it was the only pickup request on the stop, then this unassigns the stop from the trip.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid list of pickup request instance IDs must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the requested pickup requests are unassigned from the stop.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unassignPickups(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/unassignment'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation unassigns the list of requested activities from a stop.  If it was the only activity on the stop, then this unassigns the stop from the trip.  If the activities are not provided, then all activities are unassigned from the stop.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID and list of trip node sequence numbers must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the requested activities are unassigned from the stop.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unassignStopActivity(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/stops/activities/unassignment'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the line items of a pickup or hook load activity. It accepts the new list of activity line items and performs add, update, delete or move as needed.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID, trip node sequence number, and trip node activity sequence number must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the activity line items are updated.
     * <br/>2. Otherwise, an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateActivityLineItems(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/stops/{tripNodeSequenceNbr}/activities/{tripNodeActivitySequenceNbr}/line-items'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates or creates (if non-existent) the driver's current location.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Information about the current location of the driver is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the driver's current location is updated or created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateCurrentDriverLocation(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drivers/{employeeId}/current-location'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the delivery shipment audit status after the correction is applied.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid ShipmentAuditStatusCd is provided.
     * <br/>			​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated record is updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateDeliveryShipmentAuditStatus(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/audit-status'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the delivery shipment special services with with selections made by auditors.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid deliverySpecialServiceAudit is provided.
     * <br/>			​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the associated record is updated.
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateDeliveryShipmentFromAudit(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/audit'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates a drop stop with drop location and activity for the list of customer stops provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd and trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the stop and drop activity are updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateDropStops(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/trips/{tripInstId}/stops/drop-activity'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates one dispatch group and associates one dispatch group region with it.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Valid dispatch group information is on the request: SIC, name, and description.
     * <br/>2. Dispatch group ID is on the request and a valid dispatch group with that ID exists.
     * <br/>3. An optional region with a region ID and that region exists.  If this region ID is on the request, it serves as a forceful override and it will be associated with the dispatch group on the request regardless of what actual dispatch group with which that region may currently be associated.
     * <br/>4. A valid list of latitude-longitude points are on the request and correctly represent the polygonal shape of the region.  This list is required and must always be on the request.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the dispatch group is updated along with the region associated with it.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDDispatchGroup(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/dispatch-groups'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the details for an equipment-related hook stop and is only called from Edge PnD.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. The SIC code, pickup request, drop location, equipment details, and route prefix are provided and valid.
     * <br/>3. The trip stop is provided and the stop exists.
     * <br/>4. A list of DropStopEquipment is provided, which contains the activity and equipment related to that activity.  The data must be valid along with both the activity and equipment.  They must be in the correct status.
     * <br/>5. An override warning indicator is provided to override any warning messages that occur during processing of this update.  A value of true will override any and all warnings.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then all the equipment-related activities on the request are processed for that stop on that trip.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDEquipmentHookStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/equipment-hook-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates Trip (Required), Trip Drivers, Trip Nodes, Routes and Equipment (optional)
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid PnDTrip.
     * <br/>2. A valid SIC code and trip date.
     * <br/>3. Dispatch group group Id
     * <br/>4. Confirm complete flag is required
     * <br/>5. Equipment check indicator is optional
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip information will be updated and success message will be returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDTrip(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the trip status.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID and trip status are provided.
     * <br/>2. The trip exists.
     * <br/>3. If the trip status is 'Return', then the estimated arrive timestamp should be provided and all stops must have already been completed.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the trip status is updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateTripStatus(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/status'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation creates or updates a drop location.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. For a new drop location, a valid SIC and unique customer location name must be provided.  The location name must be unique in the system since one that already exists will result in an error.
     * <br/>2. For an update of an existing drop location, a valid drop location ID is required.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the drop location is either created or updated in the system and a copy of the object is included in the response to this service.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    upsertDropLocation(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drop-locations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Upserts serviceCenter preferences for automated printing of PnD manifest, FBDS, DSR check-in and Out-bound Break-out Reports.
     * <br/>
     * <br/>Precondition:
     * <br/>1. A valid sicCd must be provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Print preferences for the given sicCd are upserted.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    upsertServiceCenterPrintPreferences(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/print-preferences'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation assigns a dispatch group to the trip instance ID provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the dispatch group is set for the trip instance ID provided and the former's ID is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    assignDispatchGroupToTrip(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/dispatch-group'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation allows one to assign shipments to an existing route
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided as a path parameter
     * <br/>2. A valid route instance ID is provided as a path parameter
     * <br/>3. A request body specfying the collection of delivery shipment instance ids to be assigned to that route
     * <br/>
     * <br/>Post-condition:
     * <br/>1. The modified Trip, with the associated route instance ID, is returned along with the updated accumulators.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    assignShipmentsToRoute(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/routes/{routeInstId}/shipments'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation performs route balancing actions consisting of stop sequencing within a route, reassigning a stop from one route to another, and unassigning a stop from a route.  The request document is organized by the type of route (new or existing) as well as Unassigned Stops.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided.
     * <br/>2. A valid request body specifying the state of the routes in the Route Balancing Board is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, the specified route balancing actions are performed and committed or rolled-back as a single atomic database transaction.  The response will include the updated state of the Trips/Routes involved in the route balancing activities.
     * <br/>2. Otherwise an appropriate error message will be returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    balancePnDRoutes(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/routes/balancing'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation returns the collection of metrics associated with the specified trip or route.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. The values provided in the request must be valid.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, all metrics associated with the specified trip are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    calculatePnDTripMetrics(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/metrics'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation handles the route conversions of planning, delivery, and cartage routes.
     * <br/>	It converts planning to delivery/cartage and vice versa as well as cartage to delivery and vice versa.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid route instance ID and carrier ID must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the requested route will be converted to the specified route category.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    convertRoute(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/category-conversions'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation generates manifest documents for routes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd along with an equipmentInstId or a list of routeInstIds must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the documents are created and the list of associated routeInstIds and document data are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPnDManifestDocuments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pnd-manifest-documents'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates a new route and associates it with a new trip from a list of shipments and exports, the route and trip instance IDs, and the list of stops.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. The route prefix and suffix, date, and category code for the route are imported.
     * <br/>2. A valid area instance ID is imported.
     * <br/>3. An optional list of shipment instance IDs are imported.
     * <br/>4. A SIC is imported.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a trip is created; a route is created and associated with the trip.  If shipments were imported, they are associated with the route/trip and stops are created.  The route and trip instance IDs are exported.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createNewRoute(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/new-routes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation creates pickup positioning points.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Valid positioningPoints are provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then pickup positioning points are created.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    createPositioningPoints(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/positioning-points'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation deletes routes and possibly trips associated with the routes.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center and route instance IDs are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, all routes, and possibly trips, that meet the criteria are deleted.
     * <br/>2. Otherwise a list of error messages for why routes cannot be deleted is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deletePnDRoutes(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/routes'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation cancels a trip and deletes all its dependent child objects.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, the following are deleted: Trip Equipment, Trip Drivers, Route-Trip records, all routes of the trip, all dependent route-shipment records,
     * <br/>Sco Positioning Nodes, Sco Trip activity records, trip Notes, Trip Node Activity, and Trip Nodes.  Lastly, the Trip is deleted and an event is created
     * <br/>to notify the driver that the trip has been cancelled.
     * <br/>2. Otherwise a list of error messages for why the routes cannot be deleted is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deletePnDTrip(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/trips/{tripInstId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Evaluates the routes based on the type indicator and returns the metrics.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. Valid trips were passed on the request containing trips, routes, and any stops.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. A list of trip metrics are returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    evaluatePnDRouteBalancing(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/route-balancing'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves the final consignee destination arrival timestamp for the PRO provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid PRO is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the final consignee destination arrival timestamp for the PRO will be returned.
     * <br/>2. Otherwise a 404 - Not Found error message will be returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getFinalConsigneeDestinationArrivalTmstByPro(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/final-destination-arrival-times/{proNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the tripInstId for each pickup request provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. At least one valid pickupInstId is provided.
     * <br/>2. Each pickup has been assigned to the trip.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then each tripInstId for the requested pickupInstId is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPickupTrips(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/trips'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves a driver location and pertinent information about the driver.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid employeeId is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the driverLocation for requested employeeId is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * <br/>2.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDDriverLocation(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drivers/{employeeId}/location'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the selection profile for the selectionProfileId. The selection profile is an inbound planning profile.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid selectionProfileId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the selection profile, for the ID provided, will be returned.
     * <br/>2. Otherwise a 404 - Not Found error message will be returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDInboundSelectionProfile(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/selection-profiles/{selectionProfileId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the dock drop indicator for the pickupRequestInstId provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid PUR instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the dock drop indicator is retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDPickupDockDropInd(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/{pickupRequestInstId}/dock-drop-ind'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the driver information assigned to a pickup request.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid pickupInstId is provided.
     * <br/>2. The pickup has been assigned to a trip.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the driver information for the requested pickupInstId is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDPickupDriver(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/{pickupInstId}/driver'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a single route for the route instance ID provided.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided.
     * <br/>2. A valid route instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. The requested route, with the associated route instance ID, is retrieved.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDRoute(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/{routeInstId}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a single instance of a stop associated with a specific route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid route instance ID is provided.
     * <br/>2. A valid stop sequence number is provided
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the requested route stop is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * <br/>2.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDStop(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/{routeInstId}/stops/{tripNodeSequenceNbr}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves trip details.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided.
     * <br/>2. A valid trip instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then details for a trip are retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * <br/>
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDTrip(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/trips/{tripInstId}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the DSR location history for a trip along with the current location of the driver.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If successful, then the trip DSR location history and current location of the driver are retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPnDTripDsrLocations(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/dsr-locations'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves trip details for the corresponding equipment.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid equipment instance ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then details for the associated trip are retrieved.
     * <br/>2. Otherwise an appropriate error message is returned.
     * <br/>
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getTripByEquipmentInstId(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentInstId}/trip'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves the drivers for the corresponding trip.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the details for the drivers on the trip are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * <br/>
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getTripDrivers(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/drivers'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Using standard Elastic XRT search functionality, this operation lists the shipments in the order in which the user entered the associated PRO numbers.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. At least one valid proNbr must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the list of the delivery shipments will be returned in the order in which their PROs were entered.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listDeliveryShipmentsByPro(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/pros'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation returns all delivery shipments by selectionProfileId for a service center and planDate.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided
     * <br/>2. A valid selectionProfileId is provided
     * <br/>3. A valid planning date is provided
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All delivery shipments meeting the criteria are returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listDeliveryShipmentsByProfile(request, pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/selection-profiles/{selectionProfileId}/delivery-shipments'), {
            pathParams: pathParams,
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation retrieves a list of driver locations and pertinent information about each driver.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input provided is valid, the requested route summary is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDDriverLocations(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/drivers/locations'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists all of the unique trailers for the requested host destination SIC from alias unassignedDeliveriesPlanByTrailer.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid XRT/Elastic search filter is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If succesful, then all trailers that match the hostDestinationSicCd from unassignedDeliveriesPlanByTrailer are listed.
     * <br/>2. If the input is invalid, then an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDInboundPlannerTrailers(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/inbound-planner-trailers'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation returns all the inbound selection profiles for the SIC provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, all inbound selection profiles meeting the criteria are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDInboundSelectionProfiles(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/selection-profiles'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists delivery stops that can potentially be added to the selected route based on their proximity.
     * <br/>​
     * <br/>Pre-conditions:
     * <br/>1. A valid SIC code is provided.
     * <br/>2. A valid plan date is provided
     * <br/>3. A list of stops currently assigned is provided.
     * <br/>4. An optional cutoff time is provided. Otherwise default to 12:00 pm.
     * <br/>​
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of delivery shipments in the SIC plan, that qualify as a match to the selected input route stops, is returned.
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDProximalAddressStops(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/stops/proximal-addresses'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation returns all routes that need to be planned for delivery today.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid service center is provided
     * <br/>2. A valid planning date is provided
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All routes meeting the criteria are returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDRoutes(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/routes'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists active routes for a service center based on whether the route has any stops within the provided geofence.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid list of geofences and host destination SICs.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then lists of route and trip instance IDs are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDRoutesByGeofence(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/geo-fences/routes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation returns the collection of metrics associated with the specified service center.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid service center is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All metrics associated with the specified service center are returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDServiceCenterMetrics(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/metrics'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns the collection of all stops associated with a specific route.
     * <br/>
     * <br/>Pre-condition:
     * <br/>- A valid route instance ID is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>- All stops meeting the criteria are returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDStops(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/{routeInstId}/stops'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves a list of areas for a service center and suggests the next route name for the area.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid parent SIC is provided.
     * <br/>2. A valid planning date is provided.
     * <br/>3. A boolean to specify if satellites should be returned.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. In the input provided is valid, the list of suggested route names is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDSuggestedRouteNames(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/suggested-route-names'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation retrieves lists suggested drivers/trips for pickup.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid pickupInstId is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of recommended trips is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDSuggestedTripsForPickup(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pickup-requests/{pickupInstId}/suggested-trips'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns the collection of all trips for a SIC and trip date.  The attribute includeZoneCode is an optional indicator to include zones.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid SIC code and trip date is provided in a trip object.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All trips associated with that SIC and date are returned.
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDTrips(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/trip-dates'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation lists all stops with their details for a trip based on the trip instance ID provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid trip instance ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the requested trip, with the associated trip instance ID, and its stops are listed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDTripStops(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/stops'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation aggregates pickups by pickup callNbr/purInstId to create a list of unassigned pickups.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid XRT/Elastic search filter is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All pickups meeting the criteria are aggregated and returned.
     * <br/>2. If the input is invalid, then an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDUnassignedPickups(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/aggregations/unassigned-pickups'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation aggregates shipments by consignee to create a list of unassigned stops.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid XRT/Elastic search filter is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All shipments meeting the criteria are aggregated and returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listPnDUnassignedStops(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/aggregations/unassigned-stops'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation lists shipments on a trailer for the given equipmentInstId.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid equipment instance ID is provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the shipments associated with the trailer are returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listShipmentsOnTrailer(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/equipment/{equipmentInstId}/shipments'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation merges a list of stops from a trip into a target stop on the same trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1.  A valid trip instance ID is provided and the trip exists.
     * <br/>2.  A valid target trip node sequence number is provided and that stop exists.
     * <br/>3.  A valid list of trip node sequence numbers representing the stops to be merged into the target stop are provided and those stops are valid and exist.
     * <br/>4.  An override flag is provided to indicate if any warnings should be overridden.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then all the shipments from the list of source stops are merged into the target stop.  If the customer on the target stop is not the same as the customer on the list of source stops, a warning message is created, no further processing occurs, and the warning is exported with an http 200 response; if the override warning flag is true, this edit is skipped and normal processing continues.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    mergePnDStops(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/stop-merges'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Reassigns a list of shipments from one route to another.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid old route instance ID is provided and that route exists.
     * <br/>2. A valid new route instance ID is provided and that route exists.
     * <br/>3. A valid list of shipments instance ID numbers is provided that represent the list of shipments that need to be reassigned.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the stops are reassigned from the old route to the new route.
     * <br/>2. Otherwise, an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    reassignPnDShipments(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/{routeInstId}/reassigned-shipments'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Reassigns a list of stops to a route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid route instance ID is provided and that route exists.
     * <br/>3. A valid list of trip node sequence numbers is provided that represent the list of nodes that need to be resequenced.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the stops are removed from the route.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    reassignStops(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/stops/reassign'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Updates the sequence of the freight on the trailer by the order of their stops on a specific trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid SIC cd is given
     * <br/>2. A valid geocoordinates is given for the SIC
     * <br/>3. A valid trip and its routes to be resequenced are given in the form of RouteBalancingTrip
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the stops are resequenced based on the order input into the operation.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    resequenceFreightByTrip(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/stops/resequence'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation returns driver information that matches an employee name search string and plan date.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid partial or full employee name and plan date are provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then a list of drivers matching the employee name and plan date is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchDrivers(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/drivers'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation splits shipments from one existing stop off into a new stop.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1.  A valid trip instance ID is provided and the trip exists.
     * <br/>2.  A valid trip node sequence number for the stop is provided and the stop exists.
     * <br/>3.  A list of shipment IDs and trip node activity sequence numbers is provided and those activities exist.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, the shipments are moved to either the targetStop (if specified) or a new stop for the same customer.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    splitPnDTripStop(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/{tripInstId}/split-stops'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation performs a transfer action for a list of shipments IDs, and if needed will update the shipment external status.
     * <br/>			​
     * <br/>Pre-conditions:
     * <br/>1.  At least one valid shipment identifier is provided.
     * <br/>2.  A valid deliveryQualifierCd is provided.
     * <br/>3.  Optionally, the updateShmExternalStatusInd can be set to true, and if so a valid list of externalShmStatusIds is required.
     * <br/>			​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the status of the shipment(s) are updated.
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    transferShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/shipments/transfers'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     *
     * <br/>			This service updates the GeoCoordinates for the specified customer in CIS and ELASTIC.
     * <br/>			​
     * <br/>			Pre-conditions:
     * <br/>			1. A valid customer account number is provided.
     * <br/>			2. A valid latitude and a valid longitude are provided.
     * <br/>			​
     * <br/>			Post-conditions:
     * <br/>			1. If successful, the customer GeoCoordinates are updated.
     * <br/>			2. Otherwise an appropriate error is returned.
     * <br/>
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateCustomerGeoCoordinates(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/customers/{customerAcctId}/geo-coordinates'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Given the equipment ID, SIC code, and excluded indicator (boolean), updates the excluded trailer status in the SCO_DLVR_SHM table.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid SIC is provided.
     * <br/>2. A valid equipment ID is provided.
     * <br/>3. A valid excluded indicator is provided.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. The SCO_DLVR_SHM table is updated accordingly as defined by the inputs.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateDeliveryShipmentExcludedTrailerInd(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment/service-centers/{sicCd}/equipment/{equipmentInstId}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Updates a list of PnD Routes and release flag on each route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid request body specifying the list of routes to be updated.
     * <br/>
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. A route is updated with the door/trailer number or dock location with the nearest door.
     * <br/>2. The release indicator is updated as well per the request.
     * <br/>3. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDRoutes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the shipment route status to either Stage or Unstage and updates ScoDlvrShipment and also SHM*.
     * <br/>			​
     * <br/>Pre-conditions:
     * <br/>1. At least one valid shipmentInstId is provided.
     * <br/>2. A valid routeTypeCd is provided.
     * <br/>			​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the shipment route status updated.
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDRouteStatusForShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/shipments/route-status'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the delivery status for shipments based on user action and inputs provided.
     * <br/>			​
     * <br/>Pre-condition:
     * <br/>1. At least one DeliveryQualifierShipmentStatusChange with valid data is provided.
     * <br/>			​
     * <br/>Post-conditions:
     * <br/>1. If successful, then the delivery status for the shipment(s) is updated and any soft warnings are returned in the response.
     * <br/>2. Otherwise an appropriate error is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updatePnDDeliveryStatusForShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/shipments/delivery-status'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation updates the stop sequence for the route on a trip.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID and list of stop sequences must be provided.
     * <br/>2. A valid route instance ID must be provided and the route must exist.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the stop sequence for the route will be updated.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateRouteStopSequence(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/stop-sequences'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation unassigns a list of shipments from a route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid route instance ID is provided and the route exists.
     * <br/>2. A valid list of shipments instance ID numbers is provided that represent the list of shipments that need to be unassigned from the route.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If the input is valid, then the shipments are removed from the route.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unassignPnDShipmentsFromRoute(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/{routeInstId}/unassigned-shipments'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Unassigns a list of stops from a route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID is provided and the trip exists.
     * <br/>2. A valid list of trip node sequence numbers is provided that represent the list of nodes that need to be resequenced.
     * <br/>
     * <br/>Post-condition:
     * <br/>1. If the input is valid, then the stops are removed from the route.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unassignStops(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/stops/unassign'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation determines if it's valid to create a new route.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>. A valid service center, planning date, route prefix and suffix must be provided.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. A true value is returned if the route is valid (i.e. NOT in use).
     * <br/>2. False is returned if the route is not valid.
     * <br/>3. If the data provided is invalid, an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    validatePnDRoute(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/route-validitions'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation generates the Outbound Breakout Manifest report for a SIC based on the additional parameters provided.
     * <br/>
     * <br/>Pre-condition:
     * <br/>1. A valid sicCd and at least one of the following must be provided: tripInstId, routeInstId, equipmentInstId, or dockVisitId.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the document data representing the report is returned.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    printOutboundBreakoutManifestReport(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-centers/{sicCd}/outbound-breakout-manifest-report'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation prints a PnD report; e.g. outbound manifest or DSR check in. This is a temporary API because it's implemented using Gen proxy.
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. A valid trip instance ID, line printer, and report type are provided.
     * <br/>2. The trip exists.
     * <br/>
     * <br/>Post-conditions:
     * <br/>1. If successful, then the indicated report will be printed.
     * <br/>2. Otherwise an appropriate error message is returned.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    printPnDReport(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/mobile/trips/{tripInstId}/pnd-reports'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for routes
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedRoutes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for Routes
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterRoutes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for Routes
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterRoutes(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/routes/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for trips
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedTrips(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for Trips
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterTrips(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for Trips
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterTrips(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/trips/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for service-center-metrics
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedServiceCenterMetrics(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-center-metrics/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for ServiceCenterMetrics
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterServiceCenterMetrics(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-center-metrics/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for ServiceCenterMetrics
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterServiceCenterMetrics(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/service-center-metrics/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for pnd-optimizer
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedPndOptimizer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pnd-optimizer/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for PndOptimizer
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterPndOptimizer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pnd-optimizer/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for PndOptimizer
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterPndOptimizer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/pnd-optimizer/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for current-dsr-location
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedCurrentDsrLocation(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/current-dsr-location/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for CurrentDsrLocation
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterCurrentDsrLocation(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/current-dsr-location/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for CurrentDsrLocation
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterCurrentDsrLocation(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/current-dsr-location/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Delete Rule
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteRule(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules/{ruleInstId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Execute a rule
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    executeRule(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules/execute'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Get a Rule by identifier
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getRule(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules/{ruleInstId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     *
     * <br/>			Get full rule with rulesets, expressions and outcomes by the rule name.
     * <br/>			Pre-conditions:
     * <br/>				1. The current ruleName must be provided.
     * <br/>
     * <br/>			Post-conditions:
     * <br/>				1. If input value is valid, then the rule will be returned.
     * <br/>				2. Otherwise an appropriate error message will be returned.
     * <br/>
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getRuleByName(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules/name/{ruleName}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Lists all rules that are available.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRules(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Lists all rules filtered by supplied expressions
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRulesByExpressions(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules/rulesets'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Lists all rules that are available by ruleset name with inclusion and exclusion.
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRulesByRulesetName(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules/ruleset-names'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Lists all rules ruturning minimum properties for rule execution
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listRulesForExecution(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/executable-rules'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Upsert Rule
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    upsertRule(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-rules/rules'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Delete expired messages in the queue
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteExpiredMessages(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * This operation returns counts for the number of ensembles in each of the status cd values.
     * <br/>Pre-conditions:
     * <br/>1. Input search criteria may be provided.
     * <br/>Post-condition:
     * <br/>1. Counts for each ensemble status are returned.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getEnsembleStatusCounts(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/counts/{fromTmst}/{toTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns counts for PubSub statuses
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getPubSubStatusCounts(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/summary/counts/{fromTmst}/{toTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns counts for the number of messages pending in the queue.
     * <br/>Pre-conditions:
     * <br/>1. Queue name is provided.
     * <br/>Post-condition:
     * <br/>1. Count of all messages in ready and wait statuses
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getQueuePendingCount(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/counts/pending'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation return Status of Given Subscription
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getSubscription(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation returns a count of number of undelivered messages in this PubSub subscription
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getUndeliveredMessages(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/undelivered/counts'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * This operation updates one or more SUSPENDED Ensemble execution row to a status of KILLED, in order to indicate it is has been forced to stop processing.
     * <br/>Pre-conditions:
     * <br/>1. One or more Ensemble Instance Ids that is in a status of SUSPENDED is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. Ensemble record(s) updated to a Status of KILLED.
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    killEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/ensembles/kill'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * List all expired messages in the queue
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listExpiredMessages(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages/{enqueueFromTmst}/{enqueueToTmst}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the queues.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listQueues(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the Pub/Sub subscriptions.
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listSubscriptions(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Replay a message in expired status
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    replayMessage(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/messages/{messageId}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * This operation Restarts a STARTED or NOT_STARTED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. STARTED or NOT_STARTED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    restartEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/{ensembleInstId}/restart'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * This operation Resumes a SUSPENDED Ensemble execution based on input Ensemble Instance Id.
     * <br/>Pre-conditions:
     * <br/>1. Input Ensemble Instance Id is supplied and valid.
     * <br/>Post-condition:
     * <br/>1. SUSPENDED Ensemble execution status is updated to STARTED and execution is initiated; if any Minuets exist for the Ensemble, those are initiated in sequence and status updates are performed.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    resumeEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/ensembles/{ensembleInstId}/resume'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start a queue.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startQueue(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Start or restart a PubSub subscription.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startSubscription(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/start'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop a queue. Stopping a queue will only stop the dequeuing process, it will continue to enqueue
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopQueue(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/queues/{queueName}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Stop a queue. Stopping a queue will only stop the dequeuing process, it will continue to enqueue
     * @param {?} request
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopQueueWebhook(request, pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/webhooks/queues/{queueName}/stop'), {
            pathParams: pathParams,
            queryParams: queryParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Stop a PubSub subscription.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopSubscription(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/pubsub/subscriptions/{subscriptionId}/stop'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.put(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUnassignedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUnassignedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUnassignedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for unassigned-deliveries
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUnassignedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UnassignedDeliveries
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUnassignedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UnassignedDeliveries
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUnassignedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUnassignedDeliveriesWithUnmapped(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-with-unmapped/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUnassignedDeliveriesWithUnmapped(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-with-unmapped/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUnassignedDeliveriesWithUnmapped(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-with-unmapped/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for unassigned-deliveries-with-unmapped
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUnassignedDeliveriesWithUnmapped(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-with-unmapped/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UnassignedDeliveriesWithUnmapped
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUnassignedDeliveriesWithUnmapped(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-with-unmapped/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UnassignedDeliveriesWithUnmapped
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUnassignedDeliveriesWithUnmapped(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-with-unmapped/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUnassignedDeliveriesPlanByTrailer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-plan-by-trailer/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUnassignedDeliveriesPlanByTrailer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-plan-by-trailer/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUnassignedDeliveriesPlanByTrailer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-plan-by-trailer/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for unassigned-deliveries-plan-by-trailer
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUnassignedDeliveriesPlanByTrailer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-plan-by-trailer/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UnassignedDeliveriesPlanByTrailer
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUnassignedDeliveriesPlanByTrailer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-plan-by-trailer/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UnassignedDeliveriesPlanByTrailer
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUnassignedDeliveriesPlanByTrailer(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unassigned-deliveries-plan-by-trailer/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportYetToBeDispatchedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/yet-to-be-dispatched-deliveries/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsYetToBeDispatchedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/yet-to-be-dispatched-deliveries/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchYetToBeDispatchedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/yet-to-be-dispatched-deliveries/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for yet-to-be-dispatched-deliveries
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedYetToBeDispatchedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/yet-to-be-dispatched-deliveries/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for YetToBeDispatchedDeliveries
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterYetToBeDispatchedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/yet-to-be-dispatched-deliveries/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for YetToBeDispatchedDeliveries
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterYetToBeDispatchedDeliveries(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/yet-to-be-dispatched-deliveries/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUnbilledShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unbilled-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUnbilledShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unbilled-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUnbilledShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unbilled-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for unbilled-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUnbilledShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unbilled-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UnbilledShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUnbilledShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unbilled-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UnbilledShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUnbilledShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unbilled-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportDeliveryShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsDeliveryShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchDeliveryShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for delivery-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedDeliveryShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for DeliveryShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterDeliveryShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for DeliveryShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterDeliveryShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUnmappedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unmapped-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUnmappedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unmapped-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUnmappedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unmapped-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for unmapped-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUnmappedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unmapped-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UnmappedShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUnmappedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unmapped-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UnmappedShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUnmappedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unmapped-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportAdminRouteShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/admin-route-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsAdminRouteShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/admin-route-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchAdminRouteShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/admin-route-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for admin-route-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedAdminRouteShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/admin-route-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for AdminRouteShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterAdminRouteShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/admin-route-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for AdminRouteShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterAdminRouteShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/admin-route-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUndeletedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/undeleted-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUndeletedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/undeleted-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUndeletedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/undeleted-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for undeleted-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUndeletedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/undeleted-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UndeletedShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUndeletedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/undeleted-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UndeletedShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUndeletedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/undeleted-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportPastDueShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/past-due-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsPastDueShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/past-due-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchPastDueShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/past-due-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for past-due-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedPastDueShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/past-due-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for PastDueShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterPastDueShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/past-due-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for PastDueShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterPastDueShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/past-due-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportUnpurgedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unpurged-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsUnpurgedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unpurged-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchUnpurgedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unpurged-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for unpurged-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedUnpurgedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unpurged-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for UnpurgedShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterUnpurgedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unpurged-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for UnpurgedShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterUnpurgedShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/unpurged-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportCrossDockTrailerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/cross-dock-trailer-shipments/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsCrossDockTrailerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/cross-dock-trailer-shipments/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchCrossDockTrailerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/cross-dock-trailer-shipments/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for cross-dock-trailer-shipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedCrossDockTrailerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/cross-dock-trailer-shipments/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for CrossDockTrailerShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterCrossDockTrailerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/cross-dock-trailer-shipments/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for CrossDockTrailerShipments
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterCrossDockTrailerShipments(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/cross-dock-trailer-shipments/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportDeliveryShipmentUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment-unfiltered/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsDeliveryShipmentUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment-unfiltered/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchDeliveryShipmentUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment-unfiltered/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for delivery-shipment-unfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedDeliveryShipmentUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment-unfiltered/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for DeliveryShipmentUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterDeliveryShipmentUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment-unfiltered/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for DeliveryShipmentUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterDeliveryShipmentUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/delivery-shipment-unfiltered/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Delete Elastic document for DeliveryShipment
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteElasticDocumentDeliveryShipment(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/delivery-shipment-unfiltered/_doc/{shipmentInstId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Get Elastic document from DeliveryShipment index for given primary key
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentDeliveryShipment(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/delivery-shipment-unfiltered/_doc/{shipmentInstId}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Get document count from DeliveryShipment index
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentDeliveryShipmentCount(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/delivery-shipment-unfiltered/_count'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Update Elastic document for DeliveryShipment
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateElasticDocumentDeliveryShipment(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/delivery-shipment-unfiltered/_doc/{shipmentInstId}/_update'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Export documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. All documents matching the filter will be exported as CSV or Excel format
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    exportEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/exports'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Aggregations from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>2. One of more fields are supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Aggregations for each supplied field
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listAggregationsEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/aggregations'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Search documents from elastic index
     * <br/>
     * <br/>Pre-conditions:
     * <br/>1. Filter criteria is supplied
     * <br/>
     * <br/>Post-condition:
     * <br/>1. Documents matching the filter will be returned
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    searchEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/searches'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Check changed documents against registered filters for ensemble-state-unfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    changesFeedEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/changes'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Register a filter with XRT for EnsembleStateUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    registerFilterEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/filters/register'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Unregister a filter with XRT for EnsembleStateUnfiltered
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    unregisterFilterEnsembleStateUnfiltered(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/ensemble-state-unfiltered/filters/unregister'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Delete Elastic document for EnsembleState
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    deleteElasticDocumentEnsembleState(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Get Elastic document from EnsembleState index for given primary key
     * @param {?} pathParams
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentEnsembleState(pathParams, queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}'), {
            pathParams: pathParams,
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Get document count from EnsembleState index
     * @param {?} queryParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    getElasticDocumentEnsembleStateCount(queryParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_count'), {
            queryParams: queryParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Update Elastic document for EnsembleState
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    updateElasticDocumentEnsembleState(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/elastic/ensemble-state-unfiltered/_doc/{instId}/_update'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * List all ensemble configurations
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listEnsembleConfigurations(dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/configurations'), {}, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * List the timers.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    listTimerEnsembles(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timer-ensembles/{name}/timers'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.get(apiRequest);
    }
    /**
     * Start a timer.
     * @param {?} request
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startTimerEnsemble(request, pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timers/{name}'), {
            pathParams: pathParams,
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Stop a timer.
     * @param {?} pathParams
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    stopTimerEnsemble(pathParams, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/timers/{name}'), {
            pathParams: pathParams
        }, dataOptions, httpOptions);
        return this.dataApiService.delete(apiRequest);
    }
    /**
     * Start ensemble by invoking API
     * <br/>        	PRE-CONDITION:
     * <br/>        		valid channelName is required
     * <br/>        		valid payload is required
     * <br/>        	POST-CONDITION:
     * <br/>        		Ensemble is started and an id is returned in the response
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startPnDOptimizerEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/CTY_PND_OPTIMIZER_CH/ensembles'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start ensemble by invoking API
     * <br/>        	PRE-CONDITION:
     * <br/>        		valid channelName is required
     * <br/>        		valid payload is required
     * <br/>        	POST-CONDITION:
     * <br/>        		Ensemble is started and an id is returned in the response
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startDriverLocationEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/CTY_DRIVER_LOCATION_CH/ensembles'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start ensemble by invoking API
     * <br/>        	PRE-CONDITION:
     * <br/>        		valid channelName is required
     * <br/>        		valid payload is required
     * <br/>        	POST-CONDITION:
     * <br/>        		Ensemble is started and an id is returned in the response
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startPrintDocumentEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/CTY_PND_PRINT_DOCUMENT_CH/ensembles'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start ensemble by invoking API
     * <br/>        	PRE-CONDITION:
     * <br/>        		valid channelName is required
     * <br/>        		valid payload is required
     * <br/>        	POST-CONDITION:
     * <br/>        		Ensemble is started and an id is returned in the response
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startRuleBasedRoutingEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/CTY_RULE_BASED_ROUTING_CH/ensembles'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * Start ensemble by invoking API
     * <br/>        	PRE-CONDITION:
     * <br/>        		valid channelName is required
     * <br/>        		valid payload is required
     * <br/>        	POST-CONDITION:
     * <br/>        		Ensemble is started and an id is returned in the response
     * <br/>
     * @param {?} request
     * @param {?=} dataOptions
     * @param {?=} httpOptions
     * @return {?}
     */
    startDeviceTripStopEnsemble(request, dataOptions, httpOptions) {
        const /** @type {?} */ apiRequest = this.generateApiRequest(this.getUrl('/symphony-ensemble/channels/CTY_DEVICE_TRIP_STOP_CH/ensembles'), {
            body: request
        }, dataOptions, httpOptions);
        return this.dataApiService.post(apiRequest);
    }
    /**
     * @return {?}
     */
    getEndPoint() {
        return this.configManager.getSetting(CityOperationsApiService.CityOperationsApiEndpoint);
    }
}
CityOperationsApiService.ɵfac = function CityOperationsApiService_Factory(t) { return new (t || CityOperationsApiService)(ɵngcc0.ɵɵinject(ɵngcc1.DataApiService), ɵngcc0.ɵɵinject(ɵngcc2.ConfigManagerService)); };
CityOperationsApiService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: CityOperationsApiService, factory: CityOperationsApiService.ɵfac });
CityOperationsApiService.CityOperationsApiEndpoint = 'cityoperationsApiEndpoint';
/** @nocollapse */
CityOperationsApiService.ctorParameters = () => [
    { type: DataApiService },
    { type: ConfigManagerService }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(CityOperationsApiService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc1.DataApiService }, { type: ɵngcc2.ConfigManagerService }]; }, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
const ɵ0 = { configFileUrl: './assets/config.json' };
class SdkCityOperationsModule {
}
SdkCityOperationsModule.ɵfac = function SdkCityOperationsModule_Factory(t) { return new (t || SdkCityOperationsModule)(); };
SdkCityOperationsModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: SdkCityOperationsModule });
SdkCityOperationsModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [
        CityOperationsApiService,
        { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
    ], imports: [[
            ConfigManagerModule
        ]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(SdkCityOperationsModule, [{
        type: NgModule,
        args: [{
                imports: [
                    ConfigManagerModule
                ],
                providers: [
                    CityOperationsApiService,
                    { provide: CONFIG_MANAGER_SERVICE_CONFIGURATION, useValue: ɵ0 },
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(SdkCityOperationsModule, { imports: function () { return [ConfigManagerModule]; } }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
/**
 * Path parameter which is used to assign a dispatch group to a trip.
 */
class AssignDispatchGroupToTripPath {
    constructor() {
        /**
         * The unique identifier associated with the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Response to the request to assign a dispatch group to a trip.
 */
class AssignDispatchGroupToTripResp {
    constructor() {
        /**
         * The dispatch group identifier assigned to the trip.
         * Required: true
         * Array: false
         */
        this.dispatchGroupId = undefined;
    }
}
/**
 * Path parameter which is used to assign hook equipment to a trip.
 */
class AssignHookEquipmentToTripPath {
    constructor() {
        /**
         * The unique identifier of a trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to assign hook equipment to a trip.
 */
class AssignHookEquipmentToTripRqst {
    constructor() {
        /**
         * The list of unique identifiers for the hook requests that will be assigned to the trip.
         * Required: true
         * Array: true
         */
        this.hookInstIds = undefined;
    }
}
/**
 * Path parameter which is used to assign pickups to a dock drop route.
 */
class AssignPickupsToDockDropRoutePath {
    constructor() {
        /**
         * The unique identifier associated with the dock drop route.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Request to assign pickups to a dock drop route.
 */
class AssignPickupsToDockDropRouteRqst {
    constructor() {
        /**
         * The list of unique identifiers for the pickup requests that will be assigned to the dock drop route.
         * Required: true
         * Array: true
         */
        this.pickupRequestInstIds = undefined;
        /**
         * The unique identifier of the cartage carrier that does the dock drop for a pickup.
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
    }
}
/**
 * Path parameter for the request to specify which trip will receive the pickups.
 */
class AssignPickupsToPnDTripPath {
    constructor() {
        /**
         * The unique identifier associated with the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Query parameters used to assign pickups to an existing trip.
 */
class AssignPickupsToPnDTripRqst {
    constructor() {
        /**
         * The list of unique identifiers for pickups requests to assign to the trip.
         * Required: true
         * Array: true
         */
        this.pickupRequestInstIds = undefined;
        /**
         * The status to set on the pickups assigned to the trip.  Two possible values: ASGN or PREA.
         * Required: true
         * Array: false
         */
        this.statusCd = undefined;
    }
}
/**
 * Path parameter for the request to specify which SIC the Route is associated with.
 */
class AssignShipmentsToRoutePath {
    constructor() {
        /**
         * The service center for which the shipments are being assigned.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The instance id of the route to which shipments will be added.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Request body for assigning shipments to an existing route.
 */
class AssignShipmentsToRouteRqst {
    constructor() {
        /**
         * Mandatory list of shipments to be assigned to the desired route.
         * Required: true
         * Array: true
         */
        this.shipmentIds = undefined;
        /**
         * Indicates that shipments that are assigned to existing routes may be reassigned to the desired route.
         * Required: false
         * Array: false
         */
        this.forceReassignInd = undefined;
        /**
         * Indicates whether warnings should be added as part of the response or thrown as an exception.
         * Required: false
         * Array: false
         */
        this.returnWarningsAsErrorsInd = undefined;
        /**
         * When true, forces the service to assign the shipment anyway if the out-for-delivery SIC code is not equal to the shipment's destination SIC.  This indicator is only required when the route has been dispatched.
         * Required: false
         * Array: false
         */
        this.forceWhenOfdSicNeShmDestInd = undefined;
        /**
         * When true, forces the service to assign the shipment anyway if the shipment is not already at the SIC code at which the trailer is currently located.  This indicator is only required when the route has been dispatched.
         * Required: false
         * Array: false
         */
        this.forceShmNotAtTrailerSicInd = undefined;
        /**
         * When true, forces the service to assign the shipment anyway if the shipment is already on another trailer that is enroute to another SIC.  This indicator is only required when the route has been dispatched.
         * Required: false
         * Array: false
         */
        this.forceShmOnEnrouteTrailerInd = undefined;
        /**
         * When true, forces the service to assign the shipment anyway if the shipment is already on another trailer that is outbound from that SIC.  This indicator is only required when the route has been dispatched.
         * Required: false
         * Array: false
         */
        this.forceShmOnOutboundTrailerInd = undefined;
        /**
         * Overrides the edits which enforce TDC date validations for each shipment on the request.
         * Required: false
         * Array: false
         */
        this.overrideTdcInd = undefined;
        /**
         * Overrides the error if a shipment on the request is marked as a split shipment in the handling unit.  When the override is true, this bypasses the error and assigns the shipment to the route.
         * Required: false
         * Array: false
         */
        this.overrideSplitHandlingUnitInd = undefined;
        /**
         * Indicates whether to override the warning that the customer will be closed the same day the shipment will be included on this route.
         * Required: false
         * Array: false
         */
        this.overrideCustomerClosedInd = undefined;
        /**
         * Indicates the type of data to be included in the TripDetail response.
         * Required: false
         * Array: true
         */
        this.tripDetailCds = undefined;
        /**
         * Unique identifier for a cartage carrier.
         * Required: true
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Identifies a location for a cartage carrier.
         * Required: true
         * Array: false
         */
        this.locationId = undefined;
        /**
         * The optional date which is used only for planning routes.  It identifies the date on which the planner intends the route to be effective.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Response to the request for adding shipments to routes, returns the details for that Trip/Route
 */
class AssignShipmentsToRouteResp {
    constructor() {
        /**
         * Information about the trip/routes to which shipments were assigned.
         * Required: false
         * Array: false
         */
        this.tripDetail = undefined;
        /**
         * List of all the shipments from the request that had TDC warnings; these should be shown to the user.
         * Required: true
         * Array: true
         */
        this.tdcWarnings = undefined;
    }
}
/**
 * Path parameter which is used to autocomplete a delivery stop.
 */
class AutocompleteDeliveryStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to autocomplete a delivery stop.
 */
class AutocompleteDeliveryStopRqst {
    constructor() {
        /**
         * The delivery stop where the driver will be performing this activity.
         * Required: true
         * Array: false
         */
        this.deliveryStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operations-related note specific to the delivery activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * When true, used by the Shipment component to control how old the shipment movement can be before it can be updated through this service.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
        /**
         * When true, used to control whether or not to create an event and notify the driver with a message that the activities on the stop were or were not completed successfully.
         * Required: false
         * Array: false
         */
        this.readyToCompleteInd = undefined;
        /**
         * All delivery-related information for all the shipments on this delivery stop.  Contains activity as well as other information.
         * Required: false
         * Array: true
         */
        this.deliveryActivityDetails = undefined;
        /**
         * Equipment and activity details for this stop.
         * Required: false
         * Array: true
         */
        this.activityEquipmentDetails = undefined;
        /**
         * The unique identifier of the carrier.
         * Required: true
         * Array: false
         */
        this.carrierId = undefined;
    }
}
/**
 * Path parameter for specification of Route Balancing activity.
 */
class BalancePnDRoutesPath {
    constructor() {
        /**
         * The service center for which the routes are being balanced.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Request to balance routes when the user presses the Save button.
 */
class BalancePnDRoutesRqst {
    constructor() {
        /**
         * The state of existing routes.
         * Required: false
         * Array: true
         */
        this.existingRoutes = undefined;
        /**
         * The state of new routes.
         * Required: false
         * Array: true
         */
        this.newRoutes = undefined;
        /**
         * Any Unassigned Stops as a result of the Route Balancing.
         * Required: false
         * Array: true
         */
        this.unassignedStops = undefined;
        /**
         * Overrides the edits which enforce TDC date validations for each shipment on the request.
         * Required: false
         * Array: false
         */
        this.overrideTdcInd = undefined;
        /**
         * Indicates the type of data to be included in the TripDetail response.
         * Required: false
         * Array: true
         */
        this.tripDetailCds = undefined;
    }
}
/**
 * Response to the request to balance routes.
 */
class BalancePnDRoutesResp {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.tripDetails = undefined;
    }
}
/**
 * Request to calculate the cube for the bulk shipments.
 */
class BulkCalculateShipmentCubeRqst {
    constructor() {
        /**
         * The origin service center identification code of the shipment.
         * Required: false
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         * The unique identifier of the shipper dimension profile.
         * Required: false
         * Array: false
         */
        this.shipperId = undefined;
        /**
         * The code associated with the shipper dimension profile category, which is used to identify seasonal information.
         * Required: false
         * Array: false
         */
        this.profileCategoryCd = undefined;
        /**
         * The effective date of the shipper dimension profile.
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         * The trailer specification which is used to calculate the cube.
         * Required: false
         * Array: false
         */
        this.trailerSpecification = undefined;
        /**
         * The list of shipment cubes which are used in the calculation.
         * Required: false
         * Array: true
         */
        this.shipmentCubes = undefined;
    }
}
/**
 * Response to the request to calculate the cube for bulk shipments.
 */
class BulkCalculateShipmentCubeResp {
    constructor() {
        /**
         * The list of calculated shipment cubes.
         * Required: false
         * Array: true
         */
        this.calculatedShipmentCubes = undefined;
    }
}
/**
 * The path parameter for the request to list the collection of metrics for a trip.
 */
class CalculatePnDTripMetricsPath {
}
/**
 * Request to calculate PnD Trip metrics.
 */
class CalculatePnDTripMetricsRqst {
    constructor() {
        /**
         * The unique identifier for the trip for which the metrics are being calculated.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The unique identifier for the route for which the metrics are being calculated.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The stage of operations for which this metric snapshot was taken. E.g. for Pnd, the stages are: Planning, Dispatched, Dock
         * Required: true
         * Array: false
         */
        this.stage = undefined;
        /**
         * The specific activity performed in a stage for which this metric was created. E.g. for Pnd, Released or Sequence could be a sub stage during the Planning stage.
         * Required: true
         * Array: false
         */
        this.subStage = undefined;
        /**
         * The unique identifier for the event that triggered the calculate trip metrics call.
         * Required: true
         * Array: false
         */
        this.eventInstId = undefined;
    }
}
/**
 * Response to the request to list the collection of metrics for a trip.
 */
class CalculatePnDTripMetricsResp {
    constructor() {
        /**
         * The metrics associated with this trip.
         * Required: false
         * Array: true
         */
        this.metrics = undefined;
    }
}
/**
 * Request to calculate the cube for a shipment.
 */
class CalculateShipmentCubeRqst {
    constructor() {
        /**
         * The origin SIC code of the shipment.
         * Required: false
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         * The total pieces of the shipment.
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * The total weight in pounds of the shipment.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The unique identifier of the shipper dimension profile.
         * Required: false
         * Array: false
         */
        this.shipperId = undefined;
        /**
         * The destination postal code of the shipment.
         * Required: false
         * Array: false
         */
        this.destinationZip = undefined;
        /**
         * The shipper dimension profile category code.
         * Required: false
         * Array: false
         */
        this.shipperDimensionProfileCategoryCd = undefined;
        /**
         * The string representation of the effective date.
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         * The trailer specification to use for calculating cube.
         * Required: false
         * Array: false
         */
        this.trailerSpecification = undefined;
        /**
         * The list of shipment dimensions to use for calculating cube.
         * Required: false
         * Array: true
         */
        this.shipmentDimensions = undefined;
    }
}
/**
 * Response to the request to calculate the cube for a shipment.
 */
class CalculateShipmentCubeResp {
    constructor() {
        /**
         * The total volume in cubic feet.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * The pup volume percentage.
         * Required: false
         * Array: false
         */
        this.pupVolumePercentage = undefined;
        /**
         * The code associated with the dimension profile type.
         * Required: false
         * Array: false
         */
        this.dimensionProfileTypeCd = undefined;
        /**
         * The profile method used for the calculation.
         * Required: false
         * Array: false
         */
        this.profileMethod = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a delivery stop.
 */
class CompleteDeliveryStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a delivery stop.
 */
class CompleteDeliveryStopRqst {
    constructor() {
        /**
         * The delivery stop where the driver will be performing this activity.
         * Required: true
         * Array: false
         */
        this.deliveryStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operations-related note specific to the delivery activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * When true, used by the Shipment component to control how old the shipment movement can be before it can be updated through this service.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
        /**
         * When true, used to control whether or not to create an event and notify the driver with a message that the activities on the stop were or were not completed successfully.
         * Required: false
         * Array: false
         */
        this.readyToCompleteInd = undefined;
        /**
         * When true, used to validate when a PLT pro is not child pro exempt and there is a discrepancy between MM + LP compared to the number of child pros.
         * Required: false
         * Array: false
         */
        this.overrideChildProCountDiscrepancyInd = undefined;
        /**
         * All delivery-related information for all the shipments on this delivery stop.  Contains activity as well as other information.
         * Required: false
         * Array: true
         */
        this.deliveryActivityDetails = undefined;
        /**
         * Equipment and activity details for this stop.
         * Required: false
         * Array: true
         */
        this.activityEquipmentDetails = undefined;
    }
}
/**
 * Response to the request for completing a delivery stop.
 */
class CompleteDeliveryStopResp {
    constructor() {
        /**
         * A list of any soft warning overridable messages that should be shown to the user.
         * Required: true
         * Array: true
         */
        this.softWarnings = undefined;
    }
}
/**
 * The request to complete the dock pickup.
 */
class CompleteDockPickupRqst {
    constructor() {
        /**
         * Details about a customer's visit to a service center and the dock activities performed.
         * Required: false
         * Array: false
         */
        this.dockVisit = undefined;
        /**
         * Details about the one or more dock activities performed.
         * Required: false
         * Array: true
         */
        this.dockActivityDetails = undefined;
        /**
         * When true, used by the Shipment component to control how old the shipment movement can be before it can be updated through this service.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
    }
}
/**
 * Path parameters which are used to complete an equipment stop.
 */
class CompleteEquipmentStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of the stop containing the equipment activities that will be completed.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Query parameter which is used to filter activities that are processing.
 */
class CompleteEquipmentStopQuery {
    constructor() {
        /**
         * An activity code related to the activity that will be processed. Permitted values: SpotEmpty, HookEmpty and HookEquipment.
         * Required: true
         * Array: false
         */
        this.tripNodeActivityCd = undefined;
    }
}
/**
 * Request to complete an equipment stop.
 */
class CompleteEquipmentStopRqst {
    constructor() {
        /**
         * The location where a piece of equipment has been dropped to be hooked at a later time.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * The geo area default normally associated with this route.
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * The drop stop where the driver will be performing this hook activity.
         * Required: true
         * Array: false
         */
        this.stop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The comment text about any changes to the operational notes for this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * The comment text about any changes to the pickup instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overridePickupInstruction = undefined;
        /**
         * The service center responsible for the hook stop.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * A list containing the equipment that was dropped at a specific location for the drop stop, along with activity information which will be used for the hook activity.
         * Required: true
         * Array: true
         */
        this.dropStopEquipment = undefined;
        /**
         * The flag indicating when it is not required to check equipment for suitability.
         * Required: false
         * Array: false
         */
        this.equipmentCheckInd = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a hook loaded stop.
 */
class CompleteHookLoadedStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a hook loaded stop.
 */
class CompleteHookLoadedStopRqst {
    constructor() {
        /**
         * The stop detail that is being completed.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
        /**
         * Indicates whether the update to the hook loaded stop is to complete it.
         * Required: true
         * Array: false
         */
        this.updateAsCompleteInd = undefined;
        /**
         * Indicates whether or not to complete the stop.
         * Required: true
         * Array: false
         */
        this.doNotCompleteStopInd = undefined;
        /**
         * Equipment information.
         * Required: true
         * Array: false
         */
        this.equipment = undefined;
        /**
         * Indicates whether or not to override pro in use warnings when completing a Pickup from Edge, that failed completion in HH.
         * Required: false
         * Array: false
         */
        this.forceOverrideProInUseInd = undefined;
    }
}
/**
 * Response to the request to complete a hook loaded stop.
 */
class CompleteHookLoadedStopResp {
    constructor() {
        /**
         * The list of statuses for the shipments included in pickup activities.
         * Required: false
         * Array: true
         */
        this.pickupActivityShipmentStatuses = undefined;
        /**
         * A list of any warning overridable messages that should be shown to the user.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a pickup stop.
 */
class CompletePickupStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a pickup stop.
 */
class CompletePickupStopRqst {
    constructor() {
        /**
         * The stop detail that is being completed.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
        /**
         * Indicates whether the update to the pickup stop is to complete it.
         * Required: true
         * Array: false
         */
        this.updateAsCompleteInd = undefined;
        /**
         * Indicates whether or not to complete the stop.
         * Required: true
         * Array: false
         */
        this.doNotCompleteStopInd = undefined;
        /**
         * Indicates whether or not to override pro in use warnings when completing a Pickup from Edge, that failed completion in HH.
         * Required: true
         * Array: false
         */
        this.forceOverrideProInUseInd = undefined;
        /**
         * Equipment information.
         * Required: true
         * Array: false
         */
        this.equipment = undefined;
        /**
         * This indicates whether to override the warning condition if the driver's commercial license on this trip has expired.  Use a value of true to override this warning message.
         * Required: false
         * Array: false
         */
        this.overrideDsrLicenseInd = undefined;
    }
}
/**
 * Response to the request to complete a pickup stop.
 */
class CompletePickupStopResp {
    constructor() {
        /**
         * The list of statuses for the shipments included in pickup activities.
         * Required: false
         * Array: true
         */
        this.pickupActivityShipmentStatuses = undefined;
        /**
         * A list of any warning overridable messages that should be shown to the user.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter which is used to complete a drop stop and activity at a customer location.
 */
class CompletePnDDropStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to complete a drop stop and activity at a customer location.
 */
class CompletePnDDropStopRqst {
    constructor() {
        /**
         * The location for the drop stop.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The customer stop where the drop activity is to be performed.
         * Required: true
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The customer name of the location where the drop will be performed.
         * Required: true
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Indicates confirmation of completion despite having hazmat on board.
         * Required: true
         * Array: false
         */
        this.confirmCompletionForHazmatInd = undefined;
        /**
         * A list containing information needed about the stop and equipment involved in a drop activity at a customer stop.
         * Required: true
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Response to the request for completing a drop stop.
 */
class CompletePnDDropStopResp {
    constructor() {
        /**
         * A list of any soft warning overrideable messages that should be shown to the user.
         * Required: true
         * Array: true
         */
        this.softWarnings = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a spot loaded stop.
 */
class CompleteSpotLoadedStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a spot loaded stop.
 */
class CompleteSpotLoadedStopRqst {
    constructor() {
        /**
         * The spot-loaded stop that will be completed with this request.
         * Required: true
         * Array: false
         */
        this.spotLoadedStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operations-related note specific to the spot loaded activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * When true, used by the Shipment component to control how old the shipment movement can be before it can be updated through this service.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
        /**
         * When true, used to control whether or not to create an event and notify the driver with a message that the activities on the stop were or were not completed successfully.
         * Required: false
         * Array: false
         */
        this.readyToCompleteInd = undefined;
        /**
         * All delivery-related information for all the shipments on this spot loaded stop.  Contains activity as well as other information.
         * Required: true
         * Array: true
         */
        this.deliveryActivityDetails = undefined;
        /**
         * Equipment and activity details for this stop.
         * Required: true
         * Array: true
         */
        this.activityEquipmentDetails = undefined;
    }
}
/**
 * Response to the request to complete a spot loaded stop.
 */
class CompleteSpotLoadedStopResp {
    constructor() {
        /**
         * Pickup request information related to completing the spot loaded stop.
         * Required: false
         * Array: false
         */
        this.pickupRequest = undefined;
    }
}
/**
 * Path parameter used to complete or return a trip.
 */
class CompleteTripPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete or return a trip.
 */
class CompleteTripRqst {
    constructor() {
        /**
         * Indicates whether this is initiated by a handheld device.
         * Required: false
         * Array: false
         */
        this.invokeFromHandheldInd = undefined;
        /**
         * The time when the trip is completed.
         * Required: true
         * Array: false
         */
        this.tripCompleteDateTime = undefined;
    }
}
/**
 * Path parameter which is used to convert the stop activities to a new activity type.
 */
class ConvertStopActivitiesPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The unique identifier for the stop.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to convert the stop activities to a new activity type.
 */
class ConvertStopActivitiesRqst {
    constructor() {
        /**
         * The current trip node activity type to which all the activities on the stop are currently set.
         * Required: true
         * Array: false
         */
        this.sourceActivityType = undefined;
        /**
         * The desired trip node activity type to which all the activities on the stop should be set.
         * Required: true
         * Array: false
         */
        this.targetActivityType = undefined;
        /**
         * The stop that contains the activities that will be converted in this request.
         * Required: true
         * Array: false
         */
        this.stop = undefined;
        /**
         * The list of sequence numbers associated with the stop activities which will be converted.
         * Required: false
         * Array: true
         */
        this.tripNodeActivitySequenceNbrs = undefined;
    }
}
/**
 * Request to convert a route.
 */
class ConvertRouteRqst {
    constructor() {
        /**
         * The unique identifier of the route.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The unique identifier of the carrier.
         * Required: true
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Identifies if the route (prefix/suffix) is a planning or delivery route.
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         * The prefix component of the route name.
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * The suffix component of the route name.
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Identifies the type of the cartage trip.
         * Required: false
         * Array: false
         */
        this.cmsTripTypeCd = undefined;
        /**
         * Identifies what kind of route it should be convert into.
         * Required: false
         * Array: false
         */
        this.targetCategoryCd = undefined;
    }
}
/**
 * Request to convert a list of stops to cartage stops.
 */
class ConvertToCartageStopRqst {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The unique identifiers for the stops.
         * Required: true
         * Array: true
         */
        this.tripNodes = undefined;
        /**
         * The unique identifier for a cartage carrier.
         * Required: true
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * The unique identifier of a location for a cartage carrier.
         * Required: true
         * Array: false
         */
        this.locationId = undefined;
    }
}
/**
 * Request to create the delivery receipt DMS scanned document information for the audit process.
 */
class CreateDeliveryShipmentForAuditRqst {
    constructor() {
        /**
         * The output of the OCR run on a shipment delivery receipt.
         * Required: true
         * Array: false
         */
        this.deliverySpecialService = undefined;
    }
}
/**
 * Request to create a dock drop pickup request.
 */
class CreateDockDropPickupRequestRqst {
    constructor() {
        /**
         * The dock drop pickup request that will be created along with any shipment information when provided.
         * Required: true
         * Array: false
         */
        this.completePickupRequest = undefined;
    }
}
/**
 * Response to the request to create a dock drop pickup request.
 */
class CreateDockDropPickupRequestResp {
    constructor() {
        /**
         * The dock drop pickup request that was created, including any shipment information that was provided.
         * Required: false
         * Array: false
         */
        this.completePickupRequest = undefined;
    }
}
/**
 * Request to generate PnD Manifest documents.
 */
class CreateDsrCheckInDocumentsRqst {
    constructor() {
        /**
         * The service center identification code for the location where the request to print DSR check-ins is generated.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The list of unique identifiers for the trips for which PDF documents will be generated.
         * Required: false
         * Array: true
         */
        this.tripInstIds = undefined;
        /**
         * Indicates whether a warning page should be included.
         * Required: false
         * Array: false
         */
        this.includeWarningsPageInd = undefined;
    }
}
/**
 * Response to the request to generate PnD Manifest documents.
 */
class CreateDsrCheckInDocumentsResp {
    constructor() {
        /**
         * The list of unique identifiers for the trips for which PDF documents were generated.
         * Required: false
         * Array: true
         */
        this.tripInstIds = undefined;
        /**
         * The PDF binary data encoded in base64 for check-in documents.
         * Required: false
         * Array: true
         */
        this.documentData = undefined;
        /**
         * The list of errors that occurred for each failed PDF.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Request to generate PnD Manifest documents.
 */
class CreatePnDManifestDocumentsRqst {
    constructor() {
        /**
         * The service center identification code for the location where the request to print PnD manifests is generated.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The list of unique identifiers for the routes for which PDF documents will be generated.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The unique identifier for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * Indicates whether a warning page should be included.
         * Required: false
         * Array: false
         */
        this.includeWarningsPageInd = undefined;
    }
}
/**
 * Response to the request to generate PnD Manifest documents.
 */
class CreatePnDManifestDocumentsResp {
    constructor() {
        /**
         * The list of unique identifiers for the routes for which PDF documents were generated.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The PDF binary data encoded in base64 for manifest documents.
         * Required: false
         * Array: true
         */
        this.documentData = undefined;
        /**
         * The list of errors that occurred for each failed PDF.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Request to create a new route.
 */
class CreateNewRouteRqst {
    constructor() {
        /**
         * Information to create a new route.
         * Required: true
         * Array: false
         */
        this.route = undefined;
        /**
         * The unique identifier that represents the area that will contain the new route.
         * Required: true
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * The optional list of shipments to be assigned to the new route.
         * Required: false
         * Array: true
         */
        this.shipmentList = undefined;
        /**
         * Indicates that shipments that are assigned to existing routes may be reassigned to the new route.
         * Required: false
         * Array: false
         */
        this.forceReassignInd = undefined;
        /**
         * Indicates whether warnings should be added as part of the response or thrown as an exception.
         * Required: false
         * Array: false
         */
        this.returnWarningsAsErrorsInd = undefined;
        /**
         * Overrides the error if a shipment on the request is marked as a split shipment in the handling unit.  When the override is true, this bypasses the error and assigns the shipment to the route.
         * Required: false
         * Array: false
         */
        this.overrideSplitHandlingUnitInd = undefined;
        /**
         * Indicates whether to override the warning that the customer will be closed the same day the shipment will be included on this route.
         * Required: false
         * Array: false
         */
        this.overrideCustomerClosedInd = undefined;
        /**
         * Overrides the edits which enforce TDC date validations for each shipment on the request.
         * Required: false
         * Array: false
         */
        this.overrideTdcInd = undefined;
        /**
         * The trip associated with the stops that will be reassigned to the newly created trip in this transaction
         * Required: true
         * Array: false
         */
        this.oldSourceTrip = undefined;
        /**
         * The route associated with the stops that will be reassigned to the newly created route in this transaction.
         * Required: true
         * Array: false
         */
        this.oldSourceRoute = undefined;
        /**
         * The list of trip nodes that will be reassigned from the old source trip to the newly created trip in this transaction.
         * Required: false
         * Array: true
         */
        this.oldSourceStops = undefined;
        /**
         * Unique identifier for a cartage carrier.
         * Required: true
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Identifies a location for a cartage carrier.
         * Required: true
         * Array: false
         */
        this.locationId = undefined;
        /**
         * The optional date which is used only for planning routes.  It identifies the date on which the planner intends the route to be effective.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Indicates that shipments may be assigned to existing planning route.
         * Required: false
         * Array: false
         */
        this.assignOnPlanningRouteExistsInd = undefined;
    }
}
/**
 * Response to the request to create a new route.
 */
class CreateNewRouteResp {
    constructor() {
        /**
         * The new route that was created.
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * The new trip that was created for the new route.
         * Required: false
         * Array: false
         */
        this.trip = undefined;
        /**
         * List of all the shipments from the request that had TDC warnings; these should be shown to the user.
         * Required: false
         * Array: true
         */
        this.tdcWarnings = undefined;
    }
}
/**
 * Request to create an operations event.
 */
class CreateOpsEventRqst {
    constructor() {
        /**
         * The information needed to create the event for a specific type and sub-type code combination.
         * Required: true
         * Array: false
         */
        this.opsEvent = undefined;
    }
}
/**
 * Response to the request to create an operations event.
 */
class CreateOpsEventResp {
    constructor() {
        /**
         * The unique identifier of the newly created event.
         * Required: false
         * Array: false
         */
        this.eventInstId = undefined;
    }
}
/**
 * Request to create a dispatch group and associate it with one or more dispatch group regions.
 */
class CreatePnDDispatchGroupRqst {
    constructor() {
        /**
         * The dispatch group that will be created for this request.
         * Required: false
         * Array: false
         */
        this.dispatchGroup = undefined;
        /**
         * An optional dispatch group region that, when present, will be updated and associated with the dispatch group for this request.
         * Required: false
         * Array: false
         */
        this.dispatchGroupRegion = undefined;
    }
}
/**
 * Response to the request to create a dispatch group and associate it with one or more dispatch group regions.
 */
class CreatePnDDispatchGroupResp {
    constructor() {
        /**
         * The dispatch group that was created for this request.
         * Required: false
         * Array: false
         */
        this.dispatchGroup = undefined;
    }
}
/**
 * Path parameter used to create a drop stop and activity at a customer location.
 */
class CreateDropStopsPath {
    constructor() {
        /**
         * The location for the drop stop.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to create a drop stop and activity at a customer location.
 */
class CreateDropStopsRqst {
    constructor() {
        /**
         * The list of drops stops with dropLocation to be performed for a dropStop.
         * Required: false
         * Array: true
         */
        this.dropStops = undefined;
    }
}
/**
 * Response to the created trip.
 */
class CreatePnDTripResp {
    constructor() {
        /**
         * The unique identifier on trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to create a trip.
 */
class CreatePnDTripRqst {
    constructor() {
        /**
         * The information required to create a trip
         * Required: true
         * Array: false
         */
        this.pndTrip = undefined;
        /**
         * The flag indicating when it is not required to check equipment for suitability.
         * Required: false
         * Array: false
         */
        this.equipmentCheckInd = undefined;
    }
}
/**
 * The request to create pickup positioning points.
 */
class CreatePositioningPointsRqst {
    constructor() {
        /**
         * The list of pickup positioning points to create.
         * Required: true
         * Array: true
         */
        this.positioningPoints = undefined;
    }
}
/**
 * The path parameter which is used to delete a drop location.
 */
class DeleteDropLocationPath {
    constructor() {
        /**
         * The unique identifier of the drop location.
         * Required: true
         * Array: false
         */
        this.dropLocationInstId = undefined;
    }
}
/**
 * The path parameter which is used to delete a dispatch group.
 */
class DeletePnDDispatchGroupPath {
    constructor() {
        /**
         * The identifier of the dispatch group to be deleted.
         * Required: true
         * Array: false
         */
        this.groupId = undefined;
    }
}
/**
 * The path parameter used to identify the service center associated with the routes to be deleted.
 */
class DeletePnDRoutesPath {
    constructor() {
        /**
         * The service center for which the routes are being deleted.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameter which is used to delete a route.
 */
class DeletePnDRoutesQuery {
    constructor() {
        /**
         * The list of unique identifiers for the routes which will be deleted.
         * Required: true
         * Array: true
         */
        this.routeInstIds = undefined;
    }
}
/**
 * The path parameter used to identify the trip to be deleted.
 */
class DeletePnDTripPath {
    constructor() {
        /**
         * The unique identifier of the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Path parameter for the request to dispatch a trip.
 */
class DispatchPnDTripPath {
    constructor() {
        /**
         * The identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * The request to dispatch trip.
 */
class DispatchPnDTripRqst {
    constructor() {
        /**
         * The actual departure date time of the trip.
         * Required: false
         * Array: false
         */
        this.actualActivityDateTime = undefined;
        /**
         * Indicates that the equipment's suitability should be checked.
         * Required: false
         * Array: false
         */
        this.equipmentCheckInd = undefined;
        /**
         * This indicates whether to override the warning condition if the driver's double or triple trailer license on this trip has expired.  Use a value of true to override this warning message.
         * Required: false
         * Array: false
         */
        this.overrideDsrLicenseInd = undefined;
    }
}
/**
 * Response when dispatching a PnD trip.
 */
class DispatchPnDTripResp {
    constructor() {
        /**
         * The list of warnings that occurred while attempting to dispatch the trip; these messages are overridable and should be shown to the user.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Details that are required for completing the dockActivity.
 */
class DockActivityDetails {
    constructor() {
        /**
         * The dock activity information.
         * Required: true
         * Array: false
         */
        this.dockActivity = undefined;
        /**
         * The list of handling units for a shipment being delivered and the possible exception recorded for it.
         * Required: false
         * Array: true
         */
        this.deliveryHandlingUnits = undefined;
        /**
         * Records payments received while performing Trip or Dock activities. Payments for transportation.
         * Required: true
         * Array: false
         */
        this.freightCollectedPayment = undefined;
        /**
         * Records payments received while performing Trip or Dock activities. COD payments for a shipper.
         * Required: true
         * Array: false
         */
        this.codCollectedPayment = undefined;
        /**
         * Shipment information.
         * Required: true
         * Array: false
         */
        this.shipment = undefined;
        /**
         * Shipment movement information.
         * Required: true
         * Array: false
         */
        this.shipmentMovement = undefined;
        /**
         * The delivery exception details.
         * Required: true
         * Array: false
         */
        this.deliveryException = undefined;
        /**
         * Enables bypassing location check.
         * Required: false
         * Array: false
         */
        this.forceLocationInd = undefined;
        /**
         * Enables bypassing SCAC check.
         * Required: false
         * Array: false
         */
        this.forceScacInd = undefined;
        /**
         * Enables bypassing COD check.
         * Required: false
         * Array: false
         */
        this.forceCodInd = undefined;
        /**
         * Enables bypassing collect check.
         * Required: false
         * Array: false
         */
        this.forceCollectInd = undefined;
    }
}
/**
 * The information about a dock stop.
 */
class DockStop {
    constructor() {
        /**
         * The details of the customer, it can be shipper for dock drop or consignee for dock pickup.
         * Required: false
         * Array: false
         */
        this.customer = undefined;
        /**
         * The instance id of the route for which stops are being requested.
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The total bill count of the customer.
         * Required: false
         * Array: false
         */
        this.totalBillCount = undefined;
        /**
         * The total pieces count of the shipments.
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * The total pallet count of the shipments.
         * Required: false
         * Array: false
         */
        this.totalPalletCount = undefined;
        /**
         * The total weight of the shipments.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The total cube percentage of the shipments.
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         * The total cube percentage of the shipments.
         * Required: false
         * Array: false
         */
        this.totalMotorizedPiecesCount = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.dockActivities = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServicesSummary = undefined;
        /**
         * The code which determines whether No, Partial, or All Prints for FBDS documents will be printed for the route.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCountCd = undefined;
    }
}
/**
 * Request to calculate PnD Trip used in route balancing operations
 */
class EvaluatePnDRouteBalancingRqst {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.terminalSicCode = undefined;
        /**
         * The geo coordinates of the service center
         * 						location.
         * Required: true
         * Array: false
         */
        this.terminalGeoCoordinates = undefined;
        /**
         * Required: true
         * Array: true
         */
        this.routeBalancingTrips = undefined;
        /**
         * The metrics from the previous evaluation.
         * Required: false
         * Array: true
         */
        this.previousMetrics = undefined;
    }
}
/**
 * Response to the request to list the collection of metrics for a trip.
 */
class EvaluatePnDRouteBalancingResp {
    constructor() {
        /**
         * The trips associated with this route balancing.
         * Required: true
         * Array: true
         */
        this.trips = undefined;
        /**
         * The resulting metrics after the route balancing.
         * Required: false
         * Array: true
         */
        this.afterMetrics = undefined;
        /**
         * The resulting metrics from the previous route balancing.
         * Required: false
         * Array: true
         */
        this.beforeMetrics = undefined;
    }
}
/**
 * The path parameter for the request to get the actual driver location for an employeeId.
 */
class GetActualDriverLocationPath {
    constructor() {
        /**
         * The unique identifier for the employee which is used to get the actual driver location.
         * Required: true
         * Array: false
         */
        this.employeeId = undefined;
    }
}
/**
 * Response to requested actual driver location.
 */
class GetActualDriverLocationResp {
    constructor() {
        /**
         * Information about the actual driver location.
         * Required: false
         * Array: false
         */
        this.actualDsrLocation = undefined;
    }
}
/**
 * Query parameters which are used to retrieve carrier location trips.
 */
class GetCarrierLocationTripsQuery {
    constructor() {
        /**
         * The unique identifier for a carrier location.
         * Required: false
         * Array: false
         */
        this.carrierLocationId = undefined;
        /**
         * The unique identifier for a customer in CIS which is linked to a carrier's location.
         * Required: false
         * Array: false
         */
        this.cisCustomerNbr = undefined;
    }
}
/**
 * Response to the request to retrieve carrier location trips.
 */
class GetCarrierLocationTripsResp {
    constructor() {
        /**
         * The identifiers for an individual trip and its stops.
         * Required: false
         * Array: true
         */
        this.tripIds = undefined;
    }
}
/**
 * The path parameter used to retrieve the customer operations schedules.
 */
class GetCustomerOperationsSchedulesPath {
    constructor() {
        /**
         * The identifier for the customer account.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
    }
}
/**
 * The query parameter used to retrieve the customer operations schedules.
 */
class GetCustomerOperationsSchedulesQuery {
    constructor() {
        /**
         * The date for which the customer operations schedules will be retrieved.
         * Required: true
         * Array: false
         */
        this.scheduleDate = undefined;
    }
}
/**
 * Response to the request to retrieve the customer operations schedules.
 */
class GetCustomerOperationsSchedulesResp {
    constructor() {
        /**
         * The date for which the customer operations schedules were retrieved.
         * Required: true
         * Array: false
         */
        this.scheduleDate = undefined;
        /**
         * The list of scheduled operation hours.
         * Required: false
         * Array: true
         */
        this.operationHours = undefined;
        /**
         * The list of scheduled pickup hours.
         * Required: false
         * Array: true
         */
        this.pickupHours = undefined;
        /**
         * The list of scheduled delivery hours.
         * Required: false
         * Array: true
         */
        this.deliveryHours = undefined;
    }
}
/**
 * The path parameters which are used to retrieve the next available date for the customer schedule.
 */
class GetNextAvailableDateForCustomerSchedulePath {
    constructor() {
        /**
         * The identifier for the customer account.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * The service center identification code of the destination location that is associated with the customer.
         * Required: true
         * Array: false
         */
        this.destinationSicCd = undefined;
    }
}
/**
 * The query parameters which are used to retrieve the next available date for the customer schedule.
 */
class GetNextAvailableDateForCustomerScheduleQuery {
    constructor() {
        /**
         * The date for which the customer operations schedules will be retrieved.
         * Required: true
         * Array: false
         */
        this.scheduleDate = undefined;
        /**
         * The code associated with the type of operation.  Permitted values: AutoGen, DeliveryReceiving, OperationsGeneral, and PickupShipping.
         * Required: true
         * Array: false
         */
        this.operationTypeCd = undefined;
    }
}
/**
 * Response to the request to retrieve the next available date for the customer schedule.
 */
class GetNextAvailableDateForCustomerScheduleResp {
    constructor() {
        /**
         * The next date that the customer is available.
         * Required: false
         * Array: false
         */
        this.nextAvailableDate = undefined;
        /**
         * Details about customer's stop window, including its type, start/end times.
         * Required: false
         * Array: true
         */
        this.stopWindows = undefined;
    }
}
/**
 * The query parameter which is used to retrieve the delivery shipment for audit.
 */
class GetDeliveryShipmentForAuditQuery {
    constructor() {
        /**
         * The unique identifier of the delivery shipment which is used for the next DR.
         * Required: false
         * Array: false
         */
        this.currentShipmentInstId = undefined;
        /**
         * The unique identifier of the delivery shipment which is used to fetch the specific shipment.
         * Required: false
         * Array: false
         */
        this.fetchShipmentInstId = undefined;
        /**
         * The timestamp at which the OCR processing started for the given DR which is used to fetch the specific DR.
         * Required: false
         * Array: false
         */
        this.fetchProcessStartDateTime = undefined;
        /**
         * Respresents the type of auditor for review.
         * Required: false
         * Array: false
         */
        this.auditorTypeCd = undefined;
    }
}
/**
 * The response to the request to retrieve the delivery shipment for audit.
 */
class GetDeliveryShipmentForAuditResp {
    constructor() {
        /**
         * The audit metrics for the delivery shipment.
         * Required: false
         * Array: false
         */
        this.auditMetrics = undefined;
        /**
         * The output of the OCR run on a given DR.
         * Required: false
         * Array: false
         */
        this.deliverySpecialService = undefined;
        /**
         * The output of the manual audit done on DRs.
         * Required: false
         * Array: true
         */
        this.deliverySpecialServiceAudits = undefined;
        /**
         * The scanned image of a DR.
         * Required: true
         * Array: false
         */
        this.deliveryReceipt = undefined;
    }
}
/**
 * Path parameter which is used to get delivery shipments for an audit.
 */
class GetDeliveryShipmentsForAuditByProPath {
    constructor() {
        /**
         * The business identifier of the delivery shipments that will be retrieved.
         * Required: true
         * Array: false
         */
        this.proNbr = undefined;
    }
}
/**
 * The response to the request to get delivery shipments for an audit.
 */
class GetDeliveryShipmentsForAuditByProResp {
    constructor() {
        /**
         * The list of DeliverySpecialService records for the proNbr provided.
         * Required: false
         * Array: true
         */
        this.deliverySpecialServices = undefined;
    }
}
/**
 * Path parameter to get the drop location details.
 */
class GetDropLocationPath {
    constructor() {
        /**
         * The unique identifier for a drop location.
         * Required: true
         * Array: false
         */
        this.dropLocationInstanceId = undefined;
    }
}
/**
 * Response to the request to return drop details.
 */
class GetDropLocationResp {
    constructor() {
        /**
         * The information about the drop location.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
    }
}
/**
 * Path parameter for a given SIC to get auto print manifest preferences; e.g. PnD manifest, outbound manifest, DSR check in and FBDS.
 */
class GetServiceCenterPrintPreferencesPath {
    constructor() {
        /**
         * The unique identifier for the serviceCenter.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to return auto print preferences for the SIC provided.
 */
class GetServiceCenterPrintPreferencesResp {
    constructor() {
        /**
         * Defines the serviceCenter preferences for automated printing.
         * Required: true
         * Array: false
         */
        this.printPreferences = undefined;
    }
}
/**
 * The path parameter which is used to retrieve the operations customer profile.
 */
class GetOperationsCustomerProfilePath {
    constructor() {
        /**
         * The unique identifier associated with a customer's operational profile.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
    }
}
/**
 * The query parameter which is used optionally to include other profile details for the customer.
 */
class GetOperationsCustomerProfileQuery {
    constructor() {
        /**
         * The list of enum values used to add data to the response for any customer notes or schedules that may exist for their profile.
         * - NOTE - Returns any customer operations profile notes
         * - SCHEDULE - Returns any customer operational hours
         * Required: false
         * Array: true
         */
        this.customerProfileDetailCds = undefined;
    }
}
/**
 * Response to the request to return operations profile details for a customer number
 */
class GetOperationsCustomerProfileResp {
    constructor() {
        /**
         * Details about the customer location.
         * Required: true
         * Array: false
         */
        this.customerLocation = undefined;
        /**
         * Details about the customer profile.
         * Required: true
         * Array: false
         */
        this.customerOperationsProfile = undefined;
        /**
         * The list of profile related notes about a customer who has a profile in the system.
         * Required: true
         * Array: true
         */
        this.customerOperationsNotes = undefined;
        /**
         * The list of profile related schedules for a customer who has a profile in the system.
         * Required: true
         * Array: true
         */
        this.customerOperationsSchedules = undefined;
        /**
         * The list of customer-specific daily pickup schedules currently effective in their profile.
         * Required: false
         * Array: true
         */
        this.customerDailyPickupSchedules = undefined;
    }
}
/**
 * Request to retrieve the tripInstId associated with a given pickup request.
 */
class GetPickupTripsRqst {
    constructor() {
        /**
         * The list of unique pickup request identifiers.
         * Required: true
         * Array: true
         */
        this.pickupInstIds = undefined;
    }
}
/**
 * Response to the request to retrieve the tripInstId associated with a given pickup request.
 */
class GetPickupTripsResp {
    constructor() {
        /**
         * The list of associated trip and pickup request identifiers.
         * Required: false
         * Array: true
         */
        this.pickupTrips = undefined;
    }
}
/**
 * The path parameter for the request to get the driver location for an employeeId
 */
class GetPnDDriverLocationPath {
    constructor() {
        /**
         * The identifier for the employee which is used to get the driver location.
         * Required: true
         * Array: false
         */
        this.employeeId = undefined;
    }
}
/**
 * Response to requested driver location
 */
class GetPnDDriverLocationResp {
    constructor() {
        /**
         * Information about the driver location.
         * Required: false
         * Array: false
         */
        this.driverLocation = undefined;
    }
}
/**
 * The path parameter for the request to retrieve a single selection profile from the ID provided.
 */
class GetPnDInboundSelectionProfilePath {
    constructor() {
        /**
         * The unique identifier for the selection profile.
         * Required: true
         * Array: false
         */
        this.selectionProfileId = undefined;
    }
}
/**
 * Response to the request to retrieve a single selection profile from the ID provided.
 */
class GetPnDInboundSelectionProfileResp {
    constructor() {
        /**
         * The details for an inbound planning profile.
         * Required: false
         * Array: false
         */
        this.selectionProfile = undefined;
    }
}
/**
 * The path parameter used to retrieve the dock drop indicator for a pickup request.
 */
class GetPnDPickupDockDropIndPath {
    constructor() {
        /**
         * The unique identifier for the pickup request.
         * Required: true
         * Array: false
         */
        this.pickupRequestInstId = undefined;
    }
}
/**
 * Response to the request to retrieve the dock drop indicator.
 */
class GetPnDPickupDockDropIndResp {
    constructor() {
        /**
         * Indicates if there is a dock drop for the pickup request.
         * Required: false
         * Array: false
         */
        this.dockDropInd = undefined;
    }
}
/**
 * The path parameter for the request to retrieve the driver information for a particular pickup request.
 */
class GetPnDPickupDriverPath {
    constructor() {
        /**
         * The identifier for the pickup request which is associated with the driver information.
         * Required: true
         * Array: false
         */
        this.pickupInstId = undefined;
    }
}
/**
 * Response to the request to retrieve the driver information for a particular pickup request.
 */
class GetPnDPickupDriverResp {
    constructor() {
        /**
         * The driver information for the pickup request.
         * Required: false
         * Array: false
         */
        this.driver = undefined;
    }
}
/**
 * Path parameter for the request to retrieve a single route from the route instance ID provided.
 */
class GetPnDRoutePath {
    constructor() {
        /**
         * The instance id of the route being retrieved.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Query parameter for the request to retrieve a single route from the route instance ID provided.
 */
class GetPnDRouteQuery {
    constructor() {
        /**
         * The service center for which the routes are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The list of codes that identify the type of data to be included in the response.
         * Required: false
         * Array: true
         */
        this.routeDetailCds = undefined;
    }
}
/**
 * Response to the request to retrieve a single route from the route instance ID provided.
 */
class GetPnDRouteResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.routeShipments = undefined;
    }
}
/**
 * The path parameters for the request to retrieve a single stop associated with a route.
 */
class GetPnDStopPath {
    constructor() {
        /**
         * The instance id of the route for which stops are being requested.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The sequence number for the trip node.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Response to the request to list the stops associated with a route.
 */
class GetPnDStopResp {
    constructor() {
        /**
         * the instance of the requested stop.
         * Required: false
         * Array: false
         */
        this.stop = undefined;
    }
}
/**
 * Request to return trip details.
 */
class GetPnDTripPath {
    constructor() {
        /**
         * The location associated with the trip.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Query parameter used to return trip details.
 */
class GetPnDTripQuery {
    constructor() {
        /**
         * Indicates the type of data to be included in the TripDetail response.
         * Required: false
         * Array: true
         */
        this.tripDetailCds = undefined;
    }
}
/**
 * Response to the request to return trip details.
 */
class GetPnDTripResp {
    constructor() {
        /**
         * Information about the trip.
         * Required: false
         * Array: false
         */
        this.tripDetail = undefined;
    }
}
/**
 * Request to return the DSR locations for a trip.
 */
class GetPnDTripDsrLocationsPath {
    constructor() {
        /**
         * The identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Response to the request to return the DSR locations for a trip.
 */
class GetPnDTripDsrLocationsResp {
    constructor() {
        /**
         * The employee identifier of the DSR.
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         * The actual start date time of the trip.
         * Required: false
         * Array: false
         */
        this.startTripDateTime = undefined;
        /**
         * The actual end date time of the trip.
         * Required: false
         * Array: false
         */
        this.endTripDateTime = undefined;
        /**
         * The DSR's location from the actual start trip date time to the actual end date time of the trip.
         * Required: false
         * Array: true
         */
        this.dsrLocations = undefined;
        /**
         * The current location of the driver.
         * Required: false
         * Array: false
         */
        this.dsrCurrentLocation = undefined;
    }
}
/**
 * Path parameter which is used to retrieve a trip.
 */
class GetTripByEquipmentInstIdPath {
    constructor() {
        /**
         * The unique identifier for the equipment.
         * Required: true
         * Array: false
         */
        this.equipmentInstId = undefined;
    }
}
/**
 * Response to the request to retrieve a trip.
 */
class GetTripByEquipmentInstIdResp {
    constructor() {
        /**
         * The information about the trip.
         * Required: false
         * Array: false
         */
        this.tripDetail = undefined;
    }
}
/**
 * Path parameter which is used to retrieve the drivers for a trip.
 */
class GetTripDriversPath {
    constructor() {
        /**
         * The unique identifier of the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Response to the request to retrieve the drivers for a trip.
 */
class GetTripDriversResp {
    constructor() {
        /**
         * The list of drivers associated with the trip.
         * Required: false
         * Array: true
         */
        this.tripDrivers = undefined;
    }
}
/**
 * Request to list profile notes for customers.
 */
class ListCustomerProfileNotesRqst {
    constructor() {
        /**
         * The list of identifiers for the associated customer accounts.
         * Required: true
         * Array: true
         */
        this.customerAcctIds = undefined;
    }
}
/**
 * Response to the request to list profile notes for customers.
 */
class ListCustomerProfileNotesResp {
    constructor() {
        /**
         * The list of customers and their profile notes.
         * Required: false
         * Array: true
         */
        this.customerProfileNotes = undefined;
    }
}
/**
 * The request to list daily customer schedules with account information.
 */
class ListDailyPickupStopCustomerPreferencesRqst {
    constructor() {
        /**
         * The list of customer account identifiers which will be used as filters for the customer schedules.
         * Required: false
         * Array: true
         */
        this.customerAcctIds = undefined;
        /**
         * A schedule can be defined for these types of operations.
         * Required: true
         * Array: false
         */
        this.operationTypeCd = undefined;
        /**
         * The code associated with the week day when the batch job is run.
         * Required: true
         * Array: false
         */
        this.weekDayCd = undefined;
        /**
         * The country code which will be used as filter for the customer schedules.
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list daily customer schedules with account information.
 */
class ListDailyPickupStopCustomerPreferencesResp {
    constructor() {
        /**
         * The daily pickup stops and associated account information.
         * Required: false
         * Array: true
         */
        this.dailyPickupStopCustomerPreference = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * The request to list dock drop and dock pickups.
 */
class ListDockRouteStopsRqst {
    constructor() {
        /**
         * The unique identifiers for the routes.
         * Required: true
         * Array: true
         */
        this.routeInstIds = undefined;
    }
}
/**
 * The response to the request to list dock drop and dock pickups.
 */
class ListDockRouteStopsResp {
    constructor() {
        /**
         * The list of dock stops associated with the route.
         * Required: false
         * Array: true
         */
        this.dockStops = undefined;
    }
}
/**
 * Path parameter to list the hook equipment stops for a service center location.
 */
class ListHookEquipmentStopsPath {
    constructor() {
        /**
         * The location for which the hook equipment stops are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameter that qualifies the SIC code to indicate if hook equipment stops created for zones related to the SIC on the request should or should not be included in the response.
 */
class ListHookEquipmentStopsQuery {
    constructor() {
        /**
         * The zone indicator associated with the data to be returned.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
        /**
         * The status of hook equipment stops to be returned.
         * Required: false
         * Array: true
         */
        this.statusCds = undefined;
    }
}
/**
 * Response containing the list of hook equipment stops based on the values in the request.
 */
class ListHookEquipmentStopsResp {
    constructor() {
        /**
         * The details of hook equipment stop.
         * Required: false
         * Array: true
         */
        this.hookEquipmentStops = undefined;
    }
}
/**
 * Path parameter to list the drop locations for a service center location.
 */
class ListDropLocationsPath {
    constructor() {
        /**
         * The location for which the drop locations are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameter that qualifies the SIC code to indicate if drop locations created for zones related to the SIC on the request should or should not be included in the response.
 */
class ListDropLocationsQuery {
    constructor() {
        /**
         * The zone indicator associated with the data to be returned.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response containing the list of drop locations based on the values in the request.
 */
class ListDropLocationsResp {
    constructor() {
        /**
         * A list of drop locations for the SIC code on the request.
         * Required: false
         * Array: true
         */
        this.dropLocations = undefined;
    }
}
/**
 * Provides the necessary XRT search filters and data used to build the delivery density using delivery-shipment data.
 */
class ListPnDDeliveryDensityRqst {
    constructor() {
        /**
         * Specifies the page size, sort order, etc.
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * The query for searching elastic/XRT.
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Used to determine the density totals for each point as seven days from this date (exclusive).
         * Required: false
         * Array: false
         */
        this.densityBaselineDate = undefined;
    }
}
/**
 * Contains a list of actual delivery points with a shipment count at each specific point.
 */
class ListPnDDeliveryDensityResp {
    constructor() {
        /**
         * The list of actual lat-long points and the number of actual deliveries made at each point.
         * Required: false
         * Array: true
         */
        this.deliveryLocationCounts = undefined;
    }
}
/**
 * The query parameter which is used to list all dispatch groups.
 */
class ListPnDDispatchGroupsQuery {
    constructor() {
        /**
         * The list of service center identification codes to be used as filters for the dispatch groups.
         * Required: false
         * Array: true
         */
        this.sicCds = undefined;
    }
}
/**
 * Response to the requset to list all dispatch groups.
 */
class ListPnDDispatchGroupsResp {
    constructor() {
        /**
         * All dispatch groups with their corresponding region, which are related to the list of SICs on the request.
         * Required: false
         * Array: true
         */
        this.dispatchGroupsAndRegions = undefined;
    }
}
/**
 * The path parameter for the request to list the dispatch trips.
 */
class ListPnDDispatchTripsPath {
    constructor() {
        /**
         * The location associated with the trip.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameters for the request to list the dispatch trips.
 */
class ListPnDDispatchTripsQuery {
    constructor() {
        /**
         * A type code for zone SICs associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
        /**
         * The date associated with the trip.
         * Required: true
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * The list of statuses for the trip.
         * Required: false
         * Array: true
         */
        this.tripStatusCds = undefined;
        /**
         * The dispatch groups to which the trip belongs.
         * Required: false
         * Array: true
         */
        this.groupIds = undefined;
    }
}
/**
 * Response to the request to list the dispatch trips.
 */
class ListPnDDispatchTripsResp {
    constructor() {
        /**
         * The list of dispatch trips that matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.dispatchTrips = undefined;
        /**
         * Information about dock drop and dock pickup routes.
         * Required: false
         * Array: true
         */
        this.dockRoutes = undefined;
    }
}
/**
 * Path parameter for the request to return a list of current driver locations.
 */
class ListPnDDriverLocationsPath {
    constructor() {
        /**
         * The location associated with the trip.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameter for the request to return a list of driver locations.
 */
class ListPnDDriverLocationsQuery {
    constructor() {
        /**
         * The zone indicator associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: true
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to provide a list of current driver locations.
 */
class ListPnDDriverLocationsResp {
    constructor() {
        /**
         * Information about the driver locations.
         * Required: false
         * Array: true
         */
        this.driverLocations = undefined;
    }
}
/**
 * Request to list inbound planner trailers.
 */
class ListPnDInboundPlannerTrailersRqst {
    constructor() {
        /**
         * Specifies the page size, sort order, etc.
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * The query for searching elastic/XRT.
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to list inbound planner trailers.
 */
class ListPnDInboundPlannerTrailersResp {
    constructor() {
        /**
         * Information about the trailers including the current trailer name of the matching hostDestinationSicCd from unassigned shipments.
         * Required: false
         * Array: true
         */
        this.inboundTrailers = undefined;
    }
}
/**
 * The query parameter used for the request to list the inbound selection profiles associated with the SIC proivded.
 */
class ListPnDInboundSelectionProfilesQuery {
    constructor() {
        /**
         * The service center for which the profiles are being listed.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to list all inbound selection profiles associated with the SIC provided.
 */
class ListPnDInboundSelectionProfilesResp {
    constructor() {
        /**
         * The list of selection profiles which matched the input criterion.
         * Required: false
         * Array: true
         */
        this.selectionProfiles = undefined;
    }
}
/**
 * Request to list stops that have an address that is within the proximity provided.
 */
class ListPnDProximalAddressStopsRqst {
    constructor() {
        /**
         * The list of shipments for a stop and their corresponding proximity parameter.
         * Required: true
         * Array: true
         */
        this.stopProximityChecks = undefined;
        /**
         * The list of planning SICs.
         * Required: true
         * Array: true
         */
        this.hostDestSics = undefined;
        /**
         * The planned date and ETA at the destination.
         * Required: true
         * Array: false
         */
        this.destSicEta = undefined;
    }
}
/**
 * Response to the request to list stops that have an address that is within the proximity provided.
 */
class ListPnDProximalAddressStopsResp {
    constructor() {
        /**
         * The stops that match the criteria for proximity and the reasons why they qualified.
         * Required: false
         * Array: true
         */
        this.proximalStops = undefined;
    }
}
/**
 * Path parameter for the request to list all routes that need to be planned for today's delivery.
 */
class ListPnDRoutesPath {
    constructor() {
        /**
         * The service center for which the routes are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameters used for the request to list all routes that need to be planned for today's delivery.
 */
class ListPnDRoutesQuery {
    constructor() {
        /**
         * The date for which the retrieved shipments are being planned.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Indicates the zone.  The default is IncludeZones.
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
        /**
         * Indicates that a planner is using the software. When this flag is set to true (default), planning routes, new routes, and special services routes are included.
         * Required: false
         * Array: false
         */
        this.plannerInd = undefined;
        /**
         * Indicates newly released routes should be included.  The default is true.
         * Required: false
         * Array: false
         */
        this.newReleaseInd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.categoryCd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.statusCd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServices = undefined;
    }
}
/**
 * Response to the request to list all routes that need to be planned for today's delivery.
 */
class ListPnDRoutesResp {
    constructor() {
        /**
         * The shipments to be delivered in the current plan.
         * Required: false
         * Array: true
         */
        this.routes = undefined;
    }
}
/**
 * Request to list routes by geofence.
 */
class ListPnDRoutesByGeofenceRqst {
    constructor() {
        /**
         * The list of boundaries for the geographical area which is being examined for routes.
         * Required: true
         * Array: true
         */
        this.geofences = undefined;
        /**
         * The service center (and optionally its zones) to which the requested routes belong.
         * Required: false
         * Array: true
         */
        this.hostDestinationSicCds = undefined;
        /**
         * The plan date for routes.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Response to the request to list routes by geofence.
 */
class ListPnDRoutesByGeofenceResp {
    constructor() {
        /**
         * The list of unique identifiers for the routes to which shipments are assigned.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The list of unique identifiers for the trips.
         * Required: false
         * Array: true
         */
        this.tripInstIds = undefined;
    }
}
/**
 * Path parameter for the request to list metrics associated with the specified service center.
 */
class ListPnDServiceCenterMetricsPath {
    constructor() {
        /**
         * The service center for which the metrics are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameter for the request to list metrics associated with the specified service center.
 */
class ListPnDServiceCenterMetricsQuery {
    constructor() {
        /**
         * The planning date to use for metrics calculation.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The role of the user.
         * Required: false
         * Array: false
         */
        this.roleCd = undefined;
        /**
         * A type code for zone SICs associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to list metrics associated with the specified service center.
 */
class ListPnDServiceCenterMetricsResp {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.metrics = undefined;
    }
}
/**
 * Path parameter for the request to list the stops associated with a route.
 */
class ListPnDStopsPath {
    constructor() {
        /**
         * The instance id of the route for which stops are being requested.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Query parameters for the request to list the stops associated with a route.
 */
class ListPnDStopsQuery {
    constructor() {
        /**
         * The list of codes that identify the type of data to be included in the response.
         * Required: false
         * Array: true
         */
        this.stopDetailCds = undefined;
    }
}
/**
 * Response to the request to list the stops associated with a route.
 */
class ListPnDStopsResp {
    constructor() {
        /**
         * The route for the stops.
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * The stop(s) associated with this route.
         * Required: false
         * Array: true
         */
        this.stops = undefined;
    }
}
/**
 * The path parameter for the request to retrieve the suggested trips information for a particular pickup request.
 */
class ListPnDSuggestedTripsForPickupPath {
    constructor() {
        /**
         * The identifier for the pickup request for which trip suggestions are needed.
         * Required: true
         * Array: false
         */
        this.pickupInstId = undefined;
    }
}
/**
 * Query parameter for the request to return a list of trip recommendations.
 */
class ListPnDSuggestedTripsForPickupQuery {
    constructor() {
        /**
         * The zone indicator associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: true
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to retrieve the suggested trips information for a particular pickup request.
 */
class ListPnDSuggestedTripsForPickupResp {
    constructor() {
        /**
         * The list of suggested trips for the pickup request.
         * Required: false
         * Array: true
         */
        this.tripInstIds = undefined;
    }
}
/**
 * The query parameters used for the request to list suggested route names.
 */
class ListPnDSuggestedRouteNamesPath {
    constructor() {
        /**
         * The sic for which to create suggested route names.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Path parameter for suggested route names
 */
class ListPnDSuggestedRouteNamesQuery {
    constructor() {
        /**
         * The date for which the routes and names will be returned.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Indicates if satellite sics should be included.
         * Required: false
         * Array: false
         */
        this.satelliteInd = undefined;
        /**
         * A type code for zone SICs associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to list all suggested route names for the sic.
 */
class ListPnDSuggestedRouteNamesResp {
    constructor() {
        /**
         * The suggested route names for the sic.
         * Required: false
         * Array: true
         */
        this.suggestedRouteNames = undefined;
        /**
         * The list of existing routes.
         * Required: false
         * Array: true
         */
        this.existingRouteSummaries = undefined;
    }
}
/**
 * The path parameters for the request to list the collection of all trips for a SIC and trip date.
 */
class ListPnDTripsPath {
    constructor() {
        /**
         * The location associated with the trip.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameter for the request to list the collection of all trips for a SIC and trip date.
 */
class ListPnDTripsQuery {
    constructor() {
        /**
         * A type code for zone SICs associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
        /**
         * The status for the trip.
         * Required: false
         * Array: true
         */
        this.tripStatusCd = undefined;
        /**
         * The date associated with the trip.
         * Required: true
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * Indicates the type of data to be included in the TripDetail response.
         * Required: false
         * Array: true
         */
        this.tripDetailCds = undefined;
    }
}
/**
 * Response to the request to list trip details.
 */
class ListPnDTripsResp {
    constructor() {
        /**
         * Details about a trip.
         * Required: false
         * Array: true
         */
        this.tripDetails = undefined;
    }
}
/**
 * Path parameter for the request to return a pick up and delivery trip along with the details of its stops.
 */
class ListPnDTripStopsPath {
    constructor() {
        /**
         * The identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Response to the request to list a summary of information about the trip including all trip stops.
 */
class ListPnDTripStopsResp {
    constructor() {
        /**
         * Information about the trip stops.
         * Required: false
         * Array: true
         */
        this.tripStops = undefined;
        /**
         * Information about the trip including all trip stops.
         * Required: false
         * Array: false
         */
        this.tripSummary = undefined;
    }
}
/**
 * Request to list unassigned pickups.
 */
class ListPnDUnassignedPickupsRqst {
    constructor() {
        /**
         * Specifies the page size, sort order, etc.
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * The query for searching elastic/XRT.
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * The range of SICs in scope for filtering the list.  This could contain zones and satellites as well as the host SIC.
         * Required: false
         * Array: true
         */
        this.sicCds = undefined;
        /**
         * The enum value determine the data source used to retrieve data from the pickups.
         * Required: false
         * Array: false
         */
        this.unassignPickupsSourceCd = undefined;
    }
}
/**
 * Response to the request to list unassigned pickups.
 */
class ListPnDUnassignedPickupsResp {
    constructor() {
        /**
         * Information about the pickups.
         * Required: false
         * Array: true
         */
        this.unassignedPickups = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Request to list delivery stops that are unassigned or unmapped based on query parameter unmapped.
 */
class ListPnDUnassignedStopsRqst {
    constructor() {
        /**
         * Specifies the page size, sort order, etc.
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * The query for searching elastic/XRT.
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Determines the retrieval of the unmapped alias.
         * Required: false
         * Array: false
         */
        this.unmappedInd = undefined;
        /**
         * Determines the retrieval of the plan by trailer alias.
         * Required: false
         * Array: false
         */
        this.planByTrailerInd = undefined;
        /**
         * This directs the retrieval of shipments that are past the service date (delivery date) from the past due shipments alias.
         * Required: false
         * Array: false
         */
        this.pastDueShipmentsInd = undefined;
    }
}
/**
 * Response to the request to provide a list of delivery shipments that are unassigned.
 */
class ListPnDUnassignedStopsResp {
    constructor() {
        /**
         * Information about the unassigned shipments.
         * Required: false
         * Array: true
         */
        this.unassignedStops = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Path parameter which is used to list the shipments on a trailer.
 */
class ListShipmentsOnTrailerPath {
    constructor() {
        /**
         * The unique identifier for the equipment, which is a trailer.
         * Required: true
         * Array: false
         */
        this.equipmentInstId = undefined;
    }
}
/**
 * Response to the request to list the shipments on a trailer.
 */
class ListShipmentsOnTrailerResp {
    constructor() {
        /**
         * The identifiers for the shipments that are on the requested trailer.
         * Required: false
         * Array: true
         */
        this.shipmentIds = undefined;
    }
}
/**
 * Path parameter used to merge a list of stops into one target stop on the same trip.
 */
class MergePnDStopsPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to merge a list of stops into one target stop on the same trip.
 */
class MergePnDStopsRqst {
    constructor() {
        /**
         * The stop that the source stop list will be merged into.
         * Required: false
         * Array: false
         */
        this.targetStop = undefined;
        /**
         * The list of stops that will be merged into the target.
         * Required: false
         * Array: true
         */
        this.sourceStops = undefined;
        /**
         * Indicates that all warnings that may occur as a result of this request will be overridden.
         * Required: false
         * Array: false
         */
        this.overrideWarningsInd = undefined;
    }
}
/**
 * Response from merging a list of stops into a target stop.
 */
class MergePnDStopsResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter used to reassign a list of pickup stops to a new trip.
 */
class ReassignPnDPickupStopsPath {
    constructor() {
        /**
         * The unique identifier associated with the target trip to which pickup stops will be assigned.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to reassign a list of pickup stops to a new trip.
 */
class ReassignPnDPickupStopsRqst {
    constructor() {
        /**
         * The previous trip associated with the pickup stops.
         * Required: true
         * Array: false
         */
        this.previousTripInstId = undefined;
        /**
         * The pickup request status provided.  Permitted values: 4-Pre-assigned and 5-Dispatched.
         * Required: true
         * Array: false
         */
        this.pickupStatusCd = undefined;
        /**
         * The list of stops to be reassigned.
         * Required: true
         * Array: true
         */
        this.stops = undefined;
        /**
         * Indicates whether or not to override all soft errors.
         * Required: true
         * Array: false
         */
        this.overrideAllSoftErrorsInd = undefined;
    }
}
/**
 * List of reassigned stops.
 */
class ReassignPnDPickupStopsResp {
    constructor() {
        /**
         * Information about the trip stops.
         * Required: false
         * Array: true
         */
        this.tripStops = undefined;
    }
}
/**
 * Path parameter used to reassign a list of shipments to a new route.
 */
class ReassignPnDShipmentsPath {
    constructor() {
        /**
         * The instance identifier of the route to which shipments will be reassigned.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Request body for reassigning shipments from one route to another.
 */
class ReassignPnDShipmentsRqst {
    constructor() {
        /**
         * The old route associated with the shipments.
         * Required: true
         * Array: false
         */
        this.previousRouteInstId = undefined;
        /**
         * Mandatory list of shipments to be reassigned.
         * Required: true
         * Array: true
         */
        this.shipmentIds = undefined;
        /**
         * Overrides the edits which enforce TDC date validations for each shipment on the request.
         * Required: false
         * Array: false
         */
        this.overrideTdcInd = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Request to reassign a list of stops to a route.
 */
class ReassignStopsRqst {
    constructor() {
        /**
         * The old trip associated with the stops.
         * Required: true
         * Array: false
         */
        this.oldTrip = undefined;
        /**
         * The new trip associated with the stops.
         * Required: true
         * Array: false
         */
        this.newTrip = undefined;
        /**
         * The old route associated with the stops.
         * Required: true
         * Array: false
         */
        this.oldRoute = undefined;
        /**
         * The new route associated with the stops.
         * Required: true
         * Array: false
         */
        this.newRoute = undefined;
        /**
         * The list of trip nodes.
         * Required: false
         * Array: true
         */
        this.tripNode = undefined;
        /**
         * Indicates whether or not to override all soft errors.
         * Required: true
         * Array: false
         */
        this.overrideAllSoftErrorsInd = undefined;
        /**
         * Overrides the edits which enforce TDC date validations for each shipment on the request.
         * Required: false
         * Array: false
         */
        this.overrideTdcInd = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * List of stops for a trip assigned to a specific route.
 */
class ReassignStopsResp {
    constructor() {
        /**
         * Information about the trip stops.
         * Required: false
         * Array: true
         */
        this.tripStops = undefined;
    }
}
/**
 * Request to resequence the freight on the trailer by the order of its stops on a specific trip.
 */
class ResequenceFreightByTripRqst {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.terminalSicCode = undefined;
        /**
         * The geo coordinates of the service center location.
         * Required: true
         * Array: false
         */
        this.terminalGeoCoordinates = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.routeBalancingTrip = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates that the route sequence indicator must be overridden or not.
         * Required: false
         * Array: false
         */
        this.overrideSeqInd = undefined;
    }
}
/**
 * Path parameter used to split a list of shipments from an existing stop into another one.
 */
class SplitPnDTripStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to split a list of shipments from an existing stop into another stop or a new one.
 */
class SplitPnDTripStopRqst {
    constructor() {
        /**
         * The stop that contains the shipments that will be split off from it.
         * Required: false
         * Array: false
         */
        this.originalStop = undefined;
        /**
         * The stop to which the shipments will be split off into from the original stop.  It is optional and may be provided if that stop is known; otherwise the service will create a new stop by default.
         * Required: false
         * Array: false
         */
        this.targetStop = undefined;
        /**
         * The list of activities to be split off from the original stop.
         * Required: false
         * Array: true
         */
        this.splitActivities = undefined;
    }
}
/**
 * Response from splitting the shipments from an existing stop.
 */
class SplitPnDTripStopResp {
    constructor() {
        /**
         * The new stop created from the shipments that were split from the original stop.
         * Required: false
         * Array: false
         */
        this.newStop = undefined;
    }
}
/**
 * The path parameter for the request to get the driver location for an employee ID.
 */
class StartDriverLocationPath {
    constructor() {
        /**
         * The unique identifier for the employee which is used to get the driver location.
         * Required: true
         * Array: false
         */
        this.employeeId = undefined;
    }
}
/**
 * Request to run the stop sequencing model.
 */
class StopSequenceModelRqst {
    constructor() {
        /**
         * The list of trips to optimize.
         * Required: false
         * Array: true
         */
        this.input = undefined;
    }
}
/**
 * Request to run the stop sequencing model.
 */
class StopSequenceModelResp {
    constructor() {
        /**
         * The response with the optimized order of trips with stops.
         * Required: false
         * Array: true
         */
        this.output = undefined;
    }
}
/**
 * The request to update the status for shipments to Transfer.
 */
class TransferShipmentsRqst {
    constructor() {
        /**
         * The list of shipment identifiers for the shipments that will be updated.
         * Required: true
         * Array: true
         */
        this.shipmentIds = undefined;
        /**
         * The code which represents results of a specific delivery activity.  I.e. the status code of the shipments.
         * Required: true
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * Indicates that shipments that the shipment external status for the list of external status IDs should be updated.
         * Required: false
         * Array: false
         */
        this.updateShmExternalStatusInd = undefined;
        /**
         * The list of shipment external status ID values that will be used to update the shipment's external status.
         * Required: false
         * Array: true
         */
        this.externalShmStatusIds = undefined;
    }
}
/**
 * Path parameter used to unassign a list of shipments from a route.
 */
class UnassignPnDShipmentsFromRoutePath {
    constructor() {
        /**
         * The instance id of the route to which shipments will be unassigned.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Request body for unassigning shipments from a route.
 */
class UnassignPnDShipmentsFromRouteRqst {
    constructor() {
        /**
         * Mandatory list of shipments to be unassigned from a route.
         * Required: true
         * Array: true
         */
        this.shipmentIds = undefined;
    }
}
/**
 * Request to unassign a list of stops from a route.
 */
class UnassignStopsRqst {
    constructor() {
        /**
         * The trip associated with the stops.
         * Required: true
         * Array: false
         */
        this.trip = undefined;
        /**
         * The trip associated with the stops.
         * Required: false
         * Array: true
         */
        this.tripNodes = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * The path parameter which is used to update or create the driver's current location.
 */
class UpdateCurrentDriverLocationPath {
    constructor() {
        /**
         * The unique identifier for the employee which is used to update or create the driver's current location.
         * Required: true
         * Array: false
         */
        this.employeeId = undefined;
    }
}
/**
 * Request to update or create the driver's current location.
 */
class UpdateCurrentDriverLocationRqst {
    constructor() {
        /**
         * Information that will be used to update or create the current location of the driver.
         * Required: true
         * Array: false
         */
        this.currentDsrLocation = undefined;
    }
}
/**
 * The path parameter which is used to specify the customer profile to update.
 */
class UpdateCustomerGeoCoordinatesPath {
    constructor() {
        /**
         * The identifier for the customer account.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
    }
}
/**
 * The request to update the customer GeoCoordinates.
 */
class UpdateCustomerGeoCoordinatesRqst {
    constructor() {
        /**
         * The new latitude and longitude for the customer.
         * Required: true
         * Array: false
         */
        this.geoCoordinate = undefined;
    }
}
/**
 * Request to update the audit status for a given delivery shipment.
 */
class UpdateDeliveryShipmentAuditStatusRqst {
    constructor() {
        /**
         * The unique identifier of the delivery shipment.
         * Required: true
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The timestamp at which OCR processing started for the given DR.
         * Required: true
         * Array: false
         */
        this.processStartDateTime = undefined;
        /**
         * The overall status of the audit for the delivery shipment.
         * Required: true
         * Array: false
         */
        this.shipmentAuditStatusCd = undefined;
    }
}
/**
 * Request to update the onExcludedTrailerInd in the SCO_DLVR_SHM table.
 */
class UpdateDeliveryShipmentExcludedTrailerIndPath {
    constructor() {
        /**
         * The location where the trailer's excluded status is changed.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier of the trailer that needs its excluded status changed.
         * Required: true
         * Array: false
         */
        this.equipmentInstId = undefined;
    }
}
/**
 * Request to update the onExcludedTrailerInd in the SCO_DLVR_SHM table.
 */
class UpdateDeliveryShipmentExcludedTrailerIndRqst {
    constructor() {
        /**
         * The excluded indicator.
         * Required: true
         * Array: false
         */
        this.onExcludedTrailerInd = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Request to update the delivery shipment special services selections made by the manual auditor.
 */
class UpdateDeliveryShipmentFromAuditRqst {
    constructor() {
        /**
         * The output of the manual audit done on DRs.
         * Required: true
         * Array: false
         */
        this.deliverySpecialServiceAudit = undefined;
    }
}
/**
 * The path parameter which is used to update the operations customer profile.
 */
class UpdateOperationsCustomerProfilePath {
    constructor() {
        /**
         * The unique identifier associated with a customer's operational profile.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
    }
}
/**
 * Request to update the operations customer profile.
 */
class UpdateOperationsCustomerProfileRqst {
    constructor() {
        /**
         * Details about the customer profile.
         * Required: true
         * Array: false
         */
        this.customerOperationsProfile = undefined;
        /**
         * The list of profile related notes about a customer who has a profile in the system.
         * Required: true
         * Array: true
         */
        this.customerOperationsNotes = undefined;
        /**
         * The list of profile related schedules for a customer who has a profile in the system.
         * Required: true
         * Array: true
         */
        this.customerOperationsSchedules = undefined;
    }
}
/**
 * Response to the request to update the operations customer profile.
 */
class UpdateOperationsCustomerProfileResp {
    constructor() {
        /**
         * Details about the customer profile.
         * Required: true
         * Array: false
         */
        this.customerOperationsProfile = undefined;
        /**
         * The list of profile related notes about a customer who has a profile in the system.
         * Required: true
         * Array: true
         */
        this.customerOperationsNotes = undefined;
        /**
         * The list of profile related schedules for a customer who has a profile in the system.
         * Required: true
         * Array: true
         */
        this.customerOperationsSchedules = undefined;
    }
}
/**
 * Request to update the delivery status for shipments.
 */
class UpdatePnDDeliveryStatusForShipmentsRqst {
    constructor() {
        /**
         * The list of shipments, including details, that require a status qualification change.
         * Required: true
         * Array: true
         */
        this.deliveryQualifierShipmentStatusChanges = undefined;
        /**
         * Indicates whether to override the rule controlling back dating for the shipment movement.
         * Required: false
         * Array: false
         */
        this.dateOverrideInd = undefined;
        /**
         * Indicates whether to override soft errors during the validation.
         * Required: false
         * Array: false
         */
        this.overrideAllSoftErrorsInd = undefined;
        /**
         * Indicates whether to override soft errors during the validation of a PLT pro having the correct count of child pros.
         * Required: false
         * Array: false
         */
        this.overrideChildProCountDiscrepancyInd = undefined;
    }
}
/**
 * Response to the request to update the delivery status for shipments.
 */
class UpdatePnDDeliveryStatusForShipmentsResp {
    constructor() {
        /**
         * The list of all the shipments from the request that had soft error warnings; these should be shown to the user.
         * Required: false
         * Array: true
         */
        this.softErrorWarnings = undefined;
    }
}
/**
 * Request to update a dispatch group and associate it with one or more dispatch group regions.
 */
class UpdatePnDDispatchGroupRqst {
    constructor() {
        /**
         * The dispatch group that will be created for this request.
         * Required: false
         * Array: false
         */
        this.dispatchGroup = undefined;
        /**
         * An optional dispatch group region that, when present, will be updated and associated with the dispatch group for this request.
         * Required: false
         * Array: false
         */
        this.dispatchGroupRegion = undefined;
    }
}
/**
 * Path parameter which is used to update a drop stop and activity at a customer location.
 */
class UpdateDropStopsPath {
    constructor() {
        /**
         * The location for the drop stop.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to update a drop stop and activity at a customer location.
 */
class UpdateDropStopsRqst {
    constructor() {
        /**
         * The list of drops stops with dropLocation to be performed for a dropStop.
         * Required: false
         * Array: true
         */
        this.dropStops = undefined;
    }
}
/**
 * Path parameter used to update a list of equipment associated with a hook activity at a drop stop.
 */
class UpdatePnDEquipmentHookStopPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to update a hook stop with a list of dropped equipment.
 */
class UpdatePnDEquipmentHookStopRqst {
    constructor() {
        /**
         * The geo area default normally associated with this route.
         * Required: false
         * Array: false
         */
        this.routeArea = undefined;
        /**
         * The drop stop where the driver will be performing this hook activity.
         * Required: false
         * Array: false
         */
        this.hookStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operation-related note specific to the hook activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this hook stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * The comment text about any changes to the pickup instructions for this hook stop.
         * Required: false
         * Array: false
         */
        this.overridePickupInstruction = undefined;
        /**
         * The service center responsible for the hook stop.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates that all warnings that may occur as a result of this request will be overridden.
         * Required: false
         * Array: false
         */
        this.overrideWarningsInd = undefined;
        /**
         * A list containing the equipment that was dropped at a specific location for the drop stop, along with activity information which will be used for the hook activity.
         * Required: false
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Response to the request to update a hook stop with a list of dropped equipment.
 */
class UpdatePnDEquipmentHookStopResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Request to update list of Routes info on SCO_ROUTE table
 */
class UpdatePnDRoutesRqst {
    constructor() {
        /**
         * Information to update the route
         * Required: false
         * Array: true
         */
        this.routes = undefined;
    }
}
/**
 * Response to the request to update list of Routes info on SCO_ROUTE table
 */
class UpdatePnDRoutesResp {
    constructor() {
        /**
         * Information to update the route
         * Required: false
         * Array: true
         */
        this.routes = undefined;
        /**
         * This is for informational messages that could come from the API. If present, this would be displayed by the UI for a successful route update (as opposed to an error message for unsuccessful update).
         * Required: false
         * Array: true
         */
        this.messages = undefined;
    }
}
/**
 * The request to update the route status for shipments.
 */
class UpdatePnDRouteStatusForShipmentsRqst {
    constructor() {
        /**
         * The list of shipment identifiers for the shipments that will be updated.
         * Required: true
         * Array: true
         */
        this.shipmentIds = undefined;
        /**
         * Identifies whether the shipment is currently on a route (either planning route or dispatched route), staged (available) for routing, or is not on a route.
         * Required: true
         * Array: false
         */
        this.routeTypeCd = undefined;
    }
}
/**
 * Request to update a trip.
 */
class UpdatePnDTripRqst {
    constructor() {
        /**
         * The information required to update a trip.
         * Required: true
         * Array: false
         */
        this.pndTrip = undefined;
        /**
         * The unique identifier for a dispatch group.
         * Required: true
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * Indicates when it is not required to check equipment for suitability.
         * Required: false
         * Array: false
         */
        this.equipmentCheckInd = undefined;
        /**
         * Indicates when a trip has completed stops.
         * Required: true
         * Array: false
         */
        this.confirmCompleteInd = undefined;
        /**
         * Indicates whether to override all soft errors.
         * Required: true
         * Array: false
         */
        this.overrideAllSoftErrorsInd = undefined;
        /**
         * This indicates whether to override the warning condition if the driver's double or triple trailer license on this trip has expired.  Use a value of true to override this warning message.
         * Required: false
         * Array: false
         */
        this.overrideDsrLicenseInd = undefined;
    }
}
/**
 * Response to the request to update a trip.
 */
class UpdatePnDTripResp {
    constructor() {
        /**
         * Information about the trip.
         * Required: false
         * Array: false
         */
        this.tripDetail = undefined;
        /**
         * The list of warnings that occurred while attempting to dispatch the trip; these messages are overridable and should be shown to the user.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Request to update the stop sequence for the route on a trip.
 */
class UpdateRouteStopSequenceRqst {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The unique identifier for the route.
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The list of trip node stops and the shipments on the corresponding route with their new sequences.
         * Required: true
         * Array: true
         */
        this.stopSequences = undefined;
    }
}
/**
 * Path parameter which is used to update the trip status.
 */
class UpdateTripStatusPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to update the trip status.
 */
class UpdateTripStatusRqst {
    constructor() {
        /**
         * The estimated arrive timestamp.
         * Required: true
         * Array: false
         */
        this.estimatedArriveDateTime = undefined;
        /**
         * The status for the trip.
         * Required: true
         * Array: false
         */
        this.tripStatusCd = undefined;
    }
}
/**
 * Path parameter for the request to validate a new route.
 */
class ValidatePnDRoutePath {
    constructor() {
        /**
         * The service center for which the new route validity is being determined.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The request to validate a new route.
 */
class ValidatePnDRouteRqst {
    constructor() {
        /**
         * The date for which the new route validity is being determined.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The prefix for this route, generally the area name.
         * Required: true
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * The suffix for this route, uniquely identifying the route.
         * Required: true
         * Array: false
         */
        this.routeSuffix = undefined;
    }
}
/**
 * Response to the request to validate a new route.
 */
class ValidatePnDRouteResp {
    constructor() {
        /**
         * Indicates whether or not the new route is valid.
         * Required: false
         * Array: false
         */
        this.validRouteInd = undefined;
    }
}
/**
 * Path parameter used to build an optimizer request for submission to an optimization vendor.
 */
class BuildPnDOptimizerRequestPath {
    constructor() {
        /**
         * The number of the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Response to the request to build an optimizer request for submission to an optimization vendor.
 */
class BuildPnDOptimizerRequestResp {
    constructor() {
        /**
         * The vendor-specific optimization request.
         * Required: true
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * The request to calculate customer dwell times.
 */
class CalculateCustomerDwellTimesRqst {
    constructor() {
        /**
         * The list of trip nodes for which the dwell times will be calculated.
         * Required: true
         * Array: true
         */
        this.dwellTimeTripNodes = undefined;
    }
}
/**
 * Response to the request to calculate customer dwell times.
 */
class CalculateCustomerDwellTimesResp {
    constructor() {
        /**
         * The list of calculated customer dwell times.
         * Required: false
         * Array: true
         */
        this.customerDwellTimes = undefined;
    }
}
/**
 * Request to calculate PnD Optimizer metrics.
 */
class CalculatePnDOptimizerMetricsRqst {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The sequence number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runSequenceNbr = undefined;
        /**
         * The stage of operations for calculating the PnD metrics. E.g. for PnD, the stages are: Planning, Dispatched, Dock.
         * Required: true
         * Array: false
         */
        this.stage = undefined;
        /**
         * The specific activity performed in a stage for which the metrics were taken. E.g. for PnD, Released or Sequence could be a sub stage during the Planning stage.
         * Required: true
         * Array: false
         */
        this.subStage = undefined;
        /**
         * The list of unique route identifiers for the metrics to be calculated.
         * Required: true
         * Array: true
         */
        this.routeInstIds = undefined;
    }
}
/**
 * Response to the request to list the calculated PnD Optimizer metrics.
 */
class CalculatePnDOptimizerMetricsResp {
    constructor() {
        /**
         * The metrics that were calculated.
         * Required: false
         * Array: true
         */
        this.metrics = undefined;
    }
}
/**
 * The request to store a new optimizer run.
 */
class CreatePnDOptimizerRqst {
    constructor() {
        /**
         * The terminal location for the optimization.
         * Required: true
         * Array: false
         */
        this.terminalSicCode = undefined;
        /**
         * The plan date for the optimization.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The status of the PDO control.
         * Required: true
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * Indicates whether shipments were selected for optimization.
         * Required: true
         * Array: false
         */
        this.shipmentsSelectedForOptimizationInd = undefined;
    }
}
/**
 * Response to the request to store a new optimizer run.
 */
class CreatePnDOptimizerResp {
    constructor() {
        /**
         * The generated number for the new optimizer run.
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The path parameters used to store the Commodity and Dimension details.
 */
class CreatePnDOptimizerCommodityDimensionsPath {
    constructor() {
        /**
         * The run number for the optimizer commodities and dimensions.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Request to create optimizer drivers.
 */
class CreatePnDOptimizerDriversRqst {
    constructor() {
        /**
         * The generated number for the new optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The location with which the driver is associated.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The date for which the drivers are being planned.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Response to the request to create optimizer drivers.
 */
class CreatePnDOptimizerDriversResp {
    constructor() {
        /**
         * The list of records created for optimizer drivers.
         * Required: false
         * Array: true
         */
        this.pdoRequestDrivers = undefined;
    }
}
/**
 * The request to create optimizer equipment.
 */
class CreatePnDOptimizerEquipmentRqst {
    constructor() {
        /**
         * The run number for the optimizer equipment.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The location associated with the optimizer equipment.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to create optimizer equipment.
 */
class CreatePnDOptimizerEquipmentResp {
    constructor() {
        /**
         * The optimizer equipment record that was created.
         * Required: false
         * Array: true
         */
        this.pdoRequestEquipment = undefined;
    }
}
/**
 * The request to create a new Optimizer Failure Response.
 */
class CreatePnDOptimizerFailureResponseRqst {
    constructor() {
        /**
         * The fail reason for the optimization.
         * Required: false
         * Array: false
         */
        this.failReason = undefined;
        /**
         * The run number for the corresponding optimizer failure to create.
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence number to uniquely identify the corresponding optimizer failure to create.
         * Required: false
         * Array: false
         */
        this.runNbrSeq = undefined;
    }
}
/**
 * The response of the created the new Optimizer Failure Response.
 */
class CreatePnDOptimizerFailureResponseResp {
    constructor() {
        /**
         * The run number for the corresponding optimizer failure response created.
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to create optimizer pickup requests based upon the previous week.
 */
class CreatePnDOptimizerPickupRequestsRqst {
    constructor() {
        /**
         * The run number of the optimization.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The plan date for the optimization.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The terminal location for the optimization.
         * Required: true
         * Array: false
         */
        this.terminalSicCd = undefined;
    }
}
/**
 * Response to the request to create optimizer pickup requests based upon the previous week.
 */
class CreatePnDOptimizerPickupRequestsResp {
    constructor() {
        /**
         * The optimizer pickup request records that were created.
         * Required: false
         * Array: true
         */
        this.pickupRequests = undefined;
    }
}
/**
 * The path parameters which are used to create optimizer shipments for eligible routed shipments.
 */
class CreatePnDOptimizerRoutedShipmentsPath {
    constructor() {
        /**
         * The run number of the optimization.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to create optimizer shipments for eligible routed shipments.
 */
class CreatePnDOptimizerRoutedShipmentsRqst {
    constructor() {
        /**
         * The plan date for the optimization.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The terminal location for the optimization.
         * Required: true
         * Array: false
         */
        this.terminalSicCd = undefined;
    }
}
/**
 * The path parameters used to create PND routes from optimizer routes.
 */
class CreatePnDOptimizerRoutesPath {
    constructor() {
        /**
         * The terminal location for the optimization.
         * Required: true
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The plan date for the optimization.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * The request to create PnD routes from optimizer routes.
 */
class CreatePnDOptimizerRoutesRqst {
    constructor() {
        /**
         * Optimizer response route details that will be used to create PnD routes.
         * Required: false
         * Array: true
         */
        this.optimizerRouteStops = undefined;
    }
}
/**
 * Response to the request to create PnD routes from optimizer routes.
 */
class CreatePnDOptimizerRoutesResp {
    constructor() {
        /**
         * List of optimizer routes that have been successfully created in PnD.
         * Required: false
         * Array: true
         */
        this.pdoResponseRoute = undefined;
        /**
         * List of optimizer exception routes that have not been successfully created in PnD.
         * Required: false
         * Array: true
         */
        this.pdoException = undefined;
    }
}
/**
 * The request to create optimizer shipments.
 */
class CreatePnDOptimizerShipmentsRqst {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Optimizer shipment details that will be used to create records in SCO_PDO_RQST_SHIPMENT.
         * Required: true
         * Array: true
         */
        this.optimizerShipments = undefined;
    }
}
/**
 * Response to the request to create optimizer shipments.
 */
class CreatePnDOptimizerShipmentsResp {
    constructor() {
        /**
         * Records that were created for optimizer shipments.
         * Required: false
         * Array: true
         */
        this.pdoRequestShipments = undefined;
    }
}
/**
 * The path parameters used to create routes from the optimizer.
 */
class CreateRoutesFromOptimizerPath {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The sequence number used to uniquely identify the run response.
         * Required: true
         * Array: false
         */
        this.runSequenceNbr = undefined;
    }
}
/**
 * The request to create routes from the optimizer.
 */
class CreateRoutesFromOptimizerRqst {
    constructor() {
        /**
         * The optimizer data that will be stored in SCO_PDO_RSPNS_HDR.
         * Required: true
         * Array: false
         */
        this.optimizerResult = undefined;
    }
}
/**
 * Response to the request to create routes from the optimizer.
 */
class CreateRoutesFromOptimizerResp {
}
/**
 * The path parameters used to check whether or not there was a successful previous PnD optimizer run.
 */
class DeterminePnDOptimizerSuccessfulRunPath {
    constructor() {
        /**
         * The terminal location for the optimization.
         * Required: true
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The plan date for the optimization.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Response to the request to check whether or not there was a successful previous PnD optimizer run.
 */
class DeterminePnDOptimizerSuccessfulRunResp {
    constructor() {
        /**
         * Indicates whether or not any previous PnD optimizer run was successful.
         * Required: false
         * Array: false
         */
        this.pndOptimizerSuccessPrevRunInd = undefined;
    }
}
/**
 * Path parameter used to execute an optimizer request with an optimization vendor and create routes.
 */
class ExecutePnDOptimizerRequestPath {
    constructor() {
        /**
         * The number of the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to execute an optimizer request with an optimization vendor and create routes.
 */
class ExecutePnDOptimizerRequestRqst {
    constructor() {
        /**
         * The vendor-specific optimization request.
         * Required: true
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Response to the request to execute an optimizer request with an optimization vendor and create routes.
 */
class ExecutePnDOptimizerRequestResp {
    constructor() {
        /**
         * The sequence number for the optimizer run in which routes were created.
         * Required: true
         * Array: false
         */
        this.runSequenceNbr = undefined;
    }
}
/**
 * The query parameters used to retrieve the optimizer status associated with the SIC or runNbr provided.
 */
class GetPnDOptimizerStatusQuery {
    constructor() {
        /**
         * The terminal SIC which is related to the Optimizer for which the status is going to be retrieved.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier of the Optimizer for which the status is going to be retrieved.
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Response to the request to retrieve the optimizer status associated with the SIC or runNbr provided.
 */
class GetPnDOptimizerStatusResp {
    constructor() {
        /**
         * Details about the status of the optimizer which matched the input criteria.
         * Required: false
         * Array: false
         */
        this.pdoControl = undefined;
    }
}
/**
 * Path parameter used to retrieve the summary that is associated with an optimizer run.
 */
class GetPnDOptimizerSummaryPath {
    constructor() {
        /**
         * The number of the optimizer that will be used to filter the summary to be returned.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Query parameter used to return an optimizer summary.
 */
class GetPnDOptimizerSummaryQuery {
    constructor() {
        /**
         * The summary type codes for which the appropriate optimizer summary data will be returned.
         * Required: true
         * Array: true
         */
        this.optimizerSummaryTypeCds = undefined;
    }
}
/**
 * Response to the request to return an optimizer summary.
 */
class GetPnDOptimizerSummaryResp {
    constructor() {
        /**
         * The optimizer summary.
         * Required: false
         * Array: false
         */
        this.optimizerSummary = undefined;
        /**
         * The number of the optimizer that was used to filter the summary to be returned.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Path parameter used to list the deliveryshipments by profile.
 */
class ListDeliveryShipmentsByProfilePath {
    constructor() {
        /**
         * The service center for which the delivery shipments are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier for the selection profile.
         * Required: true
         * Array: false
         */
        this.selectionProfileId = undefined;
    }
}
/**
 * query parameter used to list the deliveryshipments by profile.
 */
class ListDeliveryShipmentsByProfileQuery {
    constructor() {
        /**
         * The date for which the retrieved shipments are being planned.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The code which determines whether the information retrieved will include zones, only zones, or no zones.  Permitted values: IncludeZones, NoZones, ZonesOnly.
         * Required: true
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Request to list delivery shipments by PRO.
 */
class ListDeliveryShipmentsByProfileRqst {
    constructor() {
        /**
         * Specifies the page size, sort order, fields to include/exclude etc.
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * The code which represents the index name to be used in deliveryShipment elastic.
         * Required: true
         * Array: false
         */
        this.deliveryShipmentIndexCd = undefined;
    }
}
/**
 * Response to the request to list delivery shipments by profile.
 */
class ListDeliveryShipmentsByProfileResp {
    constructor() {
        /**
         * The shipments to be delivered in the current plan.
         * Required: false
         * Array: true
         */
        this.deliveryShipments = undefined;
    }
}
/**
 * Request to list delivery shipments by PRO.
 */
class ListDeliveryShipmentsByProRqst {
    constructor() {
        /**
         * The business identifiers for the shipments which are used for the search criteria.
         * Required: true
         * Array: true
         */
        this.proNbrs = undefined;
    }
}
/**
 * Response to the request to list delivery shipments by PRO.
 */
class ListDeliveryShipmentsByProResp {
    constructor() {
        /**
         * The XRT search response header information.
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * The Elastic Search document for the shipment details.
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * The path parameter which is used to list operating schedules for customers.
 */
class ListOperationsSchedulesForCustomersPath {
    constructor() {
        /**
         * The code that is associated with this type of operation for the schedule.
         * Required: true
         * Array: false
         */
        this.operationTypeCd = undefined;
    }
}
/**
 * The request to list operating schedules for customers.
 */
class ListOperationsSchedulesForCustomersRqst {
    constructor() {
        /**
         * The codes that are associated with the week days for which the customer schedules will be returned.
         * Required: false
         * Array: true
         */
        this.weekDayCds = undefined;
    }
}
/**
 * Response to the request to list operating schedules for customers.
 */
class ListOperationsSchedulesForCustomersResp {
    constructor() {
        /**
         * The list of customers' operating hours schedules for pickup and delivery for every week day or just the ones that were requested.
         * Required: false
         * Array: true
         */
        this.customerOperationsSchedules = undefined;
    }
}
/**
 * Path parameter used to list the enroute shipments with same addresses associated with an optimizer run.
 */
class ListOptimizerSameAddressShipmentsPath {
    constructor() {
        /**
         * The number of the optimizer run with which the planner is working.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Response to the request to list the enroute shipments with same addresses associated with an optimizer run.
 */
class ListOptimizerSameAddressShipmentsResp {
    constructor() {
        /**
         * A list of enroute shipments with same addresses associated with an optimizer run.
         * Required: false
         * Array: true
         */
        this.sameAddressShipments = undefined;
    }
}
/**
 * Path parameter for the request to return a list of drivers.
 */
class ListPnDDriversPath {
    constructor() {
        /**
         * The code associated with the driver's location.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Query parameter for the request to return a list of drivers.
 */
class ListPnDDriversQuery {
    constructor() {
        /**
         * The plan date of the request.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Response to the request to return a list of drivers.
 */
class ListPnDDriversResp {
    constructor() {
        /**
         * A list of drivers for the SIC and plan date.
         * Required: false
         * Array: true
         */
        this.pnDDrivers = undefined;
    }
}
/**
 * The path parameter for the request to list spotted or dropped equipment for a SIC.
 */
class ListSpotOrDropEquipmentPath {
    constructor() {
        /**
         * The identification code for the location associated with the equipment.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameter for the request to list spotted or dropped equipment for a SIC.
 */
class ListSpotOrDropEquipmentQuery {
    constructor() {
        /**
         * The code which determines whether zones are included, only zones are used, or zones aren't included.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to list spotted or dropped equipment for a SIC.
 */
class ListSpotOrDropEquipmentResp {
    constructor() {
        /**
         * The list of all equipment that has been spotted or dropped, at a customer site or drop location, for a SIC.
         * Required: false
         * Array: true
         */
        this.spotOrDropEquipment = undefined;
    }
}
/**
 * The path parameter for the request to list the drivers for a trip.
 */
class ListTripDriversPath {
    constructor() {
        /**
         * The location associated with the trip.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * The query parameters for the request to list the drivers for a trip.
 */
class ListTripDriversQuery {
    constructor() {
        /**
         * The date associated with the trip.
         * Required: true
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * A type code for zone SICs associated with the data to be returned. Permitted values: NoZones, IncludeZones, and ZonesOnly.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to list the drivers for a trip.
 */
class ListTripDriversResp {
    constructor() {
        /**
         * Details for the list of drivers.
         * Required: false
         * Array: true
         */
        this.driverDetails = undefined;
    }
}
/**
 * The query parameters for the request to list trip node activities.
 */
class ListTripNodeActivitiesQuery {
    constructor() {
        /**
         * The unique identifier of the trip which is associated with the activities.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The business identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The unique identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The unique identifier for a pickup request which is associated with an activity on a trip.
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         * The code which represents the results of a specific delivery activity.
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response to the request to list trip node activities.
 */
class ListTripNodeActivitiesResp {
    constructor() {
        /**
         * The list of Trip Node Activities that matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.tripNodeActivities = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Path parameter used to list the equipment for a SIC.
 */
class ListPnDEquipmentPath {
    constructor() {
        /**
         * The location for which the equipment is being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Response to the request to list the equipment for a SIC.
 */
class ListPnDEquipmentResp {
    constructor() {
        /**
         * A list of equipment for the SIC for the optimizer.
         * Required: false
         * Array: true
         */
        this.pnDEquipment = undefined;
    }
}
/**
 * Path parameter used to retrieve a list of commodities and their dimensions.
 */
class ListPnDOptimizerCommodityDimensionsPath {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The response with the list of optimizer commodities and their dimensions.
 */
class ListPnDOptimizerCommodityDimensionsResp {
    constructor() {
        /**
         * The list of commodities and dimensions associated with each shipment.
         * Required: true
         * Array: true
         */
        this.optimizerCommodityDimensions = undefined;
    }
}
/**
 * Path parameter used to list the drivers associated with an optimizer run.
 */
class ListPnDOptimizerDriversPath {
    constructor() {
        /**
         * The optimizer run number with which the planner is working.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Response to the request to list the drivers associated with an optimizer run.
 */
class ListPnDOptimizerDriversResp {
    constructor() {
        /**
         * A list of drivers associated to an optimizer run.
         * Required: false
         * Array: true
         */
        this.pdoRequestDrivers = undefined;
    }
}
/**
 * Path parameter used to list equipment that is associated with an optimizer run.
 */
class ListPnDOptimizerEquipmentPath {
    constructor() {
        /**
         * The number of the optimizer that will be used to filter the equipment to be returned.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Response to the request to list equipment that is associated with an optimizer run.
 */
class ListPnDOptimizerEquipmentResp {
    constructor() {
        /**
         * The details for all the equipment associated with the run number.
         * Required: false
         * Array: true
         */
        this.equipmentDetails = undefined;
    }
}
/**
 * The query parameters used to list the optimizer parameters associated with the SIC proivded.
 */
class ListPnDOptimizerParametersQuery {
    constructor() {
        /**
         * The service center for which the optimizer parameters are being retrieved.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Determines if parameters related to drivers, equipment, optimizers or shipments will be retrieved.
         * Required: true
         * Array: false
         */
        this.optimizerParameterTypeCd = undefined;
    }
}
/**
 * Response to the request to list the optimizer parameters associated with the provided SIC.
 */
class ListPnDOptimizerParametersResp {
    constructor() {
        /**
         * The list of optimizer parameters which matched the input criteria.
         * Required: false
         * Array: true
         */
        this.optimizerParameters = undefined;
    }
}
/**
 * Path parameter which is used to list optimizer pickup stops associated with a route.
 */
class ListPnDOptimizerPickupStopsPath {
    constructor() {
        /**
         * The unique identifier for the route for which stops are being requested.
         * Required: true
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Response to the request to list optimizer pickup stops associated with a route.
 */
class ListPnDOptimizerPickupStopsResp {
    constructor() {
        /**
         * The route for the stops.
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * The pickup stop(s) associated with this route.
         * Required: false
         * Array: true
         */
        this.pickupStops = undefined;
    }
}
/**
 * Path parameters used to list the optimizer results.
 */
class ListPnDOptimizerResultsPath {
    constructor() {
        /**
         * The number of the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The sequence number used to uniquely identify the optimizer run.
         * Required: true
         * Array: false
         */
        this.runSequenceNbr = undefined;
    }
}
/**
 * Response to the request to list optimizer results.
 */
class ListPnDOptimizerResultsResp {
    constructor() {
        /**
         * The metrics related to the optimizer result.
         * Required: false
         * Array: true
         */
        this.metrics = undefined;
        /**
         * The routed exceptions related to the optimizer result.
         * Required: false
         * Array: true
         */
        this.pdoExceptions = undefined;
    }
}
/**
 * Path parameters used to list the routes associated with an optimizer run.
 */
class ListPnDOptimizerRoutesPath {
    constructor() {
        /**
         * The optimizer run number with which the planner is working.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The sequence number to uniquely identify the corresponding optimizer routes.
         * Required: true
         * Array: false
         */
        this.runSequenceNbr = undefined;
    }
}
/**
 * Response to the request to list the routes associated with an optimizer run.
 */
class ListPnDOptimizerRoutesResp {
    constructor() {
        /**
         * A list of routes with stops that are associated with an optimizer run.
         * Required: false
         * Array: true
         */
        this.optimizerRouteStops = undefined;
    }
}
/**
 * The request to list the collection of otimizer routes.
 */
class ListPnDOptimizerRoutesByRouteInstIdsRqst {
    constructor() {
        /**
         * The list of unique identifiers for the routes which will be returned.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
    }
}
/**
 * Response to the request to list the routes created through optimizer runs.
 */
class ListPnDOptimizerRoutesByRouteInstIdsResp {
    constructor() {
        /**
         * A list of routes with stops and shipments that had been created through optimizer runs.
         * Required: false
         * Array: true
         */
        this.optimizerRouteStops = undefined;
    }
}
/**
 * Path parameter used to list the shipments associated with an optimizer run.
 */
class ListPnDOptimizerShipmentsPath {
    constructor() {
        /**
         * The number of the optimizer run with which the planner is working.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Response to the request to list the shipments associated with an optimizer run.
 */
class ListPnDOptimizerShipmentsResp {
    constructor() {
        /**
         * A list of shipments associated with an optimizer run.
         * Required: false
         * Array: true
         */
        this.pdoRequestShipments = undefined;
    }
}
/**
 * Path parameter which is used to list the positioning points.
 */
class ListPnDPositioningPointsPath {
    constructor() {
        /**
         * The service center identification code.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The date for which the pickup positioning points will be listed.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Query parameter which is used to list the positioning points.
 */
class ListPnDPositioningPointsQuery {
    constructor() {
        /**
         * The code which determines whether the information retrieved will include zones, only zones, or no zones.  Permitted values: IncludeZones, NoZones, ZonesOnly.
         * Required: false
         * Array: false
         */
        this.zoneIndicatorCd = undefined;
    }
}
/**
 * Response to the request to list the positioning points.
 */
class ListPnDPositioningPointsResp {
    constructor() {
        /**
         * The list of positioning points for the location that matched the filter criteria.
         * Required: false
         * Array: true
         */
        this.positioningPoints = undefined;
    }
}
/**
 * Path parameter that consists of the run number used to start the Optimizer.
 */
class StartPnDOptimizerPath {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * Path parameter used to update a list of DSRs that go into the Optimizer.
 */
class UpdatePnDOptimizerDriversPath {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to update a list of DSRs that go into the Optimizer.
 */
class UpdatePnDOptimizerDriversRqst {
    constructor() {
        /**
         * A list of DSRs that will be updated for this run of the optimizer.
         * Required: true
         * Array: true
         */
        this.pdoRequestDriver = undefined;
    }
}
/**
 * The request to update a list of trailer equipment that goes into the Optimizer.
 */
class UpdatePnDOptimizerEquipmentRqst {
    constructor() {
        /**
         * A list of trailer equipment that will be updated for this run of the optimizer.
         * Required: true
         * Array: true
         */
        this.pdoRequestEquipment = undefined;
    }
}
/**
 * Path parameter used to update a list of trailer equipment that goes into the Optimizer.
 */
class UpdatePnDOptimizerEquipmentPath {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to incrementally update a PnD route from a route in the optimizer results.
 */
class UpdatePnDOptimizerRouteRqst {
    constructor() {
        /**
         * The optimizer response route detail that will be used to update the PnD route.
         * Required: true
         * Array: false
         */
        this.optimizerRouteStop = undefined;
    }
}
/**
 * Response to the request to incrementally update a PnD route from a route in the optimizer results.
 */
class UpdatePnDOptimizerRouteResp {
    constructor() {
        /**
         * The optimizer route that has been successfully updated in PnD.
         * Required: false
         * Array: false
         */
        this.pdoResponseRoute = undefined;
        /**
         * The list of optimizer exceptions for any activities that have not been successfully updated in PnD.
         * Required: false
         * Array: true
         */
        this.pdoException = undefined;
    }
}
/**
 * Path parameter used to update a list of shipments that goes into the Optimizer.
 */
class UpdatePnDOptimizerShipmentsPath {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to update a list of shipments in SCO_PDO_RQST_SHIPMENT.
 */
class UpdatePnDOptimizerShipmentsRqst {
    constructor() {
        /**
         * A list of shipments that will be updated.
         * Required: true
         * Array: true
         */
        this.pdoRequestShipments = undefined;
    }
}
/**
 * The request to update the status of an optimizer run.
 */
class UpdatePnDOptimizerStatusRqst {
    constructor() {
        /**
         * The number of the optimizer run whose status is going to be updated.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The reason why the optimizer run was aborted.
         * Required: false
         * Array: false
         */
        this.abortReason = undefined;
        /**
         * The new status for the optimizer run.
         * Required: true
         * Array: false
         */
        this.statusCd = undefined;
    }
}
/**
 * Response to the request to update the optimizer run status.
 */
class UpdatePnDOptimizerStatusResp {
    constructor() {
        /**
         * The run number of the optimizer run that was updated.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
    }
}
/**
 * The request to create or update a drop location.
 */
class UpsertDropLocationRqst {
    constructor() {
        /**
         * The information needed to either create a new or update an existing drop location.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Indicates if the physical location of the drop location has changed and needs to be geocoded.
         * Required: false
         * Array: false
         */
        this.addressChangeInd = undefined;
    }
}
/**
 * The response to the request to create or update a drop location.
 */
class UpsertDropLocationResp {
    constructor() {
        /**
         * The drop location that was either created or updated.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
    }
}
/**
 * The request to update the route results and exception details of an optimizer run.
 */
class UpsertPnDRouteResultsRqst {
    constructor() {
        /**
         * The list of optimization route results that are going to be updated.
         * Required: false
         * Array: true
         */
        this.pdoResponseRoutes = undefined;
        /**
         * The list of exception details.
         * Required: false
         * Array: true
         */
        this.pdoExceptions = undefined;
    }
}
/**
 * Path parameters which are used to assign the next shipment exception sequence number.
 */
class AssignNextShipmentExceptionSequenceNbrPath {
    constructor() {
        /**
         * The terminal location SIC responsible for the exception.
         * Required: true
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The date for when the exception will take place.
         * Required: true
         * Array: false
         */
        this.exceptionDate = undefined;
    }
}
/**
 * Response to the request to assign the next shipment exception sequence number
 */
class AssignNextShipmentExceptionSequenceNbrResp {
    constructor() {
        /**
         * The new exception sequence number that is assigned to the terminal location SIC which is responsible for it.
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
    }
}
/**
 * Path parameters which are used to complete delivery activities.
 */
class CompleteDeliveryActivitiesFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of the delivery activities that are being completed.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to complete delivery activities.
 */
class CompleteDeliveryActivitiesFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the delivery activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop containing the delivery activities that are being completed.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a delivery stop.
 */
class CompleteDeliveryStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a delivery stop.
 */
class CompleteDeliveryStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the delivery activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The delivery stop where the driver will be performing this activity.
         * Required: true
         * Array: false
         */
        this.deliveryStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operations-related note specific to the delivery activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * When true, used by the Shipment component to control how old the shipment movement can be before it can be updated through this service.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
        /**
         * When true, used to control whether or not to create an event and notify the driver with a message that the activities on the stop were or were not completed successfully.
         * Required: false
         * Array: false
         */
        this.readyToCompleteInd = undefined;
        /**
         * All delivery-related information for all the shipments on this delivery stop.  Contains activity as well as other information.
         * Required: true
         * Array: true
         */
        this.deliveryActivityDetails = undefined;
        /**
         * Equipment and activity details for this stop.
         * Required: true
         * Array: true
         */
        this.activityEquipmentDetails = undefined;
    }
}
/**
 * Response to the request to complete a delivery stop.
 */
class CompleteDeliveryStopFromDeviceResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameters which are used to complete the equipment stop from a device.
 */
class CompleteEquipmentStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of the stop containing the equipment activities that will be completed.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Query parameter which is used to filter activities that are processing.
 */
class CompleteEquipmentStopFromDeviceQuery {
    constructor() {
        /**
         * An activity code related to the activity that will be processed. Permitted values: SpotEmpty, HookEmpty and HookEquipment.
         * Required: true
         * Array: false
         */
        this.tripNodeActivityCd = undefined;
    }
}
/**
 * Request to complete a stop with a list of dropped equipment.
 */
class CompleteEquipmentStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the equipment activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The location where a piece of equipment has been dropped to be hooked at a later time.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * The geo area default normally associated with this route.
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * The drop stop where the driver will be performing this hook activity.
         * Required: true
         * Array: false
         */
        this.stop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The service center responsible for the hook stop.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * A list containing the equipment that was dropped at a specific location for the drop stop, along with activity information which will be used for the hook activity.
         * Required: true
         * Array: true
         */
        this.dropStopEquipment = undefined;
        /**
         * The flag indicating when it is not required to check equipment for suitability.
         * Required: false
         * Array: false
         */
        this.equipmentCheckInd = undefined;
    }
}
/**
 * Response to the request to complete an equipment stop with a list of dropped equipment.
 */
class CompleteEquipmentStopFromDeviceResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a hook loaded stop.
 */
class CompleteHookLoadedStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a hook loaded stop.
 */
class CompleteHookLoadedStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the hook load activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The stop detail that is being completed.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
        /**
         * Indicates whether the update to the hook loaded stop is to complete it.
         * Required: true
         * Array: false
         */
        this.updateAsCompleteInd = undefined;
        /**
         * Indicates whether or not to complete the stop.
         * Required: true
         * Array: false
         */
        this.doNotCompleteStopInd = undefined;
        /**
         * Equipment information.
         * Required: true
         * Array: false
         */
        this.equipment = undefined;
    }
}
/**
 * Response to the request to complete a hook loaded stop.
 */
class CompleteHookLoadedStopFromDeviceResp {
    constructor() {
        /**
         * The list of statuses for the shipments included in pickup activities.
         * Required: false
         * Array: true
         */
        this.pickupActivityShipmentStatuses = undefined;
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a pickup stop.
 */
class CompletePickupStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a pickup stop.
 */
class CompletePickupStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the pickup activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The stop detail that is being completed.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
        /**
         * Indicates whether the update to the pickup stop is to complete it.
         * Required: true
         * Array: false
         */
        this.updateAsCompleteInd = undefined;
        /**
         * Indicates whether or not to complete the stop.
         * Required: true
         * Array: false
         */
        this.doNotCompleteStopInd = undefined;
        /**
         * Equipment information.
         * Required: true
         * Array: false
         */
        this.equipment = undefined;
    }
}
/**
 * Response to the request to complete a pickup stop.
 */
class CompletePickupStopFromDeviceResp {
    constructor() {
        /**
         * The list of statuses for the shipments included in pickup activities.
         * Required: false
         * Array: true
         */
        this.pickupActivityShipmentStatuses = undefined;
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter which is used to complete a drop stop and activity at a customer location.
 */
class CompletePnDDropStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to complete a drop stop and activity at a customer location.
 */
class CompletePnDDropStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the drop stop.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The location for the drop stop.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The customer stop where the drop activity is to be performed.
         * Required: true
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: true
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The customer name of the location where the drop will be performed.
         * Required: true
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Indicates confirmation of completion despite having hazmat on board.
         * Required: true
         * Array: false
         */
        this.confirmCompletionForHazmatInd = undefined;
        /**
         * A list containing information needed about the stop and equipment involved in a drop activity at a customer stop.
         * Required: true
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Response to the request to complete a drop stop.
 */
class CompletePnDDropStopFromDeviceResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter used to complete or return a PnD trip, and update the mobile device end timestamp.
 */
class CompletePnDTripFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete or return a PnD trip, and update the mobile device end timestamp.
 */
class CompletePnDTripFromDeviceRqst {
    constructor() {
        /**
         * The time when the message to stop the trip is sent to the DSR's mobile device.
         * Required: true
         * Array: false
         */
        this.mobileDeviceEndDateTime = undefined;
        /**
         * The actual arrival time.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
    }
}
/**
 * Response to the request for complete trip.
 */
class CompletePnDTripFromDeviceResp {
    constructor() {
        /**
         * Identifies the default printer destination for a line (impact) printer at a service center location.
         * Required: false
         * Array: false
         */
        this.linePrinter = undefined;
        /**
         * Indicates whether to automatically print the Outbound manifest report for a service center.
         * Required: false
         * Array: false
         */
        this.printOBManifestReportInd = undefined;
        /**
         * Indicates whether to automatically print the DSR Checkin list report for a service center.
         * Required: false
         * Array: false
         */
        this.printDSRCheckinReportInd = undefined;
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameters which are used to complete spot load activities.
 */
class CompleteSpotLoadActivitiesFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of the spot load activities that are being completed.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to complete spot load activities
 */
class CompleteSpotLoadActivitiesFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the spot load activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop containing the spot load activities that are being completed.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Path parameter for the trip id used to complete a spot loaded stop.
 */
class CompleteSpotLoadedStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to complete a spot loaded stop.
 */
class CompleteSpotLoadedStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete the spot load activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The spot-loaded stop that will be completed with this request.
         * Required: true
         * Array: false
         */
        this.spotLoadedStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operations-related note specific to the spot loaded activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * When true, used by the Shipment component to control how old the shipment movement can be before it can be updated through this service.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
        /**
         * When true, used to control whether or not to create an event and notify the driver with a message that the activities on the stop were or were not completed successfully.
         * Required: false
         * Array: false
         */
        this.readyToCompleteInd = undefined;
        /**
         * All delivery-related information for all the shipments on this spot loaded stop.  Contains activity as well as other information.
         * Required: true
         * Array: true
         */
        this.deliveryActivityDetails = undefined;
        /**
         * Equipment and activity details for this stop.
         * Required: true
         * Array: true
         */
        this.activityEquipmentDetails = undefined;
    }
}
/**
 * Response to the request to complete a spot loaded stop.
 */
class CompleteSpotLoadedStopFromDeviceResp {
    constructor() {
        /**
         * Pickup request information related to completing the spot loaded stop.
         * Required: false
         * Array: false
         */
        this.pickupRequest = undefined;
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameters used to complete a trip stop on a specific trip.
 */
class CompleteTripStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip being completed.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to complete a trip stop on a specific trip.
 */
class CompleteTripStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to complete a stop.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop being completed.
         * Required: false
         * Array: false
         */
        this.deviceTripStop = undefined;
        /**
         * The error captured prior to completing the trip stop.
         * Required: false
         * Array: false
         */
        this.validationError = undefined;
    }
}
/**
 * Request to create a DSR message log from device.
 */
class CreateDsrMessageLogFromDeviceRqst {
    constructor() {
        /**
         * The unique identifier of the event.
         * Required: true
         * Array: false
         */
        this.eventInstId = undefined;
        /**
         * The identifier of the user that used the device to create a message log.
         * Required: true
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The message log that will be persisted.
         * Required: true
         * Array: false
         */
        this.messageLog = undefined;
    }
}
/**
 * Path parameter used to create a drop stop and activity at a customer location.
 */
class CreatePnDDropStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to create a drop stop and activity at a customer location.
 */
class CreatePnDDropStopFromDeviceRqst {
    constructor() {
        /**
         * The location for the drop stop.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The customer stop where the drop activity is to be performed.
         * Required: false
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The customer name of the location where the drop will be performed.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Any note made by the driver at the customer stop where the drop activity was to be performed.
         * Required: false
         * Array: false
         */
        this.stopNote = undefined;
        /**
         * A list containing information needed about the stop and equipment involved in a drop activity at a customer stop.
         * Required: false
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Response to the request to create a drop stop and activity at a customer location.
 */
class CreatePnDDropStopFromDeviceResp {
    constructor() {
        /**
         * Any errors that occurred during processing.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter used to create a trip activity.
 */
class CreatePnDTripActivityFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to create a trip activity.
 */
class CreatePnDTripActivityFromDeviceRqst {
    constructor() {
        /**
         * The type of event.  Together with sub-type code this describes the event.  It must be one of 'E' (Enter), 'V' (Leave), 'S' (Start), or 'T' (Stop).
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * The subtype of the event.  Together with the type code this describes the event.  It must be one of 'GT' (Gate), 'BK' (Lunch), or 'OT' (Other Activity).
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         * Details about the event.
         * Required: false
         * Array: false
         */
        this.event = undefined;
        /**
         * The date and time of the event.
         * Required: false
         * Array: false
         */
        this.eventDateTime = undefined;
        /**
         * The time of the trip activity.
         * Required: false
         * Array: false
         */
        this.activityDateTime = undefined;
        /**
         * The reading on the odometer of the power equipment at the time of the activity.
         * Required: false
         * Array: false
         */
        this.odometerNbr = undefined;
        /**
         * The latitude and longitude captured by the DSR's GPS.  It is the latitude and longitude where the trip activity is performed.
         * Required: false
         * Array: false
         */
        this.geoCoordinate = undefined;
        /**
         * The employee ID of the DSR assigned to the trip.
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         * When event type and sub type are not supplied, then a handheld device may send a message type that gets converted to event type and sub type.
         * Required: false
         * Array: false
         */
        this.messageType = undefined;
    }
}
/**
 * Response to the request for creating trip activity.
 */
class CreatePnDTripActivityFromDeviceResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Request to create a trip event from device.
 */
class CreateTripEventFromDeviceRqst {
    constructor() {
        /**
         * The unique identifier of the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The identifier of the user that used the device to create a trip event.
         * Required: true
         * Array: false
         */
        this.deviceUserId = undefined;
    }
}
/**
 * The path parameters used to retrieve an auto dispatch trip for a driver at a SIC.
 */
class GetAutoDispatchTripForDsrFromDevicePath {
    constructor() {
        /**
         * The location associated with the trip.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The employee identifier for the driver.
         * Required: true
         * Array: false
         */
        this.employeeId = undefined;
    }
}
/**
 * Response to the request to retrieve an auto dispatch trip for a driver at a SIC.
 */
class GetAutoDispatchTripForDsrFromDeviceResp {
    constructor() {
        /**
         * Information about the trip.
         * Required: false
         * Array: false
         */
        this.tripDetail = undefined;
        /**
         * Indicates whether or not an auto dispatch trip is available for the DSR.
         * Required: false
         * Array: false
         */
        this.autoDispatchTripAvailableInd = undefined;
        /**
         * Indicates whether or not a trip has already been dispatched for the DSR.
         * Required: false
         * Array: false
         */
        this.tripAlreadyDispatchedInd = undefined;
    }
}
/**
 * Path parameter which is used to retrieve the expectations for a trip.
 */
class GetTripExpectationPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Response to the request to retrieve the expectations for a trip.
 */
class GetTripExpectationResp {
    constructor() {
        /**
         * The expectations for the trip.
         * Required: true
         * Array: false
         */
        this.expectation = undefined;
    }
}
/**
 * Path parameter used to get trip stop activity detail on a specific trip stop.
 */
class GetTripStopActivityFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The trip node activity sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         * The code associated with the trip node activity.
         * Required: true
         * Array: false
         */
        this.tripNodeActivityCd = undefined;
    }
}
/**
 * Response to the request to get trip stop activity detail on a specific trip stop.
 */
class GetTripStopActivityFromDeviceResp {
    constructor() {
        /**
         * The stop activity that is part of a trip stop.
         * Required: true
         * Array: false
         */
        this.deviceTripStopActivity = undefined;
    }
}
/**
 * Path parameter used to get trip stop detail in a specific trip.
 */
class GetTripStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Query parameter used to get trip stop detail in a specific trip.
 */
class GetTripStopFromDeviceQuery {
    constructor() {
        /**
         * The option to just return the header only detail of the trip stop.
         * Required: true
         * Array: false
         */
        this.headerOnly = undefined;
    }
}
/**
 * Response to the request to get trip stop detail on a specific trip.
 */
class GetTripStopFromDeviceResp {
    constructor() {
        /**
         * The stop that is part of a trip.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Request to record a message sent by the driver for a trip.
 */
class RecordPnDTripMessageFromDeviceRqst {
    constructor() {
        /**
         * The unique identifier associated with the trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The employee identifier of the DSR.
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         * Contains event information related to the message sent by the driver.
         * Required: true
         * Array: false
         */
        this.scoEvent = undefined;
    }
}
/**
 * Response that contains the event and message log created for the request.
 */
class RecordPnDTripMessageFromDeviceResp {
    constructor() {
        /**
         * The event that was created from the message sent by the driver.
         * Required: false
         * Array: false
         */
        this.scoEvent = undefined;
        /**
         * The message log that was created from the message sent by the driver.
         * Required: false
         * Array: false
         */
        this.messageLog = undefined;
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Request to reschedule or cancel pickup requests.
 */
class RescheduleOrCancelPickupsRqst {
    constructor() {
        /**
         * The list of details which will be used to reschedule or cancel pickup requests.
         * Required: true
         * Array: true
         */
        this.pickupRequests = undefined;
    }
}
/**
 * Response to request to reschedule or cancel the pickup requests.
 */
class RescheduleOrCancelPickupsResp {
    constructor() {
        /**
         * The list of rescheduled and cancelled pickup requests.
         * Required: false
         * Array: true
         */
        this.rescheduledOrCancelledPickups = undefined;
    }
}
/**
 * Query parameters which are used to search for PnD drivers.
 */
class SearchDriversQuery {
    constructor() {
        /**
         * The employee name search string.
         * Required: true
         * Array: false
         */
        this.employeeName = undefined;
        /**
         * The plan date of the request.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
    }
}
/**
 * Response to the request to search for PnD drivers.
 */
class SearchDriversResp {
    constructor() {
        /**
         * The list of drivers matching the employee name and plan date.
         * Required: false
         * Array: true
         */
        this.dispatchDrivers = undefined;
    }
}
/**
 * Path parameters which are used to split trip stop activities to a new stop.
 */
class SplitTripStopActivitiesToNewStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number to which the activities originally belong.
         * Required: true
         * Array: false
         */
        this.originalTripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to split trip stop activities to a new stop.
 */
class SplitTripStopActivitiesToNewStopFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to split the activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The unique identifier for the trip node to which the new node will be tied.
         * Required: false
         * Array: false
         */
        this.newTripNodeInstanceId = undefined;
        /**
         * The code associated with the trip node to which the new node will be tied.
         * Required: false
         * Array: false
         */
        this.newTripNodeTypeCd = undefined;
        /**
         * The activities that will be split into a new stop.
         * Required: true
         * Array: true
         */
        this.splitTripStopActivities = undefined;
    }
}
/**
 * Request to unassign pickup requests.
 */
class UnassignPickupsRqst {
    constructor() {
        /**
         * The list of unique identifiers for the pickup requests that will be unassigned.
         * Required: true
         * Array: true
         */
        this.pickupRequestInstIds = undefined;
    }
}
/**
 * Request to unassign an activity.
 */
class UnassignStopActivityRqst {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The unique identifier for the stop along with the associated activities.
         * Required: true
         * Array: true
         */
        this.tripNodes = undefined;
    }
}
/**
 * Path parameters which are used to update the activity line items.
 */
class UpdateActivityLineItemsPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of a pickup activity that owns the line items being updated.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The trip node activity sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
    }
}
/**
 * Request to update the activity line items.
 */
class UpdateActivityLineItemsRqst {
    constructor() {
        /**
         * The trip stop containing the activity line items that are being updated.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Response to the request to update the activity line items.
 */
class UpdateActivityLineItemsResp {
    constructor() {
        /**
         * Indicates whether an error occurred in a line item of an activity.
         * Required: false
         * Array: false
         */
        this.lineItemErrorInd = undefined;
        /**
         * The list of statuses for the shipments included in activities.
         * Required: false
         * Array: true
         */
        this.activityShipmentStatuses = undefined;
    }
}
/**
 * Path parameters which are used to update the activity line items.
 */
class UpdateActivityLineItemsFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of a pickup activity that owns the line items being updated.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The trip node activity sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
    }
}
/**
 * Request to update the activity line items.
 */
class UpdateActivityLineItemsFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to update the activity line items.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop containing the activity line items that are being updated.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Response to the request to update the activity line items.
 */
class UpdateActivityLineItemsFromDeviceResp {
    constructor() {
        /**
         * Indicates whether an error occurred in a line item of an activity.
         * Required: false
         * Array: false
         */
        this.lineItemErrorInd = undefined;
        /**
         * The list of statuses for the shipments included in activities.
         * Required: false
         * Array: true
         */
        this.activityShipmentStatuses = undefined;
    }
}
/**
 * Path parameters used to update and unassign a hook load activity.
 */
class UpdateAndUnassignHookLoadsFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of the activities being updated and unassigned.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to update and unassign a hook load activity.
 */
class UpdateAndUnassignHookLoadsFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device to split the activities.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop being containing the hook loads that will be updated and unassigned.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Path parameters used to update a hook load activity.
 */
class UpdateHookLoadActivityFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The trip node activity sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
    }
}
/**
 * Request to complete a hook load activity.
 */
class UpdateHookLoadActivityFromDeviceRqst {
    constructor() {
        /**
         * Indicates whether the update to the hook load activity is to complete it.
         * Required: true
         * Array: false
         */
        this.updateAsCompleteInd = undefined;
        /**
         * The identifier of the user that used the device to update the hook load activity.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop containing the hook load activity that is being updated.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Response to the request to update a hook load activity.
 */
class UpdateHookLoadActivityFromDeviceResp {
    constructor() {
        /**
         * The list of statuses for the shipments included in hook load activities.
         * Required: false
         * Array: true
         */
        this.activityShipmentStatuses = undefined;
    }
}
/**
 * The path parameter which is used to update the operations customer profile from a mobile device.
 */
class UpdateOperationsCustomerProfileFromDevicePath {
    constructor() {
        /**
         * The unique identifier associated with a customer's operational profile.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
    }
}
/**
 * Request to update the operations customer profile from a mobile device.
 */
class UpdateOperationsCustomerProfileFromDeviceRqst {
    constructor() {
        /**
         * Details about the customer profile.
         * Required: true
         * Array: false
         */
        this.customerOperationsProfile = undefined;
    }
}
/**
 * Response to the request to update the operations customer profile from a mobile device.
 */
class UpdateOperationsCustomerProfileFromDeviceResp {
    constructor() {
        /**
         * Details about the customer profile.
         * Required: true
         * Array: false
         */
        this.customerOperationsProfile = undefined;
        /**
         * Any errors that occurred during processing.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameters used to update a pickup activity.
 */
class UpdatePickupActivityFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The trip node sequence number of a pickup activity that is being updated.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The trip node activity sequence number.
         * Required: true
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
    }
}
/**
 * Request to update a pickup activity.
 */
class UpdatePickupActivityFromDeviceRqst {
    constructor() {
        /**
         * Indicates whether the update to the pickup activity is to complete it.
         * Required: true
         * Array: false
         */
        this.updateAsCompleteInd = undefined;
        /**
         * The identifier of the user that used the device to update the pickup activity.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The trip stop containing the pickup activity being updated.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
    }
}
/**
 * Response to the request to update a pickup activity.
 */
class UpdatePickupActivityFromDeviceResp {
    constructor() {
        /**
         * The list of statuses for the shipments included in pickup activities.
         * Required: false
         * Array: true
         */
        this.activityShipmentStatuses = undefined;
    }
}
/**
 * Path parameter used to update a drop stop and activity at a customer location.
 */
class UpdatePnDDropStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to update a drop stop and activity at a customer location.
 */
class UpdatePnDDropStopFromDeviceRqst {
    constructor() {
        /**
         * The location for the drop stop.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The customer stop where the drop activity is to be performed.
         * Required: false
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The customer name of the location where the drop will be performed.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Any note made by the driver at the customer stop where the drop activity was to be performed.
         * Required: false
         * Array: false
         */
        this.stopNote = undefined;
        /**
         * A list containing information needed about the stop and equipment involved in a drop activity at a customer stop.
         * Required: false
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Response to the request to update a drop stop and activity at a customer location.
 */
class UpdatePnDDropStopFromDeviceResp {
    constructor() {
        /**
         * Any errors that occurred during processing.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter which is used to update a list of equipment associated with a hook activity at a drop stop.
 */
class UpdatePnDEquipmentHookStopFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to update a hook stop with a list of equipment associated with a hook activity at a drop stop.
 */
class UpdatePnDEquipmentHookStopFromDeviceRqst {
    constructor() {
        /**
         * The geo area default normally associated with this route.
         * Required: true
         * Array: false
         */
        this.routeArea = undefined;
        /**
         * The drop stop where the driver will be performing this hook activity.
         * Required: true
         * Array: false
         */
        this.hookStop = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * Any operation-related note specific to the hook activity at this stop.
         * Required: false
         * Array: false
         */
        this.overrideOpsNote = undefined;
        /**
         * The comment text about any changes to the delivery instructions for this hook stop.
         * Required: false
         * Array: false
         */
        this.overrideDeliveryInstruction = undefined;
        /**
         * The comment text about any changes to the pickup instructions for this hook stop.
         * Required: false
         * Array: false
         */
        this.overridePickupInstruction = undefined;
        /**
         * The service center responsible for the hook stop.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates that all warnings that may occur as a result of this request will be overridden.
         * Required: true
         * Array: false
         */
        this.overrideWarningsInd = undefined;
        /**
         * A list containing the equipment that was dropped at a specific location for the drop stop, along with activity information which will be used for the hook activity.
         * Required: false
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Response to the request to update a hook stop with a list of equipment associated with a hook activity at a drop stop.
 */
class UpdatePnDEquipmentHookStopFromDeviceResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameters used to update the odometer reading along with the arrival and departure timestamps for a trip stop.
 */
class UpdatePnDStopActualArrivalDepartureFromDevicePath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The sequence number of the trip node.
         * Required: true
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 * Request to update the odometer reading along with the arrival and departure timestamps for a trip stop.
 */
class UpdatePnDStopActualArrivalDepartureFromDeviceRqst {
    constructor() {
        /**
         * The identifier of the user that used the device and executed the operation.
         * Required: false
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * The time when the message to stop the trip is sent to the DSR's mobile device.
         * Required: false
         * Array: false
         */
        this.mobileDeviceEndDateTime = undefined;
        /**
         * The actual arrival time.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The actual departure time.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The odometer reading at the time of departure.
         * Required: false
         * Array: false
         */
        this.departureOdometerNbr = undefined;
        /**
         * The latitude and longitude of the trip.
         * Required: false
         * Array: false
         */
        this.geoCoordinate = undefined;
    }
}
/**
 * Response to the request to update stop actual arrival and departure times.
 */
class UpdatePnDStopActualArrivalDepartureFromDeviceResp {
    constructor() {
        /**
         * The list of all the failures that occurred while processing the request.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Path parameter for a given SIC to upsert auto print manifest preferences; e.g. PnD manifest, outbound manifest, DSR check in and FBDS.
 */
class UpsertServiceCenterPrintPreferencesPath {
    constructor() {
        /**
         * The unique identifier for the serviceCenter.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Request to upsert auto print preferences for the SIC provided.
 */
class UpsertServiceCenterPrintPreferencesRqst {
    constructor() {
        /**
         * Defines the serviceCenter preferences for automated printing.
         * Required: true
         * Array: false
         */
        this.printPreferences = undefined;
    }
}
/**
 * Response to the requested auto print preferences that are upserted.
 */
class UpsertServiceCenterPrintPreferencesResp {
    constructor() {
        /**
         * Defines the serviceCenter preferences for automated printing.
         * Required: true
         * Array: false
         */
        this.printPreferences = undefined;
    }
}
/**
 * Path parameter used to retrieve the final consignee destination arrival timestamp for the PRO provided.
 */
class GetFinalConsigneeDestinationArrivalTmstByProPath {
    constructor() {
        /**
         * The shipment for which the arrival timestamp for the final consignee destination will be retrieved.
         * Required: true
         * Array: false
         */
        this.proNbr = undefined;
    }
}
/**
 * Respone to the request to retrieve the final consignee destination arrival timestamp for the PRO provided.
 */
class GetFinalConsigneeDestinationArrivalTmstByProResp {
    constructor() {
        /**
         * The timestamp at which the shipment arrived at the final consignee destination.
         * Required: true
         * Array: false
         */
        this.destinationArrivalTmst = undefined;
    }
}
/**
 * Path parameter which is used to print an Outbound Breakout Manifest report.
 */
class PrintOutboundBreakoutManifestReportPath {
    constructor() {
        /**
         * The service center identification code for the location requesting the report.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
    }
}
/**
 * Request to print an Outbound Breakout Manifest report.
 */
class PrintOutboundBreakoutManifestReportRqst {
    constructor() {
        /**
         * The unique identifier of a trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The list of unique identifiers for the routes.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The unique identifier of an equipment.
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * The unique identifier for either a dock drop or dock pickup instance.
         * Required: false
         * Array: false
         */
        this.dockVisitInstId = undefined;
    }
}
/**
 * Response to the request to print an Outbound Breakout Manifest report.
 */
class PrintOutboundBreakoutManifestReportResp {
    constructor() {
        /**
         * The PDF report data which is encoded in base64 and represented as a collection of strings.
         * Required: false
         * Array: true
         */
        this.documentData = undefined;
    }
}
/**
 * Path parameter used to print a PnD report; e.g. outbound manifest or DSR check in.
 */
class PrintPnDReportPath {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: true
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * Request to print a PnD report; e.g. outbound manifest or DSR check in.
 */
class PrintPnDReportRqst {
    constructor() {
        /**
         * Identifies the default printer destination for a line (impact) printer at a service center location.
         * Required: true
         * Array: false
         */
        this.linePrinter = undefined;
        /**
         * >The type of report to print: BREAKOUT (for outbound manifest report), DSR (for DSR check in report), and PD (for Pickup and Delivery Manifest).
         * Required: true
         * Array: false
         */
        this.reportType = undefined;
        /**
         * The unique identifiers of the routes to print. This applies to PD Manifest.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The service center identification code for the location from where the request to print originated.
         * Required: false
         * Array: false
         */
        this.requestingSicCd = undefined;
    }
}
/**
 * Elastic Search document for Delivery Shipment details
 */
class DeliveryShipmentSearchRecord {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * DEPRECATED. USE areaNameAsString
         * Required: false
         * Array: false
         */
        this.areaName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaNameAsString = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleETA = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.displayScheduleETA = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The volume of the freight; derived from the dimensions captured by driver, or entered by a CSR or dock worker for a SLNC (Shipper Load and Count), or from one of a variety of profiles.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loosePcsCnt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         * Derived attribute representing the estimated footprint of the shipment for planning a floor-loaded route.
         * Required: false
         * Array: false
         */
        this.footprintPercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus = undefined;
        /**
         * The unique identifier of the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The equipment identifier prefix.
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * The equipment identifier suffix.
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffix = undefined;
        /**
         * The trailer on which this shipment is currently located.
         * Required: false
         * Array: false
         */
        this.currentTrailer = undefined;
        /**
         * The current status of the trailer on which this shipment is located.
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus = undefined;
        /**
         * The unique identifier of the dock on which this shipment is located.
         * Required: false
         * Array: false
         */
        this.dockInstId = undefined;
        /**
         * The dock name on which this shipment is located.
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         * The door number of the assigned route for this shipment.
         * Required: false
         * Array: false
         */
        this.doorNbr = undefined;
        /**
         * The debtor terms for a shipment.
         * Required: false
         * Array: false
         */
        this.debtorTerms = undefined;
        /**
         * The service date and time for a shipment.
         * Required: false
         * Array: false
         */
        this.serviceDateTime = undefined;
        /**
         * The code associated with the latest movement exception type for a shipment.
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd = undefined;
        /**
         * The total amount in USD which includes advance and beyond carrier charges and COD amount for a shipment.
         * Required: false
         * Array: false
         */
        this.totalUsdAmount = undefined;
        /**
         * The current service center identification code.
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * The host destination service center identification code.
         * Required: false
         * Array: false
         */
        this.hostDestSicCd = undefined;
        /**
         * The schedule destination service center identification code.
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         * This is the timestamp when this row was inserted/update in the search repository.
         * Required: false
         * Array: false
         */
        this.lstUpdtTmst = undefined;
        /**
         * This is the timestamp when the original event was created.
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * This is the timestamp when the original event was created in microseconds.
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduledLocalEta = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd = undefined;
        /**
         * This identifies the MOVR PRO that is associated with the parent PRO, which will move this handling unit across border.
         * Required: false
         * Array: false
         */
        this.moverSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.purgeBlockInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceSummaryInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.trailerCurrSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaUpdtTmstMicro = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServiceSummary = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stopWindow = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.suppRefNbr = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.shipmentRefNbrs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.loadDestinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo = undefined;
        /**
         * The timestamp when the shipment was billed.
         * Required: false
         * Array: false
         */
        this.billDateTime = undefined;
        /**
         * Indicates whether the shipment BOL has been scanned.
         * Required: false
         * Array: false
         */
        this.scannedInd = undefined;
        /**
         * This is the timestamp when shipment was last moved.
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.splitInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd = undefined;
        /**
         * Indicator showing if the shipment final delivered date was within the Time Date Critical constraints.
         * Required: false
         * Array: false
         */
        this.withinTdcCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.managementRemarks = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentServiceStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.movementExceptions = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.xdockExceptions = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.xdockExceptionTypeCdMaps = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.customsBonds = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalHandlingUnitCount = undefined;
        /**
         * List of Accessorials for a Shipment.
         * Required: false
         * Array: true
         */
        this.accessorials = undefined;
        /**
         * Information related to the parent shipment.
         * Required: false
         * Array: false
         */
        this.parentShipmentId = undefined;
        /**
         * The code representing the results of a specific delivery activity for the parent shipment.
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd = undefined;
        /**
         * The identifier of the dispatch group.
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * The list of oversized dimension measurements.
         * Required: false
         * Array: true
         */
        this.oversizedDimensions = undefined;
        /**
         * The list of commodities for a shipment.
         * Required: false
         * Array: true
         */
        this.commodities = undefined;
        /**
         * The Standard Carrier Alpha Code (SCAC) of the carrier that last transported the shipment.
         * Required: false
         * Array: false
         */
        this.lastShipmentMvmtScacCd = undefined;
        /**
         * Information about a customer who is signed up for a delivery notification.
         * Required: false
         * Array: false
         */
        this.deliveryNotificationSignup = undefined;
        /**
         * Provides the reason for placing the shipmetn into a specific delivery qualifier status. The vlaues are specific to the qualifier code.
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * Identifies the carrier agent in the CMS system.
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * The unique key that this shipment was tendered on to a third-party carrier in the CMS system.
         * Required: false
         * Array: false
         */
        this.tenderId = undefined;
        /**
         * Part of the primary key that uniquely identifies the tender detail to which this tendered shipment belongs in the CMS system.
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr = undefined;
        /**
         * The status of the shipment as tendered in the CMS system.
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd = undefined;
        /**
         * The message that describes the status code for the tendered shipment in the CMS system.
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage = undefined;
        /**
         * Name of the carrier agent in the CMS system.
         * Required: false
         * Array: false
         */
        this.carrierName = undefined;
        /**
         * The business identifiers for the handling units of a shipment.
         * Required: false
         * Array: true
         */
        this.childProNbrs = undefined;
        /**
         * This is the timestamp when the route information was last updated.
         * Required: false
         * Array: false
         */
        this.routeLstUpdtTmst = undefined;
        /**
         * This is the timestamp when the equipment information was last updated.
         * Required: false
         * Array: false
         */
        this.equipmentLstUpdtTmst = undefined;
        /**
         * This indicates a child PRO that was once missing has been found but the rest of the shipment has moved elsewhere. It will remain astray until it has caught up with the rest of the shipment, or until it is delivered.
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         * This indicates the child PRO is marked as short. When found, it can switch to normal or astray.
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * The number of times that a FBDS has been printed for this shipment.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCount = undefined;
        /**
         * Indicates if the freight has been poorly packaged or not by the shipper.
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         * The code that categorizes the type of activity, such as Deliver Shipment (DL).
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * The code associated with the source of a shipment.
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * The scheduled delivery will be recalculated any time an event comes in. E.g. if one of the agenda list items is 'NOT_MET'.
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryDateTime = undefined;
        /**
         * The final value is set when the PRO arrives at the destination SIC.  The customer might ask for the specific time (not TDC, not entered at billing). There might be other cases where this might be set.
         * Required: false
         * Array: false
         */
        this.customerExpectedDeliveryDateTime = undefined;
        /**
         * This identifies whether the customer has a schedule where the hours vary by the day of the week.
         * Required: false
         * Array: false
         */
        this.variableCustomerScheduleInd = undefined;
        /**
         * Identifies what type of route the shipment may be associated with - dock, delivery or planning route.
         * Required: false
         * Array: false
         */
        this.routeCategoryCd = undefined;
        /**
         * This identifies the driver collect information. It has 4 values N (no collect requirements), F (Freight charges), C (COD) and B (Both Freight charges and COD must be collected)
         * Required: false
         * Array: false
         */
        this.drvrCollectCd = undefined;
    }
}
class Activity {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.tripNodeActivity = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.stopWindow = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeShipment = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.shipmentSpecialServices = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.activityShipments = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityEquipment = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityPickup = undefined;
    }
}
/**
 * Contains activity information that is related to one piece of equipment.
 */
class ActivityEquipment {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.tripNodeActivity = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
    }
}
/**
 * Basic header information about an activity for a trip node but only in the context of trip metrics
 */
class ActivityInfoBasic {
    constructor() {
        /**
         * Code that categorizes the Activity.  Examples: DL, PU, HE, etc.
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * Total weight of Shipments at this activity
         * Required: false
         * Array: false
         */
        this.totalWgt = undefined;
    }
}
/**
 * Pickup request and Shipment infromation related to an activity for a customer.
 */
class ActivityPickup {
    constructor() {
        /**
         * The business identifier of a pickup activity.
         * Required: false
         * Array: false
         */
        this.callNbr = undefined;
        /**
         * The pickup call date (YYYYMMDD). In legacy, this is the CAD call number. It's part of the confirmation number.
         * Required: false
         * Array: false
         */
        this.cadCallNbr = undefined;
        /**
         * The time when the shipments will be ready for the carrier to pick them up at the customer location.
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         * The time before which the shipment needs to be picked up at the customer location.
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         * The date when the shipment is picked up from the customer location.
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         * The pickup request system identifier of a pickup activity.
         * Required: false
         * Array: false
         */
        this.pickupRequestId = undefined;
        /**
         * The shipments that are part of a pickup activity.
         * Required: false
         * Array: true
         */
        this.pickupLineItems = undefined;
        /**
         * The customer that submitted the delivery locations associated to the pickup request. Used when additional pickup requests are made for the same shipper with the same pickup date and pickup time is within 2 hours of original request.
         * Required: false
         * Array: false
         */
        this.requestor = undefined;
        /**
         * Freeform textual comments used to relate information pertinent to the pickup request or pickup location.
         * Required: false
         * Array: false
         */
        this.remarks = undefined;
        /**
         * The origin of the pickup request.
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * Indicates whether freight on the pickup request needs to be stranded.
         * Required: false
         * Array: false
         */
        this.strandInd = undefined;
        /**
         * Indicates whether the pickUp will be after hours.
         * Required: false
         * Array: false
         */
        this.afterHoursInd = undefined;
        /**
         * Indicates whether a pickup request consists of weight greater than 5000 lbs.
         * Required: false
         * Array: false
         */
        this.volumeInd = undefined;
        /**
         * Indicates whether any special equipment is required to make the pickup.
         * Required: false
         * Array: false
         */
        this.specialEquipmentCd = undefined;
        /**
         * The special sevices that are required for shipment pickup request.
         * Required: false
         * Array: true
         */
        this.pickupRequestSpecialServices = undefined;
        /**
         * Identifier of the appropriate customer in CIS
         * Required: false
         * Array: false
         */
        this.shipperCisCustomerNbr = undefined;
        /**
         * The status of an activity.
         * Required: false
         * Array: false
         */
        this.activityStatusCd = undefined;
        /**
         * The unique identifier for the dock visit.
         * Required: false
         * Array: false
         */
        this.dockVisitInstId = undefined;
        /**
         * The sequence number that identifies the activity on the dock.
         * Required: false
         * Array: false
         */
        this.dockActivitySequenceNbr = undefined;
        /**
         * The code that indicates delivery information is required for the shipment.
         * Required: false
         * Array: false
         */
        this.deliveryInformationRqdCd = undefined;
        /**
         * The unique identifier for the parent shipment.
         * Required: false
         * Array: false
         */
        this.parentInstId = undefined;
        /**
         * The status code of the freight.
         * Required: false
         * Array: false
         */
        this.freightStatusCd = undefined;
    }
}
/**
 * Shipment infromation related to an activity for a customer.
 */
class ActivityShipment {
    constructor() {
        /**
         * The unique identifier of the shipment to be delivered at the stop.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The weight of the shipment.
         * Required: false
         * Array: false
         */
        this.weight = undefined;
        /**
         * The number of motorized pieces.
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * The volume of the freight; derived from the dimensions captured by driver, or entered by a CSR or dock worker for an SLNC (Shipper
         * 						Load and Count), or from one of a variety of profiles. For details, see the description of CFT_PRFL_MTHD_CD.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * Expanded Name: Total Pallet Count
         * 						Desc: Total Number of Pallets in the Shipment
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         * Expanded Name : Loose pieces count
         * 						Desc : Total loose pieces from all commodities in the shipment.
         * 						Count of non Motor Move pieces associated with a shipment. This count plus Motor Moves defines the total number of pieces moving within the Con-way system for Operations productivity.  This does not necessarily balance with the total pieces or commodity pieces entered during bill entry.
         * Required: false
         * Array: false
         */
        this.loosePiecesCount = undefined;
        /**
         * The type of commodity class.
         * Required: false
         * Array: false
         */
        this.commodityClassType = undefined;
        /**
         * Indicates whether the handling unit is a hazardous material.
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         * Expanded Name : Freezable Indicator
         * 						Desc : Indicates whether shipment requires special handling to prevent freezing during harsh weather.
         * 						Ideally, it should be specified at the commodity level, but has been de-normalized for Shipment, since that is level at which it is currently captured
         * 						Source: MBF root - MBFZB (x 08  of MBTRAFIC)
         * 						Valid Values : Y/N
         * 						Y - Can be damaged by freezing
         * Required: false
         * Array: false
         */
        this.freezableInd = undefined;
        /**
         * The code that categorizes the Activity.  Examples: DL, PU, HE, etc.
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * Expanded Name: Bulk Liquid Indicator
         * 						Desc: Identifies the shipment as containing bulk liquid.  If volume is known, this applies to shipments >119 gallons.
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         * A list of the types of accessorial service requested or performed.
         * Required: false
         * Array: true
         */
        this.accessorialCds = undefined;
        /**
         * Expanded: Time Date Critical Date Type Code
         * 						Desc: What kind the dates on the TDC shipment is/are.  There are 3 main flavors of date on Time Date Critical shipments: Deliver on a date, Deliver by a date, or a Deliver sometime within a date range
         * 						Valid Values:
         * 						ON=On
         * 						BY=By
         * 						RNG=Range
         * Required: false
         * Array: false
         */
        this.tdcDateTypeCd = undefined;
        /**
         * Expanded: Time Date Critical Date #1
         * 						Desc: There are 3 flavors of date for a TDC shipment.  This attribute captures the following flavor of date: ON, BY or FROM
         * Required: false
         * Array: false
         */
        this.tdcDate1 = undefined;
        /**
         * Expanded: Time Date Critical Date #2
         * 						Desc: There are 3 flavors of date for a TDC shipment.  This attribute captures the following flavor of date: ON, BY or FROM
         * Required: false
         * Array: false
         */
        this.tdcDate2 = undefined;
        /**
         * Expanded: Time Date Critical Time Type
         * 						Desc: What kind the times on the TDC shipment is/are.  There are 4 main flavors of time for Time Date Critical shipments: Deliver at a time, Deliver before a time, Deliver after a time, or a Deliver sometime within a time range
         * 						Valid Values:
         * 						AT=At
         * 						BEF=Before
         * 						AFT=After
         * 						RNG=Range
         * Required: false
         * Array: false
         */
        this.tdcTimeTypeCd = undefined;
        /**
         * Expanded: Time Date Critical Time #1
         * 						Desc: There are 4 flavors of time for a TDC shipment.  This attribute captures the following flavor of time: AT, AFTER, BEFORE, FROM
         * Required: false
         * Array: false
         */
        this.tdcTime1 = undefined;
        /**
         * Expanded: Time Date Critical Time #2
         * 						Desc: There are 4 flavors of time for a TDC shipment.  This attribute captures the following flavor of time: AT, AFTER, BEFORE, FROM
         * Required: false
         * Array: false
         */
        this.tdcTime2 = undefined;
        /**
         * The date that the shipment should be delivered based on standard transit days.
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * The service center where the shipment is originated.
         * Required: false
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         * The current service center where the shipment is located.
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * The current status of where the shipment is located like Load, Enroute, Dock etc.
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         * The planned ETA for when the shipment will arrive at the destination service center.
         * Required: false
         * Array: false
         */
        this.destSicEta = undefined;
        /**
         * The trailer on which this shipment is currently located.
         * Required: false
         * Array: false
         */
        this.currentTrailer = undefined;
        /**
         * The scheduled ETA for when the shipment will arrive at the destination service center.
         * Required: false
         * Array: false
         */
        this.scheduledEta = undefined;
        /**
         * Defines the linehaul schedule status, related to the trailer, that the shipment is currently moving.
         * Required: false
         * Array: false
         */
        this.scheduleStatusCd = undefined;
        /**
         * The business identifier for a shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The service center identification code for the schedule destination of a trailer.
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         * The date when the shipment is picked up from the customer location.
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         * The pickup call date (YYYYMMDD). In legacy, this is the CAD call number. It's part of the confirmation number.
         * Required: false
         * Array: false
         */
        this.cadCallNbr = undefined;
        /**
         * The business identifier of a pickup activity.
         * Required: false
         * Array: false
         */
        this.callNbr = undefined;
        /**
         * The time when the shipments will be ready for the carrier to pick them up at the customer location.
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         * The time before which the shipment needs to be picked up at the customer location.
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         * The unique identifier of a shipment bill of lading document.
         * Required: false
         * Array: false
         */
        this.bolInstId = undefined;
        /**
         * The service center that is scheduled to deliver the shipment.
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         * The zip code where the delivery location is located.
         * Required: false
         * Array: false
         */
        this.zip6 = undefined;
        /**
         * The special sevices that are required for each line item.
         * Required: false
         * Array: true
         */
        this.shipmentSpecialServices = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The actual location where the shipment is located on dock.
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         * This indicates a child PRO that was once missing has been found but the rest of the shipment has moved elsewhere. It will remain astray until it has caught up with the rest of the shipment, or until it is delivered.
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         * This indicates the child PRO is marked as short. When found, it can switch to normal or astray.
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * Total number of handling units for the shipment.
         * Required: false
         * Array: false
         */
        this.totalHandlingUnitCount = undefined;
        /**
         * The status of an activity.
         * Required: false
         * Array: false
         */
        this.activityStatusCd = undefined;
        /**
         * The number of times that a FBDS has been printed for this shipment.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCount = undefined;
        /**
         * The unique identifier for the dock visit.
         * Required: false
         * Array: false
         */
        this.dockVisitInstId = undefined;
        /**
         * The sequence number that identifies the activity on the dock.
         * Required: false
         * Array: false
         */
        this.dockActivitySequenceNbr = undefined;
        /**
         * The code that indicates delivery information is required for the shipment.
         * Required: false
         * Array: false
         */
        this.deliveryInformationRqdCd = undefined;
        /**
         * The unique identifier for the parent shipment.
         * Required: false
         * Array: false
         */
        this.parentInstId = undefined;
        /**
         * The status code of the freight.
         * Required: false
         * Array: false
         */
        this.freightStatusCd = undefined;
        /**
         * The shipper details for the shipment.
         * Required: false
         * Array: false
         */
        this.shipper = undefined;
        /**
         * The consignee details for the shipment.
         * Required: false
         * Array: false
         */
        this.consignee = undefined;
    }
}
/**
 * The status of the shipment included in an activity.
 */
class ActivityShipmentStatus {
    constructor() {
        /**
         * The business identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The status of the shipment that is part of an activity.
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * The warning messages for the shipment.
         * Required: false
         * Array: true
         */
        this.warnings = undefined;
    }
}
/**
 * Beacon conversation details.
 */
class BeaconConversation {
    constructor() {
        /**
         * The unique identifier for conversation.
         * Required: false
         * Array: false
         */
        this.conversationId = undefined;
        /**
         * The total count of unread messages on a queue conversation.
         * Required: false
         * Array: false
         */
        this.unreadMessages = undefined;
        /**
         * The last text message on a queue conversation.
         * Required: false
         * Array: false
         */
        this.lastMessageTxt = undefined;
        /**
         * The timestamp of the last text message on a queue conversation.
         * Required: false
         * Array: false
         */
        this.lastMessageTmst = undefined;
    }
}
/**
 * Information about a calculated shipment cube.
 */
class CalculatedShipmentCube {
    constructor() {
        /**
         * The total volume in cubic feet.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * The pup volume percentage.
         * Required: false
         * Array: false
         */
        this.pupVolumePercentage = undefined;
        /**
         * The code associated with the dimension profile type.
         * Required: false
         * Array: false
         */
        this.profileTypeCd = undefined;
        /**
         * The profile method used for the calculation.
         * Required: false
         * Array: false
         */
        this.profileMethod = undefined;
    }
}
/**
 * Details that are used to determine customer dwell times.
 */
class CustomerDwellTime {
    constructor() {
        /**
         * The number associated with the node sequence.
         * Required: false
         * Array: false
         */
        this.nodeSequenceNbr = undefined;
        /**
         * The customer code. Example: 136480.
         * Required: false
         * Array: false
         */
        this.customerId = undefined;
        /**
         * The calculated node dwell time in minutes.
         * Required: false
         * Array: false
         */
        this.nodeDwellTime = undefined;
    }
}
/**
 * The customer account and their associated profile notes.
 */
class CustomerProfileNote {
    constructor() {
        /**
         * The identifier for the customer account.
         * Required: true
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * The notes for the operation.
         * Required: false
         * Array: true
         */
        this.operationNotes = undefined;
        /**
         * The notes for NOA.
         * Required: false
         * Array: true
         */
        this.noaNotes = undefined;
        /**
         * The instruction notes for pickup.
         * Required: false
         * Array: true
         */
        this.pickupInstructions = undefined;
        /**
         * The instruction notes for delivery.
         * Required: false
         * Array: true
         */
        this.deliveryInstructions = undefined;
    }
}
/**
 * The customer stop information.
 */
class CustomerStop {
    constructor() {
        /**
         * Details about the customer location.
         * Required: false
         * Array: false
         */
        this.customerLocation = undefined;
        /**
         * Indicates the customer requires notification upon arrival of driver.
         * Required: false
         * Array: false
         */
        this.notifyOnArrival = undefined;
        /**
         * Operation remark.
         * Required: false
         * Array: false
         */
        this.operationRemark = undefined;
        /**
         * Instruction for deliveries.
         * Required: false
         * Array: false
         */
        this.deliveryInstruction = undefined;
        /**
         * Instruction for pickups.
         * Required: false
         * Array: false
         */
        this.pickupInstruction = undefined;
        /**
         * Indicates that the customer is a future customer.
         * Required: false
         * Array: false
         */
        this.futureInd = undefined;
        /**
         * The dock accessibility code.
         * Required: false
         * Array: false
         */
        this.dockAccessibleCd = undefined;
        /**
         * The forklift accessibility code.
         * Required: false
         * Array: false
         */
        this.forkliftAccessibleCd = undefined;
        /**
         * The load or unload code.
         * Required: false
         * Array: false
         */
        this.loadUnloadByCd = undefined;
        /**
         * The location type identifier.
         * Required: false
         * Array: false
         */
        this.locationTypeId = undefined;
        /**
         * The location type value.
         * Required: false
         * Array: false
         */
        this.locationTypeValue = undefined;
        /**
         * The contact for delivery.
         * Required: false
         * Array: false
         */
        this.deliveryContact = undefined;
        /**
         * The contact for pickup.
         * Required: false
         * Array: false
         */
        this.pickupContact = undefined;
        /**
         * Format hh:mm - hh:mm. The delivery window.
         * Required: true
         * Array: false
         */
        this.deliveryOperatingHours = undefined;
        /**
         * Format hh:mm - hh:mm. The pickup window.
         * Required: true
         * Array: false
         */
        this.pickupOperatingHours = undefined;
        /**
         * The location type profile.
         * Required: false
         * Array: false
         */
        this.locationTypeProfileTxt = undefined;
        /**
         * The location type profile code.
         * Required: false
         * Array: false
         */
        this.locationTypeProfileCd = undefined;
    }
}
class DailyPickupStopCustomerPreference {
    constructor() {
        /**
         * The operations profile details for a customer.
         * Required: true
         * Array: false
         */
        this.customerOperationsProfile = undefined;
        /**
         * The operation schedule details for a customer.
         * Required: true
         * Array: false
         */
        this.customerOperationsSchedule = undefined;
        /**
         * The details about the customer location.
         * Required: true
         * Array: false
         */
        this.customerLocation = undefined;
    }
}
/**
 * All delivery-related information for all the shipments on this delivery stop.  Contains activity as well as other information.
 */
class DeliveryActivity {
    constructor() {
        /**
         * Activity sequence number of the delivery for the stop.
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         * The status of the shipment that is part of an activity.
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * The unique identifier of the shipment to be delivered at the stop.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The delivery qualifier code, i.e. the status code of the shipments.
         * Required: true
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * The string representation of the timestamp for when the activity took place.
         * Required: false
         * Array: false
         */
        this.actualActivityDateTimeString = undefined;
        /**
         * The date and time when the activity took place.
         * Required: false
         * Array: false
         */
        this.actualActivityDateTime = undefined;
        /**
         * The Cash on Delivery (COD) payment amount.
         * Required: false
         * Array: false
         */
        this.codPaymentAmount = undefined;
        /**
         * The Cash on Delivery (COD) payment method.
         * Required: false
         * Array: false
         */
        this.codPaymentMethodCd = undefined;
        /**
         * The Cash on Delivery (COD) payment check number.
         * Required: false
         * Array: false
         */
        this.codPaymentCheckNbr = undefined;
        /**
         * Freight charges in cash.
         * Required: false
         * Array: false
         */
        this.freightPaymentAmount = undefined;
        /**
         * Payment method type for paying the actual freight charges.
         * Required: false
         * Array: false
         */
        this.freightPaymentMethodCd = undefined;
        /**
         * Check number for the freight charges.
         * Required: false
         * Array: false
         */
        this.freightPaymentCheckNbr = undefined;
        /**
         * Payment number for the freight charges, when applicable.
         * Required: false
         * Array: false
         */
        this.freightPaymentNbr = undefined;
        /**
         * Note describing any delivery exception that occurred while attempting to complete this activity.
         * Required: false
         * Array: false
         */
        this.exceptionNote = undefined;
        /**
         * Beyond carrier SCAC code.
         * Required: false
         * Array: false
         */
        this.beyondCarrScac = undefined;
        /**
         * The actual delivery qualifier for the shipment.
         * Required: false
         * Array: false
         */
        this.qlfrReasonCd = undefined;
        /**
         * The movement exception sequence number.
         * Required: false
         * Array: false
         */
        this.movementExceptionSequenceNbr = undefined;
        /**
         * The number of pieces short.
         * Required: false
         * Array: false
         */
        this.piecesShortCount = undefined;
        /**
         * The number of pieces damaged.
         * Required: false
         * Array: false
         */
        this.piecesDamagedCount = undefined;
        /**
         * The number of pieces over.
         * Required: false
         * Array: false
         */
        this.piecesOverCount = undefined;
        /**
         * The number of pieces refused.
         * Required: false
         * Array: false
         */
        this.piecesRefusedCount = undefined;
        /**
         * Person signing for the delivery
         * Required: false
         * Array: false
         */
        this.deliverySignedName = undefined;
        /**
         * The string representation of timestamp the delivery signature was captured.
         * Required: false
         * Array: false
         */
        this.deliverySignedDateTimeString = undefined;
        /**
         * The date and time when the delivery signature was captured.
         * Required: false
         * Array: false
         */
        this.deliverySignedDateTime = undefined;
        /**
         * Enables bypassing location check.
         * Required: false
         * Array: false
         */
        this.forceLocationInd = undefined;
        /**
         * Enables bypassing SCAC check.
         * Required: false
         * Array: false
         */
        this.forceScacInd = undefined;
        /**
         * Enables bypassing COD check.
         * Required: false
         * Array: false
         */
        this.forceCodInd = undefined;
        /**
         * Enables bypassing collect check.
         * Required: false
         * Array: false
         */
        this.forceCollectInd = undefined;
        /**
         * Indicates whether the shipment involved in the activity is tracked using handling units.
         * Required: false
         * Array: false
         */
        this.trackWithHandlingUnitsInd = undefined;
        /**
         * The list of handling units for a shipment being delivered and the possible exception recorded for it.
         * Required: false
         * Array: true
         */
        this.deliveryHandlingUnits = undefined;
    }
}
/**
 * Details for a delivery's density including the location and count.
 */
class DeliveryDensity {
    constructor() {
        /**
         * The coordinates for the delivery point.
         * Required: false
         * Array: false
         */
        this.deliveryPoint = undefined;
        /**
         * The number of deliveries made at the point.
         * Required: false
         * Array: false
         */
        this.deliveryCount = undefined;
    }
}
/**
 * The handling units for a shipment being delivered and the possible exception recorded for it.
 */
class DeliveryHandlingUnit {
    constructor() {
        /**
         * The handling unit for a shipment.
         * Required: false
         * Array: false
         */
        this.handlingUnit = undefined;
        /**
         * The code associated with the qualifier reason recorded with this exception.
         * Required: false
         * Array: false
         */
        this.qualifierReasonCd = undefined;
        /**
         * Indicates whether the piece is refused.
         * Required: false
         * Array: false
         */
        this.refusedInd = undefined;
        /**
         * Indicates whether the piece is damaged.
         * Required: false
         * Array: false
         */
        this.damagedInd = undefined;
        /**
         * Indicates whether the piece is short.
         * Required: false
         * Array: false
         */
        this.shortInd = undefined;
        /**
         * The note taken by the personnel who recorded the exception.
         * Required: false
         * Array: false
         */
        this.exceptionNote = undefined;
        /**
         * Indicates whether the scanning was bypassed.
         * Required: false
         * Array: false
         */
        this.bypassScanInd = undefined;
        /**
         * The reason why scanning was bypassed.
         * Required: false
         * Array: false
         */
        this.bypassScanReason = undefined;
        /**
         * The timestamp for when the child PRO was scanned.
         * Required: false
         * Array: false
         */
        this.scannedDateTime = undefined;
        /**
         * Indicates whether the piece that is loaded on the trailer is out for delivery.
         * Required: false
         * Array: false
         */
        this.onTrailerInd = undefined;
    }
}
/**
 * Information about a delivery exception.
 */
class DeliveryException {
    constructor() {
        /**
         * The movement exception sequence number.
         * Required: false
         * Array: false
         */
        this.movementExceptionSequenceNbr = undefined;
        /**
         * The number of pieces damaged.
         * Required: false
         * Array: false
         */
        this.piecesDamagedCount = undefined;
        /**
         * The number of pieces over.
         * Required: false
         * Array: false
         */
        this.piecesOverCount = undefined;
        /**
         * The number of pieces refused.
         * Required: false
         * Array: false
         */
        this.piecesRefusedCount = undefined;
        /**
         * The number of pieces short.
         * Required: false
         * Array: false
         */
        this.piecesShortCount = undefined;
        /**
         * The code associated with the qualifier reason recorded with this exception.
         * Required: false
         * Array: false
         */
        this.qualifierReasonCd = undefined;
        /**
         * The exception note.
         * Required: false
         * Array: false
         */
        this.exceptionNote = undefined;
    }
}
/**
 * The service center and list of associated delivery densities.
 */
class DeliveryLocationDensity {
    constructor() {
        /**
         * The service center identification code for the delivery location.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The list of assocaited DeliveryDensity.
         * Required: false
         * Array: true
         */
        this.deliveryDensities = undefined;
    }
}
/**
 * Information about a customer who is signed up for a delivery notification.
 */
class DeliveryNotificationSignup {
    constructor() {
        /**
         * The unique identifier assigned to the signup user.
         * Required: false
         * Array: false
         */
        this.signupUserId = undefined;
        /**
         * The unique identifier assigned to the signup.
         * Required: false
         * Array: false
         */
        this.signupInstId = undefined;
        /**
         * The profile identifier of the signup user if they are an external registered user of XPO.
         * Required: false
         * Array: false
         */
        this.customerProfileId = undefined;
    }
}
/**
 * Information about delivery payment.
 */
class DeliveryPayment {
    constructor() {
        /**
         * The type of payment method.
         * Required: false
         * Array: false
         */
        this.paymentMethod = undefined;
        /**
         * The check number.
         * Required: false
         * Array: false
         */
        this.checkNbr = undefined;
        /**
         * The amount being paid.
         * Required: false
         * Array: false
         */
        this.paymentAmount = undefined;
    }
}
/**
 * The data needed to update the shipment on a status change based on the delivery qualifer.
 */
class DeliveryQualifierShipmentStatusChange {
    constructor() {
        /**
         * The unique identifier of the shipment to be updated.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The service center that is making the status change; this is required when changing the delivery status of a shipment.
         * Required: false
         * Array: false
         */
        this.reportingSicCd = undefined;
        /**
         * The unique identifier for the equipment involved in the status change.
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * The code which represents the results of a specific delivery activity i.e. the status code of the shipments.
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * The date and time when the status change took place.
         * Required: false
         * Array: false
         */
        this.movementDateTime = undefined;
        /**
         * The Cash on Delivery (COD) payment amount.
         * Required: false
         * Array: false
         */
        this.codPaymentAmount = undefined;
        /**
         * The code associated with the Cash on Delivery (COD) payment method.
         * Required: false
         * Array: false
         */
        this.codPaymentMethodCd = undefined;
        /**
         * The Cash on Delivery (COD) payment check number.
         * Required: false
         * Array: false
         */
        this.codPaymentCheckNbr = undefined;
        /**
         * The freight charges in cash.
         * Required: false
         * Array: false
         */
        this.freightPaymentAmount = undefined;
        /**
         * The code associated with the payment method type that is used to pay the actual freight charges.
         * Required: false
         * Array: false
         */
        this.freightPaymentMethodCd = undefined;
        /**
         * The check number for the freight charges.
         * Required: false
         * Array: false
         */
        this.freightPaymentCheckNbr = undefined;
        /**
         * The payment number for the freight charges, when applicable.
         * Required: false
         * Array: false
         */
        this.freightPaymentNbr = undefined;
        /**
         * The note which describes any delivery exception that occurred while attempting to complete this activity.
         * Required: false
         * Array: false
         */
        this.exceptionNote = undefined;
        /**
         * The SCAC code which identifies the beyond carrier.
         * Required: false
         * Array: false
         */
        this.carrierScac = undefined;
        /**
         * The reason for placing the shipment into a specific delivery qualifier status.
         * Required: false
         * Array: false
         */
        this.qualifierReasonCd = undefined;
        /**
         * The reason code associated with the refused movement exception.
         * Required: false
         * Array: false
         */
        this.refusedReasonCd = undefined;
        /**
         * The reason code associated with an undelivered movmement exception.
         * Required: false
         * Array: false
         */
        this.undeliveredReasonCd = undefined;
        /**
         * The movement exception sequence number.
         * Required: false
         * Array: false
         */
        this.movementExceptionSequenceNbr = undefined;
        /**
         * The number of pieces short.
         * Required: false
         * Array: false
         */
        this.piecesShortCount = undefined;
        /**
         * The number of pieces damaged.
         * Required: false
         * Array: false
         */
        this.piecesDamagedCount = undefined;
        /**
         * The number of pieces over.
         * Required: false
         * Array: false
         */
        this.piecesOverCount = undefined;
        /**
         * The number of pieces refused.
         * Required: false
         * Array: false
         */
        this.piecesRefusedCount = undefined;
        /**
         * The name of the person signing for the delivery.
         * Required: false
         * Array: false
         */
        this.deliverySignedName = undefined;
        /**
         * The date and time when the delivery signature was captured.
         * Required: false
         * Array: false
         */
        this.deliverySignedDateTime = undefined;
        /**
         * Indicates whether to bypass the location check.
         * Required: false
         * Array: false
         */
        this.forceLocationInd = undefined;
        /**
         * Indicates whether to bypass the SCAC check.
         * Required: false
         * Array: false
         */
        this.forceScacInd = undefined;
        /**
         * Indicates whether to bypass the COD check.
         * Required: false
         * Array: false
         */
        this.forceCodInd = undefined;
        /**
         * Indicates whether to bypass the check collection.
         * Required: false
         * Array: false
         */
        this.forceCollectInd = undefined;
        /**
         * Indicates whether the shipment involved in the activity is tracked using handling units.
         * Required: false
         * Array: false
         */
        this.trackWithHandlingUnitsInd = undefined;
        /**
         * The list of handling units for a shipment being delivered and the possible exception recorded for it.
         * Required: false
         * Array: true
         */
        this.deliveryHandlingUnits = undefined;
        /**
         * The unique identifier for a cartage carrier.
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
    }
}
/**
 * The shipment to be delivered when the stop activity is a delivery.
 */
class DeliveryShipmentAtStop {
    constructor() {
        /**
         * The business identifier for a shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The unique identifier for a shipment.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The total number of pieces.
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * The total weight in pounds.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The number of motorized pieces.
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * The number of loose pieces.
         * Required: false
         * Array: false
         */
        this.loosePiecesCount = undefined;
        /**
         * Indicates that service is guaranteed.
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         * Indicates whether the shipment contains hazardous materials.
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         * Indicates whether the shipment contains food, poision or none.
         * Required: false
         * Array: false
         */
        this.foodPoisonCd = undefined;
        /**
         * The service type code.
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         * The delivery qualifier code of the shipment.
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * The amount to collect.
         * Required: false
         * Array: false
         */
        this.collectAmount = undefined;
        /**
         * The COD amount.
         * Required: false
         * Array: false
         */
        this.codAmount = undefined;
        /**
         * Indicates if only to cash should be accespted as payment.
         * Required: false
         * Array: false
         */
        this.cashOnlyInd = undefined;
        /**
         * The delivery signatory.
         * Required: false
         * Array: false
         */
        this.deliverySignedName = undefined;
        /**
         * The string representation of timestamp the delivery signature was captured.
         * Required: false
         * Array: false
         */
        this.deliverySignedDateTimeString = undefined;
        /**
         * The date/time the delivery signature was captured. Handheld sends this in its local time.
         * Required: false
         * Array: false
         */
        this.deliverySignedDateTime = undefined;
        /**
         * The type of dates on the time date critical shipment.
         * Required: false
         * Array: false
         */
        this.tdcDateType = undefined;
        /**
         * The type of times on the time date critical shipment.
         * Required: false
         * Array: false
         */
        this.tdcTimeType = undefined;
        /**
         * The string representation of the starting TDC timestamp.
         * Required: false
         * Array: false
         */
        this.tdcStartDateTimeString = undefined;
        /**
         * The string representation of the ending TDC timestamp.
         * Required: false
         * Array: false
         */
        this.tdcEndDateTimeString = undefined;
        /**
         * The carrier SCAC code.
         * Required: false
         * Array: false
         */
        this.carrierScacCd = undefined;
        /**
         * The list of hazmats for the shipment.
         * Required: false
         * Array: true
         */
        this.hazMats = undefined;
        /**
         * The delivery exception details.
         * Required: false
         * Array: false
         */
        this.deliveryException = undefined;
        /**
         * Details for the COD payment.
         * Required: false
         * Array: false
         */
        this.codPayment = undefined;
        /**
         * Details for the collect payment.
         * Required: false
         * Array: false
         */
        this.collectPayment = undefined;
        /**
         * The list of handling units for a shipment being delivered and the possible exception recorded for it.
         * Required: false
         * Array: true
         */
        this.deliveryHandlingUnits = undefined;
        /**
         * Indicates whether the shipment is exempted to be tracked via piece level.
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionInd = undefined;
        /**
         * An explanation of why the shipment is exempted from piece level tracking.
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionReason = undefined;
    }
}
/**
 * The dimensions of an individual shipment piece recorded by linehaul projection, commodity inspection, or handling units.
 */
class DeliveryShipmentDimension {
    constructor() {
        /**
         * The source of the dimensions measurement.
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * The unique identifier of the dimension. This contains dimSequenceNbr when sourceCd is Linehaul and sequenceNbr when sourceCd is HandlingUnit. When sourceCd is Commodity, it contains commoditySequenceNbr and dimSequenceNbr separated by a dash.
         * Required: false
         * Array: false
         */
        this.dimensionId = undefined;
        /**
         * The length of the piece in inches.
         * Required: false
         * Array: false
         */
        this.lengthNbr = undefined;
        /**
         * The width of the piece in inches.
         * Required: false
         * Array: false
         */
        this.widthNbr = undefined;
        /**
         * The height of the piece in inches.
         * Required: false
         * Array: false
         */
        this.heightNbr = undefined;
    }
}
/**
 * Audit Metrics of delivery shipments.
 */
class DeliverySpecialServiceAuditMetric {
    constructor() {
        /**
         * Count of DRs audited.
         * Required: false
         * Array: false
         */
        this.completed = undefined;
        /**
         * Count of DRs pedning for audit.
         * Required: false
         * Array: false
         */
        this.pending = undefined;
        /**
         * Count of DRs audited by employee
         * Required: false
         * Array: false
         */
        this.audtisByEmployee = undefined;
    }
}
/**
 * Contains attributes required for printing a FBDS document.
 */
class DeliverySetPrint {
    constructor() {
        /**
         * The list of unique identifiers for the routes.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The list of shipment identifiers for the shipments that will be printed.
         * Required: false
         * Array: true
         */
        this.shipmentIds = undefined;
        /**
         * The service center identification code for the document.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates whether a document can be reprinted.
         * Required: false
         * Array: false
         */
        this.reprintInd = undefined;
        /**
         * The unique identifier for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
    }
}
/**
 * Details about the trip stop for a mobile device.
 */
class DeviceTripStop {
    constructor() {
        /**
         * The unique identifier for the trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The node sequence number the identifies this stop as part of the trip.
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The sequence number generated by the optimizer response for all nodes that are of type STOP.
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         * The status of the trip stop.
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * The unique identifier for the node.  For CIS stop, this is the customer instance ID. For SIC, this is the location instance ID.
         * Required: false
         * Array: false
         */
        this.nodeInstId = undefined;
        /**
         * The location SIC code of the service center encompassing the trip stop.
         * Required: false
         * Array: false
         */
        this.nodeSicCd = undefined;
        /**
         * The type of location the node instance ID is derived from.
         * Required: false
         * Array: false
         */
        this.nodeTypeCd = undefined;
        /**
         * The system identifier of the customer involved in the stop.
         * Required: false
         * Array: false
         */
        this.customerId = undefined;
        /**
         * The system identifier of the location involved in the stop.
         * Required: false
         * Array: false
         */
        this.locationId = undefined;
        /**
         * The string representation of the timestamp the driver arrived at the trip stop.
         * Required: false
         * Array: false
         */
        this.arrivalDateTimeString = undefined;
        /**
         * The string representation of the  timestamp the driver left from the trip stop
         * Required: false
         * Array: false
         */
        this.departureDateTimeString = undefined;
        /**
         * The date/time the driver arrived at the trip stop.
         * Required: false
         * Array: false
         */
        this.arrivalDateTime = undefined;
        /**
         * The date/time the driver left from the trip stop.
         * Required: false
         * Array: false
         */
        this.departureDateTime = undefined;
        /**
         * The latitude of the trip stop location.
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * The longitude of the trip stop location.
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * The precision of the trip stop location.
         * Required: false
         * Array: false
         */
        this.precision = undefined;
        /**
         * The odometer reading while departing from the origin or stop, or when arriving at the destination
         * Required: false
         * Array: false
         */
        this.odometer = undefined;
        /**
         * The string representation of the starting time range of the earliest appointment among all deliveries on the same trip stop.
         * Required: false
         * Array: false
         */
        this.earliestAppointmentFromTimeString = undefined;
        /**
         * The  string representation of the ending time range of the earliest appointment among all deliveries on the same trip stop.
         * Required: false
         * Array: false
         */
        this.earliestAppointmentToTimeString = undefined;
        /**
         * Indicates whether deletion of pick requset is to be ignored.
         * Required: false
         * Array: false
         */
        this.ignoreDeleteOfPickupRequestInd = undefined;
        /**
         * Indicates whether the driver did not actually stop at a trip stop.
         * Required: false
         * Array: false
         */
        this.stopBypassedInd = undefined;
        /**
         * Identifies why the arrival or departure time for a stop was delayed.
         * Required: false
         * Array: false
         */
        this.delayReasonCd = undefined;
        /**
         * The activities included in a trip stop.
         * Required: false
         * Array: true
         */
        this.deviceTripStopActivities = undefined;
        /**
         * Notes about the delivery and pickup remarks.
         * Required: false
         * Array: false
         */
        this.stopNote = undefined;
        /**
         * Format hh:mm. The string representation of the earliest time the customer is open to do pickup or delivery, or the latest for both.
         * Required: false
         * Array: false
         */
        this.customerOpenTimeString = undefined;
        /**
         * Format hh:mm. The string representation of the latest time before the customer closes for pickup or delivery, or the earliest for both.
         * Required: false
         * Array: false
         */
        this.customerCloseTimeString = undefined;
        /**
         * The customer stop information.
         * Required: false
         * Array: false
         */
        this.customerStop = undefined;
        /**
         * Enables bypassing date validation.
         * Required: false
         * Array: false
         */
        this.forceDateInd = undefined;
        /**
         * Enables bypassing location check.
         * Required: false
         * Array: false
         */
        this.forceLocationInd = undefined;
        /**
         * Enables bypassing SCAC check.
         * Required: false
         * Array: false
         */
        this.forceScacInd = undefined;
        /**
         * Enables bypassing COD check.
         * Required: false
         * Array: false
         */
        this.forceCodInd = undefined;
        /**
         * Enables bypassing collect check.
         * Required: false
         * Array: false
         */
        this.forceCollectInd = undefined;
        /**
         * Indicates whether this is initiated by a handheld device.
         * Required: false
         * Array: false
         */
        this.invokeFromHandheldInd = undefined;
        /**
         * The unique identifier of the geographic area to which the trip stop belongs.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
    }
}
/**
 * A stop activity detail object that contain specific information depending the activity code (DL/SL/PU/HL/HE/SE/HK/DP).
 */
class DeviceTripStopActivity {
    constructor() {
        /**
         * The system identifier of the trip in which this stop activity belongs to.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The node sequence number the identifies the stop in which this stop activity belongs to.
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The node activity sequence number the identifies the stop activity as part of a stop.
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         * The type of activity.
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * The status of the stop activity.
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * The string representation of the timestamp for when the activity took place.
         * Required: false
         * Array: false
         */
        this.actualActivityDateTimeString = undefined;
        /**
         * The date/time the activity took place.
         * Required: false
         * Array: false
         */
        this.actualActivityDateTime = undefined;
        /**
         * The shipment to be delivered when the stop activity is a delivery.
         * Required: false
         * Array: false
         */
        this.deliveryShipmentAtStop = undefined;
        /**
         * The name of the customer.
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         * The system identifier of the customer.
         * Required: false
         * Array: false
         */
        this.customerNbr = undefined;
        /**
         * The credit status of the customer.
         * Required: false
         * Array: false
         */
        this.creditedStatus = undefined;
        /**
         * Format hh:mm:ss. The string representation of the ready time for pickup activity or the starting time range of the earliest appointment for a delivery activity.
         * Required: false
         * Array: false
         */
        this.readyCloseStartTimeString = undefined;
        /**
         * Format hh:mm:ss. The string representation of the close time for pickup activity or the ending time range of the earliest appointment for a delivery activity.
         * Required: false
         * Array: false
         */
        this.readyCloseEndTimeString = undefined;
        /**
         * The equipment involved in the stop activity.
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
        /**
         * The remaining space on a trailer in feet.
         * Required: false
         * Array: false
         */
        this.feetRemainingOnTrailer = undefined;
        /**
         * The latitude of the trip stop activity location.
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * The longitude of the trip stop activity location.
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * The precision of the trip stop activity location.
         * Required: false
         * Array: false
         */
        this.precision = undefined;
        /**
         * Indicates that there is no shipment to be picked up or delivered.
         * Required: false
         * Array: false
         */
        this.noFreightInd = undefined;
        /**
         * The stop activity remark.
         * Required: false
         * Array: false
         */
        this.activityRemark = undefined;
        /**
         * The pickup request system identifier of a pickup activity.
         * Required: false
         * Array: false
         */
        this.pickupRequestId = undefined;
        /**
         * The business identifier of a pickup activity.
         * Required: false
         * Array: false
         */
        this.callNbr = undefined;
        /**
         * The origin of the pickup request.
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * The shipments that are part of a pickup activity.
         * Required: false
         * Array: true
         */
        this.pickupLineItems = undefined;
        /**
         * The list of shipper dimension profiles.
         * Required: false
         * Array: true
         */
        this.customerDimensionsProfiles = undefined;
        /**
         * Indicates whether the stop activity is to be split into a new stop.
         * Required: false
         * Array: false
         */
        this.splitStopInd = undefined;
        /**
         * Indicates whether a hook load activity is to be updated and unassigned.
         * Required: false
         * Array: false
         */
        this.updateAndUnassignInd = undefined;
        /**
         * Indicates whether the shipment involved in the activity is tracked using handling units.
         * Required: false
         * Array: false
         */
        this.trackWithHandlingUnitsInd = undefined;
        /**
         * The special sevices that are required for each activity (delivery/pickup request).
         * Required: false
         * Array: true
         */
        this.shipmentSpecialServices = undefined;
    }
}
/**
 * The payload type for the device trip stop channel.
 */
class DeviceTripStopPayload {
    constructor() {
        /**
         * The trip stop from the device.
         * Required: true
         * Array: false
         */
        this.deviceTripStop = undefined;
        /**
         * The identifier of the user that used the device to complete a stop.
         * Required: true
         * Array: false
         */
        this.deviceUserId = undefined;
        /**
         * Indicates that a payload has already been delayed for processing.
         * Required: true
         * Array: false
         */
        this.processingDelayedInd = undefined;
    }
}
/**
 * Driver details for the dispatch trip board.
 */
class DispatchDriver {
    constructor() {
        /**
         * The name of the driver to whom the trip is assigned.
         * Required: false
         * Array: false
         */
        this.dsrName = undefined;
        /**
         * The employee ID of the driver.
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         * The working start time of the driver.
         * Required: false
         * Array: false
         */
        this.startTime = undefined;
        /**
         * The working end time of the driver.
         * Required: false
         * Array: false
         */
        this.endTime = undefined;
        /**
         * The indication of whether the driver is allowed to drive based on the license expiration date.
         * Required: false
         * Array: false
         */
        this.allowedToDriveInd = undefined;
        /**
         * The time the driver went for lunch.
         * Required: false
         * Array: false
         */
        this.lunchTime = undefined;
        /**
         * The driver's handheld phone number.
         * Required: false
         * Array: false
         */
        this.handheldPhoneNumber = undefined;
        /**
         * The unique identifier for the trip which is associated with the driver.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The driver's device ID.
         * Required: false
         * Array: false
         */
        this.deviceId = undefined;
        /**
         * Details for the driver conversation.
         * Required: false
         * Array: false
         */
        this.beaconConversation = undefined;
        /**
         * The plan date of the trip.
         * Required: true
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The service center identification code for the driver's location.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The job assigned to the driver.
         * Required: true
         * Array: false
         */
        this.jobSelectionCd = undefined;
        /**
         * The code associated with the driver's availability.
         * Required: true
         * Array: false
         */
        this.availableStatusCd = undefined;
        /**
         * The code associated with the active trip's status.
         * Required: true
         * Array: false
         */
        this.activeTripStatusCd = undefined;
        /**
         * The prefix of the driver's preferred tractor.
         * Required: true
         * Array: false
         */
        this.preferredTractorPrefix = undefined;
        /**
         * The suffix number of the driver's preferred tractor.
         * Required: true
         * Array: false
         */
        this.preferredTractorSuffixNbr = undefined;
        /**
         * The identifier of the driver's preferred tractor.
         * Required: true
         * Array: false
         */
        this.preferredTractorInstId = undefined;
        /**
         * The unique identifier of the driver's preferred area.
         * Required: true
         * Array: false
         */
        this.preferredAreaInstId = undefined;
        /**
         * The prefix of the driver's currently assigned tractor.
         * Required: true
         * Array: false
         */
        this.currentlyAssignedTractorPrefix = undefined;
        /**
         * The suffix number of the driver's currently assigned tractor.
         * Required: true
         * Array: false
         */
        this.currentlyAssignedTractorSuffixNbr = undefined;
        /**
         * The unique identifier of the driver's currently assigned tractor.
         * Required: true
         * Array: false
         */
        this.currentlyAssignedTractorInstId = undefined;
    }
}
/**
 * Equipment details for the dispatch trip board.
 */
class DispatchEquipment {
    constructor() {
        /**
         * The tractor number assigned to the driver.
         * 						If SCO_TRIP_EQP.EQP_TYP_CD='P' or 'Z'
         * 						Then CONCAT(SCO_TRIP_EQP.EQP_ID_PFX_TXT, '-', SCO_TRIP_EQP.EQP_ID_SFX_NBR)
         * Required: false
         * Array: false
         */
        this.tractorNbr = undefined;
        /**
         * The unique identifier of the tractor.
         * 						If SCO_TRIP_EQP.EQP_TYP_CD='P' or 'Z'
         * 						Then SCO_TRIP_EQP.EQP_INST_ID
         * Required: false
         * Array: false
         */
        this.tractorInstId = undefined;
    }
}
/**
 * Route details for the dispatch trip board.
 */
class DispatchRoute {
    constructor() {
        /**
         * The trailer number assigned to the route.
         * 						If SCO_TRIP_EQP.EQP_TYP_CD='T' or 'Z', then CONCAT(SCO_TRIP_EQP.EQP_ID_PFX_TXT, '-', SCO_TRIP_EQP.EQP_ID_SFX_NBR)
         * Required: false
         * Array: false
         */
        this.trailerNbr = undefined;
        /**
         * The unique identifier of the trailer.
         * 						If SCO_TRIP_EQP.EQP_TYP_CD='T' or 'Z', then SCO_TRIP_EQP.EQP_INST_ID
         * Required: false
         * Array: false
         */
        this.trailerInstId = undefined;
        /**
         * The status of the trailer.
         * Required: false
         * Array: false
         */
        this.trailerStatusCd = undefined;
        /**
         * The manufacture year of the trailer.
         * Required: false
         * Array: false
         */
        this.trailerManufactureYear = undefined;
        /**
         * The space available in the trailer as indicated by the driver.
         * Required: false
         * Array: false
         */
        this.feetAvailable = undefined;
        /**
         * Indicates if the route has been sequenced.
         * Required: false
         * Array: false
         */
        this.sequenceValidInd = undefined;
        /**
         * Current Door - Prior to dispatch, the location of the trailer at the dock
         * 						Planned Door - After dispatch, the planned unload door.  this allows us to send a message to the driver so he knows where to put his trailer on return to the service center.
         * Required: false
         * Array: false
         */
        this.plannedDoor = undefined;
        /**
         * The event door for the equipment linked to the route.
         * Required: false
         * Array: false
         */
        this.eventDoor = undefined;
        /**
         * The terminal of the route.  Either the host or the zone.
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The route name prefix.
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * The route name suffix.
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * The unique identifier of the route.
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * All of the aggregated special services on the route.
         * Required: false
         * Array: true
         */
        this.specialServicesSummaries = undefined;
        /**
         * The weight of the remaining deliveries on the route.
         * Required: false
         * Array: false
         */
        this.totalRemainingDeliveryWeightCount = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The total number of motor moves that are required on the dispatched route.
         * Required: false
         * Array: false
         */
        this.totalMmCount = undefined;
    }
}
/**
 * Trip details for the dispatch trip board
 */
class DispatchTrip {
    constructor() {
        /**
         * The driver details for the dispatch trip.
         * Required: false
         * Array: false
         */
        this.dispatchDriver = undefined;
        /**
         * The equipment details for the dispatch trip.
         * Required: false
         * Array: true
         */
        this.dispatchEquipment = undefined;
        /**
         * The route details for the dispatch trip.
         * Required: false
         * Array: true
         */
        this.dispatchRoutes = undefined;
        /**
         * The unique identifier for trips.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The status for the trip.
         * Required: false
         * Array: false
         */
        this.tripStatusCd = undefined;
        /**
         * The date associated with the trip.
         * Required: true
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * The dispatch group to which the trip belongs.
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * The estimated or actual start time of the trip based on the trip status.  If the trip is New or Canceled, then the estimated start time is used.  If the trip is Dispatched, Returning or Completed, then the actual start time is used.
         * Required: false
         * Array: false
         */
        this.startDateTime = undefined;
        /**
         * The estimated time when all the deliveries are complete and the trip is ready for pickups.
         * Required: false
         * Array: false
         */
        this.estimatedEmptyDateTime = undefined;
        /**
         * The estimated or actual time of completion of the trip based on the trip status.  If the trip is New, Canceled, Dispatched or returning, then the estimated complete time is used.  If the trip is completed, then the actual complete time is used.
         * Required: false
         * Array: false
         */
        this.completeDateTime = undefined;
        /**
         * The remaining delivery stops on the trip.
         * Required: false
         * Array: false
         */
        this.deliveryRemainingCount = undefined;
        /**
         * The completed delivery stops on the trip.
         * Required: false
         * Array: false
         */
        this.deliveryCompletedCount = undefined;
        /**
         * The remaining pickup stops on the trip.
         * Required: false
         * Array: false
         */
        this.pickupRemainingCount = undefined;
        /**
         * The completed pickup stops on the trip.
         * Required: false
         * Array: false
         */
        this.pickupCompletedCount = undefined;
        /**
         * The remaining stops on the trip.
         * Required: false
         * Array: false
         */
        this.stopRemainingCount = undefined;
        /**
         * The completed stops on the trip.
         * Required: false
         * Array: false
         */
        this.stopCompletedCount = undefined;
        /**
         * When set to 'Y', the trip is prevented from automatically downloading to the driver's device.
         * Required: false
         * Array: false
         */
        this.doNotAutoDisplayInd = undefined;
        /**
         * The number of shipments that the customers have returned.
         * Required: false
         * Array: false
         */
        this.shipmentReturnCount = undefined;
        /**
         * The aggregated route special services for the trip.
         * Required: false
         * Array: true
         */
        this.specialServicesSummaries = undefined;
        /**
         * The total weight of the remaining pickups on the trip.
         * Required: false
         * Array: false
         */
        this.totalRemainingPickupWeightCount = undefined;
        /**
         * The total weight of the remaining deliveries on the trip.
         * Required: false
         * Array: false
         */
        this.totalRemainingDeliveryWeightCount = undefined;
        /**
         * The identifier for the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopInstId = undefined;
        /**
         * The sequence number which uniquely identifies the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopNodeSequenceNbr = undefined;
        /**
         * The departure time of the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopDateTime = undefined;
        /**
         * The type of the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopTypeCd = undefined;
        /**
         * The status of the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopStatus = undefined;
        /**
         * The arrival time of the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopArrivalDateTime = undefined;
        /**
         * The dwell time at the last stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopDwellTime = undefined;
        /**
         * Any note related to the trip referencing a remark text.
         * Required: false
         * Array: false
         */
        this.tripNotes = undefined;
        /**
         * Identifies the partner carrier for this cartage trip
         * Required: false
         * Array: false
         */
        this.cmsCarrierId = undefined;
        /**
         * Identifies the type of the cartage trip
         * Required: false
         * Array: false
         */
        this.cmsTripTypeCd = undefined;
        /**
         * The details about the partner carrier.
         * Required: false
         * Array: false
         */
        this.carrier = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The list of messages which each describe a failure or rejection in the tender process.  They are formatted for display in a user interface.
         * Required: false
         * Array: true
         */
        this.tenderMessages = undefined;
        /**
         * A derived tender status based on combinations of the tender detail status of each tendered shipment on the trip.
         * Required: false
         * Array: false
         */
        this.carrierTenderGroupStatusCd = undefined;
        /**
         * The code which determines whether No, Partial, or All Prints for FBDS documents will be printed for the route.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCountCd = undefined;
    }
}
/**
 * Information related to a driver's current location.
 */
class DriverLocation {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.currentLocation = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tripDriver = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.trip = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.routeDetails = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.tractors = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.employeeMobileDevice = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeStop = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.headingDegrees = undefined;
    }
}
/**
 * Deatils about the stop, dropLocation and equipment involved in a drop activity at a customer stop.
 */
class DropStop {
    constructor() {
        /**
         * The customer stop where the drop activity is to be performed.
         * Required: false
         * Array: false
         */
        this.dropStopNode = undefined;
        /**
         * The date and time when the driver actually arrived at the customer stop.
         * Required: false
         * Array: false
         */
        this.actualArrivalDateTime = undefined;
        /**
         * The date and time when the driver actually departed from the customer stop.
         * Required: false
         * Array: false
         */
        this.actualDepartureDateTime = undefined;
        /**
         * The customer name of the location where the drop will be performed.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Any note made by the driver at the customer stop where the drop activity was to be performed.
         * Required: false
         * Array: false
         */
        this.stopNote = undefined;
        /**
         * A list containing information needed about the stop and equipment involved in a drop activity at a customer stop.
         * Required: false
         * Array: true
         */
        this.dropStopEquipment = undefined;
    }
}
/**
 * Information needed about the stop and equipment involved in a drop activity at a customer stop.
 */
class DropStopEquipment {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.tripNodeActivity = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
        /**
         * Indicates if the equipment that was dropped at the customer location should be force-hooked.
         * Required: false
         * Array: false
         */
        this.forceEqpLocationInd = undefined;
    }
}
/**
 * Contains attributes required for printing a dsr checkin document.
 */
class DsrCheckinPrint {
    constructor() {
        /**
         * The list of unique identifiers for the trips.
         * Required: false
         * Array: true
         */
        this.tripInstIds = undefined;
        /**
         * The service center identification code for the document.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates whether to print an office copy.
         * Required: false
         * Array: false
         */
        this.officeCopyInd = undefined;
        /**
         * Indicates whether to print a DSR copy.
         * Required: false
         * Array: false
         */
        this.dsrCopyInd = undefined;
    }
}
/**
 * Dwell time details for a trip node.
 */
class DwellTimeTripNode {
    constructor() {
        /**
         * Sequence number to Uniquely identify the Node Response
         * Required: false
         * Array: false
         */
        this.nodeSequenceNbr = undefined;
        /**
         * Node sic code.
         * Required: false
         * Array: false
         */
        this.nodeSicCd = undefined;
        /**
         * The customer code. Example: 136480.
         * Required: false
         * Array: false
         */
        this.customerId = undefined;
        /**
         * The customer profile location type. Example: Residential, Govt.
         * Required: false
         * Array: false
         */
        this.customerLocationType = undefined;
        /**
         * The customer profile fork lift indicator.
         * Required: false
         * Array: false
         */
        this.forkliftInd = undefined;
        /**
         * The customer profile dock indicator.
         * Required: false
         * Array: false
         */
        this.dockProfileInd = undefined;
        /**
         * The list of activities associated with a customer.
         * Required: false
         * Array: true
         */
        this.activities = undefined;
    }
}
/**
 * A derived set of summary information for an existing route
 */
class ExistingRouteSummary {
    constructor() {
        /**
         * The unique identifier for the trip associated with the route.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The unique identifier of the route.
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The prefix component of the route name.
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * The suffix component of the route name.
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Identifies if the route (prefix/suffix) is a planning or delivery route.
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         * A SIC is associated with the route's prefix/suffix. For example the SIC USU can have a route of TRAP-NEXT. But USU has a satellite SIC of UEN. USU and UEN can both have a route named TRAP-NEXT. As a result, the planner would see TRAP-NEXT multiple times in their list. Adding the SIC will allow the planner to identify the planning route associated with a particular SIC.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates whether the route is a cartage route.
         * Required: true
         * Array: false
         */
        this.cartageTripInd = undefined;
        /**
         * The status of this route.
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * The unique identifier of the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The equipment identifier prefix.
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * The equipment identifier suffix.
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffix = undefined;
    }
}
/**
 * Details about a geo area.
 */
class GeoArea {
    constructor() {
        /**
         * The unique identifier for the area.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * The SIC code for the location.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The name for the geo area.
         * Required: false
         * Array: false
         */
        this.geoAreaName = undefined;
        /**
         * The description for the geo area.
         * Required: false
         * Array: false
         */
        this.geoAreaDesc = undefined;
        /**
         * Defines what the polygon coordinates represent.  Possible values are: Area (A), Boundary (B), Fence (F), and Service Center (S).
         * Required: false
         * Array: false
         */
        this.polygonTypeCd = undefined;
        /**
         * The coordinates for the locations.
         * Required: false
         * Array: true
         */
        this.polygon = undefined;
        /**
         * Standard audit trail information.
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Details about the hook equioment stop.
 */
class HookEquipmentStop {
    constructor() {
        /**
         * The unique identifier of the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The equipment identifier prefix.
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * The equipment identifier suffix.
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffix = undefined;
        /**
         * The type of equipment.
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         * The date when the shipment is picked up from the customer location.
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         * The terminal of the hook stop.
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The drop location details.
         * Required: false
         * Array: false
         */
        this.equipmentLocation = undefined;
        /**
         * The unique identifier of hookrequest.
         * Required: false
         * Array: false
         */
        this.hookInstId = undefined;
    }
}
/**
 * Details about the inbound trailer.
 */
class InboundTrailer {
    constructor() {
        /**
         * The unique identifier of the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The prefix for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentPrefix = undefined;
        /**
         * The suffix number for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentSuffixNbr = undefined;
        /**
         * The equipment prefix and suffix concatenated.
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * The schedule destination service center of a trailer.
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         * The current service center of a trailer.
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * The ETA for the trailer.
         * Required: false
         * Array: false
         */
        this.trailerEtaDateTime = undefined;
    }
}
/**
 * Contains attributes required for printing a manifest document.
 */
class ManifestPrint {
    constructor() {
        /**
         * The code associated with the type of report which was requested to be printed.
         * Required: false
         * Array: false
         */
        this.pnDReportTypeCd = undefined;
        /**
         * The list of unique identifiers for the routes.
         * Required: false
         * Array: true
         */
        this.routeInstIds = undefined;
        /**
         * The list of unique identifiers for the trips.
         * Required: false
         * Array: true
         */
        this.tripInstIds = undefined;
        /**
         * The list of shipment identifiers.
         * Required: false
         * Array: true
         */
        this.shipmentIds = undefined;
        /**
         * The service center identification code for the document.
         * Required: true
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates whether to print an office copy.
         * Required: false
         * Array: false
         */
        this.officeCopyInd = undefined;
        /**
         * Indicates whether to print a DSR copy.
         * Required: false
         * Array: false
         */
        this.dsrCopyInd = undefined;
        /**
         * The unique identifier for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The unique identifier for the dock visit.
         * Required: false
         * Array: false
         */
        this.dockVisitInstId = undefined;
    }
}
/**
 * Represents the dwell time for a stop of a trip.
 */
class NodeDwellTime {
    constructor() {
        /**
         * The unique identifier for the trip which is associated with the stop.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The sequence number of the stop.
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The customer instance ID associated with the stop.
         * Required: false
         * Array: false
         */
        this.nodeInstId = undefined;
        /**
         * The estimated dwell time at the customer stop.
         * Required: false
         * Array: false
         */
        this.estimatedDwellTimeMinutes = undefined;
    }
}
/**
 * Optimizer commodity and dimension details.
 */
class OptimizerCommodityDimension {
    constructor() {
        /**
         * The business identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The unique identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The details of the dimensions of the shipments that will be sent to the optimizer
         * Required: false
         * Array: true
         */
        this.pdoRequestLnhDimensions = undefined;
        /**
         * The details of the commodities shipments that will be sent to the optimizer
         * Required: false
         * Array: true
         */
        this.pdoRequestCommodities = undefined;
    }
}
/**
 * Optimizer driver summary details.
 */
class OptimizerDriverSummary {
    constructor() {
        /**
         * Total count of driver records
         * Required: false
         * Array: false
         */
        this.driverRecordCount = undefined;
        /**
         * Total count where JOB_TYPE_CD=BID
         * Required: false
         * Array: false
         */
        this.bidCount = undefined;
        /**
         * Total count where JOB_TYPE_CD=FLEX
         * Required: false
         * Array: false
         */
        this.flexCount = undefined;
        /**
         * Total available max hours
         * Required: false
         * Array: false
         */
        this.availableMaxHours = undefined;
    }
}
/**
 * Optimizer equipment summary details.
 */
class OptimizerEquipmentSummary {
    constructor() {
        /**
         * Total count of trailer records
         * Required: false
         * Array: false
         */
        this.trailerRecordCount = undefined;
        /**
         * Total weight in lbs
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Total pallet count
         * Required: false
         * Array: false
         */
        this.palletCount = undefined;
        /**
         * Total motor move count
         * Required: false
         * Array: false
         */
        this.motorMoveCount = undefined;
        /**
         * The total count of liftgate indicators.
         * Required: false
         * Array: false
         */
        this.liftGateIndCount = undefined;
    }
}
/**
 * A view of PdoParameter and PdoParameterSetting.
 */
class OptimizerParameter {
    constructor() {
        /**
         * The value for this parameter.
         * Required: false
         * Array: false
         */
        this.settingValue = undefined;
        /**
         * The terminal SIC.
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The unique identifier for this parameter.
         * Required: false
         * Array: false
         */
        this.parmCd = undefined;
    }
}
/**
 * The result from the optimizer.
 */
class OptimizerResult {
    constructor() {
        /**
         * The terminal sic code related to the optimizer.
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The plan date for the optimizer.
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * The status of the optimizer.
         * Required: false
         * Array: false
         */
        this.status = undefined;
        /**
         * The status message.
         * Required: false
         * Array: false
         */
        this.message = undefined;
        /**
         * The unique identifier of the optimizer.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The routes associated with the optimizer.
         * Required: false
         * Array: true
         */
        this.optimizerResultRoutes = undefined;
    }
}
/**
 * The route associated with the optimizer result.
 */
class OptimizerResultRoute {
    constructor() {
        /**
         * The unique identifier for the equipment associated with the route.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The name associated with the route. Example: '015'.
         * Required: false
         * Array: false
         */
        this.routeName = undefined;
        /**
         * Time at which city trailer loading will be complete and ready to leave.
         * Required: false
         * Array: false
         */
        this.loadCloseTime = undefined;
        /**
         * Gate out time for driver.
         * Required: false
         * Array: false
         */
        this.routeStartTime = undefined;
        /**
         * Empty time driver.
         * Required: false
         * Array: false
         */
        this.routeEmptyTime = undefined;
        /**
         * The trailer type assigned to a given route.
         * Required: false
         * Array: false
         */
        this.equipmentType = undefined;
        /**
         * A group identifier for a set of drivers that start their journey at the same time.
         * Required: false
         * Array: false
         */
        this.waveNumber = undefined;
        /**
         * The anchor point's latitude.
         * Required: false
         * Array: false
         */
        this.anchorPointLatitude = undefined;
        /**
         * The anchor point's longitude.
         * Required: false
         * Array: false
         */
        this.anchorPointLongitude = undefined;
        /**
         * The anchor point's zip code.
         * Required: false
         * Array: false
         */
        this.anchorPointZip = undefined;
        /**
         * The total numbe of stops in the route.
         * Required: false
         * Array: false
         */
        this.totalStops = undefined;
        /**
         * The total number of bills in the route.
         * Required: false
         * Array: false
         */
        this.totalBills = undefined;
        /**
         * The total weight of all shipments in the route.
         * Required: false
         * Array: false
         */
        this.totalWeight = undefined;
        /**
         * The total number of miles for the route.
         * Required: false
         * Array: false
         */
        this.totalMiles = undefined;
        /**
         * The total travel time in minutes for the route.
         * Required: false
         * Array: false
         */
        this.totalTravelTimeMinutes = undefined;
        /**
         * The number of missed appointments for the route.
         * Required: false
         * Array: false
         */
        this.missedAppts = undefined;
        /**
         * The stops per hour.
         * Required: false
         * Array: false
         */
        this.stopsPerHour = undefined;
        /**
         * The number of miles per stop.
         * Required: false
         * Array: false
         */
        this.milesPerStop = undefined;
        /**
         * The calculated Pallet Space Equivalent (PSE) of the route.
         * Required: false
         * Array: false
         */
        this.calculatedPse = undefined;
        /**
         * The earliest ETA of shipments assigned to the route.
         * Required: false
         * Array: false
         */
        this.earliestEta = undefined;
        /**
         * The latest ETA of shipments assigned to the route.
         * Required: false
         * Array: false
         */
        this.latestEta = undefined;
        /**
         * The reason code for the route to be UNRTD or any special bucket.
         * Required: false
         * Array: false
         */
        this.reasonCd = undefined;
        /**
         * The shipments associated with the route.
         * Required: false
         * Array: true
         */
        this.optimizerResultShipments = undefined;
        /**
         * The total number of stops for pickups assigned to the route.
         * Required: false
         * Array: false
         */
        this.pickupStopCount = undefined;
        /**
         * The total weight of pickups assigned to the route.
         * Required: false
         * Array: false
         */
        this.totalPickupWeightLbs = undefined;
        /**
         * The total number of motor moves for pickups assigned to the route.
         * Required: false
         * Array: false
         */
        this.totalPickupMotorMovesCount = undefined;
        /**
         * The total number of motor moves for deliveries assigned to the route.
         * Required: false
         * Array: false
         */
        this.totalDeliveryMotorMovesCount = undefined;
        /**
         * The calculated time when the route will end at the terminal after completing all assigned activities.
         * Required: false
         * Array: false
         */
        this.routeEndTime = undefined;
    }
}
/**
 * The shipment associated with the optimizer result.
 */
class OptimizerResultShipment {
    constructor() {
        /**
         * The business identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The unique identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The stop sequence number.
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         * The customer code. Example: 136480.
         * Required: false
         * Array: false
         */
        this.customerId = undefined;
        /**
         * The plan arrival time.
         * Required: false
         * Array: false
         */
        this.planArrivalTime = undefined;
        /**
         * The plan departure time.
         * Required: false
         * Array: false
         */
        this.planDepartureTime = undefined;
        /**
         * The latitude of the customer's location.
         * Required: false
         * Array: false
         */
        this.latitude = undefined;
        /**
         * The longitude of the customer's location.
         * Required: false
         * Array: false
         */
        this.longitude = undefined;
        /**
         * The customer's name.
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         * The customer's address.
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * The customer's city.
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         * The customer's state.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * The customer's zip code.
         * Required: false
         * Array: false
         */
        this.zipCd = undefined;
        /**
         * The calculated Pallet Space Equivalent (PSE) of the shipment.
         * Required: false
         * Array: false
         */
        this.calculatedPse = undefined;
        /**
         * The reason code that indicates why this shipment is an unrouted shipment.
         * Required: false
         * Array: false
         */
        this.reasonCd = undefined;
        /**
         * The activity to be performed at the stop.
         * Required: false
         * Array: false
         */
        this.stopTypeCd = undefined;
        /**
         * The unique identifier of the pickup request which is assigned to the stop.
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
    }
}
/**
 * Header information about the route details with its stops in an optimizer response.
 */
class OptimizerRouteStop {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stops = undefined;
    }
}
/**
 * Shipment details to be used in the optimizer.
 */
class OptimizerShipment {
    constructor() {
        /**
         * System identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Business identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Host terminal SIC of the shipment for which pickup/delivery is taking place.
         * Required: false
         * Array: false
         */
        this.hostSicCd = undefined;
        /**
         * SIC that is scheduled to deliver the Shipment.
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         * Date that the shipment should be delivered based on standard transit days.
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * ETA of the PRO to host destination SIC in case the PRO is inbound
         * Required: false
         * Array: false
         */
        this.proEtaDateTime = undefined;
        /**
         * Each service center is divided into multiple geographic areas for delivery. This identifies the delivery area of destination service center in which this shipment's consignee is located.
         * Required: false
         * Array: false
         */
        this.geoAreaCd = undefined;
        /**
         * Total pieces from all commodities in the shipment.
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * Number of handling units
         * Required: false
         * Array: false
         */
        this.totalHandlingUnitCount = undefined;
        /**
         * Number of motorized pieces
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Total Weight of the shipment
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The volume of the freight; derived from the dimensions captured by driver, or entered by a CSR or dock worker for an SLNC (Shipper
         * 	Load and Count), or from one of a variety of profiles. For details, see the description of CFT_PRFL_MTHD_CD.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * Total loose pieces from all commodities in the shipment.
         * Count of non Motor Move pieces associated with a shipment. This count plus Motor Moves defines the total number of pieces moving
         * 	within the XPO system for Operations productivity.  This does not necessarily balance with the total pieces or commodity pieces entered
         * during bill entry.
         * Required: false
         * Array: false
         */
        this.loosePiecesCount = undefined;
        /**
         * Total Number of Pallets in the Shipment
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         * Estimated Dwell Time at the customer for the shipment
         * Required: false
         * Array: false
         */
        this.estimatedDwellTimeMinutes = undefined;
        /**
         * Identifies that the shipment is included when sending a list of shipments to Optimizer.
         * Required: false
         * Array: false
         */
        this.includeInd = undefined;
        /**
         * Identifies that the shipment is locked to a route when sending an incremental list of shipments to Optimizer.
         * Required: false
         * Array: false
         */
        this.lockInd = undefined;
        /**
         * Identifies that the shipment is locked to a position on a route when sending an incremental list of shipments to Optimizer.
         * Required: false
         * Array: false
         */
        this.lockStopSeqInd = undefined;
        /**
         * Identifies the stop sequence number that the shipment is locked on a route when sending an incremental list of shipments to Optimizer.
         * Required: false
         * Array: false
         */
        this.lockStopSeqNbr = undefined;
        /**
         * Identifies the trailer that the shipment is on.
         * Required: false
         * Array: false
         */
        this.trailerId = undefined;
        /**
         * Identifies the equipment that is a trailer which contains the shipment.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The unique identifier of the route to which shipments will be added.
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServiceSummaries = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stopWindows = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregatedStopWindow = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee = undefined;
    }
}
/**
 * Optimizer shipment summary details.
 */
class OptimizerShipmentSummary {
    constructor() {
        /**
         * Summary data for optimizer shipment
         * Required: false
         * Array: false
         */
        this.stopCount = undefined;
        /**
         * Summary data for optimizer shipment
         * Required: false
         * Array: false
         */
        this.billCount = undefined;
        /**
         * Total weight in lbs
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Total motor moves
         * Required: false
         * Array: false
         */
        this.motorMoveCount = undefined;
        /**
         * Total pallet count
         * Required: false
         * Array: false
         */
        this.palletCount = undefined;
        /**
         * Total count of liftgate indicators
         * Required: false
         * Array: false
         */
        this.liftGateIndCount = undefined;
        /**
         * Total count of guaranteed indicators
         * Required: false
         * Array: false
         */
        this.guaranteedIndCount = undefined;
        /**
         * Total count of food indicators
         * Required: false
         * Array: false
         */
        this.foodIndCount = undefined;
        /**
         * Total count of g12 indicators
         * Required: false
         * Array: false
         */
        this.guaranteedByNoonIndCount = undefined;
        /**
         * Total count of residential indicators
         * Required: false
         * Array: false
         */
        this.residentialIndCount = undefined;
        /**
         * Total count of hazmat indicators
         * Required: false
         * Array: false
         */
        this.hazmatIndCount = undefined;
        /**
         * Total count of pup only indicators
         * Required: false
         * Array: false
         */
        this.pupOnlyIndCount = undefined;
        /**
         * Total count of appointment indicators
         * Required: false
         * Array: false
         */
        this.apptIndCount = undefined;
        /**
         * Total count of tdc indicators
         * Required: false
         * Array: false
         */
        this.tdcIndCount = undefined;
    }
}
/**
 * Header information about the route stop details in an optimizer response.
 */
class OptimizerStop {
    constructor() {
        /**
         * Contains the details of a stop for a route recommended by the optimizer for a particular run.
         * Required: false
         * Array: false
         */
        this.stop = undefined;
        /**
         * Contains the list of shipments (activities) at a stop for a route recommended by the optimizer for a particular run.
         * Required: false
         * Array: true
         */
        this.activities = undefined;
    }
}
/**
 * Optimizer summary details for shipment, driver, and equipment.
 */
class OptimizerSummary {
    constructor() {
        /**
         * Summary details for optimizer shipment.
         * Required: false
         * Array: false
         */
        this.optimizerShipmentSummary = undefined;
        /**
         * Summary details for optimizer driver.
         * Required: false
         * Array: false
         */
        this.optimizerDriverSummary = undefined;
        /**
         * Summary details for optimizer equipment.
         * Required: false
         * Array: false
         */
        this.optimizerEquipmentSummary = undefined;
    }
}
/**
 * The shipment that is part of a pickup activity.
 */
class PickupLineItem {
    constructor() {
        /**
         * The action to take place for the pickup line item.
         * Required: false
         * Array: false
         */
        this.pickupActionCd = undefined;
        /**
         * The identifier of the pickup request this line item will move to.
         * Required: false
         * Array: false
         */
        this.moveToPickupReqId = undefined;
        /**
         * Indicates whether the dimension capture was opt out.
         * Required: false
         * Array: false
         */
        this.optOutOfDimensionInd = undefined;
        /**
         * The shipment detail on the line item.
         * Required: false
         * Array: false
         */
        this.pkrShipment = undefined;
        /**
         * The list of hazardous material information on the line item.
         * Required: false
         * Array: true
         */
        this.pkrShipmentHazMats = undefined;
        /**
         * The list of handling units on the line item.
         * Required: false
         * Array: true
         */
        this.pkrShipmentHandlingUnits = undefined;
        /**
         * The list of dimensions of the line item.
         * Required: false
         * Array: true
         */
        this.pkrShipmentDimensions = undefined;
        /**
         * Indicates whether the shipment involved in the activity is tracked using handling units.
         * Required: false
         * Array: false
         */
        this.trackWithHandlingUnitsInd = undefined;
        /**
         * Indicates whether the shipment is exempted to be tracked via piece level.
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionInd = undefined;
        /**
         * An explanation of why the shipment is exempted from piece level tracking.
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionReason = undefined;
        /**
         * The special sevices that are required for each line item.
         * Required: false
         * Array: true
         */
        this.shipmentSpecialServices = undefined;
        /**
         * Indicates whether the shipment is poorly packaged.
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
    }
}
/**
 * The pickup request and its tripInstId.
 */
class PickupTrip {
    constructor() {
        /**
         * The identifier for the pickup request.
         * Required: false
         * Array: false
         */
        this.pickupInstId = undefined;
        /**
         * The identifier of the trip associated with the pickup.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
    }
}
/**
 * The dispatch group and its associated dispatch group region.
 */
class PnDDispatchGroupRegion {
    constructor() {
        /**
         * The dispatch group.
         * Required: false
         * Array: false
         */
        this.dispatchGroup = undefined;
        /**
         * The dispatch group region associated with the dispatch group.
         * Required: false
         * Array: false
         */
        this.dispatchGroupRegion = undefined;
    }
}
/**
 * Equipment and TrailerLoad data to be used in the optimizer
 */
class PnDEquipment {
    constructor() {
        /**
         * The unique identifier for equipment.
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * The prefix component for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * The suffix component for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         * The status of the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentStatusCd = undefined;
        /**
         * The date time of the status update.
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         * The current event.
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         * The date time of the current event.
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         * The ID of the device that performed the last update.
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         * The type detail for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentTypeDetailCd = undefined;
        /**
         * The type of equipment.
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         * The current SIC of the equipment.
         * Required: false
         * Array: false
         */
        this.currentSic = undefined;
        /**
         * Remarks for the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentRemarks = undefined;
        /**
         * The manufacture year of the equipment.
         * Required: false
         * Array: false
         */
        this.mfrYr = undefined;
        /**
         * The length of the trailer in feet.
         * Required: false
         * Array: false
         */
        this.trailerLengthFeet = undefined;
        /**
         * Indicates a liftgate.
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         * The status of the trailer.
         * Required: false
         * Array: false
         */
        this.trailerStatusCd = undefined;
        /**
         * The weight capacity, in pounds, of the trailer length.
         * Required: false
         * Array: false
         */
        this.weightCapacityLbs = undefined;
        /**
         * The pallet capacity (number of pallets that can fit on the equipment) of the trailer length.
         * Required: false
         * Array: false
         */
        this.palletCapacityNbr = undefined;
        /**
         * HU (Handling Unit) capacity of the trailer length
         * Required: false
         * Array: false
         */
        this.handlingUnitCapacityNbr = undefined;
        /**
         * PND Weight capacity, in pounds, of the trailer
         * Required: false
         * Array: false
         */
        this.pndWeightCapacityLbs = undefined;
        /**
         * PND Pallet capacity (number of pallets that can fit on the equipment) of the trailer
         * Required: false
         * Array: false
         */
        this.pndPalletCapacityNbr = undefined;
        /**
         * PND HU (Handling Unit) capacity of the trailer
         * Required: false
         * Array: false
         */
        this.pndHandlingUnitCapacityNbr = undefined;
    }
}
/**
 * The information required to create or update a trip.
 */
class PnDTrip {
    constructor() {
        /**
         * The trip to be created or updated.
         * Required: true
         * Array: false
         */
        this.trip = undefined;
        /**
         * The Origin/Destination trip nodes.
         * Required: false
         * Array: true
         */
        this.tripNodes = undefined;
        /**
         * Driver to assign to a trip.
         * Required: false
         * Array: false
         */
        this.tripDriver = undefined;
        /**
         * Any note related to the trip referencing a remark text.
         * Required: false
         * Array: false
         */
        this.tripNote = undefined;
        /**
         * Dimensions Check Log for equipment.
         * Required: false
         * Array: false
         */
        this.dimensionsCheckLog = undefined;
        /**
         * The trailer equipment indicated as tractor.
         * Required: false
         * Array: false
         */
        this.tractor = undefined;
        /**
         * The trailer equipment indicated as dollies.
         * Required: false
         * Array: true
         */
        this.dollies = undefined;
        /**
         * The route details with trailers assigned to route.
         * Required: false
         * Array: true
         */
        this.routeDetails = undefined;
        /**
         * The remaining feet updates for equipment.
         * Required: false
         * Array: true
         */
        this.remainingFeetUpdates = undefined;
    }
}
/**
 * The payload type for the City print documents channel.
 */
class PrintDocumentPayload {
    constructor() {
        /**
         * The list of print requests.
         * Required: false
         * Array: true
         */
        this.printRequests = undefined;
    }
}
/**
 * Contains attributes required for the print order.
 */
class PrintRequest {
    constructor() {
        /**
         * The identifier for the printer where documents are printed.
         * Required: true
         * Array: false
         */
        this.printerId = undefined;
        /**
         * The identifier of the printer tray.
         * Required: false
         * Array: false
         */
        this.paperSource = undefined;
        /**
         * Indicates whether to add a page to the PDF that lists the PROs with the reasons as to why they could not be printed.
         * Required: false
         * Array: false
         */
        this.includeWarningsPageInd = undefined;
        /**
         * The parameters of the delivery document print.
         * Required: false
         * Array: false
         */
        this.deliverySetPrint = undefined;
        /**
         * The language preference of the delivery document print.
         * Required: false
         * Array: false
         */
        this.language = undefined;
        /**
         * The parameters of the manifest document print.
         * Required: false
         * Array: false
         */
        this.manifestPrint = undefined;
        /**
         * The parameters of the dsr checkin document print.
         * Required: false
         * Array: false
         */
        this.dsrCheckinPrint = undefined;
    }
}
/**
 * The stops that match the criteria for proximity and the reasons why they qualified.
 */
class ProximalStop {
    constructor() {
        /**
         * The delivery stop that matched the proximity parameter.
         * Required: false
         * Array: false
         */
        this.unassignedStop = undefined;
        /**
         * The description of why the delivery stop was selected as a match.
         * Required: false
         * Array: false
         */
        this.matchReason = undefined;
    }
}
/**
 * The remaining feet updates for equipment.
 */
class RemainingFeetUpdate {
    constructor() {
        /**
         * The equipment prefix and suffix concatenated.
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * Amount lineal feet available on trailer.
         * Required: false
         * Array: false
         */
        this.luomFtNbr = undefined;
    }
}
/**
 * Basic header information about an activity for a trip node but only in the context of trip metrics
 */
class RouteBalancingActivity {
    constructor() {
        /**
         * The ETA for the destination SIC.
         * Required: false
         * Array: false
         */
        this.destinationSicEta = undefined;
        /**
         * The estimated delivery date.
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * The code that categorizes the Activity.  Examples: DL, PU, HE, etc.
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * Total weight of Shipments at this activity
         * Required: false
         * Array: false
         */
        this.totalWeight = undefined;
        /**
         * Total motor moves of this activity
         * Required: false
         * Array: false
         */
        this.totalMotorMoves = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServices = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalBillCount = undefined;
    }
}
/**
 * Basic header information about a route but only in the context of trip metrics
 */
class RouteBalancingRoute {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Indicates whether the route has been released for loading.
         * Required: false
         * Array: false
         */
        this.xdockReleaseInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd = undefined;
        /**
         * Delivery route departure time goal for this route
         * 						to leave the service center
         * Required: false
         * Array: false
         */
        this.departTime = undefined;
        /**
         * The identifier of the first pin trip node.
         * Required: false
         * Array: false
         */
        this.firstStopNodeSequenceNumber = undefined;
        /**
         * The identifier of the last pin trip node.
         * Required: false
         * Array: false
         */
        this.lastStopNodeSequenceNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeBalancingTypeCd = undefined;
        /**
         * Identifies the method to the Auto-Sequencer of how to optimize the sequencing on the route.
         * Required: false
         * Array: false
         */
        this.routeAutoSequenceTypeCd = undefined;
        /**
         * Indicates whether the route should be deleted.
         * Required: false
         * Array: false
         */
        this.deleteRouteInd = undefined;
        /**
         * The list of stops.
         * Required: false
         * Array: true
         */
        this.stops = undefined;
        /**
         * The point on the route selected by the user to be auto-sequenced by a specific method (routeAutoSequenceTypeCd).
         * Required: false
         * Array: false
         */
        this.selectedPositioningPoint = undefined;
        /**
         * The timestamp route was last updated.
         * Required: false
         * Array: false
         */
        this.lastUpdateTimestamp = undefined;
        /**
         * A list of conflicts that relate to the trip model or its nodes.
         * Required: false
         * Array: true
         */
        this.conflicts = undefined;
        /**
         * The unique identifier that represents the area that will contain the new route.
         * Required: true
         * Array: false
         */
        this.areaInstId = undefined;
    }
}
/**
 * Basic header information about a stop including activities and trip node information but only in the context of route balancing
 */
class RouteBalancingStop {
    constructor() {
        /**
         * The trip node sequence number
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNumber = undefined;
        /**
         * The unique identifier for the trip node.
         * Required: false
         * Array: false
         */
        this.tripNodeInstId = undefined;
        /**
         * The sequence number assigned to the stop.
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         * The customer name representing the tripNodeInstId.
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         * Indicates that a stop window will potentially be lost for a particular stop.
         * Required: false
         * Array: false
         */
        this.potentialMissedStopWindowsInd = undefined;
        /**
         * Indicates whether or not to include stop window information in auto-sequencing calculation for a particular stop.
         * Required: false
         * Array: false
         */
        this.ignoreStopWindowInd = undefined;
        /**
         * The type for the node.
         * Required: false
         * Array: false
         */
        this.nodeTypeCd = undefined;
        /**
         * The type for the trip node
         * Required: false
         * Array: false
         */
        this.tripNodeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedArriveDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedArriveDateTimeLocal = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTimeLocal = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.planDistanceFromPreviousNode = undefined;
        /**
         * The wait time (minutes) that the driver is idle between stops.
         * Required: false
         * Array: false
         */
        this.idleWaitTime = undefined;
        /**
         * List of activities for a trip node
         * Required: false
         * Array: true
         */
        this.activities = undefined;
        /**
         * The geo coordinates of this Stop.
         * Required: false
         * Array: false
         */
        this.geoCoordinate = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stopWindows = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fromRoute = undefined;
        /**
         * The timestamp stop was last updated.
         * Required: false
         * Array: false
         */
        this.lastUpdateTimestamp = undefined;
        /**
         * This represents where on the trailer the frieght is loaded. Right, Left or Tail.
         * Required: false
         * Array: false
         */
        this.positionOnTrailer = undefined;
        /**
         * List of stops that are to be merged into the trip.
         * Required: false
         * Array: true
         */
        this.mergedStops = undefined;
        /**
         * A Stop to reference when mergeStops action is canceled
         * Required: false
         * Array: false
         */
        this.originalStop = undefined;
    }
}
/**
 * Basic header information about a trip but only in the context of Route Balancing
 */
class RouteBalancingTrip {
    constructor() {
        /**
         * Unique instance ID for a trip
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * Trip date
         * Required: false
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * Status of the trip
         * Required: false
         * Array: false
         */
        this.tripStatusCd = undefined;
        /**
         * A timestamp of when the trip starts
         * Required: false
         * Array: false
         */
        this.estimatedStartDateTime = undefined;
        /**
         * Estimated Empty Time. The estimated time that a DSR will have completed his delivery stops. His trailers will be empty and available to complete pickup stops
         * Required: false
         * Array: false
         */
        this.estimatedEmptyDateTime = undefined;
        /**
         * The wait time (minutes) that the driver is idle between stops.
         * Required: false
         * Array: false
         */
        this.idleWaitTime = undefined;
        /**
         * The list of routes.
         * Required: false
         * Array: true
         */
        this.routes = undefined;
        /**
         * The list of stops that aren't assigned to any route.
         * Required: false
         * Array: true
         */
        this.tripStops = undefined;
        /**
         * The timestamp trip was last updated.
         * Required: false
         * Array: false
         */
        this.lastUpdateTimestamp = undefined;
    }
}
class RouteDetail {
    constructor() {
        /**
         * Indicates whether the route details should be modified.
         * Required: false
         * Array: false
         */
        this.modifyInd = undefined;
        /**
         * The area instance id is used to uniquely identify the location area assignment.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * The trailer indicated as assigned to route.
         * Required: false
         * Array: false
         */
        this.trailer = undefined;
    }
}
/**
 *
 */
class RouteEquipment {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.tripEquipment = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.route = undefined;
    }
}
/**
 * The initial location of a Stop prior to any Route Balancing
 */
class RouteIdentifier {
    constructor() {
        /**
         * The instance id of of the trip that this stop was originally associated.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The instance id of the route this stop was originally associated with
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * The instance id of the route this stop was originally associated with
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
    }
}
/**
 *
 */
class RouteSequenceAdjustment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSequenceNbr = undefined;
        /**
         * A list of trip node sequence numbers.
         * Required: false
         * Array: true
         */
        this.positionOnTrailerCodeList = undefined;
    }
}
/**
 * Information related to a stop in the driver's route.  The last stop is either the previous or current stop based on its status.
 */
class RouteStop {
    constructor() {
        /**
         * The identifier for the last visited stop on the trip.
         * Required: false
         * Array: false
         */
        this.lastStopInstId = undefined;
        /**
         * The name associated with the last visited stop.
         * Required: false
         * Array: false
         */
        this.lastStopName = undefined;
        /**
         * The departure time of the last visited stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopDateTime = undefined;
        /**
         * The type of the last visited stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopTypeCd = undefined;
        /**
         * The status of the last visited stop of the trip.
         * Required: false
         * Array: false
         */
        this.lastStopStatus = undefined;
        /**
         * The identifier for the next stop of the trip.
         * Required: false
         * Array: false
         */
        this.nextStopInstId = undefined;
        /**
         * The name associated with the next stop.
         * Required: false
         * Array: false
         */
        this.nextStopName = undefined;
        /**
         * The unique identifier of the route.
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
    }
}
/**
 * Contains the route information with a list of its stops
 */
class RouteSummary {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stops = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stopWindowSummary = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServicesSummary = undefined;
    }
}
/**
 * The payload type for the rule based routing channel.
 */
class RuleBasedRoutingPayload {
    constructor() {
        /**
         * The unique identifier of shipment.
         * Required: true
         * Array: false
         */
        this.shipmentInstId = undefined;
    }
}
/**
 * The shipment cube information which is used to calculate cube.
 */
class ShipmentCube {
    constructor() {
        /**
         * The total pieces of the shipment.
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * The total weight in pounds of the shipment.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The destination postal code of the shipment.
         * Required: false
         * Array: false
         */
        this.destinationZip = undefined;
        /**
         * The list of shipment dimensions which are used to calculate cube.
         * Required: false
         * Array: true
         */
        this.shipmentDimensions = undefined;
    }
}
/**
 * The shipment details which are used for the evaluation of rule based routing.
 */
class ShipmentRuleEvaluation {
    constructor() {
        /**
         * The codes that determine the types of accessorial services that are requested or performed on a shipment.
         * Required: true
         * Array: true
         */
        this.accessorialCds = undefined;
        /**
         * The codes that are associated with the types of shipments.
         * Required: true
         * Array: true
         */
        this.billClassCds = undefined;
        /**
         * The unique identifier of the customer.
         * Required: false
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * The business identifier of the customer.
         * Required: false
         * Array: false
         */
        this.customerAcctMadCd = undefined;
        /**
         * The code that is associated with the type of customer location.
         * Required: false
         * Array: false
         */
        this.customerProfileLocationTypeCd = undefined;
        /**
         * Indicates whether the customer requires their freight to be trapped for a full trailer.
         * Required: false
         * Array: false
         */
        this.trapRequiredInd = undefined;
        /**
         * Indicates whether the customer has a forklift.
         * Required: false
         * Array: false
         */
        this.forkliftInd = undefined;
        /**
         * Indicates wheter a liftgate is required at a customer site.
         * Required: false
         * Array: false
         */
        this.liftgateRequiredInd = undefined;
        /**
         * Indicates whether the customer requires an appointment for shipment deliveries.
         * Required: false
         * Array: false
         */
        this.appointmentRequiredInd = undefined;
        /**
         * Indicates whether the customer requires notification upon arrival of driver.
         * Required: false
         * Array: false
         */
        this.notifyOnArrivalInd = undefined;
        /**
         * This identifies the driver collect information. It has 4 values N (no collect requirements), F (Freight charges), C (COD) and B (Both Freight charges and COD must be collected)
         * Required: false
         * Array: false
         */
        this.driverCollectCd = undefined;
    }
}
/**
 * A stop activity detail object that contain specific information depending on the activity code (DL/SL/PU/HL/HE/SE/HK/DP).
 */
class SplitTripStopActivity {
    constructor() {
        /**
         * The node activity sequence number that identifies the stop activity as part of a stop.
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         * The unique identifier for the shipment involved in the activity.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The unique identifier for the equipment used for the activity.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
    }
}
/**
 * The details for spotted or dropped equipment.
 */
class SpotOrDropEquipment {
    constructor() {
        /**
         * The equipment details.
         * Required: false
         * Array: false
         */
        this.equipment = undefined;
        /**
         * The hook request details.
         * Required: false
         * Array: false
         */
        this.hookRequest = undefined;
        /**
         * The trip which spotted or dropped the equipment.
         * Required: false
         * Array: false
         */
        this.spotDropTrip = undefined;
        /**
         * The trip which hooked the equipment.
         * Required: false
         * Array: false
         */
        this.hookTrip = undefined;
        /**
         * The consignee or drop location details.
         * Required: false
         * Array: false
         */
        this.equipmentLocation = undefined;
    }
}
/**
 * The payload type for the City OnDemand driver location channel.
 */
class StartDriverLocationPayload {
    constructor() {
        /**
         * The unique identifier for the employee which is used to get the driver location.
         * Required: true
         * Array: false
         */
        this.employeeId = undefined;
    }
}
/**
 * The payload type for the City PnD Optimizer channel.
 */
class StartPnDOptimizerPayload {
    constructor() {
        /**
         * The generated number for the optimizer run.
         * Required: true
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * The vendor-specific optimization request.
         * Required: false
         * Array: false
         */
        this.request = undefined;
    }
}
class Stop {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customer = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.activities = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stopWindowSummary = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServicesSummary = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.stopType = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The code which determines whether No, Partial, or All Prints for FBDS documents will be printed for the route.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCountCd = undefined;
    }
}
class StopProximityCheck {
    constructor() {
        /**
         * Details about the consignee.
         * Required: false
         * Array: false
         */
        this.customerLocation = undefined;
        /**
         * The list of shipments assigned to the route for the consignee.
         * Required: false
         * Array: true
         */
        this.shipmentInstIds = undefined;
        /**
         * The proximity parameter used to apply the geo match.
         * Required: false
         * Array: false
         */
        this.proximityParameter = undefined;
    }
}
class StopSequenceModelConflict {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.conflictCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.description = undefined;
    }
}
/**
 * Information about the tripModel and its nodes.
 */
class StopSequenceModelData {
    constructor() {
        /**
         * Identifies the method to the Auto-Sequencer of how to optimize the sequencing on the route.
         * Required: false
         * Array: false
         */
        this.routeAutoSequenceTypeCd = undefined;
        /**
         * The SIC code of the service center location.
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The geo coordinates of the service center location.
         * Required: false
         * Array: false
         */
        this.terminalGeoCoordinates = undefined;
        /**
         * The time at which the wave departs.
         * Required: false
         * Array: false
         */
        this.departureTime = undefined;
        /**
         * The time at which the wave arrives.
         * Required: false
         * Array: false
         */
        this.arrivalTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trip = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.stops = undefined;
        /**
         * A matrix of all distances between all the stops
         * Required: false
         * Array: false
         */
        this.distanceMatrix = undefined;
        /**
         * A list of conflicts that relate to the trip model or its nodes.
         * Required: false
         * Array: true
         */
        this.conflicts = undefined;
    }
}
/**
 *
 */
class StopSequenceModelPickupPoint {
    constructor() {
        /**
         * The pickup cluster.
         * Required: false
         * Array: false
         */
        this.positioningPoint = undefined;
        /**
         * The distance between pickup cluster and stop in meters.
         * Required: false
         * Array: false
         */
        this.distanceFromStop = undefined;
        /**
         * The distance between pickup cluster and service center in meters.
         * Required: false
         * Array: false
         */
        this.distanceFromSic = undefined;
        /**
         * The score of proximity from the stop to a pickup cluster.  The value is between 0 and 1.
         * Required: false
         * Array: false
         */
        this.score = undefined;
    }
}
/**
 *
 */
class StopSequenceModelStop {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeInstId = undefined;
        /**
         * The customer name representing the nodeInstId.
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalMotorMoves = undefined;
        /**
         * Dwell time at this stop
         * Required: false
         * Array: false
         */
        this.dwellTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.geoCoordinates = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopWindow = undefined;
        /**
         * The pickup cluster for Pinned and PickupPoint requests.
         * Required: false
         * Array: false
         */
        this.pickupPoint = undefined;
    }
}
/**
 *
 */
class StopSequenceModelTrip {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalMotorMoves = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         * The identifier of the first Trip Node that was a Stop on this Trip.
         * Required: false
         * Array: false
         */
        this.firstStopNodeSequenceNbr = undefined;
        /**
         * The identifier of the last Trip Node that was a Stop on this Trip.
         * Required: false
         * Array: false
         */
        this.lastStopNodeSequenceNbr = undefined;
    }
}
/**
 * Information about the trip and its nodes.
 */
class StopSequenceTrip {
    constructor() {
        /**
         * Instance ID of trip if it exists
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         * The SIC code of the service center location.
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The geo coordinates of the service center location.
         * Required: false
         * Array: false
         */
        this.terminalGeoCoordinates = undefined;
        /**
         * The identifier of the first Trip Node that was a Stop on this Trip.
         * Required: false
         * Array: false
         */
        this.firstStopNodeSequenceNbr = undefined;
        /**
         * The identifier of the last Trip Node that was a Stop on this Trip.
         * Required: false
         * Array: false
         */
        this.lastStopNodeSequenceNbr = undefined;
        /**
         * The identifier of the first unassigned stop. Specify only one of firstStopNodeSequenceNbr or firstUnassignedStopNodeSequenceNbr
         * Required: false
         * Array: false
         */
        this.firstUnassignedStopNodeSequenceNbr = undefined;
        /**
         * The identifier of the last unassigned stop.
         * Required: false
         * Array: false
         */
        this.lastUnassignedStopNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedEmptyDateTime = undefined;
        /**
         * The assigned stops to optimize the order.
         * Required: false
         * Array: true
         */
        this.stops = undefined;
        /**
         * Information about the unassigned shipments.
         * Required: false
         * Array: true
         */
        this.unassignedStops = undefined;
        /**
         * A list of conflicts that relate to the trip model or its nodes.
         * Required: false
         * Array: true
         */
        this.conflicts = undefined;
    }
}
/**
 * A derived entity that contains geo area information and suggested suffix for the sic/area.
 */
class SuggestedRouteName {
    constructor() {
        /**
         * Geo Area information for a given sic
         * Required: false
         * Array: false
         */
        this.geoArea = undefined;
        /**
         * The next suggested route prefix for the sic/area
         * Required: false
         * Array: false
         */
        this.suggestedPrefix = undefined;
        /**
         * The next suggested route suffix for the sic/area
         * Required: false
         * Array: false
         */
        this.suggestedSuffix = undefined;
        /**
         * The category code
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         * Is the route associated with the parent or a satellite
         * Required: false
         * Array: false
         */
        this.isSatelliteIind = undefined;
    }
}
class Trailer {
    constructor() {
        /**
         * Trailer equipment assocaited to trip.
         * Required: false
         * Array: false
         */
        this.tripEquipment = undefined;
        /**
         * Trailer load information for the equipment
         * Required: false
         * Array: false
         */
        this.trailerLoad = undefined;
        /**
         * The code which is used to help identify the characteristics of a piece of equipment.
         * Required: false
         * Array: false
         */
        this.equipmentTypeDetailCd = undefined;
    }
}
/**
 * Shipments contained in a trailer.
 */
class TrailerContent {
    constructor() {
        /**
         * The unique identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The business identifier for the shipment.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * The end destination of the shipment.
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         * The total weight.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The unique identifier of the pickup request that is included in this shipment, if known.
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         * The sequence number that identifies the shipment as a line item on a pickup request.
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         * Indicates whether the shipment has hazardous materials.
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         * Indicates whether the shipment contains food, poision or none.
         * Required: false
         * Array: false
         */
        this.foodPoisonCd = undefined;
        /**
         * Hazardous material information.
         * Required: false
         * Array: true
         */
        this.hazmat = undefined;
    }
}
/**
 *
 */
class TripDetail {
    constructor() {
        /**
         * Information about the trip.
         * Required: false
         * Array: false
         */
        this.trip = undefined;
        /**
         * Divides up the trip so multiple dispatchers can share the trip.
         * Required: false
         * Array: false
         */
        this.dispatchGroup = undefined;
        /**
         * Information about the employee driving the trip.
         * Required: false
         * Array: false
         */
        this.tripDriver = undefined;
        /**
         * Equipment information for tractors.
         * Required: false
         * Array: false
         */
        this.tractorTripEquipment = undefined;
        /**
         * Equipment information for dollies.
         * Required: false
         * Array: true
         */
        this.dolly = undefined;
        /**
         * Details about the routes.
         * Required: false
         * Array: true
         */
        this.route = undefined;
        /**
         * Represents a stop at a location for a trip in the form of a list.
         * Required: false
         * Array: true
         */
        this.tripStops = undefined;
        /**
         * Represents the metrics for a trip in the form of a list.
         * Required: false
         * Array: true
         */
        this.metrics = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServices = undefined;
        /**
         * Summarized trailer content that is part of each route in a trip.
         * Required: false
         * Array: true
         */
        this.tripTrailerContentSummary = undefined;
        /**
         * The country code of the trip terminal location.
         * Required: false
         * Array: false
         */
        this.tripCountryCd = undefined;
        /**
         * The note associated with the trip.
         * Required: false
         * Array: false
         */
        this.tripNote = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The details about the partner carrier.
         * Required: false
         * Array: false
         */
        this.carrier = undefined;
        /**
         * A derived tender status based on combinations of the tender detail status of each tendered shipment on the trip.
         * Required: false
         * Array: false
         */
        this.carrierTenderGroupStatusCd = undefined;
        /**
         * The list of messages which each describe a failure or rejection in the tender process.  They are formatted for display in a user interface.
         * Required: false
         * Array: true
         */
        this.tenderMessages = undefined;
    }
}
/**
 * Estimated sequence and timings of arrival and departure at all the stops of a trip.
 */
class TripExpectation {
    constructor() {
        /**
         * The employee ID of the driver.
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         * The unique identifier for the trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The terminal of the trip.  Either the host or the zone.
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * The estimated or actual start time of the trip based on the trip status.
         * Required: false
         * Array: false
         */
        this.estimatedStartDateTime = undefined;
        /**
         * The estimated time when all the deliveries are complete and the trip is ready for pickups.
         * Required: false
         * Array: false
         */
        this.estimatedEmptyDateTime = undefined;
        /**
         * Sequence of stops of a trip.
         * Required: false
         * Array: true
         */
        this.nodes = undefined;
    }
}
/**
 * Estimated timings of arrival and departure at a stop of a trip.
 */
class TripExpectationNode {
    constructor() {
        /**
         * The node sequence number that identifies this stop as part of the trip.
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * The sequence number assigned to the stop.
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         * The estimated time that the driver will arrive at this stop.
         * Required: false
         * Array: false
         */
        this.estimatedArriveDateTime = undefined;
        /**
         * The estimated time that the driver will leave from this stop.
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         * The dwell time at this stop in minutes.
         * Required: false
         * Array: false
         */
        this.dwellTime = undefined;
        /**
         * The travel time from the the previous stop in minutes.
         * Required: false
         * Array: false
         */
        this.travelTimeFromPreviousStop = undefined;
        /**
         * The travel distance from the the previous stop in miles.
         * Required: false
         * Array: false
         */
        this.travelDistanceFromPreviousStop = undefined;
    }
}
/**
 * The identifiers for an individual trip and its stops.
 */
class TripId {
    constructor() {
        /**
         * The unique identifier of the trip.
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * The identifiers for an individual stop and its activities.
         * Required: false
         * Array: true
         */
        this.tripNodeIds = undefined;
    }
}
/**
 * The identifier for an individual activity.
 */
class TripNodeActivityId {
    constructor() {
        /**
         * The unique identifier for an activity.
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySeqNbr = undefined;
    }
}
/**
 * The identifiers for an individual stop and its activities.
 */
class TripNodeId {
    constructor() {
        /**
         * The unique identifier of the stop.
         * Required: false
         * Array: false
         */
        this.tripNodeSeqNbr = undefined;
        /**
         * The unique identifiers for the associated activities.
         * Required: false
         * Array: true
         */
        this.tripNodeActivities = undefined;
    }
}
/**
 * Represents a stop at a location for a trip.
 */
class TripStop {
    constructor() {
        /**
         * Represents an individual stop.
         * Required: false
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * The route associated with the stop.
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * Information about the consignee.
         * Required: false
         * Array: false
         */
        this.cisInterfaceAcct = undefined;
        /**
         * Information about the drop location.
         * Required: false
         * Array: false
         */
        this.dropLocation = undefined;
        /**
         * Activities for the trip stop.
         * Required: false
         * Array: true
         */
        this.activityTypes = undefined;
        /**
         * A list of times that freight can be delivered to the customer.
         * Required: false
         * Array: true
         */
        this.stopWindows = undefined;
        /**
         * Details for each shipment on the stop that was tendered to a third party carrier.
         * Required: false
         * Array: true
         */
        this.tenderDetails = undefined;
        /**
         * A list summary of derived tender statuses based on combinations of the tender detail status of each tendered shipment on the stop.
         * Required: false
         * Array: true
         */
        this.carrierTenderGroupStatusSummary = undefined;
    }
}
/**
 * Summary of trip information
 */
class TripSummary {
    constructor() {
        /**
         * The DSR that is assigned to this trip.
         * Required: false
         * Array: false
         */
        this.tripDriver = undefined;
        /**
         * The list of all the routes associated with this trip.
         * Required: false
         * Array: true
         */
        this.routeSummary = undefined;
    }
}
/**
 * Summarized trailer content that is part of each route in a trip.
 */
class TripTrailerContentSummary {
    constructor() {
        /**
         * The business identifier of the equipment.
         * Required: false
         * Array: false
         */
        this.equipmentIdFullName = undefined;
        /**
         * The system identifier of the equipment that is a trailer or a straight truck.
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * The route name.
         * Required: false
         * Array: false
         */
        this.routeName = undefined;
        /**
         * The list of shipments contained in a trailer.
         * Required: false
         * Array: true
         */
        this.trailerContent = undefined;
    }
}
/**
 * A derived entity that is used for aggregation of pickups by instance ID.
 */
class UnassignedPickup {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.callNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupHeader = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServiceSummary = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.pickupLines = undefined;
    }
}
/**
 * A derived entity that is used for aggregation of Delivery Shipments by Consignee
 */
class UnassignedStop {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.consignee = undefined;
        /**
         * The total number of shipments for this consignee
         * Required: false
         * Array: false
         */
        this.totalShipmentsCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loosePcsCnt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         * Derived attribute representing the aggregated footprint of the shipment for planning a floor-loaded route.
         * Required: false
         * Array: false
         */
        this.footprintPercentage = undefined;
        /**
         * The current SicCd if all at the same SIC, otherwise a message indicating mixed SICs
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * If all of the service dates are the same, this will contain the Service Date.  If the service dates are different, this will indicate mixed dates.
         * Required: false
         * Array: false
         */
        this.serviceDateMessage = undefined;
        /**
         * The minimum ETA of the shipment arriving earliest for a stop at the destination servicecenter
         * Required: false
         * Array: false
         */
        this.earliestDestSicEta = undefined;
        /**
         * The maximum ETA of the shipment arriving latest for a stop at the destination servicecenter
         * Required: false
         * Array: false
         */
        this.latestDestSicEta = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.deliveryQualifiers = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServiceSummary = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.stopWindow = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.deliveryShipments = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The number of days the shipments on this stop have remained in unassigned status.
         * Required: false
         * Array: false
         */
        this.ageInDays = undefined;
        /**
         * The code which determines whether No, Partial, or All Prints for FBDS documents will be printed for the route.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCountCd = undefined;
    }
}
class XdockTypeCdMap {
    constructor() {
        /**
         * The type code for the Xdock exception.
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCd = undefined;
        /**
         * The origin SIC where the exception was reported.
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
    }
}
/**
 * This type was created to force transformer generation.
 */
class DoNotUse {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.applicationCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deviceShiftCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deviceTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.employeeRoleCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.optimizerResultReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.processStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scoEventStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scoEventSubTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scoEventTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentAuditStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentMovementTypeCd = undefined;
    }
}
/**
 * The snapshot of a record that was modified by a transaction event.
 */
class CityOperationsEventSnapshot {
    constructor() {
        /**
         * The details for the DeliveryShipmentSnapshot.
         * Required: false
         * Array: false
         */
        this.deliveryShipmentSnapshot = undefined;
        /**
         * The details for the DeliveryShipmentEtaSnapshot
         * Required: false
         * Array: false
         */
        this.deliveryShipmentEtaSnapshot = undefined;
        /**
         * The details for the CustomerOperationsProfileSnapshot.
         * Required: false
         * Array: false
         */
        this.customerOperationsProfileSnapshot = undefined;
        /**
         * The details for the CustomerOperationsScheduleSnapshot.
         * Required: false
         * Array: false
         */
        this.customerOperationsScheduleSnapshot = undefined;
        /**
         * The details for the DsrCurrentLocationSnapshot
         * Required: false
         * Array: false
         */
        this.dsrCurrentLocationSnapshot = undefined;
        /**
         * The details for the TripNodeActivitySnapshot.
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySnapshot = undefined;
        /**
         * The details for the DropLocationSnapshot.
         * Required: false
         * Array: false
         */
        this.dropLocationSnapshot = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scoEventSnapshot = undefined;
    }
}
class RouteFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.plannerInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.newReleaseInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServices = undefined;
    }
}
class RouteChangeRecord {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.plannerInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.newReleaseInd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.categoryCd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.statusCd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.specialServices = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Request for register
 */
class RegisterFilterRoutesRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterRoutesResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterRoutesRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedRoutesRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
class TripDetailFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tripStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
    }
}
class TripDetailChangeRecord {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tripDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.tripStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Request for register
 */
class RegisterFilterTripsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterTripsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterTripsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedTripsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
class MetricFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.roleCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
    }
}
class MetricChangeRecord {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.roleCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.zoneInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.metricId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Request for register
 */
class RegisterFilterServiceCenterMetricsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterServiceCenterMetricsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterServiceCenterMetricsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedServiceCenterMetricsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
class PdoControlFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
    }
}
class PdoControlChangeRecord {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.statusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Request for register
 */
class RegisterFilterPndOptimizerRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterPndOptimizerResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterPndOptimizerRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedPndOptimizerRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
class DsrCurrentLocationFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
    }
}
class DsrCurrentLocationChangeRecord {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Request for register
 */
class RegisterFilterCurrentDsrLocationRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterCurrentDsrLocationResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterCurrentDsrLocationRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedCurrentDsrLocationRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 * Request for deleting a rule.
 */
class DeleteRulePath {
    constructor() {
        /**
         * The rule unique identifier; generated when the rule is created
         * Required: false
         * Array: false
         */
        this.ruleInstId = undefined;
    }
}
/**
 * Request to execute a rule
 */
class ExecuteRuleRqst {
    constructor() {
        /**
         * The name of the rule to be executed
         * Required: false
         * Array: false
         */
        this.ruleName = undefined;
        /**
         * Desc: Identifies a rule as being a precondition rule.  Precondition rules are used to determine which rules to run.
         * Required: false
         * Array: false
         */
        this.preconditionInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.context = undefined;
    }
}
/**
 * Response of the request to execute a rule
 */
class ExecuteRuleResp {
    constructor() {
        /**
         * Rule details with the execution outcomes
         * Required: false
         * Array: false
         */
        this.rule = undefined;
    }
}
/**
 * Request to get a rule by identifier.
 */
class GetRulePath {
    constructor() {
        /**
         * The rule unique identifier; generated when the rule is created
         * Required: false
         * Array: false
         */
        this.ruleInstId = undefined;
    }
}
/**
 * Response to get a rule by identifier.
 */
class GetRuleResp {
    constructor() {
        /**
         * Rule details
         * Required: false
         * Array: false
         */
        this.rule = undefined;
    }
}
/**
 * Request to get a rule by name.
 */
class GetRuleByNamePath {
    constructor() {
        /**
         * Descriptive Name of the rule
         * Required: false
         * Array: false
         */
        this.ruleName = undefined;
    }
}
/**
 * Response to get a rule by name.
 */
class GetRuleByNameResp {
    constructor() {
        /**
         * Rule details
         * Required: false
         * Array: false
         */
        this.rule = undefined;
    }
}
/**
 * Request for listing of rules.
 */
class ListRulesQuery {
    constructor() {
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response for listing of Rules request.
 */
class ListRulesResp {
    constructor() {
        /**
         * All rules are returned.
         * Required: false
         * Array: true
         */
        this.rules = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Request to list rules by rule expressions.
 */
class ListRulesByExpressionsRqst {
    constructor() {
        /**
         * Rule Expressions to filter the rules.
         * Required: false
         * Array: true
         */
        this.ruleExpressions = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response for listing of Rules By Expressions request.
 */
class ListRulesByExpressionsResp {
    constructor() {
        /**
         * All rules matching the expressions in the request are returned.
         * Required: false
         * Array: true
         */
        this.rules = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Request for listing of rules by rulesetName.
 */
class ListRulesByRulesetNameQuery {
    constructor() {
        /**
         * Date used to identify active rules.
         * Required: false
         * Array: false
         */
        this.ruleEffectiveDate = undefined;
        /**
         * Descriptive Names of the rulesets to include.
         * Required: false
         * Array: true
         */
        this.rulesetNameInclusion = undefined;
        /**
         * Descriptive Names of the rulesets to exclude.
         * Required: false
         * Array: true
         */
        this.rulesetNameExclusion = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response for listing of Rules By RulesetName request.
 */
class ListRulesByRulesetNameResp {
    constructor() {
        /**
         * All rules matching the parameters in the request are returned.
         * Required: false
         * Array: true
         */
        this.rules = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response for listing of Rules for execution request.
 */
class ListRulesForExecutionQuery {
    constructor() {
        /**
         * Date used to identify active rules.
         * Required: false
         * Array: false
         */
        this.ruleEffectiveDate = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * Response for listing of Rules for execution request.
 */
class ListRulesForExecutionResp {
    constructor() {
        /**
         * All rules are returned.
         * Required: false
         * Array: true
         */
        this.rules = undefined;
        /**
         * Pagination information to retrieve the list.
         * Required: false
         * Array: false
         */
        this.listInfo = undefined;
    }
}
/**
 * The basic rule data needed for execution
 */
class RuleForExecution {
    constructor() {
        /**
         * Desc: The rule Unique identifier; generated when rule is created
         * Required: false
         * Array: false
         */
        this.ruleInstId = undefined;
        /**
         * Desc: Descriptive Name of the rule
         * Required: false
         * Array: false
         */
        this.ruleName = undefined;
        /**
         * Desc: Identifies a rule as being a precondition rule.  Precondition rules are used to determine which rules to run.
         * Required: false
         * Array: false
         */
        this.preconditionInd = undefined;
        /**
         * The rule details. A ruleset is associated with a group of conditions(expressions) to evaluate using a specific logical operator. If the evaluation of the conditions is true, the ruleset will return a group of outcomes.
         * Required: false
         * Array: true
         */
        this.ruleset = undefined;
    }
}
/**
 * The rule details. A ruleset is associated with a group of conditions(expressions) to evaluate using a specific logical operator. If the evaluation of the conditions is true, the ruleset will return a group of outcomes.
 */
class RulesetForExecution {
    constructor() {
        /**
         * Desc: The ruleset Unique identifier; generated when the ruleset is created
         * Required: false
         * Array: false
         */
        this.rulesetInstId = undefined;
        /**
         * The priority order for the execution of the ruleset. If a rule is associated with multiple rulesets, the ruleset with the highest priority is executed first. The number 1 is a higher priority than 2 and so on.
         * Required: false
         * Array: false
         */
        this.priorityOrderNbr = undefined;
        /**
         * Desc: Identifies the logical operator value. The Valid Values are: AND, OR
         * Required: false
         * Array: false
         */
        this.logicalOperatorCd = undefined;
        /**
         * Desc: The action that should be performed if the logical operator function is true.
         * Required: false
         * Array: false
         */
        this.actionCallBack = undefined;
        /**
         * Descriptive Name of the ruleset.  Added 5/1/2019
         * Required: false
         * Array: false
         */
        this.rulesetName = undefined;
        /**
         * The group of conditions(expressions) to be evaluated for the ruleset.
         * Required: false
         * Array: true
         */
        this.ruleExpression = undefined;
        /**
         * The group of outcomes to be returned if the conditions of the ruleset are evaluated to true.
         * Required: false
         * Array: true
         */
        this.ruleOutcome = undefined;
    }
}
/**
 * The outcome is a key,value entry to be returned if the conditions of the ruleset are evaluated to true.
 */
class RuleOutcomeForExecution {
    constructor() {
        /**
         * Desc: the outcome Unique identifier; generated when the outcome is created.
         * Required: false
         * Array: false
         */
        this.outcomeInstId = undefined;
        /**
         * Added as an additional part of PK so that Outcome name/value pairs can be grouped. 4/15/2019
         * Required: false
         * Array: false
         */
        this.outcomeSeqNbr = undefined;
        /**
         * Desc: The key/name of the outcome.
         * Required: false
         * Array: false
         */
        this.outcomeName = undefined;
        /**
         * The value of the outcome.
         * Required: false
         * Array: false
         */
        this.outcomeValue = undefined;
    }
}
/**
 * A rule expression is a condition to be evaluated in the rule.
 */
class RuleExpressionForExecution {
    constructor() {
        /**
         * The generated unique identifier.
         * Required: false
         * Array: false
         */
        this.expressionInstId = undefined;
        /**
         * Desc: Name of the attribute to be used in the logical expression.
         * Required: false
         * Array: false
         */
        this.leftOperand = undefined;
        /**
         * Desc: The operand to be used in the logical expression. The valid values are:
         * EQUAL_TO
         * NOT_EQUAL_TO
         * GREATER_THAN
         * GREATER_THAN_OR_EQUAL
         * IN
         * NOT_IN
         * LESS_THAN
         * LESS_THAN_OR_EQUAL
         * MATCHES
         * NOT_MATCHES
         * Required: false
         * Array: false
         */
        this.operatorCd = undefined;
        /**
         * Desc: The type of value of the attribute to be used in the logical expression.. The valid values are:
         * BOOLEAN
         * DATE_TIME
         * LIST
         * NUMBER
         * TEXT
         * Required: false
         * Array: false
         */
        this.valueTypeCd = undefined;
        /**
         * Desc: The value of the attribute to be used in the logical expression.
         * Required: false
         * Array: false
         */
        this.value = undefined;
    }
}
/**
 * Response outcome from a ruleset
 */
class RulesetOutcomeResp {
    constructor() {
        /**
         * Desc: The ruleset Unique identifier; generated when the ruleset is created
         * Required: false
         * Array: false
         */
        this.rulesetInstId = undefined;
        /**
         * Desc: the outcome Unique identifier; generated when the outcome is created.
         * Required: false
         * Array: false
         */
        this.outcomeInstId = undefined;
        /**
         * Added as an additional part of PK so that Outcome name/value pairs can be grouped. 4/15/2019
         * Required: false
         * Array: false
         */
        this.outcomeSeqNbr = undefined;
        /**
         * Desc: The key/name of the outcome.
         * Required: false
         * Array: false
         */
        this.outcomeName = undefined;
        /**
         * The value of the outcome.
         * Required: false
         * Array: false
         */
        this.outcomeValue = undefined;
    }
}
/**
 * Request to create or update a rule.
 *
 */
class UpsertRuleRqst {
    constructor() {
        /**
         * The Rule to be created or updated.
         * Required: false
         * Array: false
         */
        this.rule = undefined;
    }
}
/**
 * Response for the request to create or update a Rule.
 */
class UpsertRuleResp {
    constructor() {
        /**
         * The rule unique identifier; generated when the rule is created.
         * Required: false
         * Array: false
         */
        this.ruleInstId = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetEnsembleStatusCountsPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * From timestamp
         * Required: true
         * Array: false
         */
        this.fromTmst = undefined;
        /**
         * To timestamp
         * Required: true
         * Array: false
         */
        this.toTmst = undefined;
    }
}
/**
 * Response to the request to get Ensemble status counts
 */
class GetEnsembleStatusCountsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.ensembleMetrics = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.queueMetrics = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetQueuePendingCountPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Response for getQueuePendingCount
 */
class GetQueuePendingCountResp {
    constructor() {
        /**
         * Number of messages in either ready or wait status. This count indicates the queue depth.
         * Required: false
         * Array: false
         */
        this.pendingCount = undefined;
    }
}
/**
 * Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Unique identifier for the application workflow.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsembleRqst {
}
/**
 * Response to the Request to Restart execution of a Started or Not Started  Ensemble
 */
class RestartEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Restarted.
         * Required: false
         * Array: false
         */
        this.ensembleRestartedInd = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsemblePath {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Unique identifier for the application workflow.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsembleRqst {
}
/**
 * Response to the Request to Resume execution of a Suspended ensemble
 */
class ResumeEnsembleResp {
    constructor() {
        /**
         * Ensemble that was Resumed
         * Required: false
         * Array: false
         */
        this.ensembleResumedInd = undefined;
    }
}
/**
 * Request to start ensemble.
 */
class StartMessageDrivenEnsembleRqst {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.queue = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.message = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.eventDataType = undefined;
    }
}
/**
 * Enter comments
 */
class StartMessageDrivenEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request to stop execution of an Ensemble
 */
class KillEnsembleRqst {
    constructor() {
        /**
         * Unique identifier for the ensemble.
         * Required: true
         * Array: true
         */
        this.ensembleInstIds = undefined;
    }
}
/**
 * Response to the request to stop execution of an Ensemble
 */
class KillEnsembleResp {
    constructor() {
        /**
         * Ensembles that were killed.
         * Required: false
         * Array: true
         */
        this.status = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueuePath {
    constructor() {
        /**
         * Name of the queue to stop
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueueWebhookPath {
    constructor() {
        /**
         * Name of the queue to stop
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters for stopQueue
 */
class StopQueueWebhookQuery {
    constructor() {
        /**
         * Authorization token
         * Required: false
         * Array: false
         */
        this.auth_token = undefined;
    }
}
/**
 * Path parameters for startQueue
 */
class StartQueuePath {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Response for listQueues
 */
class ListQueuesResp {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: true
         */
        this.queues = undefined;
    }
}
/**
 * Path parameters
 */
class ListExpiredMessagesPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * From enqueue timestamp
         * Required: true
         * Array: false
         */
        this.enqueueFromTmst = undefined;
        /**
         * Tom enqueue timestamp
         * Required: true
         * Array: false
         */
        this.enqueueToTmst = undefined;
    }
}
/**
 * Response
 */
class ListExpiredMessagesResp {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: true
         */
        this.message = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteExpiredMessagesPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteExpiredMessagesQuery {
    constructor() {
        /**
         * Id of the message to be deleted
         * Required: true
         * Array: true
         */
        this.messageId = undefined;
    }
}
/**
 * Response to the request to stop execution of an Ensemble
 */
class DeleteExpiredMessagesResp {
    constructor() {
        /**
         * Ensembles that were killed.
         * Required: false
         * Array: true
         */
        this.status = undefined;
    }
}
/**
 * Path parameters
 */
class ReplayMessagePath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.queueName = undefined;
        /**
         * ID of the message to replay. It needs to be in expired status
         * Required: true
         * Array: false
         */
        this.messageId = undefined;
    }
}
/**
 * Path parameters for get Ensemble status counts
 */
class GetPubSubStatusCountsPath {
    constructor() {
        /**
         * Name of the queue
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
        /**
         * From timestamp
         * Required: true
         * Array: false
         */
        this.fromTmst = undefined;
        /**
         * To timestamp
         * Required: true
         * Array: false
         */
        this.toTmst = undefined;
    }
}
/**
 * Response to the request to get Ensemble status counts
 */
class GetPubSubStatusCountsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pubsubMetrics = undefined;
    }
}
/**
 * Path parameters for startSubscription
 */
class StartSubscriptionPath {
    constructor() {
        /**
         * Id of the subscription to start
         * Required: false
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Path parameters for stopSubscription
 */
class StopSubscriptionPath {
    constructor() {
        /**
         * Id of the subscription to stop
         * Required: false
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Path parameters for get Undelivered Message counts
 */
class GetUndeliveredMessagesPath {
    constructor() {
        /**
         * Id of subscription
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Response for getUndeliveredMessages
 */
class GetUndeliveredMessagesResp {
    constructor() {
        /**
         * Number of Undelivered Messages.
         * Required: false
         * Array: false
         */
        this.undeliveredCount = undefined;
    }
}
/**
 * Response for listSubscriptions
 */
class ListSubscriptionsResp {
    constructor() {
        /**
         * Name of the queue to start
         * Required: false
         * Array: true
         */
        this.subscriptions = undefined;
    }
}
/**
 * Path parameters for Get subscription count
 */
class GetSubscriptionPath {
    constructor() {
        /**
         * Subscription id
         * Required: true
         * Array: false
         */
        this.subscriptionId = undefined;
    }
}
/**
 * Returns status of a given subscription
 */
class GetSubscriptionResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.subscription = undefined;
    }
}
class MessageDrivenEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The event occurrence timestamp
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
class DynamicMessageDrivenPayload {
    constructor() {
        /**
         * The type of the payload
         * Required: true
         * Array: false
         */
        this.eventDataType = undefined;
        /**
         * The message payload
         * Required: true
         * Array: false
         */
        this.payload = undefined;
    }
}
class TransactionEventEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The event occurrence timestamp
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
/**
 * Status of a delete expired message request
 */
class DeleteExpiredMessageStatus {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Indicates whether the ensemble was killed.
         * Required: false
         * Array: false
         */
        this.messageDeletedInd = undefined;
        /**
         * A status describing why the message could not be deleted. Populated if messageDeletedInd is false
         * Required: false
         * Array: false
         */
        this.failStatus = undefined;
    }
}
/**
 * Status of a kill ensemble request
 */
class KillEnsembleStatus {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
        /**
         * Indicates whether the ensemble was killed.
         * Required: false
         * Array: false
         */
        this.ensembleKilledInd = undefined;
        /**
         * A status describing why the ensemble could not be killed. Populated if ensembleKilledInd is false
         * Required: false
         * Array: false
         */
        this.failStatus = undefined;
    }
}
/**
 * Stores execution information for application workflows.
 */
class EnsembleStateSearchRecord {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState = undefined;
    }
}
class Payload {
    constructor() {
        /**
         * Required: true
         * Array: false
         */
        this.type = undefined;
        /**
         * Required: true
         * Array: false
         */
        this.value = undefined;
    }
}
class EnsembleState {
    constructor() {
        /**
         * Unique identifier for the ensemble record.
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * A short description of the ensemble
         * Required: false
         * Array: false
         */
        this.desc = undefined;
        /**
         * Name of the queue publishing this message.
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * Id of the message.
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * The timestamp of when the trigger event was created.
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The timestamp of when the message was received by ensemble.
         * Required: false
         * Array: false
         */
        this.recvTmst = undefined;
        /**
         * The timestamp of when this ensemble was dispatched to the executor thread.
         * Required: false
         * Array: false
         */
        this.dispatchTmst = undefined;
        /**
         * The timestamp of when this ensemble processing started.
         * Required: false
         * Array: false
         */
        this.strtTmst = undefined;
        /**
         * The timestamp of when this ensemble processing ended.
         * Required: false
         * Array: false
         */
        this.endTmst = undefined;
        /**
         * Dequeue time in milliseconds. Populated for started ensembles, and is the difference between the received and event timestamps.
         * Required: false
         * Array: false
         */
        this.dequeueTime = undefined;
        /**
         * Dispatch time in milliseconds. Populated for started ensembles, and is the difference between the dispatch and received timestamps.
         * Required: false
         * Array: false
         */
        this.dispatchTime = undefined;
        /**
         * Wait time in milliseconds. Populated for started ensembles, and is the difference between the start and dispatch timestamps.
         * Required: false
         * Array: false
         */
        this.waitTime = undefined;
        /**
         * Time in milliseconds it took to process this ensemble from the time it was started. Populated for completed ensembles, and is the difference between the end and start timestamps.
         * Required: false
         * Array: false
         */
        this.processTime = undefined;
        /**
         * Total elapsed time in milliseconds to process this ensemble from the time the event was created. Populated for completed ensembles, and is the difference between the start and event timestamps.
         * Required: false
         * Array: false
         */
        this.totalTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lstUpdtTmst = undefined;
        /**
         * Status of workflow execution.
         *
         * Valid values:
         * NOT_STARTED
         * STARTED
         * SUSPENDED
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statCd = undefined;
        /**
         * Status message for the workflow execution.  This could be an error message, stacktrace, success message, etc.
         * Required: false
         * Array: false
         */
        this.statMsg = undefined;
        /**
         * Same value for CORRELATION
         *
         * id is assigned to various rows from different tables that form a single business
         *
         * transactions.
         * In other words, this groups multiple rows across various tables for a single business
         *
         * transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Name of the host on which the ensemble was last processed
         * Required: false
         * Array: false
         */
        this.host = undefined;
        /**
         * Name of the thread on which the ensemble was last processed
         * Required: false
         * Array: false
         */
        this.thread = undefined;
        /**
         * Timestamp of when the ensemble started waiting for lock
         * Required: false
         * Array: false
         */
        this.lockWaitStartTmst = undefined;
        /**
         * Time spent on waiting for lock
         * Required: false
         * Array: false
         */
        this.elapsedLockWaitTime = undefined;
        /**
         * Number of times this ensemble was put on lock wait
         * Required: false
         * Array: false
         */
        this.lockWaitCount = undefined;
        /**
         * When the ensemble was paused
         * Required: false
         * Array: false
         */
        this.pauseStartTmst = undefined;
        /**
         * When the pause will expire
         * Required: false
         * Array: false
         */
        this.pauseExpiryTime = undefined;
        /**
         * Number of times this ensemble was put on pause
         * Required: false
         * Array: false
         */
        this.pauseCount = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.minuetStates = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.gotoEnsembleStates = undefined;
        /**
         * Payload that is input to the ensemble execution.
         * Required: false
         * Array: false
         */
        this.payload = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.executionContext = undefined;
    }
}
class NestedEnsembleState {
    constructor() {
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.minuetStates = undefined;
    }
}
class EnsembleExecutionContext {
    constructor() {
        /**
         * Name of the application workflow.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.minuetPtr = undefined;
    }
}
/**
 * Stores execution information for the tasks that compose the application workflow.
 */
class MinuetState {
    constructor() {
        /**
         * Name of the task.
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Response payload that is output  from the task.
         * Required: false
         * Array: false
         */
        this.respPayload = undefined;
        /**
         * Datetime the task execution started
         * Required: false
         * Array: false
         */
        this.strtTmst = undefined;
        /**
         * Datetime the task execution ended
         * Required: false
         * Array: false
         */
        this.endTmst = undefined;
        /**
         * Time in milliseconds it took to process this minuet from the time it was started. Populated for completed minuets, and is the difference between the end and start timestamps.
         * Required: false
         * Array: false
         */
        this.processTime = undefined;
        /**
         * Number of times this minuet was retried
         * Required: false
         * Array: false
         */
        this.executionCount = undefined;
        /**
         * Status of task execution.
         *
         * Valid values:
         * STARTED
         * FAILED
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statCd = undefined;
        /**
         * Status message for the task.  This could be an error message, stacktrace, success message, etc.
         * Required: false
         * Array: false
         */
        this.statMsg = undefined;
    }
}
class EnsembleMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.notStartedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.startedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.completedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suspendedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.failedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.killedCount = undefined;
    }
}
class PubSubMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.sentMessageCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ackedMessageCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.expiredAckDeadLinesCount = undefined;
    }
}
class QueueMetrics {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.readyCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.waitCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.processedCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.expiredCount = undefined;
    }
}
class AuxMessagePublishers {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.queueTypeCd = undefined;
    }
}
class EnsembleConfiguration {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.component = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.mode = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shortName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceTable = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.payloadType = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.cacheEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lockEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.retriggerEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.waitEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.rulesEngineEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.messagePublisherEnabled = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.auxMessagePublishers = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileAccessEnabled = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.synchronousResponseType = undefined;
    }
}
class Subscription {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.id = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
    }
}
class ScoDlvrShmMessageDrivenEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
class ScoEventMessageDrivenEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
class IntrfcAcctMessageDrivenEnsemblePayload {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The event occurrence timestamp in microseconds
         * Required: false
         * Array: false
         */
        this.eventTmstMicro = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: false
         */
        this.eventData = undefined;
    }
}
class LTLEventCityOperations {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * Contains a list of additional event types that are included in this event.
         * Required: false
         * Array: true
         */
        this.auxEventTypes = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The name of component from which this event originated.
         * Required: false
         * Array: false
         */
        this.srcComponent = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * A unique id for the event. If this is chunked, the eventId will help in combining chunks of the same message
         * Required: false
         * Array: false
         */
        this.eventId = undefined;
        /**
         * If there are a large number of payloads, the events can be chunked and published as smaller payloads. Set chunkInd to true if this is a chunk of a larger event message
         * Required: false
         * Array: false
         */
        this.chunkedInd = undefined;
        /**
         * If this is a chunked event message, specifies the sequence number of this instance
         * Required: false
         * Array: false
         */
        this.chunkSeqNbr = undefined;
        /**
         * If this is a chunked event message, specifies the total number of chunks
         * Required: false
         * Array: false
         */
        this.totalChunks = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: true
         */
        this.eventData = undefined;
    }
}
class LTLEventCustomer {
    constructor() {
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventType = undefined;
        /**
         * Contains a list of additional event types that are included in this event.
         * Required: false
         * Array: true
         */
        this.auxEventTypes = undefined;
        /**
         * The type of event. This will determine which ensemble to execute
         * Required: false
         * Array: false
         */
        this.eventTmst = undefined;
        /**
         * The name of component from which this event originated.
         * Required: false
         * Array: false
         */
        this.srcComponent = undefined;
        /**
         * The correlation ID of the originating transaction
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * A unique id for the event. If this is chunked, the eventId will help in combining chunks of the same message
         * Required: false
         * Array: false
         */
        this.eventId = undefined;
        /**
         * If there are a large number of payloads, the events can be chunked and published as smaller payloads. Set chunkInd to true if this is a chunk of a larger event message
         * Required: false
         * Array: false
         */
        this.chunkedInd = undefined;
        /**
         * If this is a chunked event message, specifies the sequence number of this instance
         * Required: false
         * Array: false
         */
        this.chunkSeqNbr = undefined;
        /**
         * If this is a chunked event message, specifies the total number of chunks
         * Required: false
         * Array: false
         */
        this.totalChunks = undefined;
        /**
         * The event data. Use JSON format with previous and current values of all attributes that were changed by this event
         * Required: false
         * Array: true
         */
        this.eventData = undefined;
    }
}
/**
 * An Update response from a BULK update to elastic
 */
class BulkUpdateResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this._index = undefined;
        /**
         * Required: false
         * Array: false
         */
        this._id = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.result = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.status = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.error = undefined;
    }
}
/**
 * Details of an error for an item
 */
class BulkUpdateItemError {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.type = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.reason = undefined;
    }
}
/**
 * An item in the bulk api response
 */
class BulkApiRespItem {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.update = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.index = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.create = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.delete = undefined;
    }
}
class EsAggregationFieldRqst {
    constructor() {
        /**
         * the name of the property to aggregate on (siteCode, employeeCode, etc.)
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * aggregation type on that property. Options for field object: term, date, distinctCount. Options for subfield object: term, stats, date, distinctCount
         * Required: false
         * Array: false
         */
        this.type = undefined;
        /**
         * max number of rows to return
         * Required: false
         * Array: false
         */
        this.size = undefined;
        /**
         * second, minute, hour, day, week, month, quarter, year. Applies only when type is set to date
         * Required: false
         * Array: false
         */
        this.dateInterval = undefined;
        /**
         * min number of documents to filter date interval on. Applies only when type is set to date
         * Required: false
         * Array: false
         */
        this.minimumDocumentCount = undefined;
        /**
         * min number of documents to filter date interval on. Applies only when type is set to date
         * Required: false
         * Array: true
         */
        this.subFields = undefined;
    }
}
class EsDynamicPivotRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
    }
}
class EsAggregationRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.dynamicPivot = undefined;
    }
}
class EsAggregationFieldResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.key = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.date = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.docCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.subFields = undefined;
    }
}
class EsAggregationResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.dynamicPivot = undefined;
    }
}
class DeliveryShipmentSearchFilterNot {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
    }
}
class DeliveryShipmentSearchFilterOr {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.proNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.hostDestSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.hostDestSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleETA = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleETA_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailer = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailer_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServiceSummary_specialService = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServiceSummary_specialService_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_geoCoordinatesGeo = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_geoCoordinatesGeo_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suppRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suppRefNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryDate_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryFromTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryFromTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryToTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryToTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_statusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_statusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDateTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDateTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTimeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTimeTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.withinTdcCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.withinTdcCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_typeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_typeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_auditInfo_createdById = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_auditInfo_createdById_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_remarks = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_remarks_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByLastName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByLastName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByFirstName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByFirstName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.moverSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.moverSuffix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_originTerminalSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_originTerminalSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scannedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scannedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalPiecesCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalWeightLbs_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalUsdAmount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalUsdAmount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_refNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_refNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_typeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_typeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.doorNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.doorNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.accessorials_accessorialCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.accessorials_accessorialCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_originalDescription = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_originalDescription_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_description = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_description_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_shipmentDirectionCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_shipmentDirectionCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.debtorTerms = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.debtorTerms_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.childProNbrs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.childProNbrs_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentShipmentId_proNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentShipmentId_proNumber_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipmentInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.drvrCollectCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.drvrCollectCd_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "proNbr": this.proNbr,
                "proNbr.keyword": this.proNbr_keyword,
                "shipmentInstId": this.shipmentInstId,
                "shipmentInstId.keyword": this.shipmentInstId_keyword,
                "currentSicCd": this.currentSicCd,
                "currentSicCd.keyword": this.currentSicCd_keyword,
                "hostDestSicCd": this.hostDestSicCd,
                "hostDestSicCd.keyword": this.hostDestSicCd_keyword,
                "destinationSicCd": this.destinationSicCd,
                "destinationSicCd.keyword": this.destinationSicCd_keyword,
                "scheduleDestinationSicCd": this.scheduleDestinationSicCd,
                "scheduleDestinationSicCd.keyword": this.scheduleDestinationSicCd_keyword,
                "scheduleETA": this.scheduleETA,
                "scheduleETA.keyword": this.scheduleETA_keyword,
                "estimatedDeliveryDate": this.estimatedDeliveryDate,
                "estimatedDeliveryDate.keyword": this.estimatedDeliveryDate_keyword,
                "currentTrailer": this.currentTrailer,
                "currentTrailer.keyword": this.currentTrailer_keyword,
                "specialServiceSummary.specialService": this.specialServiceSummary_specialService,
                "specialServiceSummary.specialService.keyword": this.specialServiceSummary_specialService_keyword,
                "consignee.geoCoordinatesGeo": this.consignee_geoCoordinatesGeo,
                "consignee.geoCoordinatesGeo.keyword": this.consignee_geoCoordinatesGeo_keyword,
                "routeInstId": this.routeInstId,
                "routeInstId.keyword": this.routeInstId_keyword,
                "routePrefix": this.routePrefix,
                "routePrefix.keyword": this.routePrefix_keyword,
                "destSicEta": this.destSicEta,
                "destSicEta.keyword": this.destSicEta_keyword,
                "shipmentPndStatus": this.shipmentPndStatus,
                "shipmentPndStatus.keyword": this.shipmentPndStatus_keyword,
                "destSicEtaLastUpdtTmst": this.destSicEtaLastUpdtTmst,
                "destSicEtaLastUpdtTmst.keyword": this.destSicEtaLastUpdtTmst_keyword,
                "pickupDt": this.pickupDt,
                "pickupDt.keyword": this.pickupDt_keyword,
                "originSic": this.originSic,
                "originSic.keyword": this.originSic_keyword,
                "responsibleSic": this.responsibleSic,
                "responsibleSic.keyword": this.responsibleSic_keyword,
                "shipmentDeletedInd": this.shipmentDeletedInd,
                "shipmentDeletedInd.keyword": this.shipmentDeletedInd_keyword,
                "suppRefNbr": this.suppRefNbr,
                "suppRefNbr.keyword": this.suppRefNbr_keyword,
                "consignee.acctInstId": this.consignee_acctInstId,
                "consignee.acctInstId.keyword": this.consignee_acctInstId_keyword,
                "consignee.name1": this.consignee_name1,
                "consignee.name1.keyword": this.consignee_name1_keyword,
                "consignee.name2": this.consignee_name2,
                "consignee.name2.keyword": this.consignee_name2_keyword,
                "consignee.addressLine1": this.consignee_addressLine1,
                "consignee.addressLine1.keyword": this.consignee_addressLine1_keyword,
                "consignee.addressLine2": this.consignee_addressLine2,
                "consignee.addressLine2.keyword": this.consignee_addressLine2_keyword,
                "consignee.cityName": this.consignee_cityName,
                "consignee.cityName.keyword": this.consignee_cityName_keyword,
                "consignee.stateCd": this.consignee_stateCd,
                "consignee.stateCd.keyword": this.consignee_stateCd_keyword,
                "consignee.postalCd": this.consignee_postalCd,
                "consignee.postalCd.keyword": this.consignee_postalCd_keyword,
                "consignee.countryCd": this.consignee_countryCd,
                "consignee.countryCd.keyword": this.consignee_countryCd_keyword,
                "consignee.acctMadCd": this.consignee_acctMadCd,
                "consignee.acctMadCd.keyword": this.consignee_acctMadCd_keyword,
                "billInd": this.billInd,
                "billInd.keyword": this.billInd_keyword,
                "billTo.acctInstId": this.billTo_acctInstId,
                "billTo.acctInstId.keyword": this.billTo_acctInstId_keyword,
                "billTo.name1": this.billTo_name1,
                "billTo.name1.keyword": this.billTo_name1_keyword,
                "billTo.name2": this.billTo_name2,
                "billTo.name2.keyword": this.billTo_name2_keyword,
                "billTo.addressLine1": this.billTo_addressLine1,
                "billTo.addressLine1.keyword": this.billTo_addressLine1_keyword,
                "billTo.addressLine2": this.billTo_addressLine2,
                "billTo.addressLine2.keyword": this.billTo_addressLine2_keyword,
                "billTo.cityName": this.billTo_cityName,
                "billTo.cityName.keyword": this.billTo_cityName_keyword,
                "billTo.stateCd": this.billTo_stateCd,
                "billTo.stateCd.keyword": this.billTo_stateCd_keyword,
                "billTo.postalCd": this.billTo_postalCd,
                "billTo.postalCd.keyword": this.billTo_postalCd_keyword,
                "billTo.countryCd": this.billTo_countryCd,
                "billTo.countryCd.keyword": this.billTo_countryCd_keyword,
                "billTo.acctMadCd": this.billTo_acctMadCd,
                "billTo.acctMadCd.keyword": this.billTo_acctMadCd_keyword,
                "shipper.acctInstId": this.shipper_acctInstId,
                "shipper.acctInstId.keyword": this.shipper_acctInstId_keyword,
                "shipper.name1": this.shipper_name1,
                "shipper.name1.keyword": this.shipper_name1_keyword,
                "shipper.name2": this.shipper_name2,
                "shipper.name2.keyword": this.shipper_name2_keyword,
                "shipper.addressLine1": this.shipper_addressLine1,
                "shipper.addressLine1.keyword": this.shipper_addressLine1_keyword,
                "shipper.addressLine2": this.shipper_addressLine2,
                "shipper.addressLine2.keyword": this.shipper_addressLine2_keyword,
                "shipper.cityName": this.shipper_cityName,
                "shipper.cityName.keyword": this.shipper_cityName_keyword,
                "shipper.stateCd": this.shipper_stateCd,
                "shipper.stateCd.keyword": this.shipper_stateCd_keyword,
                "shipper.postalCd": this.shipper_postalCd,
                "shipper.postalCd.keyword": this.shipper_postalCd_keyword,
                "shipper.countryCd": this.shipper_countryCd,
                "shipper.countryCd.keyword": this.shipper_countryCd_keyword,
                "shipper.acctMadCd": this.shipper_acctMadCd,
                "shipper.acctMadCd.keyword": this.shipper_acctMadCd_keyword,
                "movementStatusCd": this.movementStatusCd,
                "movementStatusCd.keyword": this.movementStatusCd_keyword,
                "movementTypeCd": this.movementTypeCd,
                "movementTypeCd.keyword": this.movementTypeCd_keyword,
                "onExcludedTrailerInd": this.onExcludedTrailerInd,
                "onExcludedTrailerInd.keyword": this.onExcludedTrailerInd_keyword,
                "movementExceptionTypeCd": this.movementExceptionTypeCd,
                "movementExceptionTypeCd.keyword": this.movementExceptionTypeCd_keyword,
                "deliveryQualifierCd": this.deliveryQualifierCd,
                "deliveryQualifierCd.keyword": this.deliveryQualifierCd_keyword,
                "apptDtls.scheduledDeliveryDate": this.apptDtls_scheduledDeliveryDate,
                "apptDtls.scheduledDeliveryDate.keyword": this.apptDtls_scheduledDeliveryDate_keyword,
                "apptDtls.scheduledDeliveryFromTime": this.apptDtls_scheduledDeliveryFromTime,
                "apptDtls.scheduledDeliveryFromTime.keyword": this.apptDtls_scheduledDeliveryFromTime_keyword,
                "apptDtls.scheduledDeliveryToTime": this.apptDtls_scheduledDeliveryToTime,
                "apptDtls.scheduledDeliveryToTime.keyword": this.apptDtls_scheduledDeliveryToTime_keyword,
                "apptDtls.statusCd": this.apptDtls_statusCd,
                "apptDtls.statusCd.keyword": this.apptDtls_statusCd_keyword,
                "tdc.tdcDateTypeCd": this.tdc_tdcDateTypeCd,
                "tdc.tdcDateTypeCd.keyword": this.tdc_tdcDateTypeCd_keyword,
                "tdc.tdcDate1": this.tdc_tdcDate1,
                "tdc.tdcDate1.keyword": this.tdc_tdcDate1_keyword,
                "tdc.tdcDate2": this.tdc_tdcDate2,
                "tdc.tdcDate2.keyword": this.tdc_tdcDate2_keyword,
                "tdc.tdcTimeTypeCd": this.tdc_tdcTimeTypeCd,
                "tdc.tdcTimeTypeCd.keyword": this.tdc_tdcTimeTypeCd_keyword,
                "tdc.tdcTime1": this.tdc_tdcTime1,
                "tdc.tdcTime1.keyword": this.tdc_tdcTime1_keyword,
                "tdc.tdcTime2": this.tdc_tdcTime2,
                "tdc.tdcTime2.keyword": this.tdc_tdcTime2_keyword,
                "correctiveAction": this.correctiveAction,
                "correctiveAction.keyword": this.correctiveAction_keyword,
                "billClassCd": this.billClassCd,
                "billClassCd.keyword": this.billClassCd_keyword,
                "withinTdcCd": this.withinTdcCd,
                "withinTdcCd.keyword": this.withinTdcCd_keyword,
                "adminAsofDateTime": this.adminAsofDateTime,
                "adminAsofDateTime.keyword": this.adminAsofDateTime_keyword,
                "destinationArrivalDateTime": this.destinationArrivalDateTime,
                "destinationArrivalDateTime.keyword": this.destinationArrivalDateTime_keyword,
                "shipmentLocationSicCd": this.shipmentLocationSicCd,
                "shipmentLocationSicCd.keyword": this.shipmentLocationSicCd_keyword,
                "actualDeliveryDateTime": this.actualDeliveryDateTime,
                "actualDeliveryDateTime.keyword": this.actualDeliveryDateTime_keyword,
                "displayActualDeliveryDateTime": this.displayActualDeliveryDateTime,
                "displayActualDeliveryDateTime.keyword": this.displayActualDeliveryDateTime_keyword,
                "onTimeDeliveryCd": this.onTimeDeliveryCd,
                "onTimeDeliveryCd.keyword": this.onTimeDeliveryCd_keyword,
                "afterHoursPickupInd": this.afterHoursPickupInd,
                "afterHoursPickupInd.keyword": this.afterHoursPickupInd_keyword,
                "shipperRefNbr": this.shipperRefNbr,
                "shipperRefNbr.keyword": this.shipperRefNbr_keyword,
                "movementExcpActionReason": this.movementExcpActionReason,
                "movementExcpActionReason.keyword": this.movementExcpActionReason_keyword,
                "movementExcpActionControllableInd": this.movementExcpActionControllableInd,
                "movementExcpActionControllableInd.keyword": this.movementExcpActionControllableInd_keyword,
                "movementExceptions.typeCd": this.movementExceptions_typeCd,
                "movementExceptions.typeCd.keyword": this.movementExceptions_typeCd_keyword,
                "movementExceptions.auditInfo.createdById": this.movementExceptions_auditInfo_createdById,
                "movementExceptions.auditInfo.createdById.keyword": this.movementExceptions_auditInfo_createdById_keyword,
                "movementExceptions.remarks": this.movementExceptions_remarks,
                "movementExceptions.remarks.keyword": this.movementExceptions_remarks_keyword,
                "movementExceptions.createdByLastName": this.movementExceptions_createdByLastName,
                "movementExceptions.createdByLastName.keyword": this.movementExceptions_createdByLastName_keyword,
                "movementExceptions.createdByFirstName": this.movementExceptions_createdByFirstName,
                "movementExceptions.createdByFirstName.keyword": this.movementExceptions_createdByFirstName_keyword,
                "motorizedPiecesCount": this.motorizedPiecesCount,
                "motorizedPiecesCount.keyword": this.motorizedPiecesCount_keyword,
                "moverSuffix": this.moverSuffix,
                "moverSuffix.keyword": this.moverSuffix_keyword,
                "xdockExceptionTypeCdMaps.xdockExceptionTypeCd": this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd,
                "xdockExceptionTypeCdMaps.xdockExceptionTypeCd.keyword": this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd_keyword,
                "xdockExceptionTypeCdMaps.originTerminalSicCd": this.xdockExceptionTypeCdMaps_originTerminalSicCd,
                "xdockExceptionTypeCdMaps.originTerminalSicCd.keyword": this.xdockExceptionTypeCdMaps_originTerminalSicCd_keyword,
                "calculatedServiceDays": this.calculatedServiceDays,
                "calculatedServiceDays.keyword": this.calculatedServiceDays_keyword,
                "standardTransitDays": this.standardTransitDays,
                "standardTransitDays.keyword": this.standardTransitDays_keyword,
                "billDateTime": this.billDateTime,
                "billDateTime.keyword": this.billDateTime_keyword,
                "scannedInd": this.scannedInd,
                "scannedInd.keyword": this.scannedInd_keyword,
                "lstMvmtTmst": this.lstMvmtTmst,
                "lstMvmtTmst.keyword": this.lstMvmtTmst_keyword,
                "shipmentPurgedInd": this.shipmentPurgedInd,
                "shipmentPurgedInd.keyword": this.shipmentPurgedInd_keyword,
                "exemptReasonCd": this.exemptReasonCd,
                "exemptReasonCd.keyword": this.exemptReasonCd_keyword,
                "totalPiecesCount": this.totalPiecesCount,
                "totalPiecesCount.keyword": this.totalPiecesCount_keyword,
                "totalWeightLbs": this.totalWeightLbs,
                "totalWeightLbs.keyword": this.totalWeightLbs_keyword,
                "totalUsdAmount": this.totalUsdAmount,
                "totalUsdAmount.keyword": this.totalUsdAmount_keyword,
                "shipmentRefNbrs.refNbr": this.shipmentRefNbrs_refNbr,
                "shipmentRefNbrs.refNbr.keyword": this.shipmentRefNbrs_refNbr_keyword,
                "shipmentRefNbrs.typeCd": this.shipmentRefNbrs_typeCd,
                "shipmentRefNbrs.typeCd.keyword": this.shipmentRefNbrs_typeCd_keyword,
                "managementRemarkCount": this.managementRemarkCount,
                "managementRemarkCount.keyword": this.managementRemarkCount_keyword,
                "shipmentClaimsExistInd": this.shipmentClaimsExistInd,
                "shipmentClaimsExistInd.keyword": this.shipmentClaimsExistInd_keyword,
                "currentTrailerStatus": this.currentTrailerStatus,
                "currentTrailerStatus.keyword": this.currentTrailerStatus_keyword,
                "dockInstId": this.dockInstId,
                "dockInstId.keyword": this.dockInstId_keyword,
                "dockName": this.dockName,
                "dockName.keyword": this.dockName_keyword,
                "doorNbr": this.doorNbr,
                "doorNbr.keyword": this.doorNbr_keyword,
                "accessorials.accessorialCd": this.accessorials_accessorialCd,
                "accessorials.accessorialCd.keyword": this.accessorials_accessorialCd_keyword,
                "routeTypeCd": this.routeTypeCd,
                "routeTypeCd.keyword": this.routeTypeCd_keyword,
                "dispatchGroupId": this.dispatchGroupId,
                "dispatchGroupId.keyword": this.dispatchGroupId_keyword,
                "areaInstId": this.areaInstId,
                "areaInstId.keyword": this.areaInstId_keyword,
                "commodities.originalDescription": this.commodities_originalDescription,
                "commodities.originalDescription.keyword": this.commodities_originalDescription_keyword,
                "commodities.description": this.commodities_description,
                "commodities.description.keyword": this.commodities_description_keyword,
                "routeSuffix": this.routeSuffix,
                "routeSuffix.keyword": this.routeSuffix_keyword,
                "appointmentStatusCd": this.appointmentStatusCd,
                "appointmentStatusCd.keyword": this.appointmentStatusCd_keyword,
                "deliveryQualifierReasonCd": this.deliveryQualifierReasonCd,
                "deliveryQualifierReasonCd.keyword": this.deliveryQualifierReasonCd_keyword,
                "carrierId": this.carrierId,
                "carrierId.keyword": this.carrierId_keyword,
                "tenderId": this.tenderId,
                "tenderId.keyword": this.tenderId_keyword,
                "tenderDetailSequenceNbr": this.tenderDetailSequenceNbr,
                "tenderDetailSequenceNbr.keyword": this.tenderDetailSequenceNbr_keyword,
                "tenderDetailStatusCd": this.tenderDetailStatusCd,
                "tenderDetailStatusCd.keyword": this.tenderDetailStatusCd_keyword,
                "tenderDetailStatusMessage": this.tenderDetailStatusMessage,
                "tenderDetailStatusMessage.keyword": this.tenderDetailStatusMessage_keyword,
                "customsBonds.bondNbr": this.customsBonds_bondNbr,
                "customsBonds.bondNbr.keyword": this.customsBonds_bondNbr_keyword,
                "customsBonds.bondTypeCd": this.customsBonds_bondTypeCd,
                "customsBonds.bondTypeCd.keyword": this.customsBonds_bondTypeCd_keyword,
                "customsBonds.bondStatusCd": this.customsBonds_bondStatusCd,
                "customsBonds.bondStatusCd.keyword": this.customsBonds_bondStatusCd_keyword,
                "customsBonds.shipmentDirectionCd": this.customsBonds_shipmentDirectionCd,
                "customsBonds.shipmentDirectionCd.keyword": this.customsBonds_shipmentDirectionCd_keyword,
                "inbondInd": this.inbondInd,
                "inbondInd.keyword": this.inbondInd_keyword,
                "debtorTerms": this.debtorTerms,
                "debtorTerms.keyword": this.debtorTerms_keyword,
                "serviceDateTime": this.serviceDateTime,
                "serviceDateTime.keyword": this.serviceDateTime_keyword,
                "parentDeliveryQualifierCd": this.parentDeliveryQualifierCd,
                "parentDeliveryQualifierCd.keyword": this.parentDeliveryQualifierCd_keyword,
                "carrierName": this.carrierName,
                "carrierName.keyword": this.carrierName_keyword,
                "childProNbrs": this.childProNbrs,
                "childProNbrs.keyword": this.childProNbrs_keyword,
                "handlingUnitSplitInd": this.handlingUnitSplitInd,
                "handlingUnitSplitInd.keyword": this.handlingUnitSplitInd_keyword,
                "handlingUnitPartialInd": this.handlingUnitPartialInd,
                "handlingUnitPartialInd.keyword": this.handlingUnitPartialInd_keyword,
                "latestMovementExceptionTypeCd": this.latestMovementExceptionTypeCd,
                "latestMovementExceptionTypeCd.keyword": this.latestMovementExceptionTypeCd_keyword,
                "poorlyPackagedInd": this.poorlyPackagedInd,
                "poorlyPackagedInd.keyword": this.poorlyPackagedInd_keyword,
                "activityCd": this.activityCd,
                "activityCd.keyword": this.activityCd_keyword,
                "sourceCd": this.sourceCd,
                "sourceCd.keyword": this.sourceCd_keyword,
                "parentShipmentId.proNumber": this.parentShipmentId_proNumber,
                "parentShipmentId.proNumber.keyword": this.parentShipmentId_proNumber_keyword,
                "equipmentInstId": this.equipmentInstId,
                "equipmentInstId.keyword": this.equipmentInstId_keyword,
                "routeCategoryCd": this.routeCategoryCd,
                "routeCategoryCd.keyword": this.routeCategoryCd_keyword,
                "drvrCollectCd": this.drvrCollectCd,
                "drvrCollectCd.keyword": this.drvrCollectCd_keyword,
                "and": this.and,
                "not": this.not
            };
        };
    }
}
class DeliveryShipmentSearchFilterAnd {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.proNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.hostDestSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.hostDestSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleETA = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleETA_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailer = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailer_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServiceSummary_specialService = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServiceSummary_specialService_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_geoCoordinatesGeo = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_geoCoordinatesGeo_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suppRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suppRefNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryDate_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryFromTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryFromTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryToTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryToTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_statusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_statusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDateTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDateTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTimeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTimeTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.withinTdcCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.withinTdcCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_typeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_typeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_auditInfo_createdById = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_auditInfo_createdById_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_remarks = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_remarks_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByLastName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByLastName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByFirstName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByFirstName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.moverSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.moverSuffix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_originTerminalSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_originTerminalSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scannedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scannedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalPiecesCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalWeightLbs_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalUsdAmount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalUsdAmount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_refNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_refNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_typeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_typeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.doorNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.doorNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.accessorials_accessorialCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.accessorials_accessorialCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_originalDescription = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_originalDescription_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_description = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_description_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_shipmentDirectionCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_shipmentDirectionCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.debtorTerms = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.debtorTerms_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.childProNbrs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.childProNbrs_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentShipmentId_proNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentShipmentId_proNumber_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipmentInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.drvrCollectCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.drvrCollectCd_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "proNbr": this.proNbr,
                "proNbr.keyword": this.proNbr_keyword,
                "shipmentInstId": this.shipmentInstId,
                "shipmentInstId.keyword": this.shipmentInstId_keyword,
                "currentSicCd": this.currentSicCd,
                "currentSicCd.keyword": this.currentSicCd_keyword,
                "hostDestSicCd": this.hostDestSicCd,
                "hostDestSicCd.keyword": this.hostDestSicCd_keyword,
                "destinationSicCd": this.destinationSicCd,
                "destinationSicCd.keyword": this.destinationSicCd_keyword,
                "scheduleDestinationSicCd": this.scheduleDestinationSicCd,
                "scheduleDestinationSicCd.keyword": this.scheduleDestinationSicCd_keyword,
                "scheduleETA": this.scheduleETA,
                "scheduleETA.keyword": this.scheduleETA_keyword,
                "estimatedDeliveryDate": this.estimatedDeliveryDate,
                "estimatedDeliveryDate.keyword": this.estimatedDeliveryDate_keyword,
                "currentTrailer": this.currentTrailer,
                "currentTrailer.keyword": this.currentTrailer_keyword,
                "specialServiceSummary.specialService": this.specialServiceSummary_specialService,
                "specialServiceSummary.specialService.keyword": this.specialServiceSummary_specialService_keyword,
                "consignee.geoCoordinatesGeo": this.consignee_geoCoordinatesGeo,
                "consignee.geoCoordinatesGeo.keyword": this.consignee_geoCoordinatesGeo_keyword,
                "routeInstId": this.routeInstId,
                "routeInstId.keyword": this.routeInstId_keyword,
                "routePrefix": this.routePrefix,
                "routePrefix.keyword": this.routePrefix_keyword,
                "destSicEta": this.destSicEta,
                "destSicEta.keyword": this.destSicEta_keyword,
                "shipmentPndStatus": this.shipmentPndStatus,
                "shipmentPndStatus.keyword": this.shipmentPndStatus_keyword,
                "destSicEtaLastUpdtTmst": this.destSicEtaLastUpdtTmst,
                "destSicEtaLastUpdtTmst.keyword": this.destSicEtaLastUpdtTmst_keyword,
                "pickupDt": this.pickupDt,
                "pickupDt.keyword": this.pickupDt_keyword,
                "originSic": this.originSic,
                "originSic.keyword": this.originSic_keyword,
                "responsibleSic": this.responsibleSic,
                "responsibleSic.keyword": this.responsibleSic_keyword,
                "shipmentDeletedInd": this.shipmentDeletedInd,
                "shipmentDeletedInd.keyword": this.shipmentDeletedInd_keyword,
                "suppRefNbr": this.suppRefNbr,
                "suppRefNbr.keyword": this.suppRefNbr_keyword,
                "consignee.acctInstId": this.consignee_acctInstId,
                "consignee.acctInstId.keyword": this.consignee_acctInstId_keyword,
                "consignee.name1": this.consignee_name1,
                "consignee.name1.keyword": this.consignee_name1_keyword,
                "consignee.name2": this.consignee_name2,
                "consignee.name2.keyword": this.consignee_name2_keyword,
                "consignee.addressLine1": this.consignee_addressLine1,
                "consignee.addressLine1.keyword": this.consignee_addressLine1_keyword,
                "consignee.addressLine2": this.consignee_addressLine2,
                "consignee.addressLine2.keyword": this.consignee_addressLine2_keyword,
                "consignee.cityName": this.consignee_cityName,
                "consignee.cityName.keyword": this.consignee_cityName_keyword,
                "consignee.stateCd": this.consignee_stateCd,
                "consignee.stateCd.keyword": this.consignee_stateCd_keyword,
                "consignee.postalCd": this.consignee_postalCd,
                "consignee.postalCd.keyword": this.consignee_postalCd_keyword,
                "consignee.countryCd": this.consignee_countryCd,
                "consignee.countryCd.keyword": this.consignee_countryCd_keyword,
                "consignee.acctMadCd": this.consignee_acctMadCd,
                "consignee.acctMadCd.keyword": this.consignee_acctMadCd_keyword,
                "billInd": this.billInd,
                "billInd.keyword": this.billInd_keyword,
                "billTo.acctInstId": this.billTo_acctInstId,
                "billTo.acctInstId.keyword": this.billTo_acctInstId_keyword,
                "billTo.name1": this.billTo_name1,
                "billTo.name1.keyword": this.billTo_name1_keyword,
                "billTo.name2": this.billTo_name2,
                "billTo.name2.keyword": this.billTo_name2_keyword,
                "billTo.addressLine1": this.billTo_addressLine1,
                "billTo.addressLine1.keyword": this.billTo_addressLine1_keyword,
                "billTo.addressLine2": this.billTo_addressLine2,
                "billTo.addressLine2.keyword": this.billTo_addressLine2_keyword,
                "billTo.cityName": this.billTo_cityName,
                "billTo.cityName.keyword": this.billTo_cityName_keyword,
                "billTo.stateCd": this.billTo_stateCd,
                "billTo.stateCd.keyword": this.billTo_stateCd_keyword,
                "billTo.postalCd": this.billTo_postalCd,
                "billTo.postalCd.keyword": this.billTo_postalCd_keyword,
                "billTo.countryCd": this.billTo_countryCd,
                "billTo.countryCd.keyword": this.billTo_countryCd_keyword,
                "billTo.acctMadCd": this.billTo_acctMadCd,
                "billTo.acctMadCd.keyword": this.billTo_acctMadCd_keyword,
                "shipper.acctInstId": this.shipper_acctInstId,
                "shipper.acctInstId.keyword": this.shipper_acctInstId_keyword,
                "shipper.name1": this.shipper_name1,
                "shipper.name1.keyword": this.shipper_name1_keyword,
                "shipper.name2": this.shipper_name2,
                "shipper.name2.keyword": this.shipper_name2_keyword,
                "shipper.addressLine1": this.shipper_addressLine1,
                "shipper.addressLine1.keyword": this.shipper_addressLine1_keyword,
                "shipper.addressLine2": this.shipper_addressLine2,
                "shipper.addressLine2.keyword": this.shipper_addressLine2_keyword,
                "shipper.cityName": this.shipper_cityName,
                "shipper.cityName.keyword": this.shipper_cityName_keyword,
                "shipper.stateCd": this.shipper_stateCd,
                "shipper.stateCd.keyword": this.shipper_stateCd_keyword,
                "shipper.postalCd": this.shipper_postalCd,
                "shipper.postalCd.keyword": this.shipper_postalCd_keyword,
                "shipper.countryCd": this.shipper_countryCd,
                "shipper.countryCd.keyword": this.shipper_countryCd_keyword,
                "shipper.acctMadCd": this.shipper_acctMadCd,
                "shipper.acctMadCd.keyword": this.shipper_acctMadCd_keyword,
                "movementStatusCd": this.movementStatusCd,
                "movementStatusCd.keyword": this.movementStatusCd_keyword,
                "movementTypeCd": this.movementTypeCd,
                "movementTypeCd.keyword": this.movementTypeCd_keyword,
                "onExcludedTrailerInd": this.onExcludedTrailerInd,
                "onExcludedTrailerInd.keyword": this.onExcludedTrailerInd_keyword,
                "movementExceptionTypeCd": this.movementExceptionTypeCd,
                "movementExceptionTypeCd.keyword": this.movementExceptionTypeCd_keyword,
                "deliveryQualifierCd": this.deliveryQualifierCd,
                "deliveryQualifierCd.keyword": this.deliveryQualifierCd_keyword,
                "apptDtls.scheduledDeliveryDate": this.apptDtls_scheduledDeliveryDate,
                "apptDtls.scheduledDeliveryDate.keyword": this.apptDtls_scheduledDeliveryDate_keyword,
                "apptDtls.scheduledDeliveryFromTime": this.apptDtls_scheduledDeliveryFromTime,
                "apptDtls.scheduledDeliveryFromTime.keyword": this.apptDtls_scheduledDeliveryFromTime_keyword,
                "apptDtls.scheduledDeliveryToTime": this.apptDtls_scheduledDeliveryToTime,
                "apptDtls.scheduledDeliveryToTime.keyword": this.apptDtls_scheduledDeliveryToTime_keyword,
                "apptDtls.statusCd": this.apptDtls_statusCd,
                "apptDtls.statusCd.keyword": this.apptDtls_statusCd_keyword,
                "tdc.tdcDateTypeCd": this.tdc_tdcDateTypeCd,
                "tdc.tdcDateTypeCd.keyword": this.tdc_tdcDateTypeCd_keyword,
                "tdc.tdcDate1": this.tdc_tdcDate1,
                "tdc.tdcDate1.keyword": this.tdc_tdcDate1_keyword,
                "tdc.tdcDate2": this.tdc_tdcDate2,
                "tdc.tdcDate2.keyword": this.tdc_tdcDate2_keyword,
                "tdc.tdcTimeTypeCd": this.tdc_tdcTimeTypeCd,
                "tdc.tdcTimeTypeCd.keyword": this.tdc_tdcTimeTypeCd_keyword,
                "tdc.tdcTime1": this.tdc_tdcTime1,
                "tdc.tdcTime1.keyword": this.tdc_tdcTime1_keyword,
                "tdc.tdcTime2": this.tdc_tdcTime2,
                "tdc.tdcTime2.keyword": this.tdc_tdcTime2_keyword,
                "correctiveAction": this.correctiveAction,
                "correctiveAction.keyword": this.correctiveAction_keyword,
                "billClassCd": this.billClassCd,
                "billClassCd.keyword": this.billClassCd_keyword,
                "withinTdcCd": this.withinTdcCd,
                "withinTdcCd.keyword": this.withinTdcCd_keyword,
                "adminAsofDateTime": this.adminAsofDateTime,
                "adminAsofDateTime.keyword": this.adminAsofDateTime_keyword,
                "destinationArrivalDateTime": this.destinationArrivalDateTime,
                "destinationArrivalDateTime.keyword": this.destinationArrivalDateTime_keyword,
                "shipmentLocationSicCd": this.shipmentLocationSicCd,
                "shipmentLocationSicCd.keyword": this.shipmentLocationSicCd_keyword,
                "actualDeliveryDateTime": this.actualDeliveryDateTime,
                "actualDeliveryDateTime.keyword": this.actualDeliveryDateTime_keyword,
                "displayActualDeliveryDateTime": this.displayActualDeliveryDateTime,
                "displayActualDeliveryDateTime.keyword": this.displayActualDeliveryDateTime_keyword,
                "onTimeDeliveryCd": this.onTimeDeliveryCd,
                "onTimeDeliveryCd.keyword": this.onTimeDeliveryCd_keyword,
                "afterHoursPickupInd": this.afterHoursPickupInd,
                "afterHoursPickupInd.keyword": this.afterHoursPickupInd_keyword,
                "shipperRefNbr": this.shipperRefNbr,
                "shipperRefNbr.keyword": this.shipperRefNbr_keyword,
                "movementExcpActionReason": this.movementExcpActionReason,
                "movementExcpActionReason.keyword": this.movementExcpActionReason_keyword,
                "movementExcpActionControllableInd": this.movementExcpActionControllableInd,
                "movementExcpActionControllableInd.keyword": this.movementExcpActionControllableInd_keyword,
                "movementExceptions.typeCd": this.movementExceptions_typeCd,
                "movementExceptions.typeCd.keyword": this.movementExceptions_typeCd_keyword,
                "movementExceptions.auditInfo.createdById": this.movementExceptions_auditInfo_createdById,
                "movementExceptions.auditInfo.createdById.keyword": this.movementExceptions_auditInfo_createdById_keyword,
                "movementExceptions.remarks": this.movementExceptions_remarks,
                "movementExceptions.remarks.keyword": this.movementExceptions_remarks_keyword,
                "movementExceptions.createdByLastName": this.movementExceptions_createdByLastName,
                "movementExceptions.createdByLastName.keyword": this.movementExceptions_createdByLastName_keyword,
                "movementExceptions.createdByFirstName": this.movementExceptions_createdByFirstName,
                "movementExceptions.createdByFirstName.keyword": this.movementExceptions_createdByFirstName_keyword,
                "motorizedPiecesCount": this.motorizedPiecesCount,
                "motorizedPiecesCount.keyword": this.motorizedPiecesCount_keyword,
                "moverSuffix": this.moverSuffix,
                "moverSuffix.keyword": this.moverSuffix_keyword,
                "xdockExceptionTypeCdMaps.xdockExceptionTypeCd": this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd,
                "xdockExceptionTypeCdMaps.xdockExceptionTypeCd.keyword": this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd_keyword,
                "xdockExceptionTypeCdMaps.originTerminalSicCd": this.xdockExceptionTypeCdMaps_originTerminalSicCd,
                "xdockExceptionTypeCdMaps.originTerminalSicCd.keyword": this.xdockExceptionTypeCdMaps_originTerminalSicCd_keyword,
                "calculatedServiceDays": this.calculatedServiceDays,
                "calculatedServiceDays.keyword": this.calculatedServiceDays_keyword,
                "standardTransitDays": this.standardTransitDays,
                "standardTransitDays.keyword": this.standardTransitDays_keyword,
                "billDateTime": this.billDateTime,
                "billDateTime.keyword": this.billDateTime_keyword,
                "scannedInd": this.scannedInd,
                "scannedInd.keyword": this.scannedInd_keyword,
                "lstMvmtTmst": this.lstMvmtTmst,
                "lstMvmtTmst.keyword": this.lstMvmtTmst_keyword,
                "shipmentPurgedInd": this.shipmentPurgedInd,
                "shipmentPurgedInd.keyword": this.shipmentPurgedInd_keyword,
                "exemptReasonCd": this.exemptReasonCd,
                "exemptReasonCd.keyword": this.exemptReasonCd_keyword,
                "totalPiecesCount": this.totalPiecesCount,
                "totalPiecesCount.keyword": this.totalPiecesCount_keyword,
                "totalWeightLbs": this.totalWeightLbs,
                "totalWeightLbs.keyword": this.totalWeightLbs_keyword,
                "totalUsdAmount": this.totalUsdAmount,
                "totalUsdAmount.keyword": this.totalUsdAmount_keyword,
                "shipmentRefNbrs.refNbr": this.shipmentRefNbrs_refNbr,
                "shipmentRefNbrs.refNbr.keyword": this.shipmentRefNbrs_refNbr_keyword,
                "shipmentRefNbrs.typeCd": this.shipmentRefNbrs_typeCd,
                "shipmentRefNbrs.typeCd.keyword": this.shipmentRefNbrs_typeCd_keyword,
                "managementRemarkCount": this.managementRemarkCount,
                "managementRemarkCount.keyword": this.managementRemarkCount_keyword,
                "shipmentClaimsExistInd": this.shipmentClaimsExistInd,
                "shipmentClaimsExistInd.keyword": this.shipmentClaimsExistInd_keyword,
                "currentTrailerStatus": this.currentTrailerStatus,
                "currentTrailerStatus.keyword": this.currentTrailerStatus_keyword,
                "dockInstId": this.dockInstId,
                "dockInstId.keyword": this.dockInstId_keyword,
                "dockName": this.dockName,
                "dockName.keyword": this.dockName_keyword,
                "doorNbr": this.doorNbr,
                "doorNbr.keyword": this.doorNbr_keyword,
                "accessorials.accessorialCd": this.accessorials_accessorialCd,
                "accessorials.accessorialCd.keyword": this.accessorials_accessorialCd_keyword,
                "routeTypeCd": this.routeTypeCd,
                "routeTypeCd.keyword": this.routeTypeCd_keyword,
                "dispatchGroupId": this.dispatchGroupId,
                "dispatchGroupId.keyword": this.dispatchGroupId_keyword,
                "areaInstId": this.areaInstId,
                "areaInstId.keyword": this.areaInstId_keyword,
                "commodities.originalDescription": this.commodities_originalDescription,
                "commodities.originalDescription.keyword": this.commodities_originalDescription_keyword,
                "commodities.description": this.commodities_description,
                "commodities.description.keyword": this.commodities_description_keyword,
                "routeSuffix": this.routeSuffix,
                "routeSuffix.keyword": this.routeSuffix_keyword,
                "appointmentStatusCd": this.appointmentStatusCd,
                "appointmentStatusCd.keyword": this.appointmentStatusCd_keyword,
                "deliveryQualifierReasonCd": this.deliveryQualifierReasonCd,
                "deliveryQualifierReasonCd.keyword": this.deliveryQualifierReasonCd_keyword,
                "carrierId": this.carrierId,
                "carrierId.keyword": this.carrierId_keyword,
                "tenderId": this.tenderId,
                "tenderId.keyword": this.tenderId_keyword,
                "tenderDetailSequenceNbr": this.tenderDetailSequenceNbr,
                "tenderDetailSequenceNbr.keyword": this.tenderDetailSequenceNbr_keyword,
                "tenderDetailStatusCd": this.tenderDetailStatusCd,
                "tenderDetailStatusCd.keyword": this.tenderDetailStatusCd_keyword,
                "tenderDetailStatusMessage": this.tenderDetailStatusMessage,
                "tenderDetailStatusMessage.keyword": this.tenderDetailStatusMessage_keyword,
                "customsBonds.bondNbr": this.customsBonds_bondNbr,
                "customsBonds.bondNbr.keyword": this.customsBonds_bondNbr_keyword,
                "customsBonds.bondTypeCd": this.customsBonds_bondTypeCd,
                "customsBonds.bondTypeCd.keyword": this.customsBonds_bondTypeCd_keyword,
                "customsBonds.bondStatusCd": this.customsBonds_bondStatusCd,
                "customsBonds.bondStatusCd.keyword": this.customsBonds_bondStatusCd_keyword,
                "customsBonds.shipmentDirectionCd": this.customsBonds_shipmentDirectionCd,
                "customsBonds.shipmentDirectionCd.keyword": this.customsBonds_shipmentDirectionCd_keyword,
                "inbondInd": this.inbondInd,
                "inbondInd.keyword": this.inbondInd_keyword,
                "debtorTerms": this.debtorTerms,
                "debtorTerms.keyword": this.debtorTerms_keyword,
                "serviceDateTime": this.serviceDateTime,
                "serviceDateTime.keyword": this.serviceDateTime_keyword,
                "parentDeliveryQualifierCd": this.parentDeliveryQualifierCd,
                "parentDeliveryQualifierCd.keyword": this.parentDeliveryQualifierCd_keyword,
                "carrierName": this.carrierName,
                "carrierName.keyword": this.carrierName_keyword,
                "childProNbrs": this.childProNbrs,
                "childProNbrs.keyword": this.childProNbrs_keyword,
                "handlingUnitSplitInd": this.handlingUnitSplitInd,
                "handlingUnitSplitInd.keyword": this.handlingUnitSplitInd_keyword,
                "handlingUnitPartialInd": this.handlingUnitPartialInd,
                "handlingUnitPartialInd.keyword": this.handlingUnitPartialInd_keyword,
                "latestMovementExceptionTypeCd": this.latestMovementExceptionTypeCd,
                "latestMovementExceptionTypeCd.keyword": this.latestMovementExceptionTypeCd_keyword,
                "poorlyPackagedInd": this.poorlyPackagedInd,
                "poorlyPackagedInd.keyword": this.poorlyPackagedInd_keyword,
                "activityCd": this.activityCd,
                "activityCd.keyword": this.activityCd_keyword,
                "sourceCd": this.sourceCd,
                "sourceCd.keyword": this.sourceCd_keyword,
                "parentShipmentId.proNumber": this.parentShipmentId_proNumber,
                "parentShipmentId.proNumber.keyword": this.parentShipmentId_proNumber_keyword,
                "equipmentInstId": this.equipmentInstId,
                "equipmentInstId.keyword": this.equipmentInstId_keyword,
                "routeCategoryCd": this.routeCategoryCd,
                "routeCategoryCd.keyword": this.routeCategoryCd_keyword,
                "drvrCollectCd": this.drvrCollectCd,
                "drvrCollectCd.keyword": this.drvrCollectCd_keyword,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
class DeliveryShipmentSearchFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.q = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.b = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.proNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.hostDestSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.hostDestSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleETA = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scheduleETA_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailer = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailer_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServiceSummary_specialService = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.specialServiceSummary_specialService_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_geoCoordinatesGeo = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_geoCoordinatesGeo_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routePrefix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEta_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPndStatus_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destSicEtaLastUpdtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pickupDt_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.originSic_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.responsibleSic_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentDeletedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suppRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.suppRefNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.consignee_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billTo_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_name2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_addressLine2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_cityName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_cityName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_stateCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_stateCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_postalCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_postalCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_countryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_countryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctMadCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipper_acctMadCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryDate = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryDate_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryFromTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryFromTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryToTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_scheduledDeliveryToTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_statusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.apptDtls_statusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDateTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDateTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcDate2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTimeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTimeTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tdc_tdcTime2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.correctiveAction_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billClassCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.withinTdcCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.withinTdcCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.actualDeliveryDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.displayActualDeliveryDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.onTimeDeliveryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.afterHoursPickupInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipperRefNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionReason_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExcpActionControllableInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_typeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_typeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_auditInfo_createdById = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_auditInfo_createdById_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_remarks = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_remarks_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByLastName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByLastName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByFirstName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.movementExceptions_createdByFirstName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.moverSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.moverSuffix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_originTerminalSicCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.xdockExceptionTypeCdMaps_originTerminalSicCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.standardTransitDays_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.billDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scannedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.scannedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.lstMvmtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentPurgedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.exemptReasonCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalPiecesCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalWeightLbs_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalUsdAmount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.totalUsdAmount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_refNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_refNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_typeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentRefNbrs_typeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.managementRemarkCount_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.shipmentClaimsExistInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.currentTrailerStatus_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dockName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.doorNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.doorNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.accessorials_accessorialCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.accessorials_accessorialCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.dispatchGroupId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.areaInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_originalDescription = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_originalDescription_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_description = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.commodities_description_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeSuffix_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.deliveryQualifierReasonCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailSequenceNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.tenderDetailStatusMessage_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondNbr = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondNbr_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondStatusCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_bondStatusCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_shipmentDirectionCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customsBonds_shipmentDirectionCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.inbondInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.debtorTerms = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.debtorTerms_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceDateTime = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.serviceDateTime_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentDeliveryQualifierCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.carrierName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.childProNbrs = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.childProNbrs_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.latestMovementExceptionTypeCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.activityCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.sourceCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentShipmentId_proNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.parentShipmentId_proNumber_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.equipmentInstId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.routeCategoryCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.drvrCollectCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.drvrCollectCd_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "q": this.q,
                "b": this.b,
                "proNbr": this.proNbr,
                "proNbr.keyword": this.proNbr_keyword,
                "shipmentInstId": this.shipmentInstId,
                "shipmentInstId.keyword": this.shipmentInstId_keyword,
                "currentSicCd": this.currentSicCd,
                "currentSicCd.keyword": this.currentSicCd_keyword,
                "hostDestSicCd": this.hostDestSicCd,
                "hostDestSicCd.keyword": this.hostDestSicCd_keyword,
                "destinationSicCd": this.destinationSicCd,
                "destinationSicCd.keyword": this.destinationSicCd_keyword,
                "scheduleDestinationSicCd": this.scheduleDestinationSicCd,
                "scheduleDestinationSicCd.keyword": this.scheduleDestinationSicCd_keyword,
                "scheduleETA": this.scheduleETA,
                "scheduleETA.keyword": this.scheduleETA_keyword,
                "estimatedDeliveryDate": this.estimatedDeliveryDate,
                "estimatedDeliveryDate.keyword": this.estimatedDeliveryDate_keyword,
                "currentTrailer": this.currentTrailer,
                "currentTrailer.keyword": this.currentTrailer_keyword,
                "specialServiceSummary.specialService": this.specialServiceSummary_specialService,
                "specialServiceSummary.specialService.keyword": this.specialServiceSummary_specialService_keyword,
                "consignee.geoCoordinatesGeo": this.consignee_geoCoordinatesGeo,
                "consignee.geoCoordinatesGeo.keyword": this.consignee_geoCoordinatesGeo_keyword,
                "routeInstId": this.routeInstId,
                "routeInstId.keyword": this.routeInstId_keyword,
                "routePrefix": this.routePrefix,
                "routePrefix.keyword": this.routePrefix_keyword,
                "destSicEta": this.destSicEta,
                "destSicEta.keyword": this.destSicEta_keyword,
                "shipmentPndStatus": this.shipmentPndStatus,
                "shipmentPndStatus.keyword": this.shipmentPndStatus_keyword,
                "destSicEtaLastUpdtTmst": this.destSicEtaLastUpdtTmst,
                "destSicEtaLastUpdtTmst.keyword": this.destSicEtaLastUpdtTmst_keyword,
                "pickupDt": this.pickupDt,
                "pickupDt.keyword": this.pickupDt_keyword,
                "originSic": this.originSic,
                "originSic.keyword": this.originSic_keyword,
                "responsibleSic": this.responsibleSic,
                "responsibleSic.keyword": this.responsibleSic_keyword,
                "shipmentDeletedInd": this.shipmentDeletedInd,
                "shipmentDeletedInd.keyword": this.shipmentDeletedInd_keyword,
                "suppRefNbr": this.suppRefNbr,
                "suppRefNbr.keyword": this.suppRefNbr_keyword,
                "consignee.acctInstId": this.consignee_acctInstId,
                "consignee.acctInstId.keyword": this.consignee_acctInstId_keyword,
                "consignee.name1": this.consignee_name1,
                "consignee.name1.keyword": this.consignee_name1_keyword,
                "consignee.name2": this.consignee_name2,
                "consignee.name2.keyword": this.consignee_name2_keyword,
                "consignee.addressLine1": this.consignee_addressLine1,
                "consignee.addressLine1.keyword": this.consignee_addressLine1_keyword,
                "consignee.addressLine2": this.consignee_addressLine2,
                "consignee.addressLine2.keyword": this.consignee_addressLine2_keyword,
                "consignee.cityName": this.consignee_cityName,
                "consignee.cityName.keyword": this.consignee_cityName_keyword,
                "consignee.stateCd": this.consignee_stateCd,
                "consignee.stateCd.keyword": this.consignee_stateCd_keyword,
                "consignee.postalCd": this.consignee_postalCd,
                "consignee.postalCd.keyword": this.consignee_postalCd_keyword,
                "consignee.countryCd": this.consignee_countryCd,
                "consignee.countryCd.keyword": this.consignee_countryCd_keyword,
                "consignee.acctMadCd": this.consignee_acctMadCd,
                "consignee.acctMadCd.keyword": this.consignee_acctMadCd_keyword,
                "billInd": this.billInd,
                "billInd.keyword": this.billInd_keyword,
                "billTo.acctInstId": this.billTo_acctInstId,
                "billTo.acctInstId.keyword": this.billTo_acctInstId_keyword,
                "billTo.name1": this.billTo_name1,
                "billTo.name1.keyword": this.billTo_name1_keyword,
                "billTo.name2": this.billTo_name2,
                "billTo.name2.keyword": this.billTo_name2_keyword,
                "billTo.addressLine1": this.billTo_addressLine1,
                "billTo.addressLine1.keyword": this.billTo_addressLine1_keyword,
                "billTo.addressLine2": this.billTo_addressLine2,
                "billTo.addressLine2.keyword": this.billTo_addressLine2_keyword,
                "billTo.cityName": this.billTo_cityName,
                "billTo.cityName.keyword": this.billTo_cityName_keyword,
                "billTo.stateCd": this.billTo_stateCd,
                "billTo.stateCd.keyword": this.billTo_stateCd_keyword,
                "billTo.postalCd": this.billTo_postalCd,
                "billTo.postalCd.keyword": this.billTo_postalCd_keyword,
                "billTo.countryCd": this.billTo_countryCd,
                "billTo.countryCd.keyword": this.billTo_countryCd_keyword,
                "billTo.acctMadCd": this.billTo_acctMadCd,
                "billTo.acctMadCd.keyword": this.billTo_acctMadCd_keyword,
                "shipper.acctInstId": this.shipper_acctInstId,
                "shipper.acctInstId.keyword": this.shipper_acctInstId_keyword,
                "shipper.name1": this.shipper_name1,
                "shipper.name1.keyword": this.shipper_name1_keyword,
                "shipper.name2": this.shipper_name2,
                "shipper.name2.keyword": this.shipper_name2_keyword,
                "shipper.addressLine1": this.shipper_addressLine1,
                "shipper.addressLine1.keyword": this.shipper_addressLine1_keyword,
                "shipper.addressLine2": this.shipper_addressLine2,
                "shipper.addressLine2.keyword": this.shipper_addressLine2_keyword,
                "shipper.cityName": this.shipper_cityName,
                "shipper.cityName.keyword": this.shipper_cityName_keyword,
                "shipper.stateCd": this.shipper_stateCd,
                "shipper.stateCd.keyword": this.shipper_stateCd_keyword,
                "shipper.postalCd": this.shipper_postalCd,
                "shipper.postalCd.keyword": this.shipper_postalCd_keyword,
                "shipper.countryCd": this.shipper_countryCd,
                "shipper.countryCd.keyword": this.shipper_countryCd_keyword,
                "shipper.acctMadCd": this.shipper_acctMadCd,
                "shipper.acctMadCd.keyword": this.shipper_acctMadCd_keyword,
                "movementStatusCd": this.movementStatusCd,
                "movementStatusCd.keyword": this.movementStatusCd_keyword,
                "movementTypeCd": this.movementTypeCd,
                "movementTypeCd.keyword": this.movementTypeCd_keyword,
                "onExcludedTrailerInd": this.onExcludedTrailerInd,
                "onExcludedTrailerInd.keyword": this.onExcludedTrailerInd_keyword,
                "movementExceptionTypeCd": this.movementExceptionTypeCd,
                "movementExceptionTypeCd.keyword": this.movementExceptionTypeCd_keyword,
                "deliveryQualifierCd": this.deliveryQualifierCd,
                "deliveryQualifierCd.keyword": this.deliveryQualifierCd_keyword,
                "apptDtls.scheduledDeliveryDate": this.apptDtls_scheduledDeliveryDate,
                "apptDtls.scheduledDeliveryDate.keyword": this.apptDtls_scheduledDeliveryDate_keyword,
                "apptDtls.scheduledDeliveryFromTime": this.apptDtls_scheduledDeliveryFromTime,
                "apptDtls.scheduledDeliveryFromTime.keyword": this.apptDtls_scheduledDeliveryFromTime_keyword,
                "apptDtls.scheduledDeliveryToTime": this.apptDtls_scheduledDeliveryToTime,
                "apptDtls.scheduledDeliveryToTime.keyword": this.apptDtls_scheduledDeliveryToTime_keyword,
                "apptDtls.statusCd": this.apptDtls_statusCd,
                "apptDtls.statusCd.keyword": this.apptDtls_statusCd_keyword,
                "tdc.tdcDateTypeCd": this.tdc_tdcDateTypeCd,
                "tdc.tdcDateTypeCd.keyword": this.tdc_tdcDateTypeCd_keyword,
                "tdc.tdcDate1": this.tdc_tdcDate1,
                "tdc.tdcDate1.keyword": this.tdc_tdcDate1_keyword,
                "tdc.tdcDate2": this.tdc_tdcDate2,
                "tdc.tdcDate2.keyword": this.tdc_tdcDate2_keyword,
                "tdc.tdcTimeTypeCd": this.tdc_tdcTimeTypeCd,
                "tdc.tdcTimeTypeCd.keyword": this.tdc_tdcTimeTypeCd_keyword,
                "tdc.tdcTime1": this.tdc_tdcTime1,
                "tdc.tdcTime1.keyword": this.tdc_tdcTime1_keyword,
                "tdc.tdcTime2": this.tdc_tdcTime2,
                "tdc.tdcTime2.keyword": this.tdc_tdcTime2_keyword,
                "correctiveAction": this.correctiveAction,
                "correctiveAction.keyword": this.correctiveAction_keyword,
                "billClassCd": this.billClassCd,
                "billClassCd.keyword": this.billClassCd_keyword,
                "withinTdcCd": this.withinTdcCd,
                "withinTdcCd.keyword": this.withinTdcCd_keyword,
                "adminAsofDateTime": this.adminAsofDateTime,
                "adminAsofDateTime.keyword": this.adminAsofDateTime_keyword,
                "destinationArrivalDateTime": this.destinationArrivalDateTime,
                "destinationArrivalDateTime.keyword": this.destinationArrivalDateTime_keyword,
                "shipmentLocationSicCd": this.shipmentLocationSicCd,
                "shipmentLocationSicCd.keyword": this.shipmentLocationSicCd_keyword,
                "actualDeliveryDateTime": this.actualDeliveryDateTime,
                "actualDeliveryDateTime.keyword": this.actualDeliveryDateTime_keyword,
                "displayActualDeliveryDateTime": this.displayActualDeliveryDateTime,
                "displayActualDeliveryDateTime.keyword": this.displayActualDeliveryDateTime_keyword,
                "onTimeDeliveryCd": this.onTimeDeliveryCd,
                "onTimeDeliveryCd.keyword": this.onTimeDeliveryCd_keyword,
                "afterHoursPickupInd": this.afterHoursPickupInd,
                "afterHoursPickupInd.keyword": this.afterHoursPickupInd_keyword,
                "shipperRefNbr": this.shipperRefNbr,
                "shipperRefNbr.keyword": this.shipperRefNbr_keyword,
                "movementExcpActionReason": this.movementExcpActionReason,
                "movementExcpActionReason.keyword": this.movementExcpActionReason_keyword,
                "movementExcpActionControllableInd": this.movementExcpActionControllableInd,
                "movementExcpActionControllableInd.keyword": this.movementExcpActionControllableInd_keyword,
                "movementExceptions.typeCd": this.movementExceptions_typeCd,
                "movementExceptions.typeCd.keyword": this.movementExceptions_typeCd_keyword,
                "movementExceptions.auditInfo.createdById": this.movementExceptions_auditInfo_createdById,
                "movementExceptions.auditInfo.createdById.keyword": this.movementExceptions_auditInfo_createdById_keyword,
                "movementExceptions.remarks": this.movementExceptions_remarks,
                "movementExceptions.remarks.keyword": this.movementExceptions_remarks_keyword,
                "movementExceptions.createdByLastName": this.movementExceptions_createdByLastName,
                "movementExceptions.createdByLastName.keyword": this.movementExceptions_createdByLastName_keyword,
                "movementExceptions.createdByFirstName": this.movementExceptions_createdByFirstName,
                "movementExceptions.createdByFirstName.keyword": this.movementExceptions_createdByFirstName_keyword,
                "motorizedPiecesCount": this.motorizedPiecesCount,
                "motorizedPiecesCount.keyword": this.motorizedPiecesCount_keyword,
                "moverSuffix": this.moverSuffix,
                "moverSuffix.keyword": this.moverSuffix_keyword,
                "xdockExceptionTypeCdMaps.xdockExceptionTypeCd": this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd,
                "xdockExceptionTypeCdMaps.xdockExceptionTypeCd.keyword": this.xdockExceptionTypeCdMaps_xdockExceptionTypeCd_keyword,
                "xdockExceptionTypeCdMaps.originTerminalSicCd": this.xdockExceptionTypeCdMaps_originTerminalSicCd,
                "xdockExceptionTypeCdMaps.originTerminalSicCd.keyword": this.xdockExceptionTypeCdMaps_originTerminalSicCd_keyword,
                "calculatedServiceDays": this.calculatedServiceDays,
                "calculatedServiceDays.keyword": this.calculatedServiceDays_keyword,
                "standardTransitDays": this.standardTransitDays,
                "standardTransitDays.keyword": this.standardTransitDays_keyword,
                "billDateTime": this.billDateTime,
                "billDateTime.keyword": this.billDateTime_keyword,
                "scannedInd": this.scannedInd,
                "scannedInd.keyword": this.scannedInd_keyword,
                "lstMvmtTmst": this.lstMvmtTmst,
                "lstMvmtTmst.keyword": this.lstMvmtTmst_keyword,
                "shipmentPurgedInd": this.shipmentPurgedInd,
                "shipmentPurgedInd.keyword": this.shipmentPurgedInd_keyword,
                "exemptReasonCd": this.exemptReasonCd,
                "exemptReasonCd.keyword": this.exemptReasonCd_keyword,
                "totalPiecesCount": this.totalPiecesCount,
                "totalPiecesCount.keyword": this.totalPiecesCount_keyword,
                "totalWeightLbs": this.totalWeightLbs,
                "totalWeightLbs.keyword": this.totalWeightLbs_keyword,
                "totalUsdAmount": this.totalUsdAmount,
                "totalUsdAmount.keyword": this.totalUsdAmount_keyword,
                "shipmentRefNbrs.refNbr": this.shipmentRefNbrs_refNbr,
                "shipmentRefNbrs.refNbr.keyword": this.shipmentRefNbrs_refNbr_keyword,
                "shipmentRefNbrs.typeCd": this.shipmentRefNbrs_typeCd,
                "shipmentRefNbrs.typeCd.keyword": this.shipmentRefNbrs_typeCd_keyword,
                "managementRemarkCount": this.managementRemarkCount,
                "managementRemarkCount.keyword": this.managementRemarkCount_keyword,
                "shipmentClaimsExistInd": this.shipmentClaimsExistInd,
                "shipmentClaimsExistInd.keyword": this.shipmentClaimsExistInd_keyword,
                "currentTrailerStatus": this.currentTrailerStatus,
                "currentTrailerStatus.keyword": this.currentTrailerStatus_keyword,
                "dockInstId": this.dockInstId,
                "dockInstId.keyword": this.dockInstId_keyword,
                "dockName": this.dockName,
                "dockName.keyword": this.dockName_keyword,
                "doorNbr": this.doorNbr,
                "doorNbr.keyword": this.doorNbr_keyword,
                "accessorials.accessorialCd": this.accessorials_accessorialCd,
                "accessorials.accessorialCd.keyword": this.accessorials_accessorialCd_keyword,
                "routeTypeCd": this.routeTypeCd,
                "routeTypeCd.keyword": this.routeTypeCd_keyword,
                "dispatchGroupId": this.dispatchGroupId,
                "dispatchGroupId.keyword": this.dispatchGroupId_keyword,
                "areaInstId": this.areaInstId,
                "areaInstId.keyword": this.areaInstId_keyword,
                "commodities.originalDescription": this.commodities_originalDescription,
                "commodities.originalDescription.keyword": this.commodities_originalDescription_keyword,
                "commodities.description": this.commodities_description,
                "commodities.description.keyword": this.commodities_description_keyword,
                "routeSuffix": this.routeSuffix,
                "routeSuffix.keyword": this.routeSuffix_keyword,
                "appointmentStatusCd": this.appointmentStatusCd,
                "appointmentStatusCd.keyword": this.appointmentStatusCd_keyword,
                "deliveryQualifierReasonCd": this.deliveryQualifierReasonCd,
                "deliveryQualifierReasonCd.keyword": this.deliveryQualifierReasonCd_keyword,
                "carrierId": this.carrierId,
                "carrierId.keyword": this.carrierId_keyword,
                "tenderId": this.tenderId,
                "tenderId.keyword": this.tenderId_keyword,
                "tenderDetailSequenceNbr": this.tenderDetailSequenceNbr,
                "tenderDetailSequenceNbr.keyword": this.tenderDetailSequenceNbr_keyword,
                "tenderDetailStatusCd": this.tenderDetailStatusCd,
                "tenderDetailStatusCd.keyword": this.tenderDetailStatusCd_keyword,
                "tenderDetailStatusMessage": this.tenderDetailStatusMessage,
                "tenderDetailStatusMessage.keyword": this.tenderDetailStatusMessage_keyword,
                "customsBonds.bondNbr": this.customsBonds_bondNbr,
                "customsBonds.bondNbr.keyword": this.customsBonds_bondNbr_keyword,
                "customsBonds.bondTypeCd": this.customsBonds_bondTypeCd,
                "customsBonds.bondTypeCd.keyword": this.customsBonds_bondTypeCd_keyword,
                "customsBonds.bondStatusCd": this.customsBonds_bondStatusCd,
                "customsBonds.bondStatusCd.keyword": this.customsBonds_bondStatusCd_keyword,
                "customsBonds.shipmentDirectionCd": this.customsBonds_shipmentDirectionCd,
                "customsBonds.shipmentDirectionCd.keyword": this.customsBonds_shipmentDirectionCd_keyword,
                "inbondInd": this.inbondInd,
                "inbondInd.keyword": this.inbondInd_keyword,
                "debtorTerms": this.debtorTerms,
                "debtorTerms.keyword": this.debtorTerms_keyword,
                "serviceDateTime": this.serviceDateTime,
                "serviceDateTime.keyword": this.serviceDateTime_keyword,
                "parentDeliveryQualifierCd": this.parentDeliveryQualifierCd,
                "parentDeliveryQualifierCd.keyword": this.parentDeliveryQualifierCd_keyword,
                "carrierName": this.carrierName,
                "carrierName.keyword": this.carrierName_keyword,
                "childProNbrs": this.childProNbrs,
                "childProNbrs.keyword": this.childProNbrs_keyword,
                "handlingUnitSplitInd": this.handlingUnitSplitInd,
                "handlingUnitSplitInd.keyword": this.handlingUnitSplitInd_keyword,
                "handlingUnitPartialInd": this.handlingUnitPartialInd,
                "handlingUnitPartialInd.keyword": this.handlingUnitPartialInd_keyword,
                "latestMovementExceptionTypeCd": this.latestMovementExceptionTypeCd,
                "latestMovementExceptionTypeCd.keyword": this.latestMovementExceptionTypeCd_keyword,
                "poorlyPackagedInd": this.poorlyPackagedInd,
                "poorlyPackagedInd.keyword": this.poorlyPackagedInd_keyword,
                "activityCd": this.activityCd,
                "activityCd.keyword": this.activityCd_keyword,
                "sourceCd": this.sourceCd,
                "sourceCd.keyword": this.sourceCd_keyword,
                "parentShipmentId.proNumber": this.parentShipmentId_proNumber,
                "parentShipmentId.proNumber.keyword": this.parentShipmentId_proNumber_keyword,
                "equipmentInstId": this.equipmentInstId,
                "equipmentInstId.keyword": this.equipmentInstId_keyword,
                "routeCategoryCd": this.routeCategoryCd,
                "routeCategoryCd.keyword": this.routeCategoryCd_keyword,
                "drvrCollectCd": this.drvrCollectCd,
                "drvrCollectCd.keyword": this.drvrCollectCd_keyword,
                "and": this.and,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
/**
 * Path parameters
 */
class GetElasticDocumentDeliveryShipmentPath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.shipmentInstId = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentDeliveryShipmentQuery {
    constructor() {
        /**
         * Source filter
         * Required: false
         * Array: false
         */
        this._source = undefined;
        /**
         * Source include filter
         * Required: false
         * Array: false
         */
        this._source_includes = undefined;
        /**
         * Source exclude filter
         * Required: false
         * Array: false
         */
        this._source_excludes = undefined;
    }
}
/**
 * Response from getElasticDocument.
 */
class GetElasticDocumentDeliveryShipmentResp {
    constructor() {
        /**
         * The record if found
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentDeliveryShipmentCountQuery {
    constructor() {
        /**
         * Query filter
         * Required: false
         * Array: false
         */
        this.q = undefined;
    }
}
/**
 * Response from getElasticDocumentCount.
 */
class GetElasticDocumentDeliveryShipmentCountResp {
    constructor() {
        /**
         * The count of the documents
         * Required: false
         * Array: false
         */
        this.count = undefined;
    }
}
/**
 * Request message to update information for DeliveryShipment.
 */
class UpdateElasticDocumentDeliveryShipmentRqst {
    constructor() {
        /**
         * The record to upsert in elastic
         * Required: false
         * Array: false
         */
        this.doc = undefined;
        /**
         * Indicate if an upsert operation should be executed if the document was not found
         * Required: false
         * Array: false
         */
        this.doc_as_upsert = undefined;
    }
}
/**
 * Path parameters
 */
class UpdateElasticDocumentDeliveryShipmentPath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.shipmentInstId = undefined;
    }
}
/**
 * Response from updateElasticDocument.
 */
class UpdateElasticDocumentDeliveryShipmentResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Response from bulkUpdateElasticDocument.
 */
class BulkUpdateElasticDocumentDeliveryShipmentResp {
    constructor() {
        /**
         * The time it took to process this request
         * Required: false
         * Array: false
         */
        this.took = undefined;
        /**
         * Indicates if there are any errors
         * Required: false
         * Array: false
         */
        this.errors = undefined;
        /**
         * Results of the bulk operations
         * Required: false
         * Array: true
         */
        this.items = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteElasticDocumentDeliveryShipmentPath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.shipmentInstId = undefined;
    }
}
/**
 * Request message to delete information for DeliveryShipment.
 */
class DeleteElasticDocumentDeliveryShipmentResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Request message to search information for unassigned-deliveries.
 */
class SearchUnassignedDeliveriesRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for unassigned-deliveries.
 */
class SearchUnassignedDeliveriesResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for unassigned-deliveries.
 */
class ExportUnassignedDeliveriesRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUnassignedDeliveriesRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUnassignedDeliveriesResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUnassignedDeliveriesRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUnassignedDeliveriesRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUnassignedDeliveriesRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUnassignedDeliveriesResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for unassigned-deliveries-with-unmapped.
 */
class SearchUnassignedDeliveriesWithUnmappedRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for unassigned-deliveries-with-unmapped.
 */
class SearchUnassignedDeliveriesWithUnmappedResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for unassigned-deliveries-with-unmapped.
 */
class ExportUnassignedDeliveriesWithUnmappedRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUnassignedDeliveriesWithUnmappedRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUnassignedDeliveriesWithUnmappedResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUnassignedDeliveriesWithUnmappedRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUnassignedDeliveriesWithUnmappedRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUnassignedDeliveriesWithUnmappedRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUnassignedDeliveriesWithUnmappedResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for unassigned-deliveries-plan-by-trailer.
 */
class SearchUnassignedDeliveriesPlanByTrailerRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for unassigned-deliveries-plan-by-trailer.
 */
class SearchUnassignedDeliveriesPlanByTrailerResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for unassigned-deliveries-plan-by-trailer.
 */
class ExportUnassignedDeliveriesPlanByTrailerRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUnassignedDeliveriesPlanByTrailerRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUnassignedDeliveriesPlanByTrailerResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUnassignedDeliveriesPlanByTrailerRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUnassignedDeliveriesPlanByTrailerRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUnassignedDeliveriesPlanByTrailerRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUnassignedDeliveriesPlanByTrailerResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for yet-to-be-dispatched-deliveries.
 */
class SearchYetToBeDispatchedDeliveriesRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for yet-to-be-dispatched-deliveries.
 */
class SearchYetToBeDispatchedDeliveriesResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for yet-to-be-dispatched-deliveries.
 */
class ExportYetToBeDispatchedDeliveriesRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterYetToBeDispatchedDeliveriesRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterYetToBeDispatchedDeliveriesResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterYetToBeDispatchedDeliveriesRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedYetToBeDispatchedDeliveriesRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsYetToBeDispatchedDeliveriesRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsYetToBeDispatchedDeliveriesResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for unbilled-shipments.
 */
class SearchUnbilledShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for unbilled-shipments.
 */
class SearchUnbilledShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for unbilled-shipments.
 */
class ExportUnbilledShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUnbilledShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUnbilledShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUnbilledShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUnbilledShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUnbilledShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUnbilledShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for delivery-shipments.
 */
class SearchDeliveryShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for delivery-shipments.
 */
class SearchDeliveryShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for delivery-shipments.
 */
class ExportDeliveryShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterDeliveryShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterDeliveryShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterDeliveryShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedDeliveryShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsDeliveryShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsDeliveryShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for unmapped-shipments.
 */
class SearchUnmappedShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for unmapped-shipments.
 */
class SearchUnmappedShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for unmapped-shipments.
 */
class ExportUnmappedShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUnmappedShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUnmappedShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUnmappedShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUnmappedShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUnmappedShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUnmappedShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for admin-route-shipments.
 */
class SearchAdminRouteShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for admin-route-shipments.
 */
class SearchAdminRouteShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for admin-route-shipments.
 */
class ExportAdminRouteShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterAdminRouteShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterAdminRouteShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterAdminRouteShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedAdminRouteShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsAdminRouteShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsAdminRouteShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for undeleted-shipments.
 */
class SearchUndeletedShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for undeleted-shipments.
 */
class SearchUndeletedShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for undeleted-shipments.
 */
class ExportUndeletedShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUndeletedShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUndeletedShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUndeletedShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUndeletedShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUndeletedShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUndeletedShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for past-due-shipments.
 */
class SearchPastDueShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for past-due-shipments.
 */
class SearchPastDueShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for past-due-shipments.
 */
class ExportPastDueShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterPastDueShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterPastDueShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterPastDueShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedPastDueShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsPastDueShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsPastDueShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for unpurged-shipments.
 */
class SearchUnpurgedShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for unpurged-shipments.
 */
class SearchUnpurgedShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for unpurged-shipments.
 */
class ExportUnpurgedShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterUnpurgedShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterUnpurgedShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterUnpurgedShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedUnpurgedShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsUnpurgedShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsUnpurgedShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for cross-dock-trailer-shipments.
 */
class SearchCrossDockTrailerShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for cross-dock-trailer-shipments.
 */
class SearchCrossDockTrailerShipmentsResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for cross-dock-trailer-shipments.
 */
class ExportCrossDockTrailerShipmentsRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterCrossDockTrailerShipmentsRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterCrossDockTrailerShipmentsResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterCrossDockTrailerShipmentsRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedCrossDockTrailerShipmentsRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsCrossDockTrailerShipmentsRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsCrossDockTrailerShipmentsResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 * Request message to search information for delivery-shipment-unfiltered.
 */
class SearchDeliveryShipmentUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for delivery-shipment-unfiltered.
 */
class SearchDeliveryShipmentUnfilteredResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for delivery-shipment-unfiltered.
 */
class ExportDeliveryShipmentUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterDeliveryShipmentUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterDeliveryShipmentUnfilteredResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterDeliveryShipmentUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedDeliveryShipmentUnfilteredRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsDeliveryShipmentUnfilteredRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsDeliveryShipmentUnfilteredResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
class EnsembleStateSearchFilterNot {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
    }
}
class EnsembleStateSearchFilterOr {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "and": this.and,
                "not": this.not
            };
        };
    }
}
class EnsembleStateSearchFilterAnd {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
class EnsembleStateSearchFilter {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.q = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.b = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.instId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_messageId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_queueName_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_name_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_desc_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_eventTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_strtTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_endTmst_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_statCd_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_correlationId_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey1_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey2_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey3_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey4_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_businessKey5_keyword = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.ensembleState_host_keyword = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.and = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.or = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.not = undefined;
        this.toJSON = function () {
            return {
                "q": this.q,
                "b": this.b,
                "instId": this.instId,
                "instId.keyword": this.instId_keyword,
                "ensembleState.messageId": this.ensembleState_messageId,
                "ensembleState.messageId.keyword": this.ensembleState_messageId_keyword,
                "ensembleState.queueName": this.ensembleState_queueName,
                "ensembleState.queueName.keyword": this.ensembleState_queueName_keyword,
                "ensembleState.name": this.ensembleState_name,
                "ensembleState.name.keyword": this.ensembleState_name_keyword,
                "ensembleState.desc": this.ensembleState_desc,
                "ensembleState.desc.keyword": this.ensembleState_desc_keyword,
                "ensembleState.eventTmst": this.ensembleState_eventTmst,
                "ensembleState.eventTmst.keyword": this.ensembleState_eventTmst_keyword,
                "ensembleState.strtTmst": this.ensembleState_strtTmst,
                "ensembleState.strtTmst.keyword": this.ensembleState_strtTmst_keyword,
                "ensembleState.endTmst": this.ensembleState_endTmst,
                "ensembleState.endTmst.keyword": this.ensembleState_endTmst_keyword,
                "ensembleState.statCd": this.ensembleState_statCd,
                "ensembleState.statCd.keyword": this.ensembleState_statCd_keyword,
                "ensembleState.correlationId": this.ensembleState_correlationId,
                "ensembleState.correlationId.keyword": this.ensembleState_correlationId_keyword,
                "ensembleState.businessKey1": this.ensembleState_businessKey1,
                "ensembleState.businessKey1.keyword": this.ensembleState_businessKey1_keyword,
                "ensembleState.businessKey2": this.ensembleState_businessKey2,
                "ensembleState.businessKey2.keyword": this.ensembleState_businessKey2_keyword,
                "ensembleState.businessKey3": this.ensembleState_businessKey3,
                "ensembleState.businessKey3.keyword": this.ensembleState_businessKey3_keyword,
                "ensembleState.businessKey4": this.ensembleState_businessKey4,
                "ensembleState.businessKey4.keyword": this.ensembleState_businessKey4_keyword,
                "ensembleState.businessKey5": this.ensembleState_businessKey5,
                "ensembleState.businessKey5.keyword": this.ensembleState_businessKey5_keyword,
                "ensembleState.host": this.ensembleState_host,
                "ensembleState.host.keyword": this.ensembleState_host_keyword,
                "and": this.and,
                "or": this.or,
                "not": this.not
            };
        };
    }
}
/**
 * Path parameters
 */
class GetElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentEnsembleStateQuery {
    constructor() {
        /**
         * Source filter
         * Required: false
         * Array: false
         */
        this._source = undefined;
        /**
         * Source include filter
         * Required: false
         * Array: false
         */
        this._source_includes = undefined;
        /**
         * Source exclude filter
         * Required: false
         * Array: false
         */
        this._source_excludes = undefined;
    }
}
/**
 * Response from getElasticDocument.
 */
class GetElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The record if found
         * Required: false
         * Array: false
         */
        this.record = undefined;
    }
}
/**
 * Query parameters
 */
class GetElasticDocumentEnsembleStateCountQuery {
    constructor() {
        /**
         * Query filter
         * Required: false
         * Array: false
         */
        this.q = undefined;
    }
}
/**
 * Response from getElasticDocumentCount.
 */
class GetElasticDocumentEnsembleStateCountResp {
    constructor() {
        /**
         * The count of the documents
         * Required: false
         * Array: false
         */
        this.count = undefined;
    }
}
/**
 * Request message to update information for EnsembleState.
 */
class UpdateElasticDocumentEnsembleStateRqst {
    constructor() {
        /**
         * The record to upsert in elastic
         * Required: false
         * Array: false
         */
        this.doc = undefined;
        /**
         * Indicate if an upsert operation should be executed if the document was not found
         * Required: false
         * Array: false
         */
        this.doc_as_upsert = undefined;
    }
}
/**
 * Path parameters
 */
class UpdateElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Response from updateElasticDocument.
 */
class UpdateElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Response from bulkUpdateElasticDocument.
 */
class BulkUpdateElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The time it took to process this request
         * Required: false
         * Array: false
         */
        this.took = undefined;
        /**
         * Indicates if there are any errors
         * Required: false
         * Array: false
         */
        this.errors = undefined;
        /**
         * Results of the bulk operations
         * Required: false
         * Array: true
         */
        this.items = undefined;
    }
}
/**
 * Path parameters
 */
class DeleteElasticDocumentEnsembleStatePath {
    constructor() {
        /**
         * Uniquely identify the elastic search document
         * Required: true
         * Array: false
         */
        this.instId = undefined;
    }
}
/**
 * Request message to delete information for EnsembleState.
 */
class DeleteElasticDocumentEnsembleStateResp {
    constructor() {
        /**
         * The result of the operation
         * Required: false
         * Array: false
         */
        this.result = undefined;
    }
}
/**
 * Request message to search information for ensemble-state-unfiltered.
 */
class SearchEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.sortExpressions = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: true
         */
        this.excludeFields = undefined;
        /**
         * Query for searching
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response to the request to search information for ensemble-state-unfiltered.
 */
class SearchEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Required: false
         * Array: false
         */
        this.header = undefined;
        /**
         * Search results
         * Required: false
         * Array: true
         */
        this.result = undefined;
    }
}
/**
 * Request message to export information for ensemble-state-unfiltered.
 */
class ExportEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Required: false
         * Array: true
         */
        this.fields = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.fileType = undefined;
        /**
         * Query for exporting
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Request for register filter
 */
class RegisterFilterEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The filter to register
         * Required: false
         * Array: false
         */
        this.filter = undefined;
    }
}
/**
 * Response for register filter
 */
class RegisterFilterEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Hash for the filter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
        /**
         * Duration for the API execution
         * Required: false
         * Array: false
         */
        this.duration = undefined;
        /**
         * Number of registrations created
         * Required: false
         * Array: false
         */
        this.numberOfRegistrations = undefined;
    }
}
/**
 * Request for register
 */
class UnregisterFilterEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Id of the  user. For future use.
         * Required: false
         * Array: false
         */
        this.userId = undefined;
        /**
         * Connection token
         * Required: false
         * Array: false
         */
        this.connectionToken = undefined;
        /**
         * The hash of the filter to unregister. Filter has is returned by registerFilter
         * Required: false
         * Array: false
         */
        this.filterHash = undefined;
    }
}
/**
 * Request for changes feed
 */
class ChangesFeedEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * A list of document Ids
         * Required: false
         * Array: true
         */
        this.changedDocumentIds = undefined;
        /**
         * A list of changed documents
         * Required: false
         * Array: true
         */
        this.changedDocuments = undefined;
    }
}
/**
 *
 * 				Request for list aggregations
 * 				To aggregate data by any field you need to specify field properties on top level and subfield properties on any deeper level of aggregation.
 *
 */
class ListAggregationsEnsembleStateUnfilteredRqst {
    constructor() {
        /**
         * Filter for aggregations
         * Required: false
         * Array: false
         */
        this.filter = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
    }
}
/**
 * Response for list aggregations
 */
class ListAggregationsEnsembleStateUnfilteredResp {
    constructor() {
        /**
         * Aggregations
         * Required: false
         * Array: false
         */
        this.aggregations = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.resultCount = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageNumber = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.pageSize = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.duration = undefined;
    }
}
/**
 *
 */
class Contact {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.keyNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactLastName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactFirstName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactTitle = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactAreaCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhone = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhoneExt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.city = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.state = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.country = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip6 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip4RestUs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.email = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.faxAreaCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.faxNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Remarks associated with the request to upsert the contact.
         * Required: false
         * Array: false
         */
        this.remarks = undefined;
        /**
         * Free form descriptive text about a CIS contact.
         * Required: false
         * Array: false
         */
        this.notes1Txt = undefined;
        /**
         * Additional free form descriptive text about a CIS contact.
         * Required: false
         * Array: false
         */
        this.notes2Txt = undefined;
    }
}
/**
 * Address can be either a street address or a PO Box address.
 * In rare cases a street address might host more than one business (customer location).
 * PO BOX address is always in context of a customer location
 * Therefore, PO Box address is provided only when a customer location has a single function, namely Bill-To function.
 *
 * In theory a customer location can have both a street address and also might use a PO Box at nearby facility (Post office). However at present when po box is recorded, there is no requirement to know the street address.
 * Note to designer: If such a requirement were to arise in the future, the current relationship between Address and cust_location 1:Many will need to change to Many:Many resulting in a new address-cust_loc relationship entity.
 *
 *
 */
class CustomerAddress {
    constructor() {
        /**
         * System Generated Number to uniquely identify an Address. This is internal ID.
         * Required: false
         * Array: false
         */
        this.addressId = undefined;
        /**
         * Address Line 1
         * Examples:
         * PO BOX 182655
         * 150 S LOMBARD RD
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * City, town Name
         * Example
         * PORTLAND
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         * US and Mexico have states, Canada has provinces.
         * The two char abbreviateions are recorded here.
         * Currently LTL only records US, CA and MX parties. Hence the State codes are two char.
         *
         * Example
         * OR - Oregon USA
         * TM - Tamaulipas Mexico
         * AB - Alberta
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * This records ZIP codes also called PIN codes etc.
         * US and Mexico use 5 digit codes, Canada uses 6 character codes.
         * Example
         * T2H2K4 - Canada
         * 97209 - USA
         * 55540 - Mexico
         *
         * There could be common zip codes numbers between US and Mexico.
         * Required: false
         * Array: false
         */
        this.zipCd = undefined;
        /**
         * USPS uses additional numbers as extension to the 5 digit zip codes
         * Required: false
         * Array: false
         */
        this.zip4Cd = undefined;
        /**
         * Country Code is 2 character Standard country codes.
         * US -USA
         * MX - Mexico
         * CA - Canada
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         * This is system generated ID for a record that describes geo area as defined in OPS Application.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Latitude of the site that is defined by the address
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * Longitude of the site that is defined by the address
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Whether the address was validated by Postal software
         * Valid Values
         * Y - Address was validated and adjusted as required.
         * N - This address is not yet validated
         * Required: false
         * Array: false
         */
        this.postalCertifiedInd = undefined;
        /**
         * Is this a POBOX address or a street address
         * Y - This is a PO Box and not a streeet address
         * N - This is not a PO Box address, but is a street address
         * Required: false
         * Array: false
         */
        this.poBoxInd = undefined;
        /**
         * Abbreviated address used by CIS to New CIS conversion program to identify same address with some different white space and editing chars
         * Required: false
         * Array: false
         */
        this.addressKey = undefined;
        /**
         * A same value is assigned to CORRELATION id of various rows from different tables that form a single business transactions. In other words, this groups multipple rows across various tables for a single business transaction..
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cstRequestId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of CustomerAddress. Contains the details at the time on an event. It also provides the previous values if an update occured
 * Address can be either a street address or a PO Box address.
 * In rare cases a street address might host more than one business (customer location).
 * PO BOX address is always in context of a customer location
 * Therefore, PO Box address is provided only when a customer location has a single function, namely Bill-To function.
 *
 * In theory a customer location can have both a street address and also might use a PO Box at nearby facility (Post office). However at present when po box is recorded, there is no requirement to know the street address.
 * Note to designer: If such a requirement were to arise in the future, the current relationship between Address and cust_location 1:Many will need to change to Many:Many resulting in a new address-cust_loc relationship entity.
 *
 *
 */
class CustomerAddressSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * System Generated Number to uniquely identify an Address. This is internal ID.
         * Required: false
         * Array: false
         */
        this.addressId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * There are three functions that can happen at a customer liocation.
 * P - Freight is either picked up or delivered. AKA Pkup_dlvr
 * B - Invoices are sent to this location. AKA Bill-to
 * C - Corporate function done at this location (e.g. pricing negotiation, Sales related). AKA Corp
 */
class CustomerLocationFunction {
    constructor() {
        /**
         * The numbers are 10digits or longer.
         * Reason: Other applications have data that refer to legacy CIS number, which are 9 digit random number.
         * As the legacy apps are rewritten/modified to use this new CIS application, they will start storing new CIS number namely CUST_LOCATION_FUNC_ID.
         * At some point, legacy CIS will cease to exist.
         * As other applications will have historic data, in order for them to clearly differentiate between CIS and new CIS IDS, we start numbering this ID with 10 digit numbers.
         * Required: false
         * Array: false
         */
        this.customerLocationFuncId = undefined;
        /**
         * MAD code is meanigful code that is used by users to identify a cust location function.
         * Required: false
         * Array: false
         */
        this.madCd = undefined;
        /**
         * This is a unique ID in legacy CIS database. It identifes unique function aka CIS_BILL2_LOC, CIS_PKUP_DLVR_LOC, and CIS_CUST_HRCHY.
         * Required: false
         * Array: false
         */
        this.legacyCisCustomerNbr = undefined;
        /**
         * There are three functions that can happen at a customer liocation.
         * P - Freight is either picked up or delivered. AKA Pkup_dlvr
         * B - Invoices are sent to this location. AKA Bill-to
         * C - Corporate function done at this location (e.g. pricing negotiation, Sales related). AKA Corp
         * Required: false
         * Array: false
         */
        this.functionCd = undefined;
        /**
         * Identifies the Cust Location at which the functions are done. This is foreign key to Cust Location
         * Required: false
         * Array: false
         */
        this.customerLocationId = undefined;
        /**
         * Valid values are:
         *
         * ACTIVE,
         * INACTIVE (CIS do_not_match_ind)
         * EXPIRED  - Also see EXPR_RSN_CD
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * This indicates that the customer produces a certain amount of revenue. Valid values are A, B, C or M. The exact rules for calculating this are still being discussed.
         * A > $5,000 per month O/B or I/B but not combined, seasonally adj'd
         * B > $3,000 per month O/B + I/B combined, seasonally adjusted
         * C Potential to become a 'B' account within 6 months, but not automatically measured by system
         * M All others
         * Required: false
         * Array: false
         */
        this.acctClassCd = undefined;
        /**
         * Date that the account class value was assigned
         * Required: false
         * Array: false
         */
        this.acctClassDate = undefined;
        /**
         * This is a customer supplied number that they use to identify the location. The text will include a label for the field. (eg. Store 3000 or Cisco 1100)
         * Required: false
         * Array: false
         */
        this.customerSuplyNbr = undefined;
        /**
         * This is number assigned by Dun and bradstreet. It is used to access commercial data for the customer for credit rating, analytics etc.
         * Required: false
         * Array: false
         */
        this.dbCustomerNbr = undefined;
        /**
         * 4225.  Per published list, this stands for : Standard Industrial Code
         * Required: false
         * Array: false
         */
        this.standardIndlCd = undefined;
        /**
         * Credit status is used to determine, if customer needs to pay upfront at the time freight is picked up / delivered.
         *
         * VVOLUNTARY
         * $CASH
         * CCREDIT
         * BBANKRUPT
         * NNO CREDIT
         * Required: false
         * Array: false
         */
        this.creditStatusCd = undefined;
        /**
         * Is this a government customer
         * Valid Values:
         * Y - Yes
         * N - No
         * Required: false
         * Array: false
         */
        this.govtCustomerInd = undefined;
        /**
         * This is the authorization number that is returned from the credit service
         *
         * BNKxxx means BANKRUPT_IND=Y
         * Required: false
         * Array: false
         */
        this.authrzNbr = undefined;
        /**
         * This is the credit limit given from the credit service.
         * Example
         * 5000 - This means customer is worthy of receiving up to $5000 credit.
         * Required: false
         * Array: false
         */
        this.authrzLimitAmount = undefined;
        /**
         * Application ensures all Billt-to grouped under one service rcpnt have party2 name and Address same as the one defined in the CST_SVC_RCPNT.
         * That also means, Bill-to with service rcpnt cannot be updated to use a different party2 or Address. If that is required, the bill-to must be either grouped under a different svc rcpnt that has the new party2, address or the svc rcpnt should be wiped out from the bill-to
         * Required: false
         * Array: false
         */
        this.serviceRecipientNbr = undefined;
        /**
         * The date when this function stops being used for new transactions.
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         * There are two reasons for Expiration. In either case, this function might be hidden from certain views to prevent it from being used or unnecessarily cluttering the display.
         *
         * MERGED   - The record of this function has been merged into another function. The function into which this function is merged is recorded in the field MERGED_TO_CUST_LOC_FUNC_ID. This is done probably because there was a duplicate function record set up.
         * INACTIVE - The function is no more in use and hence logically deleted.
         * Required: false
         * Array: false
         */
        this.expiryReasonCd = undefined;
        /**
         * This is the CUST_LOCATION_FUNC_ID of the function record into which this function record is merged. Note the EXPR_RSN_CD in this case will be MERGE, and EXPR_DT will be set to the date when the merge occured. This is a foreign key for a self referencing relationship
         * Required: false
         * Array: false
         */
        this.mergedToCustomerLocationFuncId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentCorpCustomerLocationFuncId = undefined;
        /**
         * A same value is assigned to CORRELATION id of various rows from different tables that form a single business transactions. In other words, this groups multipple rows across various tables for a single business transaction..
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * These comments added by users (generally coding dept) when a row is added, updated. Typically it describes the reason for update
         * Required: false
         * Array: false
         */
        this.lastUpdateRemarks = undefined;
        /**
         * Specifies number of days after which an invoice is due.  The due date is generally calculated by adding days associated with the payment term to Pickup Date. E.g. NET30 means 30 days after the pickup date. Added 5/22/19 by Cathy
         * Required: false
         * Array: false
         */
        this.paymentTermCd = undefined;
        /**
         * govt_cust_ind =A set AFFILIATE_IND = Y, NULL or N AFFILIATE_IND = N
         * Required: false
         * Array: false
         */
        this.affiliateInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cstRequestId = undefined;
        /**
         * The Tax ID will be used to capture an Employer Identification Number,
         * 		which is a 9 digit Federal Tax Identification number that the IRS assigns to businesses in the United States
         * Required: false
         * Array: false
         */
        this.taxId = undefined;
        /**
         * C - Credit Inherited Values from the immediate parent (Credit Status, Credit Limit, Credit Authorization Number, and Payment Terms)
         * D - Dun and Bradstreet Inherited Values from the immediate parent (DUNS Number and Standard Industrial Code)
         * B - Both values are inherited from the immediate parent
         * NULL - no inheritance
         * Required: false
         * Array: false
         */
        this.inheritCd = undefined;
        /**
         * Indicates if account is national or not.
         * N for National Account
         * L for Local Account
         * I for Inherit from Parent (All Bill-To and PND function records will be set to Inherit.)
         * Required: false
         * Array: false
         */
        this.acctTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bankruptInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates if pricing is attached to the customer account.  If the user doesn't exist in the Pricing API, then a message will be returned stating theat the information isn't available.  If the service fails, then a message saying so will be returned.  If the user does exist for Pricing, then true or false will be returned as a string.  If this is set to true, then the customer account can't be deleted or expired.  Also, when adding a customer, the request may want to have pricing attached. Therefore, the coder needs to work with pricing to ensure there is pricing attached to the location. This indicator will help the coder know when pricing has been set up correctly.
         * Required: false
         * Array: false
         */
        this.pricingInd = undefined;
        /**
         * This is the estimated monthly revenue of the customer.
         * Required: false
         * Array: false
         */
        this.estMonthlyRevNbr = undefined;
        /**
         * Represents the top parent of the customer for which we are looking.
         * Required: false
         * Array: false
         */
        this.customerLocationFunctionTopParent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerLocationFunctionParent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerLocationFunctionMergedTo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.customerLocationFunctionMergedFrom = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.customerLocationFunctionChildren = undefined;
    }
}
/**
 * Holds a snapshot of CustomerLocationFunction. Contains the details at the time on an event. It also provides the previous values if an update occured
 * There are three functions that can happen at a customer liocation.
 * P - Freight is either picked up or delivered. AKA Pkup_dlvr
 * B - Invoices are sent to this location. AKA Bill-to
 * C - Corporate function done at this location (e.g. pricing negotiation, Sales related). AKA Corp
 */
class CustomerLocationFunctionSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * The numbers are 10digits or longer.
         * Reason: Other applications have data that refer to legacy CIS number, which are 9 digit random number.
         * As the legacy apps are rewritten/modified to use this new CIS application, they will start storing new CIS number namely CUST_LOCATION_FUNC_ID.
         * At some point, legacy CIS will cease to exist.
         * As other applications will have historic data, in order for them to clearly differentiate between CIS and new CIS IDS, we start numbering this ID with 10 digit numbers.
         * Required: false
         * Array: false
         */
        this.customerLocationFuncId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class CustomerOperationsNote {
    constructor() {
        /**
         * Implied FK to CIS_INTRFC_ACCT
         * Required: false
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * Identifies type of note this is.
         *
         * VALID VALUES
         * PICKUP_INSTRUCTIONS
         * DLVRY_INSTRUCTIONS
         * OPERATIONS_NOTE
         * Required: false
         * Array: false
         */
        this.noteTypeCd = undefined;
        /**
         * Part of unique identifier.
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * Text of note
         * Required: false
         * Array: false
         */
        this.note = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is assigned to various rows from different tables that form a single database transactions.
         * Multiple database transactions could make up one business transaction, which is indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single database transaction.
         * Typically this is used to publish a message to a queue for consumption or a workflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class CustomerOperationsProfile {
    constructor() {
        /**
         * Implied FK to CIS_INTRFC_ACCT
         * Required: false
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * Maximum age of trailer customer will allow on site
         *
         * .
         * Required: false
         * Array: false
         */
        this.trailerAgeYear = undefined;
        /**
         * Predicted dwell time, in minutes, for deliveries.
         * Required: false
         * Array: false
         */
        this.deliveryDwellTimeMinutes = undefined;
        /**
         * Predicted dwell time, in minutes, for pickups.
         * Required: false
         * Array: false
         */
        this.pickupDwellTimeMinutes = undefined;
        /**
         * Predicted dwell time, in minutes, for equipment stops (hooks and spots)
         * Required: false
         * Array: false
         */
        this.equipmentDwellTimeMinutes = undefined;
        /**
         * The type of location
         *
         * Valid Values:
         * BusinessCommercialBUSINESS/COMMERCIAL
         * DistributionCenterDISTRIBUTION CENTER
         * GroceryWareHouseGROCERY WAREHOUSE
         * ShoppingMallSHOPPING MALL/STRIP MALL
         * UnversityCollegeUNIVERSITY/COLLEGE
         * ConstructionSiteCONSTRUCTION/UTILITY SITES: CONSTRUCTION SITE
         * ConstructionUtilitySiteCONSTRUCTION/UTILITY SITES: UTILITY SITE
         * GovtMineGOVERNMENT SITES: MINE
         * GovtGasOilFieldGOVERNMENT SITES: NATURAL GAS OR OIL FIELD
         * GovtPostOfficeGOVERNMENT SITES: POST OFFICE
         * GovtMilitaryGOVERNMENT SITES: MILITARY
         * GovtPrisonCorrectionGOVERNMENT SITES: PRISON/CORRECTION FACILITY
         * GovtAirportGOVERNMENT SITES: AIRPORT
         * GovtOtherGOVERNMENT SITES: OTHER GOVERNMENT SITES
         * PierContainerFreightStationPIERS, WHARFS, CFS: CONTAINER FREIGHT STATION
         * PierWharfPIERS, WHARFS, CFS: PIER OR WHARF
         * ResidentialHomeRESIDENTIAL: RESIDENCE
         * ResidentialApartmentRESIDENTIAL: APARTMENT
         * ResidentialPlaceofWorshipRESIDENTIAL: PLACE OF WORSHIP
         * ResidentialSchoolRESIDENTIAL: SCHOOL (K-12)
         * ResidentialCampRESIDENTIAL: CAMP
         * ResidentialFarmRESIDENTIAL: FARM
         * ResidentialPublicStorageRESIDENTIAL: PUBLIC STORAGE FACILITY
         * ResidentialBusinessRESIDENTIAL: BUSINESS OPERATING WITHIN A PRIVATE RESIDENCE
         * ExhibitionConvCtrEXHIBITION SITES: EXHIBITION SITE OR CONVENTION CENTER
         * Required: false
         * Array: false
         */
        this.locationTypeCd = undefined;
        /**
         * Identifies who loads/unloads trailers at a customer location.
         *
         * VALID VALUES
         * CUSTOMER - Customer loads and or unloads our trailers
         * DSR - DSR loads or unloads our trailers
         * Required: false
         * Array: false
         */
        this.loadUnloadCd = undefined;
        /**
         * Indicates the size of the trailer required at this site.
         *
         * VALID VALUES
         * STRAIGHT - Straight truck/box truck required
         * PUP - Pup or smaller required
         * VANOK - an OK even in a geographic area otherwise restricted to PUPs
         * Required: false
         * Array: false
         */
        this.equipmentSizeCd = undefined;
        /**
         * Y indicates that the customer receives freight that can be stacked in city operations.
         * Required: false
         * Array: false
         */
        this.deliveryStackableInd = undefined;
        /**
         * Y indicates that the customer ships freight that can be stacked in city operations.
         * Required: false
         * Array: false
         */
        this.pickupStackableInd = undefined;
        /**
         * Indicates that the customer requires their freight to be trapped for full trailer or specific day delivery.
         * Required: false
         * Array: false
         */
        this.trapRequiredInd = undefined;
        /**
         * Indicates if customer has a forklift or not.
         * Required: false
         * Array: false
         */
        this.forkliftInd = undefined;
        /**
         * Indicates if a liftgate is required at this site.
         * Required: false
         * Array: false
         */
        this.liftgateRequiredInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is assigned to various rows from different tables that form a single database transactions.
         * Multiple database transactions could make up one business transaction, which is indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single database transaction.
         * Typically this is used to publish a message to a queue for consumption or a workflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates Customer has a freight loading/unloading dock.  Added 06/22/2020
         * Required: false
         * Array: false
         */
        this.hasDockInd = undefined;
        /**
         * Notice of Arrival requirement for Customer.  Added 06/22/2020
         * Required: false
         * Array: false
         */
        this.noaInd = undefined;
        /**
         * Customer ships high volume shipments.  Added 06/22/2020
         * Required: false
         * Array: false
         */
        this.volumeShipperInd = undefined;
        /**
         * Customer requests a Delivery Manifest in lieu of delivery receipt.  Added 06/22/2020
         * Required: false
         * Array: false
         */
        this.dmanInLieuOfDoorInd = undefined;
        /**
         * Indicates Customer requires an appointment for shipment deliveries.  Added 08/18/2020 LISG-6893
         *
         * Usage:
         *
         *
         * In order entry, this can be used to auto-prompt Biller, to add the appointment accessorial for the service.
         *
         * In PnD, helps planner or auto routing to put these shipments on an appointment route.
         *
         * Improves route optimization and revenue related to appointment services.
         * Required: false
         * Array: false
         */
        this.appointmentRequiredInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of CustomerOperationsProfile. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class CustomerOperationsProfileSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Implied FK to CIS_INTRFC_ACCT
         * Required: false
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class CustomerOperationsSchedule {
    constructor() {
        /**
         * Implied FK to CIS_INTRFC_ACCT
         * Required: false
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * A schedule can be defined for these types of operations.
         *
         * VALID VALUES
         * PICKUP_SHIPPING - Pickup / Shipping hours
         * DELIVERY_RECEIVING - Delivery / Receiving hours
         * GENERAL_OPERATIONS - General operations hours
         * AUTO_GEN - Daily Pickup Hours; used to auto-generate daily pickup requests
         * Required: false
         * Array: false
         */
        this.operationTypeCd = undefined;
        /**
         * Effective start date of this record
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * If there are schedule hour differences on differnet days, then this number allows them to be defined.  For example, Schedule 1 defines hours for days 1-5, and Schedule 2 defines hours for day 6.
         * Required: false
         * Array: false
         */
        this.scheduleNbr = undefined;
        /**
         * Effect end date of this record.
         * Required: false
         * Array: false
         */
        this.endDate = undefined;
        /**
         * Start time (customer's local start time).
         * Required: false
         * Array: false
         */
        this.startTime = undefined;
        /**
         * End time
         * Required: false
         * Array: false
         */
        this.endTime = undefined;
        /**
         * Schedule is followed on Sundays
         * Required: false
         * Array: false
         */
        this.sundayInd = undefined;
        /**
         * Schedule is followed on Mondays
         * Required: false
         * Array: false
         */
        this.mondayInd = undefined;
        /**
         * Schedule is followed on Tuesdays
         * Required: false
         * Array: false
         */
        this.tuesdayInd = undefined;
        /**
         * Schedule is followed on Wednesdays
         * Required: false
         * Array: false
         */
        this.wednesdayInd = undefined;
        /**
         * Schedule is followed on Thursdays
         * Required: false
         * Array: false
         */
        this.thursdayInd = undefined;
        /**
         * Schedule is followed on Fridays
         * Required: false
         * Array: false
         */
        this.fridayInd = undefined;
        /**
         * Schedule is followed on Saturdays
         * Required: false
         * Array: false
         */
        this.saturdayInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is assigned to various rows from different tables that form a single database transactions.
         * Multiple database transactions could make up one business transaction, which is indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single database transaction.
         * Typically this is used to publish a message to a queue for consumption or a workflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates the type of pickup request to be generated.  Added 10/18/2021  LISG-15342
         *
         * Values:
         * NORMAL - Normal pickup
         * SPOT_EMPTY - Spot Empty Trailer
         * HOOK_LOADED - Hook a Loaded Trailer
         * HOOK_EMPTY - Hook an Empty Trailer
         * Required: false
         * Array: false
         */
        this.custOperationsPickupTypeCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of CustomerOperationsSchedule. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class CustomerOperationsScheduleSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Implied FK to CIS_INTRFC_ACCT
         * Required: false
         * Array: false
         */
        this.customerAcctId = undefined;
        /**
         * A schedule can be defined for these types of operations.
         *
         * VALID VALUES
         * PICKUP_SHIPPING - Pickup / Shipping hours
         * DELIVERY_RECEIVING - Delivery / Receiving hours
         * GENERAL_OPERATIONS - General operations hours
         * AUTO_GEN - Daily Pickup Hours; used to auto-generate daily pickup requests
         * Required: false
         * Array: false
         */
        this.operationTypeCd = undefined;
        /**
         * Effective start date of this record
         * Required: false
         * Array: false
         */
        this.startDate = undefined;
        /**
         * If there are schedule hour differences on differnet days, then this number allows them to be defined.  For example, Schedule 1 defines hours for days 1-5, and Schedule 2 defines hours for day 6.
         * Required: false
         * Array: false
         */
        this.scheduleNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class CustomerProfile {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileCodeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileCodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileNumInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileValueNum = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileValue = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DeliveryNotification {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.elecNotificationCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callerId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhoneNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhoneExtensionNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryFromTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryToTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeRefNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduledReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.internalNote = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fbdsNote = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The unique identifier of the shipment.
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
    }
}
/**
 *
 */
class DeliveryShipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleIdNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastMovementDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerCurrentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerEtaDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.splitInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationLiftgateInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inbondInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.purgeBlockInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.adminAsofDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationArrivalDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.timeDateCriticalInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationNotifyInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceSummaryInd = undefined;
        /**
         * The trailer on which this shipment is currently located
         * Required: false
         * Array: false
         */
        this.currentTrailer = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loosePiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pupVolumePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodPoisonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.drvrCollectCd = undefined;
        /**
         * Desc: Identifies if the
         *
         * shipment is on a trailer that has been excluded from the destination SICs inbound plan
         *
         * (Y) or not (N).
         *
         * 05/01/2019 Added by Cathy Doucet as per Bill Muchmore (PND Tower)
         * Required: false
         * Array: false
         */
        this.onExcludedTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         * This is the ETA at
         *
         * the destination SIC, and is recalculated for every linehaul dispatch and arrive event.
         *
         * 	Added 10/23/2019
         * Required: false
         * Array: false
         */
        this.destinationSicEtaDateTime = undefined;
        /**
         * An appointment should be created for delivering the shipment.  Added 03/11/2021 Cathy Doucet LISG-10771
         * Required: false
         * Array: false
         */
        this.appointmentRequiredInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Stores the ETA at destination SIC for a shipment.
 * A BI batch process computes the ETA and UpSerts this table.
 * When rows are inserted, trigger inserts into SCO_EVT_TRANSACTION and SCO_EVT_TRANSACTION_DTL which causes a message to be published to AQ CTY_EVT_TRANS_ETA_Q.
 * Added 10/09/2020  LISG-7763
 *
 */
class DeliveryShipmentEta {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.predRunDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCode = undefined;
        /**
         * Event that
         *
         * triggered this row.
         * Required: false
         * Array: false
         */
        this.currentEventCd = undefined;
        /**
         * Time of the
         *
         * event that triggered this row.
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         * This is the
         *
         * ETA at the destination SIC, and is recalculated for every linehaul dispatch and arrive
         *
         * event.
         * Required: false
         * Array: false
         */
        this.destinationSicEtaDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of DeliveryShipmentEta. Contains the details at the time on an event. It also provides the previous values if an update occured
 * Stores the ETA at destination SIC for a shipment.
 * A BI batch process computes the ETA and UpSerts this table.
 * When rows are inserted, trigger inserts into SCO_EVT_TRANSACTION and SCO_EVT_TRANSACTION_DTL which causes a message to be published to AQ CTY_EVT_TRANS_ETA_Q.
 * Added 10/09/2020  LISG-7763
 *
 */
class DeliveryShipmentEtaSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Holds a snapshot of DeliveryShipment. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class DeliveryShipmentSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * This table holds the outputs of the OCR run on a given DR (Delivery Receipt). Stores the mapping of shipments with OCR processing time and its outputs.  Added 01/22/2021  LISG-9825
 */
class DeliverySpecialService {
    constructor() {
        /**
         * Shipment Instance ID corresponding to the DR for which OCR was run
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Timestamp at which OCR processing started
         * Required: false
         * Array: false
         */
        this.processStartDateTime = undefined;
        /**
         * PRO Number associated with the shipment
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Timestamp at which the DR was scanned
         * Required: false
         * Array: false
         */
        this.drScanDateTime = undefined;
        /**
         * OCR processing is PENDing or COMPlete
         * Required: false
         * Array: false
         */
        this.processStatusCd = undefined;
        /**
         * Timestamp at which OCR processing completed
         * Required: false
         * Array: false
         */
        this.processEndDateTime = undefined;
        /**
         * True if OCR detected Inside Delivery Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceInsideInd = undefined;
        /**
         * True if OCR detected Lift Gate Service Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceLiftgateInd = undefined;
        /**
         * True if OCR detected Residential Delivery Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceResidentialInd = undefined;
        /**
         * True if OCR detected Construction/Utility Site Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceConstructionInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Overall Status of Audit for this shipment.
         *
         *
         *
         * Possible Values:
         *
         * Manual_Audit_Pending (PENDING)
         *
         * Auditor_Conflict (CONFLICT)
         *
         * Shipment_Ignored (IGNORED)
         *
         * Ready_for_Correction (COR_READY)
         *
         * Correction_Done (COR_DONE)
         * Required: false
         * Array: false
         */
        this.shipmentAuditStatusCd = undefined;
        /**
         * Total number of audits completed for this shipment. Default 0.
         * Required: false
         * Array: false
         */
        this.shipmentAuditCount = undefined;
        /**
         * DMS Doc-ID for the DR-scan image corresponding to this shipment.  Added 02/02/2021  LISG-10054  Length increased 07/13/2021  LISG-13687
         * Required: false
         * Array: false
         */
        this.drDmsDocId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * This table holds the outputs of the Manual-Audit done on DR's. Added 01/22/2021  LISG-9825
 */
class DeliverySpecialServiceAudit {
    constructor() {
        /**
         * Shipment Instance ID corresponding to the DR for which OCR was run
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Timestamp at which OCR processing started
         * Required: false
         * Array: false
         */
        this.processStartDateTime = undefined;
        /**
         * Employee ID for the auditor who performed this audit.
         * Required: false
         * Array: false
         */
        this.auditEmployeeId = undefined;
        /**
         * Audit for this DR by auditor with given AUDITOR_SEQ_NBR is PENDing or COMPlete
         * Required: false
         * Array: false
         */
        this.auditStatusCd = undefined;
        /**
         * Sequence number for audits of given DR. Starting with 1.
         * Required: false
         * Array: false
         */
        this.auditSequenceNbr = undefined;
        /**
         * Identifies whether this manual audit was by NORMal auditor or by ADMIN auditor.
         * Required: false
         * Array: false
         */
        this.auditTypeCd = undefined;
        /**
         * Timestamp at which the DR appeared on UI for Manual Auditor
         * Required: false
         * Array: false
         */
        this.auditStartDateTime = undefined;
        /**
         * Timestamp at which Manual Auditor marked audit for this DR as complete
         * Required: false
         * Array: false
         */
        this.auditEndDateTime = undefined;
        /**
         * True if Audit detected Inside Delivery Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceInsideInd = undefined;
        /**
         * True if Audit detected Lift Gate Service Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceLiftgateInd = undefined;
        /**
         * True if Audit detected Residential Delivery Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceResidentialInd = undefined;
        /**
         * True if Audit detected Construction/Utility Site Checkbox ticked, else False.
         * Required: false
         * Array: false
         */
        this.specialServiceConstructionInd = undefined;
        /**
         * Notes added by Manual Auditor for the DR audit.
         * Required: false
         * Array: false
         */
        this.auditNote = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The employee name of the auditor who performed the audit.
         * Required: false
         * Array: false
         */
        this.auditEmployeeName = undefined;
    }
}
/**
 *
 */
class DeliverySpecialServiceHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.processStartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.drScanDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.processStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.processEndDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.specialServiceInsideInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.specialServiceLiftgateInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.specialServiceResidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.specialServiceConstrInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCreatedProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceLastUpdateProgramId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentAuditStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentAuditCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.drDmsDocId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DimensionsCheckDsrRecheck {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.recheckStartDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.recheckByDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.recheckCompletedDate = undefined;
        /**
         * Same value for CORR
         * ELATION id is assigned to various rows from different tables that form a single
         * business transactions. In other words, this groups multiple rows across various
         * tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event
         * id is assigned to various rows from different tables that form a single database
         * 	transactions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DimensionsCheckLog {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.checkDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideExistsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrSelectionTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrSelectedByUserId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cycleEndDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELATION
         * id is assigned to various rows from different tables that form a single business
         * 	transactions. In other words, this groups multiple rows across various tables f
         * or a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is as
         * signed to various rows from different tables that form a single database transac
         * tions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DimensionsCheckTrailer {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.checkDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.processedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideByEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELATION
         * 	id is assigned to various rows from different tables that form a single busines
         * s transactions. In other words, this groups multiple rows across various tables
         * for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is a
         * ssigned to various rows from different tables that form a single database transa
         * ctions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DispatchArea {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DispatchGroup {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.groupDescription = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activeDate = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * This table stores the regions associated with a dispatch group.  Added 10/13/2020  LISG-7976
 */
class DispatchGroupRegion {
    constructor() {
        /**
         * Dispatch group ID the region is associated with.
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
        /**
         * The identifier for the dispatch group region
         * Required: false
         * Array: false
         */
        this.regionId = undefined;
        /**
         * The polygon shape values of the dispatch group region - should be a GeoJson
         * Required: false
         * Array: false
         */
        this.regionShapeGeojson = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DockActivity {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockVisitInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockActivitySequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockActivityCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryInformationRqdCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualActivityDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualActivityDateTimeLocal = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Same value for CORRELATIO
         * N id is assigned to various rows from different tables that form a single busine
         * ss transactions. In other words, this groups multiple rows across various tables
         * 	for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is
         * assigned to various rows from different tables that form a single database trans
         * actions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DockLoadedHandlingUnit {
    constructor() {
        /**
         * Unique ID of
         * 	a
         * Dock Location.
         * Required: false
         * Array: false
         */
        this.dockInstanceId = undefined;
        /**
         * The
         * 	Child PRO number that identifies a handling unit that is loaded in a
         * 	dock location.
         * Required: false
         * Array: false
         */
        this.childProNbr = undefined;
        /**
         * This
         * 	parent pro number identifies which handling units are part
         * 					of the same shipment. This is the pro number found in the BOL
         * document.
         * Required: false
         * Array: false
         */
        this.parentProNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DockLoadedShipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class DockLocation {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockClosestDoorNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
    }
}
/**
 *
 */
class DockVisit {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockVisitInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockVisitSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerArriveDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerArriveDateTimeLocal = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerDepartDateTimeLocal = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperUnloadAtDockInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeLoadedAtDockInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cashDueCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freezeCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentRtndCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalBillCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPalletCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalMmCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Same value for CORRELATION i
         * d is assigned to various rows from different tables that form a single business
         * transactions. In other words, this groups multiple rows across various tables fo
         * r a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is ass
         * igned to various rows from different tables that form a single database transact
         * ions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * This field stores the CARRIER_ID of the Cartage Carrier that does the dock drop for a pickup.  Added 01/28/2022  LISG-16011
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.dockActivity = undefined;
    }
}
/**
 *
 */
class DropLocation {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.dropLocationInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cisCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhone = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhoneExtension = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip6 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip4RestUs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhoneCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.contactPhoneAreaCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstanceId = undefined;
        /**
         * Same value for CORRELATIO
         * N id is assigned to various rows from different tables that form a single busine
         * ss transactions. In other words, this groups multiple rows across various tables
         * 	for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is
         * assigned to various rows from different tables that form a single database trans
         * actions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Latitude of the drop location. Geo-coded based on the user-provided address  Added 04/08/2022  LISG-16566
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * Longitude of the drop location.Geo-coded based on the user-provided address. Added 04/08/2022  LISG-16566
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of DropLocation. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class DropLocationSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dropLocationInstanceId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * This table holds the last known location (lat / long) for the driver as collected from the GPS on the handheld.  Added 06/29/2020
 */
class DsrCurrentLocation {
    constructor() {
        /**
         * Employee from PS/SF/
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * The handheld device id used by the DSR.
         * Required: false
         * Array: false
         */
        this.deviceId = undefined;
        /**
         * The last known lat value as collected from the handheld
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * The last know long value as collected from the handheld
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * The tractor speed at the time the lat / long point was collected
         * Required: false
         * Array: false
         */
        this.speedNbr = undefined;
        /**
         * The location elevation when the lat / long point was collected
         * Required: false
         * Array: false
         */
        this.elevationNbr = undefined;
        /**
         * The timestamp in UTC when lat / long point was collected
         * Required: false
         * Array: false
         */
        this.eventDateTimeUtc = undefined;
        /**
         * The sic code of the trip when the lat / long point was collected
         * Required: false
         * Array: false
         */
        this.domicileSic = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The identifier of the vehicle, which is autogenerated by Samsara.
         * Required: false
         * Array: false
         */
        this.vehicleId = undefined;
        /**
         * The formatted address for the location.
         * Required: false
         * Array: false
         */
        this.formattedAddress = undefined;
    }
}
/**
 * Holds a snapshot of DsrCurrentLocation. Contains the details at the time on an event. It also provides the previous values if an update occured
 * This table holds the last known location (lat / long) for the driver as collected from the GPS on the handheld.  Added 06/29/2020
 */
class DsrCurrentLocationSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Employee from PS/SF/
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * This table holds all location points (lat / long) collected by the DSR handheld device from the GPS after the trip was dispatched.  Added 06/29/2020
 */
class DsrLocation {
    constructor() {
        /**
         * The handheld device id used by the DSR
         * Required: false
         * Array: false
         */
        this.deviceId = undefined;
        /**
         * The timestamp in UTC when lat / long point was collected
         * Required: false
         * Array: false
         */
        this.eventDateTimeUtc = undefined;
        /**
         * The day portion of the EVENT_DTTM
         * Required: false
         * Array: false
         */
        this.eventDd = undefined;
        /**
         * Employee id from PS/SF.
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * The tractor speed at the time the lat / long point was collected
         * Required: false
         * Array: false
         */
        this.speedNbr = undefined;
        /**
         * The location elevation when the lat / long point was collected
         * Required: false
         * Array: false
         */
        this.elevationNbr = undefined;
        /**
         * The sic code of the trip when the lat / long points were collected
         * Required: false
         * Array: false
         */
        this.domicileSic = undefined;
        /**
         * The last known lat value as collected from the handheld
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * The last know long value as collected from the handheld
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class EmployeeDevice {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.devSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.primaryDeviceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serverAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.devicePhoneCountryCdNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.devicePhoneAreaCdNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.devicePhoneNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.logonSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.applCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceIpAddress = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Equipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeDetailCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleSequence = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvnt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvntDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnScacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnReturnSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRespSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRntlCntrNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRntlInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateTransactionSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mfrYr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmclSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.wrkRequestInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pmDueDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inServiceDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.safeStackInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeToRsiInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.extdSlprLaneInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.limitedAvailableInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatusQlfrCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The equipment prefix and suffix concatenated.
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         * The validation message associated with Equipment.
         * Required: false
         * Array: false
         */
        this.validationMessage = undefined;
        /**
         * Indicates whether any shipments have been loaded onto the truck or trailer.
         * Required: false
         * Array: false
         */
        this.loadedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoad = undefined;
    }
}
/**
 *
 */
class EquipmentHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeDetailCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleSequence = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleIdNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnScacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnReturnSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRespSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRntlCntrNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRntlInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateTransactionSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.updatedTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mfrYr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.domicileSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.wrkRequestInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pmDueDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inServiceDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.safeStackInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeToRsiInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.extdSlprLaneInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.limitedAvailableInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatusQlfrCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class EquipmentIdXref {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdMbf = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdMcms = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actDateTime = undefined;
    }
}
/**
 * Holds a snapshot of Equipment. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class EquipmentSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class Event {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventOccurredDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedReleaseNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCurrentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.histCurrentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmsDocId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.doorNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transactionSrcCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.operationsShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reason = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class EventLog {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.programId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalChargeAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trlrIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trlrIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperCustNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeCustNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bil21StCustNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bil22NdCustNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.adminInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeToCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billTo1StRelationshipCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.adminStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billTo2NdRelationshipCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ediSenderId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ediSenderShipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.debtorCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.purInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bolInstId = undefined;
        /**
         * A unique identifier to correlate multiple transactions. This can be used by a log aggregator to tie events from multiple technologies such as UI, Middle ware and integration.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.thirdPartyInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Surrogate key for SHM_EVENT_LOG.  Added 02/11/2020 for Project Radar (Saravanan)
         * Required: false
         * Array: false
         */
        this.shipmentEventLogId = undefined;
    }
}
/**
 * Stores the snapshot of all the tables that were updated as part of the transaction. The snapshot is a json representation of all columns that were changed. The snapshot will be created by insert/update/delete triggers on the source table.  Added 02/11/2020 for Project Radar (Saravanan)
 */
class EventLogDetail {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.snapshotId = undefined;
        /**
         * Foreign Key from new column on SHM_EVENT_LOG
         * Required: false
         * Array: false
         */
        this.shipmentEventLogId = undefined;
        /**
         * Name of the table for which the snapshot is stored.
         * Required: false
         * Array: false
         */
        this.tableName = undefined;
        /**
         * Json string containing the before and after values for all the columns that were changes by the transaction.
         * Required: false
         * Array: false
         */
        this.snapshotJson = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of EventLog. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class EventLogSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Holds a snapshot of Event. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class EventSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class HeadLoadDetail {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lseOrSkdCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cbePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cbeProfitability = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nbrOfFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
    }
}
/**
 *
 */
class HookRequest {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.hookInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dropLocationInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.completedStopInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.createdStopInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cisCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeSicCd = undefined;
        /**
         * Same value for CORRELATION
         * 	id is assigned to various rows from different tables that form a single busines
         * s transactions. In other words, this groups multiple rows across various tables
         * for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is a
         * ssigned to various rows from different tables that form a single database transa
         * ctions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class InterfaceAcct {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.acctInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.acctMadCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.creditStatusCode = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.locationTypeCode = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentAcctInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nationalAcctInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addressLine1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.addressLine2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.postalCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deletedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastCreatedBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip4RestUs = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.geoCoordinates = undefined;
        /**
         * Geo coordinations
         * Required: false
         * Array: false
         */
        this.geoCoordinatesGeo = undefined;
        /**
         * The value comes from SCO_CUSTOMER_PROFILE.LOCATION_TYPE_CD.
         *
         * The type of location
         *
         * Valid Values:
         * BusinessCommercialBUSINESS/COMMERCIAL
         * DistributionCenterDISTRIBUTION CENTER
         * GroceryWareHouseGROCERY WAREHOUSE
         * ShoppingMallSHOPPING MALL/STRIP MALL
         * UnversityCollegeUNIVERSITY/COLLEGE
         * ConstructionSiteCONSTRUCTION/UTILITY SITES: CONSTRUCTION SITE
         * ConstructionUtilitySiteCONSTRUCTION/UTILITY SITES: UTILITY SITE
         * GovtMineGOVERNMENT SITES: MINE
         * GovtGasOilFieldGOVERNMENT SITES: NATURAL GAS OR OIL FIELD
         * GovtPostOfficeGOVERNMENT SITES: POST OFFICE
         * GovtMilitaryGOVERNMENT SITES: MILITARY
         * GovtPrisonCorrectionGOVERNMENT SITES: PRISON/CORRECTION FACILITY
         * GovtAirportGOVERNMENT SITES: AIRPORT
         * GovtOtherGOVERNMENT SITES: OTHER GOVERNMENT SITES
         * PierContainerFreightStationPIERS, WHARFS, CFS: CONTAINER FREIGHT STATION
         * PierWharfPIERS, WHARFS, CFS: PIER OR WHARF
         * ResidentialHomeRESIDENTIAL: RESIDENCE
         * ResidentialApartmentRESIDENTIAL: APARTMENT
         * ResidentialPlaceofWorshipRESIDENTIAL: PLACE OF WORSHIP
         * ResidentialSchoolRESIDENTIAL: SCHOOL (K-12)
         * ResidentialCampRESIDENTIAL: CAMP
         * ResidentialFarmRESIDENTIAL: FARM
         * ResidentialPublicStorageRESIDENTIAL: PUBLIC STORAGE FACILITY
         * ResidentialBusinessRESIDENTIAL: BUSINESS OPERATING WITHIN A PRIVATE RESIDENCE
         * ExhibitionConvCtrEXHIBITION SITES: EXHIBITION SITE OR CONVENTION CENTER
         * Required: false
         * Array: false
         */
        this.customerProfileLocationTypeCd = undefined;
        /**
         * The phone number for the account.
         * Required: false
         * Array: false
         */
        this.phoneNbr = undefined;
    }
}
/**
 * Holds a snapshot of InterfaceAcct. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class InterfaceAcctSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.acctInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Details about employees that process and approve claims.
 */
class InterfaceEmployee {
    constructor() {
        /**
         * Employee Id of the Claims employee.
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * First Name of the Claims employee.
         * Required: false
         * Array: false
         */
        this.firstName = undefined;
        /**
         * Middle Name of the Supervisor of the Claims employee.
         * Required: false
         * Array: false
         */
        this.middleName = undefined;
        /**
         * Last Name of the Supervisor of the Claims employee.
         * Required: false
         * Array: false
         */
        this.lastName = undefined;
        /**
         * Job Description of the Supervisor of the Claims employee.
         * Required: false
         * Array: false
         */
        this.jobDescription = undefined;
        /**
         * The domicile SIC location for the employee.
         * Required: false
         * Array: false
         */
        this.domicileSicCd = undefined;
        /**
         * The employee id of this employee's supervisor.
         * Required: false
         * Array: false
         */
        this.supervisorEmployeeId = undefined;
        /**
         * Desc: The business phone number of the employee.  It is sourced from HRS_EMPL_PHONE_VW in DB2 for typ_cd = 'B'.  01/10/2019 Added by Cathy
         * Required: false
         * Array: false
         */
        this.businessPhoneTxt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.racfId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrCellPhone = undefined;
        /**
         * Name the employee prefers to be called by.  Added 10/08/2020 LISG-7899
         * Required: false
         * Array: false
         */
        this.preferredFirstName = undefined;
        /**
         * The Active Directory Domain (a collection of objects within a Microsoft Active Directory network) that an emplyee is part of (CONWAY, CFI, CTS, MENLO)
         * Required: false
         * Array: false
         */
        this.domain = undefined;
        /**
         * Network or domain ID assigned to the employee. It is normally a combination of first and last name of the employee.
         * Required: false
         * Array: false
         */
        this.networkUserId = undefined;
        /**
         * XPO email address assigned to the employee
         * Required: false
         * Array: false
         */
        this.emailAddress = undefined;
        /**
         * The Con-way/Menlo 3 character representation of the SF company code.
         * Required: false
         * Array: false
         */
        this.companyCd = undefined;
        /**
         * Business cell phone number
         * Required: false
         * Array: false
         */
        this.businessCell = undefined;
        /**
         * Office phone number
         * Required: false
         * Array: false
         */
        this.businessPhone = undefined;
        /**
         * Indicates Employees status with the company.
         *
         * Valid Values are (04/13/2011: Cathy Doucet synced these with PeopleSoft):
         *
         * A = Active
         * P = Leave with pay
         * L = Leave without pay
         * S = Suspended
         * D = Deceased
         * R = Retired
         * Q = Retired with pay
         * T = Terminated
         * Required: false
         * Array: false
         */
        this.employeeStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.primaryRoleId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.primaryRoleDescription = undefined;
        /**
         * Indicates if employee is fulltime or partime.
         *
         * Valid values are
         *
         * F = Full time
         * P = Part time
         * Required: false
         * Array: false
         */
        this.fullPartTimeCd = undefined;
        /**
         * Identifies the type of work the employee performs
         *
         * Format of the Job Code is CC9999, where
         * CC - Two character component prefix for which the code is valid.
         * Example: CC- Con-Way Central, CS- Con-Way Southern etc.
         * 9999 - Four digit code assigned to the Job
         * Required: false
         * Array: false
         */
        this.jobCode = undefined;
        /**
         * Indicates if employee has Commercial Drivers License; for XPO includes doubles, triples enforcements.
         * Required: false
         * Array: false
         */
        this.cdlLicInd = undefined;
        /**
         * If applicable, date the CDL license expires
         * Required: false
         * Array: false
         */
        this.cdlLicExpiryDate = undefined;
        /**
         * Indicates if employee has license for pulling high volumes or certain types of HazMat shipments
         * Required: false
         * Array: false
         */
        this.hazmLicenseInd = undefined;
        /**
         * If applicable, date the HAZM license expires
         * Required: false
         * Array: false
         */
        this.hazmLicenseExpiryDate = undefined;
        /**
         * Indicates if employee has license for pulling high volumes of liquid shipments
         * Required: false
         * Array: false
         */
        this.tankLicenseInd = undefined;
        /**
         * If applicable, date the TANK license expires
         * Required: false
         * Array: false
         */
        this.tankLicenseExpiryDate = undefined;
        /**
         * Indicates if employee has license for cross-border schedules
         * Required: false
         * Array: false
         */
        this.fastidLicenseInd = undefined;
        /**
         * If applicable, date the FASTID license expires
         * Required: false
         * Array: false
         */
        this.fastidLicenseExpiryDate = undefined;
        /**
         * Indicates if employee has license for access to ports or high-security locations
         * Required: false
         * Array: false
         */
        this.twicLicenseInd = undefined;
        /**
         * If applicable, date the TWIC license expires
         * Required: false
         * Array: false
         */
        this.twicLicenseExpiryDate = undefined;
        /**
         * OSHA requires that every forklift operator be trained and certified to operate the powered industrial truck in the workplace, and that the operator's performance be evaluated on the provisions of 1910.178(l)(3) every three years. ... That is the only operating license required by OSHA.
         * Required: false
         * Array: false
         */
        this.forkliftLicenseInd = undefined;
        /**
         * If applicable, date the FORKLIFT license expires
         * Required: false
         * Array: false
         */
        this.forkliftLicenseExpiryDate = undefined;
        /**
         * The Success Factors version of Employee ID.  Added 02/19/2021 forLNH and PND.
         * Required: false
         * Array: false
         */
        this.sfEmployeeId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of InterfaceEmployee. Contains the details at the time on an event. It also provides the previous values if an update occured
 * Details about employees that process and approve claims.
 */
class InterfaceEmployeeSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Employee Id of the Claims employee.
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class LoadDeck {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
    }
}
/**
 *
 */
class LoadedEquipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadEquipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * The loaded handling
 * unit of the trailer load.
 */
class LoadedHandlingUnit {
    constructor() {
        /**
         * This is the
         * 	surrogate id for equipment.
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * The sequence number
         * 	of the handling unit on the trailer with respect to all other handling
         * 	units loaded on the trailer.  This is mostly used for dock
         * 	automation.
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The Child
         * 	PRO number of the loaded handling unit.
         * Required: false
         * Array: false
         */
        this.childProNbr = undefined;
        /**
         * This
         * 	parent pro number identifies which handling units are part of the same
         * 	shipment. This is the pro number found in the BOL document.
         * Required: false
         * Array: false
         */
        this.parentProNbr = undefined;
        /**
         * This is the
         * 	4 character prefix of the equipment id.  (eg.  0415 or SWFT etc)
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * This is the
         * 	six digit equipment id suffix.  (eg.  123456 or 37 or 3737).
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         * This is a
         * 	representation of the placement on a trailer.  The trailer is divided
         * 	length-wise into to and bottom, left and right, and then across into 7
         * 	sections.  This creates 28 positions in the trailer.  Each byte of this
         * 	field will represent  one of th
         * Required: false
         * Array: false
         */
        this.dckLrgMarks = undefined;
        /**
         * The device
         * 	id of the terminal where the last update to this record was
         * made.
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         * Indicates
         * 	whether a handling unit is part of the headload or not.
         * Required: false
         * Array: false
         */
        this.headLoadInd = undefined;
        /**
         * Desc:
         * 	Indicates if the hanlding unit occupies this section.
         * Required: false
         * Array: false
         */
        this.trailerSectn1Ind = undefined;
        /**
         * Desc:
         * 	Indicates if the hanlding unit occupies this section.
         * Required: false
         * Array: false
         */
        this.trailerSectn2Ind = undefined;
        /**
         * Desc:
         * 	Indicates if the hanlding unit occupies this section.
         * Required: false
         * Array: false
         */
        this.trailerSectn3Ind = undefined;
        /**
         * Desc:
         * 	Indicates if the hanlding unit occupies this section.
         * Required: false
         * Array: false
         */
        this.trailerSectn4Ind = undefined;
        /**
         * Desc:
         * 	Indicates if the hanlding unit occupies this section.
         * Required: false
         * Array: false
         */
        this.trailerSectn5Ind = undefined;
        /**
         * Desc:
         * 	Indicates if the hanlding unit occupies this section.
         * Required: false
         * Array: false
         */
        this.trailerSectn6Ind = undefined;
        /**
         * Bill
         * 	destination SIC.
         * Required: false
         * Array: false
         */
        this.billDestinationSic = undefined;
        /**
         * Indicates this
         * 	load event was a mis load or not.
         * Required: false
         * Array: false
         */
        this.misloadInd = undefined;
        /**
         * Indicates that the child PROs are split.  Added 03/24/2021  LISG-11031
         * Required: false
         * Array: false
         */
        this.splitInd = undefined;
        /**
         * The user that authorized the split or misload.  Added 03/24/2021  LISG-11031
         * Required: false
         * Array: false
         */
        this.overrideUserId = undefined;
        /**
         * Timestamp of when the spit or misload was authorized.  Added 03/24/2021  LISG-11031
         * Required: false
         * Array: false
         */
        this.overrideDateTime = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.positionOnTrailerCd = undefined;
        /**
         * The name and employee ID of the person who loaded the shipment.
         * Required: false
         * Array: false
         */
        this.loadedByEmployee = undefined;
        /**
         * The service center identification code for the dock where the shipment was loaded.
         * Required: false
         * Array: false
         */
        this.loadedAtSic = undefined;
        /**
         * The service center identification code of the dock worker that loaded the PRO.
         * Required: false
         * Array: false
         */
        this.loadByEmployeeSicCd = undefined;
        /**
         * Indicates whether the Handling Unit's parent is also loaded on the same trailer.
         * Required: false
         * Array: false
         */
        this.parentLoadedInd = undefined;
    }
}
/**
 *
 */
class LoadedShipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dckLrgMarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSectn1Ind = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSectn2Ind = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSectn3Ind = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSectn4Ind = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSectn5Ind = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSectn6Ind = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.misloadInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
    }
}
/**
 *
 */
class LocationExceptionCtl {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
        /**
         * Same value for CORRELATION
         * 	id is assigned to various rows from different tables that form a single busines
         * s transactions. In other words, this groups multiple rows across various tables
         * for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is a
         * ssigned to various rows from different tables that form a single database transa
         * ctions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Table captures comments by a Customer care person managing key accounts regarding reason and resolution of movement exceptions. This information is sent proactively to the customer. The goal is to keep customer happy.
 * 10/24/2019 This table now stores remarks for Shipments as well as Movement Exceptions.  PK has to be changed.
 */
class ManagementRemark {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementExceptionSequenceNbr = undefined;
        /**
         * Remark entered by the customer care person, describing the exception. This is typically shared with the customer.
         * Required: false
         * Array: false
         */
        this.remark = undefined;
        /**
         * Remarks in this table will be of type:
         * Customer Service Remarks
         * Exception Remarks
         * 11/14/2019 Renamed from REASON_CD to TYPE_CD
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Y- default. The data is shown to the external customer. N- These comments are for XPO internal documentation and will not be shown to the customer.
         * Required: false
         * Array: false
         */
        this.showToCustomerInd = undefined;
        /**
         * A unique identifier to correlate multiple Invoices. This is used as a batch# when reprint for Email is requested and they need to be processed as a group.
         * 	This is a value that API gateway assigns for a call. API then loops through creating multiple invoices with the same value
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
    }
}
/**
 * Holds a snapshot of ManagementRemark. Contains the details at the time on an event. It also provides the previous values if an update occured
 * Table captures comments by a Customer care person managing key accounts regarding reason and resolution of movement exceptions. This information is sent proactively to the customer. The goal is to keep customer happy.
 * 10/24/2019 This table now stores remarks for Shipments as well as Movement Exceptions.  PK has to be changed.
 */
class ManagementRemarkSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class MessageLog {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.messageInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.messageStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.messageSentDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.messageStatusDetailCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.messageStatusDetailDescription = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.messageData = undefined;
        /**
         * Same value for CORRELATION id i
         * s assigned to various rows from different tables that form a single business tra
         * nsactions. In other words, this groups multiple rows across various tables for a
         * 	single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is assign
         * ed to various rows from different tables that form a single database transaction
         * s.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * This table will be used for capturing SCO Metrics associated with both current metrics
 *
 * and snapshots.  An example is Trip Level metrics.  Added 07/12/2019
 */
class Metric {
    constructor() {
        /**
         * Unique identifier
         * Required: false
         * Array: false
         */
        this.metricId = undefined;
        /**
         * The stage of operations for which this metric snapshot was taken. E.g. for Pnd, the stages are: Planning, Dispatched, Dock
         * Required: false
         * Array: false
         */
        this.stage = undefined;
        /**
         * The specific activity performed in a stage for which this metric was created. E.g. for Pnd, Released or Sequence could be a sub stage during the Planning stage.
         * Required: false
         * Array: false
         */
        this.subStage = undefined;
        /**
         * Service Center Code that owns the Route or Trip, etc.
         * Required: false
         * Array: false
         */
        this.sicCode = undefined;
        /**
         * Entity associated with the metric (e.g. Route, Trip, Service Center)
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Actual Metric Name (e.g. CWT, PPMH) an enumerated value.
         * Required: false
         * Array: false
         */
        this.valueKeyCd = undefined;
        /**
         * Goals from external source.
         * Required: false
         * Array: false
         */
        this.goal = undefined;
        /**
         * The variance between actual goal.
         *
         * Valid Values
         * NEUTRAL
         * ABOVE
         * BELOW
         * Required: false
         * Array: false
         */
        this.variance = undefined;
        /**
         * A description describing how the metric varies from the goal.
         * Required: false
         * Array: false
         */
        this.varianceDesc = undefined;
        /**
         * A description of the metric value. E.g. Delivery Weight per Trip, Delivery PPMH
         * Required: false
         * Array: false
         */
        this.description = undefined;
        /**
         * The actual value in decimal form
         * Required: false
         * Array: false
         */
        this.value = undefined;
        /**
         * Date for which the plan is active. (added for LH 08/02/2019)
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Depending on the Metric, this could be Dest SIC, Next SIC, FAC SIC, etc. (added for LH 08/02/2019)
         * Required: false
         * Array: false
         */
        this.targetSicCode = undefined;
        /**
         * A Metric may be at the Region level. (added for LH 08/02/2019)
         * Required: false
         * Array: false
         */
        this.regionCd = undefined;
        /**
         * Shift Code this Metric applies to (added for LH 08/02/2019)
         *
         * Valid values are
         *
         * D = Day FAC
         * I = Inbound
         * N = Night FAC
         * O = Outbound
         * P or blank = Pand D
         * Required: false
         * Array: false
         */
        this.shiftCd = undefined;
        /**
         * The formatted string representing the metric value, derived by API.  Added 09/11/2019
         * Required: false
         * Array: false
         */
        this.displayValue = undefined;
        /**
         * Code that assign a value judgement to the variance e.g, GOOD, BAD or NEUTRAL.  Added 03/30/2020
         * Required: false
         * Array: false
         */
        this.varianceAlignmentCd = undefined;
        /**
         * RUN_NBR from PnD Optimizer.  Added 05/15/2020
         * Required: false
         * Array: false
         */
        this.pdoRunNbr = undefined;
        /**
         * SEQ_NBR from PnD Optimizer (could be from one of multiple tables).  Added 05/15/2020
         * Required: false
         * Array: false
         */
        this.pdoRunSequenceNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * An integer representing the order that this metric should appear in the list of metrics.  While this attribute is used internally for
         * Required: false
         * Array: false
         */
        this.priority = undefined;
        /**
         * The result of the difference between the previous and the new metric value.
         * Required: false
         * Array: false
         */
        this.deltaValue = undefined;
        /**
         * The difference between the previous and the new metric value expressed as a percentage.
         * Required: false
         * Array: false
         */
        this.deltaPercentage = undefined;
        /**
         * The type of variance: good, bad or neutral.
         * Required: false
         * Array: false
         */
        this.deltaVariance = undefined;
        /**
         * Indicates the sign of the delta.
         * Required: false
         * Array: false
         */
        this.deltaSign = undefined;
    }
}
/**
 *
 */
class Movement {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementReportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadedSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rshpCreditedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.obcPickupDlvRouteCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.qlfrReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerLocationArrivalDateTime = undefined;
        /**
         * Unique identifier for the carrier that executed this movement.  Added 04/06/2021  LISG-11313
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Expanded Name : Movement status
         *
         * Desc : The status of the latest movement for a Shipment from an operations point-of-view. This is a denormalized atribute from the MOVEMENT entity.
         *
         * Source: MBF
         * Name: Various
         *
         * Valid Values :
         * Spaces - Not applicable
         * 1 - On dock
         * 2 - On trailer
         * 3 - Out for delivery
         * 4-  Interim Delivery
         * 5 - Final Delivered
         * 6 - Not Applicable
         * 6 - No movements allowed
         * 7 - Cancelled
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.movementException = undefined;
    }
}
/**
 *
 */
class MovementException {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.piecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rfsdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.undlvdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmgdCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.remarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionReportingSicCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The first name of the createdById user.
         * Required: false
         * Array: false
         */
        this.createdByFirstName = undefined;
        /**
         * The last name of the createdById user.
         * Required: false
         * Array: false
         */
        this.createdByLastName = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.movementExceptionAction = undefined;
    }
}
/**
 * Describes the action that is being taken for a shipment exception as well as the Service Center that is responsible for taking the action and the reason for the action.  Added 01/02/2020
 */
class MovementExceptionAction {
    constructor() {
        /**
         * Part of unique identifier
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Part of unique identifier
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         * Part of unique identifier
         * Required: false
         * Array: false
         */
        this.movementExceptionSequenceNbr = undefined;
        /**
         * Uniquely identifies a row
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * Describes the action that is being taken for a shipment exception as well as the Service Center that is responsible for taking the action and the reason for the action.
         * Required: false
         * Array: false
         */
        this.reasonCd = undefined;
        /**
         * Responsible sic could be determined to be different from what SHM_movement has recorded
         * Required: false
         * Array: false
         */
        this.responsibleSicCd = undefined;
        /**
         * Describes what corrective action XPO has taken to address the exception. This is typically shared with the customer.
         * Required: false
         * Array: false
         */
        this.correctiveAction = undefined;
        /**
         * A unique identifier to correlate multiple Invoices. This is used as a batch# when reprint for Email is requested and they need to be processed as a group.
         * 	This is a value that API gateway assigns for a call. API then loops through creating multiple invoices with the same value
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Indicates whether the carrier was in a position to control or prevent the exception.  Added 03/23/2020 LISG-4722
         * Required: false
         * Array: false
         */
        this.controlInd = undefined;
    }
}
/**
 * Holds a snapshot of MovementExceptionAction. Contains the details at the time on an event. It also provides the previous values if an update occured
 * Describes the action that is being taken for a shipment exception as well as the Service Center that is responsible for taking the action and the reason for the action.  Added 01/02/2020
 */
class MovementExceptionActionSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Part of unique identifier
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Part of unique identifier
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         * Part of unique identifier
         * Required: false
         * Array: false
         */
        this.movementExceptionSequenceNbr = undefined;
        /**
         * Uniquely identifies a row
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Holds a snapshot of MovementException. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class MovementExceptionSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Holds a snapshot of Movement. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class MovementSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class Note {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.ownerInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.noteSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.note = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ownerSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class NotificationShipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentProNbr = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class OperationsDetail {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.assignmentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.availableStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handheldId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.jobSelectionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rankNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.speedDialNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.todayDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.todayHrsTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.todayStartTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripStartOffsetMinutes = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripStartWindowMinutes = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeAvailableDeliveryMinutes = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripDrtnMaxMinutes = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prfrdTrailerLengthFoot = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lftgtInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hrsEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class OperationsEquipmentSnapshot {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeDetailCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleSequence = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleIdNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnScacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnReturnSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRespSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRntlCntrNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnRntlInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frgnCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateTransactionSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.updatedTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mfrYear = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.domicileSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.wrkRequestInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pmDueDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inServiceDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.safeStackInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeToRsiInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.extdSlprLaneInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.limitedAvailableInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatusQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class OperationsTrailerLoadSnapshot {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedLoadDestination = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDestination = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadWeight = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadCbePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadCbeProfitability = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadReleaseNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.brkOrProprCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routingRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Control table to keep track of the optimizer run and its status.  Added 03/30/2020
 */
class PdoControl {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Planning SIC
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * Plan date
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         * Identifies the status of the run.
         *
         * Valid Values
         * NEW
         * INPROGRESS
         * ABORTED     (by user)
         * FAILED
         * TIMEDOUT
         * COMPLETED
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * Reason planner aborted the optimizer run
         * Required: false
         * Array: false
         */
        this.abortReason = undefined;
        /**
         * Date/Time when details are sent to optimizer
         * Required: false
         * Array: false
         */
        this.startDateTime = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * For every Optimizer run, the planner usually selects only those shipments that have arrived at the service center. If we are expecting same address shipments to be dispatched and arriving at the service center at a later time of the day we alert the user so that he can choose to hold off on planning such same address shipments until all of them arrived at the SIC.  This table stores the shipments with the same address that have not yet arrived.  Added 01/20/2021  LISG-9819
 */
class PdoEnrouteSameAddressShipment {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each shipment for that optimizer run
         * Required: false
         * Array: false
         */
        this.shipmentSequenceNbr = undefined;
        /**
         * Identifies the customer match group id the shipments have been assigned via Customer Matching API.
         * Required: false
         * Array: false
         */
        this.sameAddressGroupId = undefined;
        /**
         * System identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Business identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Date that the shipment should be delivered based on standard transit days.
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * ETA of the PRO to host destination SIC in case the PRO is inbound
         * Required: false
         * Array: false
         */
        this.proEtaDateTime = undefined;
        /**
         * Identifies the trailer that the shipment is on.
         * Required: false
         * Array: false
         */
        this.trailerId = undefined;
        /**
         * Number of motorized pieces
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Total Weight of the shipment
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Identifier of the appropriate customer, determined by the matching sub-routine
         * Required: false
         * Array: false
         */
        this.customerNbr = undefined;
        /**
         * Name of the customer
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         * Street address of the customer location
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * City where the customer is located
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         * State where the customer is located
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * Specifies the code of the country where the customer is located
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         * First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the customer is located.
         * Required: false
         * Array: false
         */
        this.zip6Cd = undefined;
        /**
         * Last 4 characters of the zip code for a US address
         * Required: false
         * Array: false
         */
        this.zip4Cd = undefined;
        /**
         * Date of the appointment, if applicable.
         * Required: false
         * Array: false
         */
        this.appointmentDate = undefined;
        /**
         * Early committed time to the customer (Appointment Start Time or TDC Start Time)
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryFromTime = undefined;
        /**
         * Late committed time to the customer (Appointment End Time or TDC End Time or Noon for G12 shipments)
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryToTime = undefined;
        /**
         * Customer receiving hours start time (from customer profile)
         * Required: false
         * Array: false
         */
        this.receivingStartTm1 = undefined;
        /**
         * Customer receiving hours end time (from customer profile)
         * Required: false
         * Array: false
         */
        this.receivingEndTm1 = undefined;
        /**
         * Customer receiving hours start time (from customer profile - in case customer has multiple receiving hours)
         * Required: false
         * Array: false
         */
        this.receivingStartTm2 = undefined;
        /**
         * Customer receiving hours end time (from customer profile - in case customer has multiple receiving hours)
         * Required: false
         * Array: false
         */
        this.receivingEndTm2 = undefined;
        /**
         * Customer operations ready time
         * Required: false
         * Array: false
         */
        this.operationsReadyTime = undefined;
        /**
         * Customer operations close time
         * Required: false
         * Array: false
         */
        this.operationsCloseTime = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * SIC where Shipment currently is.  Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.currentSicCode = undefined;
        /**
         * Prefix of trailer that shipment is on.   Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * Prefix of trailer that shipment is on.   Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         * SIC where the trailer currently is.  Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.trailerCurrentSicCode = undefined;
        /**
         * Timestamp when the trailer is estimated to arrive at its next destination.   Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.trailerEtaDateTime = undefined;
        /**
         * This is the status of the schedule. It can be HELD, ENROUTE DELAYED. If it has been dispatched, it will have an enroute status. If the enroute dispatch change is entered it can have a status of delayed. If it arrives and is going to be swapped in the future it can be marked as arrived. This will hold the schedule until the swap is complete, at which time it will be deleted.    Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.scheduleStatusCd = undefined;
        /**
         * The status of the latest movement for the Shipment.  Added 01/25/2021  LISG-9866
         *
         * Valid Values :
         * Spaces - Not applicable
         * 1 - On dock
         * 2 - On trailer
         * 3 - Out for delivery
         * 4- Interim Delivery
         * 5 - Final Delivered
         * 6 - No movements allowed
         * 7 - Cancelled
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         * Derived location of the shipment based on other fields.  Added 01/25/2021  LISG-9866
         * Required: false
         * Array: false
         */
        this.shipmentLocationSicCode = undefined;
    }
}
/**
 * Contains the exceptions encountered by the optimizer or during the internal processing of the optimizer response.
 */
class PdoException {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the Run Response
         * Required: false
         * Array: false
         */
        this.runSequenceNbr = undefined;
        /**
         * Unique sequence to identify each exception for the run.
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
        /**
         * Indicates whether it is an exception from optimizer or internal processing of the response
         *
         * Values:
         * OPTIMIZER
         * INTERNALPROCESSING
         * Required: false
         * Array: false
         */
        this.categoryTypeCd = undefined;
        /**
         * Sequence Number of route returned by the optimizer that has exception encountered during internal processing.
         * Required: false
         * Array: false
         */
        this.optimizedRouteSequenceNbr = undefined;
        /**
         * Sequence Number of route stop returned by the optimizer that has exception during internal processing
         * Required: false
         * Array: false
         */
        this.optimizedStopSequenceNbr = undefined;
        /**
         * Sequence Number of route stop activity returned by the optimizer that has exception during internal processing
         * Required: false
         * Array: false
         */
        this.optimizedActivitySequenceNbr = undefined;
        /**
         * Route Inst Id from SCO_ROUTE that has exception
         * Required: false
         * Array: false
         */
        this.routeInstanceId = undefined;
        /**
         * Trip Inst id of the stop or activity from SCO_TRIP_NODE/SCO_TRIP_NODE_ACTIVITY that has exception
         * Required: false
         * Array: false
         */
        this.tripInstanceId = undefined;
        /**
         * Trip Node Seq Nbr of the stop or activity from SCO_TRIP_NODE/SCO_TRIP_NODE_ACTIVITY that has exception
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         * Trip Node Activity Seq Nbr of the activity from SCO_TRIP_NODE_ACTIVITY that has exception
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         * Shipment that has an exception
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Pro number that has an exception
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Exception Message
         * Required: false
         * Array: false
         */
        this.exceptionMessage = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Pickup Request that resulted in exception from the optimizer call.  Added 10/05/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the valid parameters used by Pand D optimizer for each terminal.  Added 03/30/2020
 */
class PdoParameter {
    constructor() {
        /**
         * Code given to a parameter,
         * e.g. OPZTMOUT - Optimizer Time Out Threshold
         * Required: false
         * Array: false
         */
        this.parmCd = undefined;
        /**
         * Description of the parameter
         * Required: false
         * Array: false
         */
        this.parmDescription = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Settings for a Parameter at a particular Location. Added 03/30/2020
 *
 * When setting the values:
 * - Blank SIC means that the related Parameter applies globally.
 * - If just a SIC is entered, the Parameter applies to the entire SIC.
 *
 */
class PdoParameterSetting {
    constructor() {
        /**
         * Uniquely identifies the parameter value
         * Required: false
         * Array: false
         */
        this.settingId = undefined;
        /**
         * SIC (optional) for which the parameter is applicable
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * The actual value for the setting.
         * Required: false
         * Array: false
         */
        this.settingValue = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the pickup positioning points for each service center. These are the strategic locations such that if the list delivery on the route takes place near that location, then pickups can be started efficiently.  Added 03/30/2020
 */
class PdoRequestAnchorPoint {
    constructor() {
        /**
         * SIC Code
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         * Sequence number to uniquely identiy each strategic pickup positioning point for each SIC
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * Latitude of the pickup positioning point.
         * Required: false
         * Array: false
         */
        this.ancrptLatitudeNbr = undefined;
        /**
         * Longitude of the pickup positioning point
         * Required: false
         * Array: false
         */
        this.ancrptLongitudeNbr = undefined;
        /**
         * Address of the pickup positioning point
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * City of the pickup positioning point.
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         * State of the pickup positioning point.
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the SIC is located.
         * Required: false
         * Array: false
         */
        this.zip6Cd = undefined;
        /**
         * Last 4 characters of the zip code for a US address.
         * Required: false
         * Array: false
         */
        this.zip4Cd = undefined;
        /**
         * Time by when the trailer must be empty to start pickups.
         * Required: false
         * Array: false
         */
        this.emptyTime = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of the commodities shipments that will be sent to the optimizer. Added 05/15/2020
 */
class PdoRequestCommodity {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each shipment for that optimizer run
         * Required: false
         * Array: false
         */
        this.shipmentSequenceNbr = undefined;
        /**
         * Sequence Number to uniquely identify each commodity for the optimizer run
         * Required: false
         * Array: false
         */
        this.commoditySequenceNbr = undefined;
        /**
         * System identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Business identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Type of packaging the commodity was originally picked up in.
         * Required: false
         * Array: false
         */
        this.packageCd = undefined;
        /**
         * A textual description of the commodity
         * Required: false
         * Array: false
         */
        this.description = undefined;
        /**
         * Total weight of the commdity in pounds.
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         * Number of pieces of the commodity in the shipment.
         * Required: false
         * Array: false
         */
        this.piecesCount = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of drivers available at the SIC on the plan date that can be sent to the optimizer for a particular run.  Added 03/30/2020
 */
class PdoRequestDriver {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each driver for the optimizer run.
         * Required: false
         * Array: false
         */
        this.dsrSequenceNbr = undefined;
        /**
         * Employee Id of the DSR.
         * Required: false
         * Array: false
         */
        this.driverEmployeeId = undefined;
        /**
         * Name of the DSR.
         * Required: false
         * Array: false
         */
        this.driverName = undefined;
        /**
         * Job type of the driver.
         *
         * Bid - Drivers with Bid will have specified start and end times in the start and end times columns next
         * Flex - Drivers with Flex will have the start and end time columns empty and disabled for edition
         * Required: false
         * Array: false
         */
        this.jobTypeCd = undefined;
        /**
         * The total time, in minutes, that the DSR is available to complete the deliveries on a route.
         * Required: false
         * Array: false
         */
        this.routeAvailableDeliveryMinutes = undefined;
        /**
         * Indicates whetherthis should be sent to the optimizer or not.
         *
         * Valid Values:
         * Y - Yes, include in the optimizer
         * N - No, exclude from the optimizer
         * Required: false
         * Array: false
         */
        this.includeInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Wage (dollars per hour) for regular time.  Added 04/20/2020
         * Required: false
         * Array: false
         */
        this.regularTimeWageRate = undefined;
        /**
         * Wage (dollars per hour) for overtime.  Added 04/20/2020
         * Required: false
         * Array: false
         */
        this.overtimeWageRate = undefined;
        /**
         * Earliest time employee can be dispatched today.  05/15/2020 - renamed from START_TM
         * Required: false
         * Array: false
         */
        this.earliestDispatchTime = undefined;
        /**
         * End Time of the DSR.  05/15//2020 - renamed from END_TM
         * Required: false
         * Array: false
         */
        this.shiftEndTime = undefined;
        /**
         * Latest time employee can be dispatched today.  Added 05/15/2020
         * Required: false
         * Array: false
         */
        this.latestDispatchTime = undefined;
        /**
         * The geographical area preferrred by the driver.  Source is GEO_AREAS in SCOPRD.  Added 05/15/2020
         * Required: false
         * Array: false
         */
        this.preferredGeoAreaCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of equipment available at the SIC on the plan date that can be sent to the optimizer for a particular run.  Added 03/30/2020
 */
class PdoRequestEquipment {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table).
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each equipment for the optimizer run
         * Required: false
         * Array: false
         */
        this.equipmentSequenceNbr = undefined;
        /**
         * This is the surrogate id for equipment.
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * This is the 4 character prefix of the equipment id. (eg. 0415 or SWFT etc)
         * Required: false
         * Array: false
         */
        this.equipmentPrefix = undefined;
        /**
         * This is the six digit equipment id suffix. (eg. 123456 or 37 or 3737).
         * Required: false
         * Array: false
         */
        this.equipmentSuffixNbr = undefined;
        /**
         * One character code that tells what type of equipment type this is.
         *
         * Z - Straight Truck
         * T - Trailer
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         * Status code of the trailer (Available, Inbound, etc).
         * Required: false
         * Array: false
         */
        this.equipmentStatusCd = undefined;
        /**
         * The year the equipment was manufactured or renovated.
         * Required: false
         * Array: false
         */
        this.manufactureYear = undefined;
        /**
         * Length of the trailer/straight truck.
         * Required: false
         * Array: false
         */
        this.trailerLengthNbr = undefined;
        /**
         * Indicates whetherthis should be sent to the optimizer or not.
         *
         * Valid Values:
         * Y - Yes, include in the optimizer
         * N - No, exclude from the optimizer
         * Required: false
         * Array: false
         */
        this.includeInd = undefined;
        /**
         * Weight capacity, in pounds, of the equipment per state regulations.
         * Required: false
         * Array: false
         */
        this.weightCapacityLbs = undefined;
        /**
         * Pallet capacity (number of pallets that can fit on the equipment) of the equipment per state regulations.
         * Required: false
         * Array: false
         */
        this.palletCapacityNbr = undefined;
        /**
         * HU (Handling Unit) capacity of the equipment per state regulations
         * Required: false
         * Array: false
         */
        this.handlingUnitCapacityNbr = undefined;
        /**
         * Cubic Feet (in cubic feet) capacity of the equipment per state regulations.
         * Required: false
         * Array: false
         */
        this.cubeCapacityCubicFeet = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether trailer has liftgate installed or not.
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrName = undefined;
    }
}
/**
 * Contains the details of the handling units of shipments that will be sent to the optimizer.  Added 03/30/2020
 */
class PdoRequestHandlingUnit {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each shipment for that optimizer run
         * Required: false
         * Array: false
         */
        this.shipmentSequenceNbr = undefined;
        /**
         * A system assigned number to uniquely identify each handling unit for a shipment.
         * Required: false
         * Array: false
         */
        this.handlingUnitSequenceNbr = undefined;
        /**
         * The volume of the freight; the height multiplied by the width multiplied by the depth
         * Required: false
         * Array: false
         */
        this.volumeCubicFeet = undefined;
        /**
         * Length of the handling unit.
         * Required: false
         * Array: false
         */
        this.lengthNbr = undefined;
        /**
         * Width of the handling unit.
         * Required: false
         * Array: false
         */
        this.widthNbr = undefined;
        /**
         * Height of the handling unit.
         * Required: false
         * Array: false
         */
        this.heightNbr = undefined;
        /**
         * Total weight of the handling unit.
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         * Indicates whether the handling unit is a hazardous material.
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of the dimensions of the shipments that will be sent to the optimizer. Added 05/15/2020
 */
class PdoRequestLnhDimension {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each shipment for that optimizer run
         * Required: false
         * Array: false
         */
        this.shipmentSequenceNbr = undefined;
        /**
         * Sequence Number to uniquely identify each dimension for the optimizer run
         * Required: false
         * Array: false
         */
        this.dimensionsSequenceNbr = undefined;
        /**
         * System identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Business identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * This is the number of pieces the recorded dimensions apply to.
         * Required: false
         * Array: false
         */
        this.piecesCount = undefined;
        /**
         * This is the length of the whole or part of the shipment. The number of pieces attribute indicates how many pieces of the shipment the dimensions apply to.
         * Required: false
         * Array: false
         */
        this.lengthNbr = undefined;
        /**
         * This is the width of the whole or part of the shipment. The number of pieces attribute indicates how many pieces of the shipment the dimensions apply to.
         * Required: false
         * Array: false
         */
        this.widthNbr = undefined;
        /**
         * This is the height of the whole or part of the shipment. The number of pieces attribute indicates how many pieces of the shipment the dimensions apply to.
         * Required: false
         * Array: false
         */
        this.heightNbr = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Stages the pickup shipments before sending it to the optimizer.  Added 09/10/20  LISG-7250
 */
class PdoRequestPickupRequest {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each shipment for that optimizer run
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         * Reference pick up Date used for  getting the pick up shipments.
         * Required: false
         * Array: false
         */
        this.refPickupDate = undefined;
        /**
         * SIC where the shipment should be picked up.
         * Required: false
         * Array: false
         */
        this.pickupSicCode = undefined;
        /**
         * Reference pick up instance id for each pick up request.
         * Required: false
         * Array: false
         */
        this.refPickupRequestInstanceId = undefined;
        /**
         * A unique, randomly-generated number that identifies a Pickup Request in the existing CAD (Computer Aided Dispatch) System.
         * Required: false
         * Array: false
         */
        this.refCadCallNbr = undefined;
        /**
         * Earliest available time that freight can be picked up from a Pickup Location.
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         * Latest available time that freight can be picked up from a Pickup Location.
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         * The volume of the freight; derived from the dimensions captured by driver, or entered by a CSR or dock worker for an SLNC (Shipper Load and Count), or from one of a variety of profiles. For details, see the description of CFT_PRFL_MTHD_CD on SHM_SHIPMENT.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * Total Pallets from all Shipments on the Pickup Request.
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         * Total loose pieces from all Shipments on the Pickup Request.
         * Required: false
         * Array: false
         */
        this.totalLoosePiecesCount = undefined;
        /**
         * Total weight of all the freight being moved as part of a Pickup Request.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Total number of motorized pieces.
         * Required: false
         * Array: false
         */
        this.totalMotorMovesCount = undefined;
        /**
         * Indicates whether or not a pick up request includes  any commodities with hazardous materials.
         * Valid Values:
         * 	Y - Hazmat shipment
         * 	N - Non-hazmat shipment
         * Required: false
         * Array: false
         */
        this.hazMatInd = undefined;
        /**
         * Indicates whether or not a pick up request  includes  any commodities are freezable.
         * Valid Values:
         * 	Y - Freezable shipment
         * 	N - Freezable shipment
         * Required: false
         * Array: false
         */
        this.freezableInd = undefined;
        /**
         * Indicates whether foodstuffs, feed or other edible material intended for consumption by humans or animals is included in this pick up request .
         * Valid Values:
         * 	Y - Has food, feed or other edible material included in pick up request
         * 	N - No food included in shipment
         * Required: false
         * Array: false
         */
        this.foodInd = undefined;
        /**
         * Indicates if the pick up request  is under the Con-Way Guaranteed Service Program.
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         * Indicates whether only PUP has to be used for pick up
         * Valid Values:
         * 	Y - Only PUP can be used
         * 	N - Any equipment can be used
         * Required: false
         * Array: false
         */
        this.pupOnlyInd = undefined;
        /**
         * Indicates whether customer location is a residential area
         * Valid Values:
         * 	Y - Residential
         * 	N - Non-residential
         * Required: false
         * Array: false
         */
        this.residentialInd = undefined;
        /**
         * Indicates whether Lift Gate is required at the customer location
         * Valid Values:
         * 	Y - Liftgate required
         * 	N - Liftgate not required
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         * Identifier of the appropriate customer in CIS, determined by the matching sub-routine
         * Required: false
         * Array: false
         */
        this.shipperCisCustomerNbr = undefined;
        /**
         * Business identifier of the appropriate customer in CIS, determined by the matching sub-routine
         * Required: false
         * Array: false
         */
        this.shipperMadCd = undefined;
        /**
         * Name Line 1 of the Company that is the Pickup Location.
         * Required: false
         * Array: false
         */
        this.shipperName1 = undefined;
        /**
         * Name Line 2 of the Company that is the Pickup Location.
         * Required: false
         * Array: false
         */
        this.shipperName2 = undefined;
        /**
         * Address of the Company that is the Pickup Location.
         * Required: false
         * Array: false
         */
        this.shipperAddress = undefined;
        /**
         * City where the Pickup Location is located.
         * Required: false
         * Array: false
         */
        this.shipperCity = undefined;
        /**
         * State where the Pickup Location is located.
         * Required: false
         * Array: false
         */
        this.shipperStateCd = undefined;
        /**
         * Country where the Pickup Location is located
         * Required: false
         * Array: false
         */
        this.shipperCountryCd = undefined;
        /**
         * Zip Code where the Pickup Location is located. First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the customer is located.
         * Required: false
         * Array: false
         */
        this.shipperZip6 = undefined;
        /**
         * Last 4 characters of the zip code for a US address for the Pickup Location.
         * Required: false
         * Array: false
         */
        this.shipperZip4RestUs = undefined;
        /**
         * Latitude of the customer location.
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * Longitude of the customer location.
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Estimated Dwell Time at the customer for the shipment
         * Required: false
         * Array: false
         */
        this.estimatedDwellTimeMinutes = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Identifies that shipment is included when sending a list of shipments to Optimizer.  Added 10/15/2020 LISG-8069
         * Required: false
         * Array: false
         */
        this.includeInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of the shipments that will be sent to the optimizer. Added 03/30/2020
 */
class PdoRequestShipment {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to uniquely identify each shipment for that optimizer run
         * Required: false
         * Array: false
         */
        this.shipmentSequenceNbr = undefined;
        /**
         * System identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Business identifier of the shipment to be sent to the optimizer.
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Host terminal SIC of the shipment for which pickup/delivery is taking place.
         * Required: false
         * Array: false
         */
        this.hostSicCd = undefined;
        /**
         * SIC that is scheduled to deliver the Shipment.
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         * Date that the shipment should be delivered based on standard transit days.
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * ETA of the PRO to host destination SIC in case the PRO is inbound
         * Required: false
         * Array: false
         */
        this.proEtaDateTime = undefined;
        /**
         * Each service center is divided into multiple geographic areas for delivery. This identifies the delivery area of destination service center in which this shipment's consignee is located.
         * Required: false
         * Array: false
         */
        this.geoAreaCd = undefined;
        /**
         * Total pieces from all commodities in the shipment.
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * Number of handling units
         * Required: false
         * Array: false
         */
        this.totalHandlingUnitCount = undefined;
        /**
         * Number of motorized pieces
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Total Weight of the shipment
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * The volume of the freight; derived from the dimensions captured by driver, or entered by a CSR or dock worker for an SLNC (Shipper
         * 	Load and Count), or from one of a variety of profiles. For details, see the description of CFT_PRFL_MTHD_CD.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * Total loose pieces from all commodities in the shipment.
         * Count of non Motor Move pieces associated with a shipment. This count plus Motor Moves defines the total number of pieces moving
         * 	within the XPO system for Operations productivity.  This does not necessarily balance with the total pieces or commodity pieces entered
         * during bill entry.
         * Required: false
         * Array: false
         */
        this.loosePiecesCount = undefined;
        /**
         * Total Number of Pallets in the Shipment
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         * Indicates that this shipment is time date critical.
         * Required: false
         * Array: false
         */
        this.tdcInd = undefined;
        /**
         * Indicates that an appointment is Set for this shipment.
         * Required: false
         * Array: false
         */
        this.appointmentInd = undefined;
        /**
         * Date of the appointment, if applicable.
         * Required: false
         * Array: false
         */
        this.appointmentDate = undefined;
        /**
         * Early committed time to the customer (Appointment Start Time or TDC Start Time)
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryFromTime = undefined;
        /**
         * Late committed time to the customer (Appointment End Time or TDC End Time or Noon for G12 shipments)
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryToTime = undefined;
        /**
         * Customer receiving hours start time (from customer profile)
         * Required: false
         * Array: false
         */
        this.receivingStartTm1 = undefined;
        /**
         * Customer receiving hours end time (from customer profile)
         * Required: false
         * Array: false
         */
        this.receivingEndTm1 = undefined;
        /**
         * Customer receiving hours start time (from customer profile - in case customer has multiple receiving hours)
         * Required: false
         * Array: false
         */
        this.receivingStartTm2 = undefined;
        /**
         * Customer receiving hours end time (from customer profile - in case customer has multiple receiving hours)
         * Required: false
         * Array: false
         */
        this.receivingEndTm2 = undefined;
        /**
         * Customer operations ready time
         * Required: false
         * Array: false
         */
        this.operationsReadyTime = undefined;
        /**
         * Customer operations close time
         * Required: false
         * Array: false
         */
        this.operationsCloseTime = undefined;
        /**
         * Delivery Window Start Time
         * Required: false
         * Array: false
         */
        this.deliveryWindowStartTime = undefined;
        /**
         * Delivery Window End Time
         * Required: false
         * Array: false
         */
        this.deliveryWindowEndTime = undefined;
        /**
         * Estimated Dwell Time at the customer for the shipment
         * Required: false
         * Array: false
         */
        this.estimatedDwellTimeMinutes = undefined;
        /**
         * Indicates whether only PUP can be used for delivery
         *
         * Valid Values:
         * 	Y - Only PUP can be used
         * 	N - Any equipment can be used
         * Required: false
         * Array: false
         */
        this.pupOnlyInd = undefined;
        /**
         * Indicates whether or not a shipment includes  any commodities with hazardous materials.
         *
         * Valid Values:
         * 	Y - Hazmat shipment
         * 	N - Non-hazmat shipment
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         * Indicates whether trailer needs to be dropped/spotted at the customer location
         *
         * Valid Values:
         * 	Y - Trailer has to be dropped/spotted
         * 	N - Trailer drop/spot not required
         * Required: false
         * Array: false
         */
        this.trailerDropInd = undefined;
        /**
         * Indicates whether it is an excessive length shipment
         *
         * Valid Values:
         * 	Y - Excessive length shipment
         * 	N - Not an excessive length shipment
         * Required: false
         * Array: false
         */
        this.excessiveLengthInd = undefined;
        /**
         * Indicates whether customer location is a residential area
         *
         * Valid Values:
         * 	Y - Residential
         * 	N - Non-residential
         * Required: false
         * Array: false
         */
        this.residentialInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         * Indicates if the shipment is under the Con-Way Guaranteed Service Program.
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         * Indicates whether foodstuffs, feed or other edible material intended for consumption by humans or animals is included in this shipment.
         *
         * Valid Values:
         * 	Y - Has food, feed or other edible material included in shipment
         * 	N - No food included in shipment
         * Required: false
         * Array: false
         */
        this.foodInd = undefined;
        /**
         * Indicates if the shipment is under the Guaranteed By Noon Service Program.
         * Required: false
         * Array: false
         */
        this.g12Ind = undefined;
        /**
         * Indicates if the shipment is under the Rapid Remote Service Program.
         * Required: false
         * Array: false
         */
        this.rrsInd = undefined;
        /**
         * Identifier of the appropriate customer, determined by the matching sub-routine
         * Required: false
         * Array: false
         */
        this.customerNbr = undefined;
        /**
         * Name of the customer
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         * Street address of the customer location
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * City where the customer is located
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         * State where the customer is located
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * Specifies the code of the country where the customer is located
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         * First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the customer is located.
         * Required: false
         * Array: false
         */
        this.zip6Cd = undefined;
        /**
         * Last 4 characters of the zip code for a US address
         * Required: false
         * Array: false
         */
        this.zip4Cd = undefined;
        /**
         * Latitude of the customer location
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * Longitude of the customer location
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Identifies that shipment is included when sending a list of shipments to Optimizer  Added 07/24/2020 LISG-6445
         * Required: false
         * Array: false
         */
        this.includeInd = undefined;
        /**
         * Identifies the trailer that the shipment is on.  Added 07/24/2020  LISG-6445
         * Required: false
         * Array: false
         */
        this.trailerId = undefined;
        /**
         * Internal Id of the delivery route to which the shipment is already assigned.  Added 10/07/2020  LISG-7884
         * Required: false
         * Array: false
         */
        this.routeInstanceId = undefined;
        /**
         * Flag to indicate whether optimizer should change the route of the shipment or not. If it is Y, then planner does not want optimizer to assign this shipment to another route.  Added 10/07/2020  LISG-7884
         * Required: false
         * Array: false
         */
        this.lockInd = undefined;
        /**
         * Identifies the city trailer that the shipment is on.  Added 10/13/20202   LISG-7973
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * Identifies the customer match group id the shipments have been assigned.  Added 12/01/2020  LISG-8921
         * Required: false
         * Array: false
         */
        this.sameAddressGroupId = undefined;
        /**
         * Identifies the target customer ID after merge.  Added 12/01/2020  LISG-8921
         * Required: false
         * Array: false
         */
        this.mergedIntoCustomerId = undefined;
        /**
         * Identifies the Shipments whose sequence needs to preserved as a part of the Incremental Optimization Run with Injected Solution.  Added 12/11/2020  LISG-9082
         * Required: false
         * Array: false
         */
        this.lockStopSequenceInd = undefined;
        /**
         * Identifies the Sequence Number of the Shipment in the Route whose sequence needs to preserved as a part of the Incremental Optimization Run with Injected Solution.  Added 12/11/2020 LISG-9082
         * Required: false
         * Array: false
         */
        this.lockStopSequenceNbr = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.pdoRequestCommodity = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.pdoRqstLnhDimension = undefined;
    }
}
/**
 * Optimizer run will typically have only one response, but we want the flexibility to have more than one.  This table will contain End time and Fail Reason (if applicable).
 */
class PdoResponseHeader {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the Run Response
         * Required: false
         * Array: false
         */
        this.runSequenceNbr = undefined;
        /**
         * Reason for failure returned from optimizer
         * Required: false
         * Array: false
         */
        this.failReason = undefined;
        /**
         * End time can be for aborted, failed, completed or timedout
         * Required: false
         * Array: false
         */
        this.endDateTime = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Holds the status of optimizer response from vendor.  Added 05/21/2020
         *
         * Valid Values:
         * SUCCESS
         * WARNING
         * ERROR
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         * Describes the reason for the optimizer status.  Added 05/21/2020
         * Required: false
         * Array: false
         */
        this.statusReason = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of routes recommended by the optimizer in its response for a particular run.  Added 03/30/2020
 */
class PdoResponseRoute {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.runSequenceNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the routes returned by the optimizer
         * Required: false
         * Array: false
         */
        this.optimizedRouteSequenceNbr = undefined;
        /**
         * Prefix of Route Name. Is usually the name of the Geographic area.
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * Suffix of Route Name. Is usually user assigned and could be a number assigned to uniquely identify the route within geographic area.
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Total number of stops on the route
         * Required: false
         * Array: false
         */
        this.totalStopCount = undefined;
        /**
         * Total number of Shipment BOLs (Bill of Lading) assigned to Route Includes undelivered shipments and completed pickups.
         * Required: false
         * Array: false
         */
        this.totalBillCount = undefined;
        /**
         * Total weight  in pounds fof the trailer after it is closed.
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * This is the time that the trailer was closed.  The trailer is closed when a seal number is entered on the trailer contents screen
         * Required: false
         * Array: false
         */
        this.loadCloseTime = undefined;
        /**
         * This is the Delivery Route Departure Time goal for this route to leave the Service Center.
         * Required: false
         * Array: false
         */
        this.routeStartTime = undefined;
        /**
         * The estimated time that a DSR will have completed his delivery stops.
         * The trailer will be empty and available to complete pickup stops.  06/02/2020 Switched from DATE to TMST
         * Required: false
         * Array: false
         */
        this.routeEmptyDateTime = undefined;
        /**
         * Equipment type is carried in the TMT database. This is an intelligen' code that will help identify the characteristics of a piece of equipment.
         * eg. TTC28 - could mean linehaul trailer 28ft long. .  05/05/2020 Changed from NUMBER to VARCHAR
         * Required: false
         * Array: false
         */
        this.equipmentTypeDetailCd = undefined;
        /**
         * Total Miles till route is empty.  06/02/2020  Added decimal places
         * Required: false
         * Array: false
         */
        this.totalMilesNbr = undefined;
        /**
         * Total time till route is empty 06/02/2020 - added MN suffix
         * Required: false
         * Array: false
         */
        this.totalTravelTimeMinutes = undefined;
        /**
         * Number of stops per hour.
         * Required: false
         * Array: false
         */
        this.stopsPerHourNbr = undefined;
        /**
         * Number of miles per stop.
         * Required: false
         * Array: false
         */
        this.milesPerStopNbr = undefined;
        /**
         * Number of deliveries that will miss the committed time.
         * Required: false
         * Array: false
         */
        this.missedDeliveryCount = undefined;
        /**
         * The number of Guaranteed by 12 shipments on this Trip
         * Required: false
         * Array: false
         */
        this.deliveryBeforeNoonCount = undefined;
        /**
         * Number of deliveries per hour.
         * Required: false
         * Array: false
         */
        this.deliveryPerHourCount = undefined;
        /**
         * Total cost for the route.
         * Required: false
         * Array: false
         */
        this.routeCostAmount = undefined;
        /**
         * Earliest ETA of the PROs assigned to the route.
         * Required: false
         * Array: false
         */
        this.earliestEtaDateTime = undefined;
        /**
         * Latest ETA of the PROs assigned to the route.
         * Required: false
         * Array: false
         */
        this.latestEtaDateTime = undefined;
        /**
         * Y/N - Successfully created route, trip, stops and activities for this route in SCO.
         * Required: false
         * Array: false
         */
        this.createdInScoInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Holds the reason code - if any - when vendor sends the response.  Addd 05/21/2020
         *
         * Valid values:
         * EXTRATRLR - This route causes a violation of the trailer count at the respective service centre.
         *
         * LARGEPSE - This is when shipments going to a customer location exceed the trailer capacity. Note that RouteMAX will create a route with all the shipments going to the customer location
         * Required: false
         * Array: false
         */
        this.reasonCd = undefined;
        /**
         * Holds the routes created in Edge PnD.  Added 06/02/2020
         * Required: false
         * Array: false
         */
        this.routeInstanceId = undefined;
        /**
         * Latitude of the anchor point (the location where the route starts it first pickup).  Added 06/02/2020
         * Required: false
         * Array: false
         */
        this.ancrptLatitudeNbr = undefined;
        /**
         * Longitude of the anchor point (the location where the route starts it first pickup).  Added 06/02/2020
         * Required: false
         * Array: false
         */
        this.ancrptLongitudeNbr = undefined;
        /**
         * Zip code of the anchor point (the location where the route starts it first pickup).  Added 06/02/2020
         * Required: false
         * Array: false
         */
        this.ancrptZip6Cd = undefined;
        /**
         * The calculated Pallet Space Equivalent of the route. EX: if we have a calculated PSE as 4.50 it means the shipment takes up space of 4.50 pallets.  Added 08/05/2020  LISG-6670
         * Required: false
         * Array: false
         */
        this.totalPalletSpcNbr = undefined;
        /**
         * Total Stops for Pickups assigned to the route by Optimizer.  Added 10/05/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.pickupStopCount = undefined;
        /**
         * Total Weight of Pickups assigned to the route by Optimizer.  Added 10/05/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.totalPickupWeightLbs = undefined;
        /**
         * Total Motor Moves of pickups assigned to the route by Optimizer.  Added 10/05/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.totalPickupMotorMovesCount = undefined;
        /**
         * Optimizer calculated time when route will end at the terminal after completing all assigned activities.  Added 10/05/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.routeEndTime = undefined;
        /**
         * Total Motor Moves of deliveries assigned to the route by Optimizer.  Added 10/06/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.totalDeliveryMotorMovesCount = undefined;
        /**
         * Identifies the city trailer that the shipment is on.  Added 10/13/20202   LISG-7973
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the details of stops for each route recommended by the optimizer in its response for a particular run.  Added 03/30/2020
 */
class PdoResponseRouteStop {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the routes returned by the optimizer  (Foreign Key - Route)
         * Required: false
         * Array: false
         */
        this.optimizedRouteSequenceNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the stops for each route returned by the optimizer
         * Required: false
         * Array: false
         */
        this.optimizedStopSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.runSequenceNbr = undefined;
        /**
         * Identifier of the appropriate customer in CIS
         * Required: false
         * Array: false
         */
        this.customerNbr = undefined;
        /**
         * Customer Name.
         * Required: false
         * Array: false
         */
        this.customerName = undefined;
        /**
         * Street address of the customer location
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         * City where the customer is located
         * Required: false
         * Array: false
         */
        this.cityName = undefined;
        /**
         * State where the customer is located
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         * First 5 characters of the zip code for a US address or first 6 characters of the Canadian zip code of the address where the customer is located.
         * Required: false
         * Array: false
         */
        this.zip6Cd = undefined;
        /**
         * Last 4 characters of the zip code for a US address
         * Required: false
         * Array: false
         */
        this.zip4Cd = undefined;
        /**
         * The estimated time that the driver will arrive at this Stop. The date is determined by the Trip date.
         * Estimated times can be determined by the Mapping software, or entered by the user.
         * As actual times are entered for earlier stops, then the variance will be applied to all estimates for subsequent stops.  06/02/2020 switched from DATE to TMST
         * Required: false
         * Array: false
         */
        this.expectedArriveDateTime = undefined;
        /**
         * The estimated time that the driver will leave from this Stop.
         * The date is determined by the Trip date.
         * Estimated times can be determined by the Mapping software, or entered by the user.
         * As actual times are entered for earlier stops, then the variance will be applied to all estimates for subsequent stops.  06/02/2020 switched from DATE to TMST
         * Required: false
         * Array: false
         */
        this.expectedDepartDateTime = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Stores the sequence number generated by the optimizer response.  Added 05/05/2020
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         * The calculated Pallet Space Equivalent of the stop. EX: if we have a calculated PSE as 4.50 it means the shipment takes up space of 4.50 pallets.  Added 08/05/2020  LISG-6670
         * Required: false
         * Array: false
         */
        this.totalPalletSpcNbr = undefined;
        /**
         * Activity to be performed at that Stop.
         *
         * Examples: PICKUP, DELIVERY
         * Required: false
         * Array: false
         */
        this.stopTypeCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Contains the list of shipments (activities) at a stop for a route that is recommended by the optimizer in its response for a particular run.  Added 03/30/2020
 */
class PdoResponseRouteStopAcvty {
    constructor() {
        /**
         * Run Number (Foreign Key - Optimizer control table)
         * Required: false
         * Array: false
         */
        this.runNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the routes returned by the optimizer  (Foreign Key - Route)
         * Required: false
         * Array: false
         */
        this.optimizedRouteSequenceNbr = undefined;
        /**
         * Sequence Number to Uniquely identify the stops for each route returned by the optimizer (Foreign Key Route Stop)
         * Required: false
         * Array: false
         */
        this.optimizedStopSequenceNbr = undefined;
        /**
         * Sequence Number to uniquely identify the activities at every route stops returned by the optimizer
         * Required: false
         * Array: false
         */
        this.optimizedActivitySequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.runSequenceNbr = undefined;
        /**
         * PRO Number to be delivered at the Stop
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Unique identifier of the PRO to be delivered at the Stop.
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * The calculated Pallet Space Equivalent of the stop. EX: if we have a calculated PSE as 4.50 it means the shipment takes up space of 4.50 pallets. Added 08/24/2020  LISG-6973
         * Required: false
         * Array: false
         */
        this.totalPalletSpcNbr = undefined;
        /**
         * Pickup Request assigned by the Optimizer to Route Stop.  Added 10/05/2020  LISG-7755
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Placard {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.placardType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.unnaCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
    }
}
/**
 *
 */
class PlanningTrailer {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.etaDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.endDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nextFacSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleOriginSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedCubeProfitability = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDestinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadOriginSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billCountNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frzblInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedServiceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDfrdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsr1EmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsr1Name = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsr2EmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsr2Name = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedCbePrfCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedNbrOfFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plannedDoorSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plannedSectorId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plannedYardCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleViaSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrBrkOrPropCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrExcludeTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrManualInclInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrOverrideEtaDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrPlanAccptInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadedRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrDoorAssignmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bestDoorSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bestDoorStepsNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.assignmentDoorStepsNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inclHdldProsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrLoadedDestinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrTmanRvwInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrDoorReqInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ssrPupTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planInclFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reviewRequiredFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.properInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerActnCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mustMoveSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedBrkInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nextOutboundSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.partialTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.partialTrailerManualOverrideInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELATIO
         * N id is assigned to various rows from different tables that form a single busine
         * ss transactions. In other words, this groups multiple rows across various tables
         * 	for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is
         * assigned to various rows from different tables that form a single database trans
         * actions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.xdockShipment = undefined;
    }
}
/**
 * Holds PND driver information required by PnD Optimizer.  Added 05/19/2020
 */
class PndDriver {
    constructor() {
        /**
         * The home SIC of the Driver.
         * Required: false
         * Array: false
         */
        this.terminalSicCode = undefined;
        /**
         * Employee Id of the DSR.
         * Required: false
         * Array: false
         */
        this.driverEmployeeId = undefined;
        /**
         * Name of the DSR.
         * Required: false
         * Array: false
         */
        this.driverName = undefined;
        /**
         * Job type of the driver.
         *
         * Bid - Drivers with Bid will have specified start and end times in the start and end times columns next
         * Flex - Drivers with Flex will have the start and end time columns empty and disabled for edition
         * Required: false
         * Array: false
         */
        this.jobTypeCd = undefined;
        /**
         * Earliest time employee can be dispatched today.
         * Required: false
         * Array: false
         */
        this.earliestDispatchTime = undefined;
        /**
         * End Time of the DSR.
         * Required: false
         * Array: false
         */
        this.shiftEndTime = undefined;
        /**
         * The total time, in minutes, that the DSR is available to complete the deliveries on a route.
         * Required: false
         * Array: false
         */
        this.routeAvailableDeliveryMinutes = undefined;
        /**
         * Wage (dollars per hour) for regular time.
         * Required: false
         * Array: false
         */
        this.regularTimeWageRate = undefined;
        /**
         * Wage (dollars per hour) for overtime.
         * Required: false
         * Array: false
         */
        this.overtimeWageRate = undefined;
        /**
         * Latest time employee can be dispatched on any given day.
         * Required: false
         * Array: false
         */
        this.latestDispatchTime = undefined;
        /**
         * The geographical area preferrred by the driver. Source is GEO_AREAS in SCOPRD.
         * Required: false
         * Array: false
         */
        this.preferredGeoAreaCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Sourced from M5, stores the PND City trailers that have liftgate installed. It is used during inbound planning to identify equipment with liftgate for Pand D Optimizer.  Added 06/02/2020
 */
class PndLiftgateTrailer {
    constructor() {
        /**
         * This is the 4 character prefix of the equipment id. (eg. 0415 or SWFT etc) - park of PK.
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         * This is the six digit equipment id suffix. (eg. 123456 or 37 or 3737). Park of PK.
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         * Identifies whether trailer has liftgate or not.
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds the driver schedule for a particular day of the week; it will override the default.  Added 09/10/2020  LISG-7270
 */
class PndOverrideDaySchedule {
    constructor() {
        /**
         * The home SIC of the Driver.
         * Required: false
         * Array: false
         */
        this.terminalSicCode = undefined;
        /**
         * Employee Id of the DSR.
         * Required: false
         * Array: false
         */
        this.driverEmployeeId = undefined;
        /**
         * Override schedule day  of the week
         * Required: false
         * Array: false
         */
        this.overrideDay = undefined;
        /**
         * Earliest time employee can be dispatched today.
         * Required: false
         * Array: false
         */
        this.earliestDispatchTime = undefined;
        /**
         * End Time of the DSR.
         * Required: false
         * Array: false
         */
        this.shiftEndTime = undefined;
        /**
         * Latest time employee can be dispatched on any given day.
         * Required: false
         * Array: false
         */
        this.latestDispatchTime = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class PositioningNode {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedArriveDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.localEstimatedArriveDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.localEstimatedDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planOpenTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planCloseTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planDistanceFromPrevNodeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.address = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.city = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stateCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.countryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.zip6 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dwellTimeMinimum = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELA
         * TION id is assigned to various rows from different tables that form a single bus
         * iness transactions. In other words, this groups multiple rows across various tab
         * les for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id
         * is assigned to various rows from different tables that form a single database tr
         * ansactions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Rows in this table represent the location, time and capacity required for a cluster of pickups projected based on historical actual pickups. They are used to optimize routes such that they when most deliveries are complete, the driver is near to where pickups are most likely to be ready.  Added 09/02/2020  LISG-7152
 */
class PositioningPoint {
    constructor() {
        /**
         * Unique instance id
         * Required: false
         * Array: false
         */
        this.positioningPointId = undefined;
        /**
         * Host SIC of the service center responsible for the pickups in this area.
         * Required: false
         * Array: false
         */
        this.sicCode = undefined;
        /**
         * Projected date on which this pickup capacity will be required.
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         * Likely earliest ready time for the projected pickup cluster
         * Required: false
         * Array: false
         */
        this.startTime = undefined;
        /**
         * Likely close time for the cluster.
         * Required: false
         * Array: false
         */
        this.endTime = undefined;
        /**
         * Projected weight of the projected pickups
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         * Projected motor moves of the projected pickups
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Projected pallet space equivalent of the project pickups
         * Required: false
         * Array: false
         */
        this.totalPalletSpcNbr = undefined;
        /**
         * Latitude of the point in the cluster where pickups should start
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         * Longitude of the point in the cluster where pickups should start
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Name associated with the point
         * Required: false
         * Array: false
         */
        this.pointName = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Projected count of pickups associated with this point.  Added 10/20/2020 - add on to LISG-7152
         * Required: false
         * Array: false
         */
        this.pickupCount = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Defines the SIC print preferences for PnD manifest printing.  Added 12/06/2021  LISG-15527
 */
class PrintOption {
    constructor() {
        /**
         * Standard identification code of a service center
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         * Indicates what will trigger automatic printing of <report type> for the SIC.
         *
         * Valid Values:
         * DISPATCH
         * TRAILER_CLOSE
         * DO_NOT_AUTO_PRINT
         * Required: false
         * Array: false
         */
        this.pndManifestTriggerCd = undefined;
        /**
         * Indicates printer selection for PnD Manifest Printing
         * Required: false
         * Array: false
         */
        this.pndManifestPrinter = undefined;
        /**
         * Indicates tray selection for PnD Manifest Printing
         * Required: false
         * Array: false
         */
        this.pndManifestTray = undefined;
        /**
         * Indicates what will trigger automatic printing of <report type> for FBDS printing.
         *
         * Valid Values:
         * DISPATCH
         * TRAILER_CLOSE
         * DO_NOT_AUTO_PRINT
         * Required: false
         * Array: false
         */
        this.fbdsTriggerCd = undefined;
        /**
         * Indicates printer selection for FBDS printing
         * Required: false
         * Array: false
         */
        this.fbdsPrinter = undefined;
        /**
         * Indicates tray selection for FBDS Printing
         * Required: false
         * Array: false
         */
        this.fbdsTray = undefined;
        /**
         * Indicates Re-print Indicator for FBDS Printing
         * Required: false
         * Array: false
         */
        this.fbdsReprintInd = undefined;
        /**
         * Indicates what will trigger automatic printing of <report type> for Outbound Break-Out printing.
         *
         * Valid Values:
         * TRIP_RETURN
         * TRIP_COMPLETE
         * DO_NOT_AUTO_PRINT
         * Required: false
         * Array: false
         */
        this.obBreakoutTriggerCd = undefined;
        /**
         * Indicates printer selection for Outbound Break-Out printing
         * Required: false
         * Array: false
         */
        this.obBreakoutPrinter = undefined;
        /**
         * Indicates tray selection for Outbound Break-Out printing
         * Required: false
         * Array: false
         */
        this.obBreakoutTray = undefined;
        /**
         * Indicates Shipper Load and Count option for Outbound Breakout
         * Required: false
         * Array: false
         */
        this.obBreakoutSlcInd = undefined;
        /**
         * Indicates what will trigger automatic printing of <report type> for DSR Check-in printing.
         *
         * Valid Values:
         * TRIP_RETURN
         * TRIP_COMPLETE
         * DO_NOT_AUTO_PRINT
         * Required: false
         * Array: false
         */
        this.dsrCheckinTriggerCd = undefined;
        /**
         * Indicates printer selection for DSR Check-in printing
         * Required: false
         * Array: false
         */
        this.dsrCheckinPrinter = undefined;
        /**
         * Indicates tray selection for DSR Check-in printing
         * Required: false
         * Array: false
         */
        this.dsrCheckinTray = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Determines the language preferences for FBDS.  Added 05/17/2022  LISG-16856
         *
         * Valid Values:
         * DEFAULT
         * FRENCH_BILINGUAL
         * Required: false
         * Array: false
         */
        this.fbdsVersionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ProMovementHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedAtSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedServiceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.updatedTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.misloadInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.misloadAuthrzUserId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDoor = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of ProMovementHist. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class ProMovementHistSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.updatedTimestamp = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class ProfileExcludeTrailer {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.selectionProfileId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdSuffixNbr = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ProfileSic {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.selectionProfileId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileSicSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.arrivalCutoffTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dateOffsetNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicGroupName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicTypeCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Projection {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.planInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prjctnSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.adjustedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nextFacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.divertSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELATION i
         * d is assigned to various rows from different tables that form a single business
         * transactions. In other words, this groups multiple rows across various tables fo
         * r a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is ass
         * igned to various rows from different tables that form a single database transact
         * ions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class PurDeliveryLocationRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionReason = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.appointmentRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class PurEventLogRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventSourceCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.requesterName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperZip6 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperCisCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationZip6 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalLoosePiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cubeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazardousMaterialInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freezableInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headloadInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.strandInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bolInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ediSenderId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ediSenderShipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventReason = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventCreatedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSourceCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduleCancelContactName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduleCancelContactPhoneNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class PurPickupRequestRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduleCancelReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduleCancelReason = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cadCallNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduleCancelContactName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rescheduleCancelContactPhoneNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.strandInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.strandReasonCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class PurRequestorRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.requesterInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class RefEquipmentIdXrefRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdMbf = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdMcms = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actDateTime = undefined;
    }
}
/**
 *
 */
class RefScacCodeRo {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.scacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierMcn = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierCity = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierState = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierCountry = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierZip = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierPhone = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierChngFld = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierAddressChngCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierAdoptionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierUserCrtdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierLastUpdateUserid = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierLastUpdateDateTime = undefined;
    }
}
/**
 *
 */
class RefStandardCodeRo {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.codeType = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.codeValue = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shortDescription = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.longDescription = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveEndDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveStartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Remark {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Expanded Name :  Type code
         *
         * Desc : This code can be used to categorize the remarks.
         *
         * Valid Values :
         * 1 - Adhoc Hazardous Material Remarks
         * 2 - Shipping Remarks
         * 3 - Authority Line text Remarks
         * 4 - Delivery Attachment Remarks
         * 5 - Operational Freight Handling Remarks
         * 6 - Final Delivery Date Correction Remarks
         *
         * Source: New/Entered
         *
         * Design Considerations : This can be used for subsetting purposes and for programatically pulling certain remarks to display and/or print.
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Expanded Name: Remark Text
         *
         * Desc: Remark Text
         * Required: false
         * Array: false
         */
        this.remark = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of Remark. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class RemarkSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Expanded Name :  Type code
         *
         * Desc : This code can be used to categorize the remarks.
         *
         * Valid Values :
         * 1 - Adhoc Hazardous Material Remarks
         * 2 - Shipping Remarks
         * 3 - Authority Line text Remarks
         * 4 - Delivery Attachment Remarks
         * 5 - Operational Freight Handling Remarks
         * 6 - Final Delivery Date Correction Remarks
         *
         * Source: New/Entered
         *
         * Design Considerations : This can be used for subsetting purposes and for programatically pulling certain remarks to display and/or print.
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class Route {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.plannedDoor = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockLocationInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.liftgateCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalBillCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPalletCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalMmCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.warrantyCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryStopCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupStopCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceValidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.xdockReleaseInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryRouteDepartTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originNodeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originNodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originNodeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationNodeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationNodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationNodeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedTrailerTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedNodeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedNodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedNodeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalDeliveryWeightCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.protectRouteInd = undefined;
        /**
         * Same value for event id is assigned
         * 	to various rows from different tables that form a single database transactions.
         *
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * The number of RRS shipments on this
         *
         * Route.  Added 10/23/2019
         * Required: false
         * Array: false
         */
        this.rrsCount = undefined;
        /**
         * The number of Guaranteed by
         *
         * 12 shipments on this Route.  Added 10/23/2019
         * Required: false
         * Array: false
         */
        this.guaranteedBy12Count = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteEarliestDueTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteEarliestMessage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteLatestMessage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteLatestDueTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDispatchTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedClearTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockLocationName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fullTrailerId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentDockLocation = undefined;
        /**
         * Special services requested by a customer for a shipment delivery.
         * Required: false
         * Array: true
         */
        this.specialServices = undefined;
        /**
         * Indicates whether the shipment has a scheduled appointment for delivery.
         * Required: false
         * Array: false
         */
        this.appointmentStatusCd = undefined;
        /**
         * The earliest date of service.
         * Required: false
         * Array: false
         */
        this.earliestServiceDate = undefined;
        /**
         * The latest date of service.
         * Required: false
         * Array: false
         */
        this.latestServiceDate = undefined;
        /**
         * The number of deliveries left to be done.
         * Required: false
         * Array: false
         */
        this.deliveryRemainingCount = undefined;
        /**
         * The number of deliveries that have been completed.
         * Required: false
         * Array: false
         */
        this.deliveryCompletedCount = undefined;
        /**
         * The number of pickups that have not been done.
         * Required: false
         * Array: false
         */
        this.pickupRemainingCount = undefined;
        /**
         * The number of pickups that have been completed.
         * Required: false
         * Array: false
         */
        this.pickupCompletedCount = undefined;
        /**
         * The code which determines whether No, Partial, or All Prints for FBDS documents will be printed for the route.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCountCd = undefined;
    }
}
/**
 *
 */
class RouteShipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeShipmentSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.positionOnTrailer = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadedFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.operatingRouteFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.diversionLoadDestinationSicCd = undefined;
        /**
         * Same value for event id is
         * 	assigned to various rows from different tables that form a single database tran
         * sactions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates if
         * 	the route shipment in a route is incomplete. This means not all
         * 	handling units are in the same route. Route Handling Unit records are
         * 	expected in this case, to identify which handling units are part of
         * 	this route shipment.
         * Required: false
         * Array: false
         */
        this.completeShipmentInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of RouteShipment. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class RouteShipmentSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.historyActionCd = undefined;
        /**
         * The timestamp of when this snapshot was created.
         * Required: false
         * Array: false
         */
        this.historyCreatedTimestamp = undefined;
        /**
         * Snapshot.
         * Required: false
         * Array: false
         */
        this.routeShipment = undefined;
        /**
         * Previous values. Only attributes that are different from snapshot will be populated
         * Required: false
         * Array: false
         */
        this.routeShipmentDiff = undefined;
    }
}
/**
 * Holds a snapshot of Route. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class RouteSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.historyActionCd = undefined;
        /**
         * The timestamp of when this snapshot was created.
         * Required: false
         * Array: false
         */
        this.historyCreatedTimestamp = undefined;
        /**
         * Snapshot.
         * Required: false
         * Array: false
         */
        this.route = undefined;
        /**
         * Previous values. Only attributes that are different from snapshot will be populated
         * Required: false
         * Array: false
         */
        this.routeDiff = undefined;
    }
}
/**
 *
 */
class RouteTrip {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeTripSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fromDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.toDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Same value for event id is ass
         * igned to various rows from different tables that form a single database transact
         * ions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of RouteTrip. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class RouteTripSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.historyActionCd = undefined;
        /**
         * The timestamp of when this snapshot was created.
         * Required: false
         * Array: false
         */
        this.historyCreatedTimestamp = undefined;
        /**
         * Snapshot.
         * Required: false
         * Array: false
         */
        this.routeTrip = undefined;
        /**
         * Previous values. Only attributes that are different from snapshot will be populated
         * Required: false
         * Array: false
         */
        this.routeTripDiff = undefined;
    }
}
/**
 *
 */
class Schedule {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleIdNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.milg = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.displayUserid = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.finalDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.etaDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.intmdlScac = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.discrInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduleRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.onDfrdLegInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ScoCollectedPayment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.paymentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.paymentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.paymentMethodCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.checkNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.paymentAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.paymentNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentTypeCd = undefined;
        /**
         * Same value for CORREL
         * ATION id is assigned to various rows from different tables that form a single bu
         * siness transactions. In other words, this groups multiple rows across various ta
         * bles for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id
         * 	is assigned to various rows from different tables that form a single database t
         * ransactions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ScoEvent {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.evtInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupReqInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.employeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sendToDeviceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.event = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serverAddress = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hookInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deviceTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrUserId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.applicationCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activitySpfcNodeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activitySpfcNodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activitySpfcName = undefined;
        /**
         * Unique ID assigned to Route.
         *
         * Added 7/2/2019
         * Required: false
         * Array: false
         */
        this.routeInstId = undefined;
        /**
         * Same value for CORRELATION id is
         * assigned to various rows from different tables that form a single business trans
         * actions. In other words, this groups multiple rows across various tables for a s
         * ingle business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of ScoEvent. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class ScoEventSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.evtInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class SelectionProfile {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.selectionProfileId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.createDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteStartTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteEndTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inboundDestinationSicInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundArrivalTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundOverheadTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundTrapTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundEnrouteTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outboundCloseTrailerInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.weekDayNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.owningSicCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.profileSic = undefined;
    }
}
/**
 *
 */
class Shipment {
    constructor() {
        /**
         * Expanded Name : Instance identifier
         *
         * Desc : System generated unique identifier (as per CBD standard).
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Expanded Name : Type code
         *
         * Desc : Identifies the type of shipment.
         *
         *
         * Valid Values:
         * A = Normal Movement(NORM)
         * B = Master Shipment(MSTR)
         * C = Segment of a Master shipment (MSEG)
         * D = Revenue Bill for Split segments (PTLT)
         * E = Partial Shipment Segment (PSEG)
         * G = Claims overage Reporting Bill (OREP)
         * H = Genral Claims Business Shipment (GCBZ)
         * I =  Astray Freight Segment (MOVR)
         * J = Company Business Shipment (COBZ)
         * K = Expedite
         *
         *
         * Note:
         * Overage Reporting Bill (OREP) is entered into the system just  to indicate to the claims system there is an overage shipment on dock.
         *
         * Astray freight(MOVR) is used to move freight to it s correct destination after it accidentally ended up somewhere else and its original PRO number is known.
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         * Expanded Name : PRO number (in text format)
         *
         * Desc : This number identifies a shipment. This is the identifier given to the customer as a reference.
         *
         * The numbers are printed on labels for each service center.  Labels are put on the Bill-Of-Lading and on the individual pieces of freight for identification.
         *
         * Internal PRO number format is Oppp-sssss-c where 'ppp' is the pro-prefix, 'ssss' is the pro-suffix and 'c' is the check digit (modulus 7). It is usually displayed in the format 'ppp-sssss-c'
         *
         * An expedited shipment may not always have a real PRO number. It may be given a dummy number in the same format and should not be written out to MBF.
         *
         * MBF: root MBPRO
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         * Expanded Name : Bill Status Code
         *
         * Desc : Current status of the Shipment from billing point of view.
         *
         * Valid Values:
         * 1 - Unbilled
         * 2 - In FBES Suspense Queue
         * 3 - Billed
         * 4 - Rated
         * 5 - Paid
         * 6 - Cancelled
         *
         * Source : MBF, E-commerce
         * Required: false
         * Array: false
         */
        this.billStatusCd = undefined;
        /**
         * Expanded Name :  Charge to code
         *
         * Desc : Identifies who is responsible for charges for the Shipment - either the Shipper or the Consignee or the responsibility may be split between BOTH parties. For BOTH bills, the split for each line item will be stored along with the line item in the commodity, ac_svc or the misc_line_item table in future. Currently the total PPD and total COLLECT charges are currently stored as separate line items in the misc_line_item entity.
         *
         * Valid Values :
         * 	P    -  Prepaid - shipper responsible for all charges
         * 	C    -  Collect - consignee responsible for all charges
         * 	B    -  Both - shipper and consignee responsible
         * 								-  Valid only for Non-Revenue bills.
         *
         * Source: MBF : MBPRTPPD, MBPRTCOL
         * Required: false
         * Array: false
         */
        this.chargeToCd = undefined;
        /**
         * Expanded Name : Original terminal SIC
         *
         * Desc : SIC where the Shipment originates.  Usually associated with PRO prefix and can be overriden.
         *
         * Source : MBF: root MBORGIN
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
        /**
         * Expanded Name : Destination terminal SIC
         *
         * Desc : SIC that is scheduled to deliver the Shipment.
         *
         * Source : MBF: root MBDEST
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         * Expanded Name : Current SIC
         *
         * Desc : SIC where the Shipment currently is.
         *
         * This is a denormalization to avoid scanning all movements to find current location.
         *
         * Source : MBF: root MBLOCX
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         * Expanded Name : Source Code
         *
         * Desc : Indicates the source or origination of the Pro.  This code is needed for ACOR processing to determine whether to roll back to the initial data.
         *
         * Valid Values:
         * 	1  - Web        (Pickup Request web page)
         * 	2  - EDI        (set 204 or set 211)
         * 	3  - FTP        (file transfer)
         * 	4  - FBES    (manual Bill Entry)
         * 	5  - TCON or LOAD    (i.e. skeleton TCON or non-TCON LOAD)
         * 	6  - Pickup Request
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * Expanded Name : Pickup Date
         *
         * Desc : Earliest available date that Shipment can be
         * picked up from a customer.
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         * Expanded Name : Ready Time
         *
         * Desc : Earliest available time that freight can be
         * picked up from a customer.
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.readyTime = undefined;
        /**
         * Expanded Name : Close Time
         *
         * Desc : Latest available time that freight can be
         * pickup up from a customer.
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.closeTime = undefined;
        /**
         * Expanded Name : Total pieces count
         *
         * Desc : Total pieces from all commodities in the shipment.
         *
         * This can be derived from the individual commodities, but besides performance, there is a timing issue as well. SMART needs the information when freight is loaded on the trailer (TCON) and it may happen before the bill has been entered into the system.
         *
         * Source : MBF: root: MBTOTPCS
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         * Exapnded Name : Motorized piece count
         *
         * Desc : Identifies the number of moves or lifts made with a forklift or a pallet jack to move freight without manual intervention by the driver. This count may be equal to or less than the number of pieces in the shipment.  This is used by the costing system.
         *
         * Source : MBF root -  MBMTRPCS
         * Required: false
         * Array: false
         */
        this.motorizedPiecesCount = undefined;
        /**
         * Expanded Name : Total weight (in pounds)
         *
         * Desc : Total weight of all the freight being moved as a unit.  If this is a  Mother  bill then this is the total of all of the  Daughter  bills.
         *
         * Any rating weight allowances or variations (AW, DW, RW, NW) are not included.
         *
         * This can be derived from the individual commodities, but besides performance, there is a timing issue as well. SMART needs the information when freight is loaded on the trailer (TCON) and it may happen before the bill has been entered into the system.
         *
         * Source : MBF: root: MBTOTWT
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         * Expanded Name : Default Tariff
         *
         * Desc : This identifies the default qualifying tariff for the shipment. If the rating process does not identify any other tariffs for the customer from rate aids, this is used as the rating tariff.
         *
         * Source : MBF root - MBTARIFF
         * Required: false
         * Array: false
         */
        this.defaultTariffsId = undefined;
        /**
         * Expanded Name : Total charges
         *
         * Desc : Includes all charges that contribute to the CTS revenue minus discount.  Includes advance+beyond carrier charges and COD amount.
         *
         * Source : MBF: root: MBTOTAL
         * Required: false
         * Array: false
         */
        this.totalChargeAmount = undefined;
        /**
         * Expanded Name : Total charges in US dollars
         *
         * Desc : Includes all charges that contribute to the CTS revenue minus discount in US dollars.  Includes advance+beyond carrier charges and COD amount.
         *
         * Source : MBF: root: MBTOTAL
         *
         * Design Notes: This field was added as a denorm to keep the total charges in USD. Normally this field is same as the tot charges field. However this field is differnt when the rating currncy is not USD.
         * Required: false
         * Array: false
         */
        this.totalUsdAmount = undefined;
        /**
         * Expanded Name: Split Indicator
         *
         * Desc: Indicates whether the shipment has been split or not.
         *
         * Valid values: Y/N
         *
         * Source: MBF MBCLAS2 -  This actually replaces the BILL CLASS 2 field in the MBF.
         * Required: false
         * Array: false
         */
        this.splitInd = undefined;
        /**
         * Expanded Name : Hazardous Materials indicator
         *
         * Desc : Indicates whether or not a shipment includes  any commodities with hazardous materials. It is a denormalized attribute for performance puposes.
         *
         * Valid Values : Y/N
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         * Expanded Name : Freezable Indicator
         *
         * Desc : Indicates whether shipment requires special handling to prevent freezing during harsh weather.
         *
         * Ideally, it should be specified at the commodity level, but has been de-normalized for Shipment, since that is level at which it is currently captured
         *
         * Source: MBF root - MBFZB (x 08  of MBTRAFIC)
         *
         * Valid Values : Y/N
         *
         * Y - Can be damaged by freezing
         * Required: false
         * Array: false
         */
        this.freezableInd = undefined;
        /**
         * Expanded Name : Signature service indicator
         *
         * Desc : Indicates if anyone handling the freight is required to sign on a signature sheet.
         *
         * Source : MBF: root: MBSGSRVC (x 04  of MBFLAG9)
         *
         * Valid Values : Y/N
         * Required: false
         * Array: false
         */
        this.signatureServiceInd = undefined;
        /**
         * Expanded Name : Revenue Bill Indicator
         *
         * Desc : Indicates whether the shipment will generate revenue for Con-way or not.
         *
         * Valid Values :
         *
         * Y - generates revenue
         * N - does not generate revenue
         * Required: false
         * Array: false
         */
        this.revenueBillInd = undefined;
        /**
         * Expanded Name : Manual rating indicator
         *
         * Desc : Specifies whether the shipment  in question needs to be manually rated or not. If required, it may be changed to a code indicating the reason as well for manually rating it.
         *
         * It is same as special instructions field on the FBES screen.
         *
         * ==NOTE==
         * When this indicator is set, it means the rates and charges must be manully reviewed before the bill is marked as RATED. Howerver the auto rating system is allowed to put rates and charges on the bill prior to putting it in a manual entry or manual audit queue.
         *
         * This is different from the indicator AUTO_RATEABLE_IND which indicates whether the auto rating system is even allowed to put rates and charges on the bill.
         *
         * Source : MBF: root: MBSCHDI (x 80  of MBFLAGB)
         *
         * Valid Values : Y/N
         * Required: false
         * Array: false
         */
        this.manualRatingRequiredInd = undefined;
        /**
         * Expanded Name : Audit Required Indicator
         *
         * Desc : Used to determine if the pro requires auditing after rating.
         *
         * Valid Values :
         * Y - Pro does require auditing
         * N - Pro does not require auditing
         *
         * Source : MBF:  Root: MBFBAUDT (X 01  of MBFLAGI)
         * Required: false
         * Array: false
         */
        this.auditInd = undefined;
        /**
         * Expanded Name : Cash indicator
         *
         * Desc : This indicates if cash was collected at the tim eof pick up or should be collected at the time of delivery.
         *
         * This information is also printed on the delivery set to inform the driver to collect cash.
         *
         * Source : MBF: root: MBCASH (x 10  of MBFLAG2)
         *
         * Valid Values : Y/N
         * Required: false
         * Array: false
         */
        this.cashInd = undefined;
        /**
         * Expanded Name : Cash collected code
         *
         * Desc : A code indicating whether any cash was
         * collected at the time of pick up or delivery or not. Cash may be collected  regardless of whether it was required or not and if  so, the invoice may need to be stopped.
         *
         * It has been modelled as a code instead of an indicator
         * since it needs to be initialized to a value other than
         * NO.
         *
         * Source : New
         *
         * Valid Values :
         * blank - Initial value
         * Y - Yes, cash was collected
         * N - No cash collected on delivery
         * Required: false
         * Array: false
         */
        this.cashCollectInd = undefined;
        /**
         * Expanded Name : Government Bill of Lading indicator
         *
         * Desc : Indicates whether this is a government shipment or not.
         *
         * Source : MBF: root: MBGBLT (x 01  of MBTRAFIC)
         *
         * Valid Values : Y/N
         * Required: false
         * Array: false
         */
        this.govtBolTrafficInd = undefined;
        /**
         * Expanded Name : Guaranteed Indicator
         *
         * Desc : Indicates if the shipment is under the Con-Way Guaranteed Service Program.
         *
         * Values : Y/N
         *
         * Source : MBF, E-commerce
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         * Expanded Name : Guaranteed Coupon Indicator
         *
         * Desc : Indicates that a Coupon has been used to allow the shipment to participate in the Con-Way Guaranteed Service Program for free.
         *
         * Values : Y/N
         *
         * Source : BASE
         * Required: false
         * Array: false
         */
        this.guaranteedCouponInd = undefined;
        /**
         * Expanded Name : Purge blocked indicator
         *
         * Desc : Indicates that the details cannot be purged. Usually set when there are inconsistencies for Master / MSEG or Part lot/PSEG  shipments.
         *
         * Valid values : Y/N
         *
         * Source : MBF: root: MBPUBLK (x 10  of MBFLAG3)
         * Required: false
         * Array: false
         */
        this.purgeBlockInd = undefined;
        /**
         * Expanded Name: Last MOVR PRO Number
         *
         * Desc: The Pro number for the last MOVR PRO billed for this shipment.
         * Required: false
         * Array: false
         */
        this.lastMoverPro = undefined;
        /**
         * Expanded Name: Archive Indicator
         *
         * Desc: Indicates whether the record for the PRO is an active one or one retrieved from the archive.
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Expanded Name: COD Indicator
         *
         * Desc: Indictaes whether it is a COD shipment or not. It is a denormalized attribute for performance puposes. The actual COD information is in the MISC_LINE_ITEM entity.
         * Required: false
         * Array: false
         */
        this.codInd = undefined;
        /**
         * Expanded Name: Discount Code
         *
         * Desc: This is the discount code that was applied to the shipment when it was rated.
         *
         * Source: MBF - MBRCCODE
         * Required: false
         * Array: false
         */
        this.discountCd = undefined;
        /**
         * Expanded Name :Movement Reporting SIC Code
         *
         * Desc : SIC where the last movement event for the shipment  happened and reported.  This is a denormalized attribute from the MOVEMENT entity for performance purposes.
         *
         * Source : MBF: root MBMOVE
         * Required: false
         * Array: false
         */
        this.lastMoveRptgSicCd = undefined;
        /**
         * Expanded Name : Movement Timestamp
         *
         * Desc : Date and time, when the last movement for this shipment  occurred. This is a denormalized attribute from the MOVEMENT entity.
         *
         * Source: MBF - MBMVTM
         * Required: false
         * Array: false
         */
        this.lastMovementDateTime = undefined;
        /**
         * Expanded Name : Movement status
         *
         * Desc : The status of the latest movement for a Shipment from an operations point-of-view. This is a denormalized atribute from the MOVEMENT entity.
         *
         * Source: MBF
         * Name: Various
         *
         * Valid Values :
         * Spaces - Not applicable
         * 1 - On dock
         * 2 - On trailer
         * 3 - Out for delivery
         * 4-  Interim Delivery
         * 5 - Final Delivered
         * 6 - Not Applicable
         * 6 - No movements allowed
         * 7 - Cancelled
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         * Expanded Name : Delivery qualifier
         *
         * Desc : Code representing results of a specific delivery activity. The values are the same as what used to be  Event that stopped service).
         *
         * This has been denormalized from Movement for performance reasons and should always be kept in sync.
         *
         * Source : MBF: root: MBD1FLG / MBD2FLG
         *
         * Valid Values :
         * A= Over Shipment
         * B=Trapped shipment/MBD1TRAP
         * C=Hold for customs/MBD1CSTM
         * D=Undelivered/MBD1UNDL
         * E=Hold for appointment/MBD1APPT
         * F=On spotted trailer/MBD1SPOT
         * G=Cartage
         * H=Attempted delivery/MBD1ATMP
         * J=All short
         * K=Partial short/MBD1PSRT
         * L=Refused delivery/MBD1RFSD
         * M=Damaged shipment/MBD1RDMG
         * N=Suspense/MBD1SUSP     - Not Used
         * O=Wait/MBD1WAIT              - Not Used
         * P=Purge Blocked/MBD2PBLK
         * R=Return Status/MBD2RTRN
         * T=Transfer/MBD2TSFR
         * Z=Final delivery/MBD1FINL
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         * Expanded Name : Rating Currency Code
         *
         * Desc: The rates and charges for the shipment were calculated using this currency
         *
         * Valid values:
         *
         * USD - US Dollars
         * CDN - Canadian Dollars
         * Required: false
         * Array: false
         */
        this.ratingCurrencyCd = undefined;
        /**
         * Expanded Name : Invoicing Currency Code
         *
         * Desc:  PLEASE DO NOT USE THIS ATTRIBUTE. THIS ATTRIBUTE WILL BE DROPPED IN NEAR FUTURE.
         *
         * The total charges for the shipment should be displayed using this currency
         *
         * Valid values:
         *
         * USD - US Dollars
         * CDN - Canadian Dollars
         * Required: false
         * Array: false
         */
        this.invoicingCurrencyCd = undefined;
        /**
         * Expanded Name: Currency Converstion Factor
         *
         * Desc: PLEASE DO NOT USE THIS ATTRIBUTE. THIS ATTRIBUTE WILL BE DROPPED IN NEAR FUTURE.
         *
         * Provides the currency conversion factor on the day of shipment pick up between the rating currency and invoicing currency, if they are different.
         * Required: false
         * Array: false
         */
        this.currencyConversionFctr = undefined;
        /**
         * Expanded Name : Required Delivery Date
         *
         * Desc : Date the shipment is required to be delivered
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.requiredDeliveryDate = undefined;
        /**
         * Expanded Name : Delivery Time Start (if any)
         *
         * Desc : The start time of the window specified by the customer for delivering the shipment.
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.startDeliveryTime = undefined;
        /**
         * Expanded Name : Delivery Time End (if any)
         *
         * Desc : The end time of the window specified by the customer for delivering the shipment.
         *
         * Source : E-commerce
         * Required: false
         * Array: false
         */
        this.endDeliveryTime = undefined;
        /**
         * Expanded Name : International manifest from (port) code
         *
         * Desc : Generated by rating. Identifies port of entry code through which freight will enter the US from another country. This may be associated with the destination city or the customer s broker.
         *
         * Source : MBF: root: MBFROMPT
         * Required: false
         * Array: false
         */
        this.fromPortCd = undefined;
        /**
         * Expanded Name : International  manifest to (port) code
         *
         * Desc : Generated by rating: Identifies the port of entry through which freight will enter another country.  This is either associated with the destination or with the customer s broker.
         *
         * Source : MBF: root: MBTOPORT
         * Required: false
         * Array: false
         */
        this.toPortCd = undefined;
        /**
         * Expanded Name : OBC pickup delivery route
         *
         * Desc : Pickup delivery route code from CAD used for sorting to print delivery sets in this order.
         *
         * Source : MBF: root: MBPDRTE
         * Required: false
         * Array: false
         */
        this.obcPickupDlvRouteCd = undefined;
        /**
         * Expanded Name : Customer Profile Instance Id
         *
         * Desc : A unique, randomly-generated number that identifies a registered customer in the Con-Way Customer Profile Component.
         *
         * Source : Customer Profile Component
         * Required: false
         * Array: false
         */
        this.custProfitabilityInstId = undefined;
        /**
         * Expanded Name :  Rating tariff
         *
         * Desc : This identifies the actual tariff used to rate the shipment.
         *
         * Source : MBF root - MBRATTAR
         * Required: false
         * Array: false
         */
        this.ratingTariffsId = undefined;
        /**
         * Expanded Name : Rate auditor initials
         *
         * Desc : Initials of the rating auditor who last audited the shipment for rates.
         *
         * Source : MBF: root: MBRTADID
         * Required: false
         * Array: false
         */
        this.rateAuditorInitials = undefined;
        /**
         * Expanded Name : Rate or rate audit queue name
         *
         * Desc : The name of the rating (or the rate audit) queue used when the shipment was last rated / audited.
         *
         * Source : MBF: root: MBRATEQ
         * Required: false
         * Array: false
         */
        this.rateOrRateAudtqNm = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.heaviestCommoditySequenceNo = undefined;
        /**
         * Expanded name:  Service type code.
         *
         * Description:  Defines the specific type of service provided for a shipment.
         *
         * Values:  1 - Normal LTL, 2 - Con-Way Deferred Express.
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         * Expanded name: Estimated Delivery Date
         *
         * Description: Date that the shipment should be delivered based on standard lane days.
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         * Expanded name: Freight Bill Delivery Set Print Count
         *
         * Description:
         * The number of times that a FBDS has been printed for this shipment.
         * Required: false
         * Array: false
         */
        this.fbdsPrintCount = undefined;
        /**
         * Expanded Name : Archive Control Code
         *
         * Desc : Identifies a row of this table as archived or retrieved from archive.  The Princeton Archive tool sets the value upon archive or retrieval of data for selected PROs.
         *
         * FOR USE BY PRINCETON ARCHIVE/RETRIEVE PROCESSING ONLY!
         *
         * Valid Values:
         * A - Row is flagged for Archive processing
         * R - Row has been retrieved from archive
         * blank - row is associated with a currently active PRO (default)
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         * Expanded name: Delivery Information Required Code
         *
         * Desc: This code indicates that there is a requirement to capture delivery information for this shipment because of customer special requirements.  This is usually an EDI customer requirement that must be met.  The name of the person that signed for delivery and the timestamp are also captured in this record.
         *
         * Values:
         * R - delivery info is required
         * C - delivery informaiton has been captured  and exists with this record
         * blank - No delivery information required for this shipment
         * Required: false
         * Array: false
         */
        this.deliveryInformationRqdCd = undefined;
        /**
         * Expanded name: Delivery Signature Name
         *
         * Desc: This is the name of the person that signed for the delivery of the freight.  This name is required by some customers and is passed along to them via EDI.  This name is captured if the DLVR_UNFO_RQD_CD is set.
         *
         * Since a shipment can be delivered in parts, (part short, etc) there can be more than one signature.  This field will hold the name of the last delivery movement.  The previous signatures can be retrieved from shipment history records.
         * Required: false
         * Array: false
         */
        this.deliverySignatureNm = undefined;
        /**
         * Expanded name: Delivery Signature TimeStamp
         *
         * Desc: This is the date and time recorded when the person named in DLVR_SIG_NM_TXT signed for the shipment.
         * Required: false
         * Array: false
         */
        this.deliverySignatureDateTime = undefined;
        /**
         * Expanded Name : Standard Transit Days (in number of days)
         *
         * Desc : The standard number of days it should take to move a shipment from origin to destination, as defined in the OSS_SVC_STDS_A/B tables.
         *
         * Source : OSS_SVC_STDS_A/B - TRNST_DAYS
         * Required: false
         * Array: false
         */
        this.standardTransitDays = undefined;
        /**
         * Expanded Name : Actual Transit Days (in number of days)
         *
         * Desc : The number of days it actually takes to move a shipment from shipper to consignee.
         *
         * Source : calculated by subtracting PKUP_DT from the date portion of the (LST_)MVMT_TMST of the qualifying delivery event / movement, adjusted appropriately for non-work days occuring between the two dates.  There are a number of interim delivery qualifiers (DLVRY_QALFR_CD) that can cause the calculation  (SPOT, PSRT?, RDMG?, RFSD?, FINL).
         * Required: false
         * Array: false
         */
        this.actualTransitDays = undefined;
        /**
         * Expanded Name : Transit Movement Sequence Number
         *
         * Desc : The sequence number identifying the movement (qualifying delivery event) for the calculation of ACTL_TRNST_DAYS.
         * NOTE - VALUE CAN BE ZERO FOR PARTLOT (PTLT) PROS.
         *
         * Source : SHM_MOVEMENT - MVMT_SEQ_NBR
         * Required: false
         * Array: false
         */
        this.transitMovementSequenceNbr = undefined;
        /**
         * Expanded Name : Warranty Indicator
         *
         * Desc : Indicates whether the shipment is eligible for the money-back guarantee if moving between direct points under Tariff CNWY-599.
         *
         * Valid Values :
         *
         * Y - eligible for warranty
         * N - not eligible for warranty - default value
         * Required: false
         * Array: false
         */
        this.warrantyInd = undefined;
        /**
         * Expanded Name : Warranty Status Code
         *
         * Desc : Identifies the warranty status of a shipment
         *
         * For example, one value of this code would indicate that a warranty  claim has been filed  by the debtor if a shipment has failed service.
         *
         * Valid Values:
         * 0 - default value; This means either the shipment is not eligible (Warranty flag =  N ) or it is eligible and no one has filed a claim.
         * 1 - The customer has filed a claim and it has not been audited.
         * 2 - The customer has filed a claim and it was approved.
         * 3 - The customer has filed a claim and it was denied.
         * Required: false
         * Array: false
         */
        this.warrantyStatusCd = undefined;
        /**
         * Expanded name: Notification Code
         *
         * Desc: Indicates whether or not an notification exists for this shipment.  If it exists, the details will exist in the SHM NOTFICATION table.  The notification is created in the SCO (Smart4) system.
         *
         * Values:
         * A - Appointment exists
         * N - Notification exists
         * O -Appointment Not Required
         * R- Reappointment candidate.  PSRT/RDMG Pro  could be reappointment for delivery
         *
         * blank - Neither exist
         * Required: false
         * Array: false
         */
        this.notificationCd = undefined;
        /**
         * Expanded Name: Absolute minimum charge indicator
         *
         * Desc: This inidcates if the charge for this shipment is the absolute minimum charge.
         * Required: false
         * Array: false
         */
        this.absoluteMinimumChargeInd = undefined;
        /**
         * Expanded Name: Discount Percentage
         *
         * Desc: This is the percentage discount applied to the freigt chrages and discountable accessorial charges of the shipment.
         * Required: false
         * Array: false
         */
        this.discountPercentage = undefined;
        /**
         * Expanded Name: Pricing Agreement Instance Id
         *
         * Desc: This is the instance id of the pricing agreement that was used to rate the shipment.
         *
         * ==NOTE== Link to BASE 5 PRC component
         * Required: false
         * Array: false
         */
        this.priceAgreementId = undefined;
        /**
         * Expanded Name: Rule set Number
         *
         * Desc: This identifies the rule set of the pricing agreement that was used to rate the shipment.
         *
         * ==NOTE== link to BASE 5 PRC component
         * Required: false
         * Array: false
         */
        this.priceRulesetNbr = undefined;
        /**
         * Expanded Name: Route prefix
         *
         * Desc: Identifies the last Route the shipment was put on.
         *
         * ==NOTE== Link to SMART 4 SCO component
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         * Expanded Name: Route Suffix
         *
         * Desc: Identifies the last Route the shipment was put on.
         *
         * ==NOTE== Link to SMART 4 SCO component
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         * Expanded Name: Route Type Code
         *
         * Desc: Identifies whether the shipment is currently on a  Route (either planning route or dispatched route) Staged (available) for Routing, or is Not on a route.
         *
         * Valid Values
         * 				- Not Routed
         * R - Routed
         * S - Staged
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         * Expanded Name : Volume in Cubic feet
         *
         * Desc : The volume of the freight; derived from the dimensions captured by driver, or entered by a CSR or dock worker for an SL  (Shipper Load and Count), or from one of a variety of profiles. For details, see the description of CFT_PRFL_MTHD_CD.
         *
         * 06/21/2016 Cathy Doucet modified description as per Ariel/Sunya.
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         * Expanded Name: Total Pallet Count
         *
         * Desc: Total Number of Pallets in the Shipment
         * Required: false
         * Array: false
         */
        this.totalPalletsCount = undefined;
        /**
         * Expanded Name: Area instance Id
         *
         * Desc: Each service center is divided into multiple geographic areas for delivery. This identifies the delivery area of destination service center in which this shipment s consignee is located.
         *
         * Source: SMART 4 Requirement
         *
         * == NOTE ==  Ties back to the geo-area stored in Oracle spatial database for each service center.
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         * Expanded Name: Auto Rateable Indicator
         *
         * Desc: This indicates whether the auto rating system is allowed to put rates and charges on the bill. This is irrespective of whether the bill needs to be manually reviewed or not before being marked RATED.
         *
         * Valid Values:
         * Y - Auto rating system may put rates and charges
         * N - Auto Rating system should not out or change any rates and charges on the bill
         * Required: false
         * Array: false
         */
        this.autoRateableInd = undefined;
        /**
         * Expanded Name: Motorized-Move Pieces Known Indicator
         *
         * Desc: This indicates whether MTRZD_PCS_CNT is known or applicable..
         *
         * Valid Values:
         * Y - Yes, MTRZD_PCS_CNT is known/applicable
         * N - No, MTRZD_PCS_CNT is NOT known/applicable
         *
         * Source:  FBES
         * Required: false
         * Array: false
         */
        this.motorizedPiecesKnownInd = undefined;
        /**
         * Expanded Name : Estimated Transit Days (in number of days)
         *
         * Desc : The standard number of days it should take to move a shipment from origin to destination, as defined in the OSS_SVC_STDS_A/B tables, adjusted by +1 if LATE_TENDER_IND =  Y
         *
         * Source : STD_TRNST_DAYS +1, if LATE_TENDER_IND =  Y
         * Required: false
         * Array: false
         */
        this.estimatedTransitDays = undefined;
        /**
         * Expanded Name : Calculated Service Days (in number of days)
         *
         * Desc : The number of days it takes to move a shipment from shipper until it reaches a service stopping event.
         *
         * Source : calculated by subtracting PKUP_DT from the date portion of the (LST_)MVMT_TMST of the first service-stopping event / movement, adjusted appropriately for non-work days occuring between the two dates.  There are a number of interim delivery qualifiers (DLVRY_QALFR_CD) that can cause the calculation (ie. stop service).
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays = undefined;
        /**
         * Expanded Name : Calculated Service Timestamp
         *
         * Desc : The timestamp of a service stopping event.
         *
         * Source : (LST_)MVMT_TMST of the first service-stopping event / movement.  There are a number of interim delivery qualifiers (DLVRY_QALFR_CD) that can cause the calculation (ie. stop service).
         * Required: false
         * Array: false
         */
        this.calculatedServiceDateTime = undefined;
        /**
         * Expanded Name : Calculated Movement Sequence Number
         *
         * Desc : The sequence number identifying the movement (service-stopping event) for the calculation of CALC_SVC_DAYS.
         * NOTE - VALUE CAN BE ZERO FOR PARTLOT (PTLT) PROS.
         *
         * Source : SHM_MOVEMENT - MVMT_SEQ_NBR
         * Required: false
         * Array: false
         */
        this.calculatedMovementSequenceNbr = undefined;
        /**
         * Expanded Name : Difference of Calculated Days
         *
         * Desc : Difference between CALC_SVC_DAYS and EST_TRNST_DAYS.
         *
         * Source : CALC_SVC_DAYS - EST_TRNST_DAYS.
         * Required: false
         * Array: false
         */
        this.diffCalculatedDays = undefined;
        /**
         * Expanded Name : Difference of Transit Days
         *
         * Desc : Difference between ACTL_TRNST_DAYS and EST_TRNST_DAYS.
         *
         * Source : ACTL_TRNST_DAYS - EST_TRNST_DAYS.
         * Required: false
         * Array: false
         */
        this.diffTransitDays = undefined;
        /**
         * Expanded Name : Late Tender Code
         *
         * Desc : Indicates whether shipment could not be picked up in time (due to late tender by shipper) to make service.
         *
         * Values: Y - Late Tender; blank or N - Not Late Tender
         *
         * Source : Bill Entry/FBES; can be populated at Pick-up Request create time if a skeleton PRO is available; may be changed in Corrections
         * Required: false
         * Array: false
         */
        this.lateTenderCd = undefined;
        /**
         * Expanded Name : Shipment Service Status Code
         *
         * Desc : Indicates whether shipment met service standards.
         *
         * Values: Fail, Success, Exempt, Unknown
         *
         * Source : Calculated
         * Required: false
         * Array: false
         */
        this.shipmentServiceStatusCd = undefined;
        /**
         * Expanded Name : Service Calculation Status Code
         *
         * Desc : Indicates whether shipment service calculations have been performed.
         *
         * Values:
         * 			0 - Not Calculated Yet
         * 			1 - Cannot Calculate (all variables not defined)
         * 			? - Calculated from (service stopping event or appointment - replace '?' with DLVRY_QALFR_CD values)
         *
         * Source : Calculated
         * Required: false
         * Array: false
         */
        this.serviceCalculatedStatusCd = undefined;
        /**
         * Expanded Name : Exemption Reason Code
         *
         * Desc : Indicates why a shipment may be exempt from meeting service standards.
         *
         * Values:
         * 			blank - Not Exempt;
         * 			any valid exemption reason code
         *
         * Source : OPS_EXEMPT_SHPMT; shipment exemptions are created immediately upon full trailer and service center exemption events.
         * Required: false
         * Array: false
         */
        this.exemptReasonCd = undefined;
        /**
         * Expanded Name : Pickup Date Back-Date Indicator
         *
         * Desc : Indicates whether a shipment pickup date was backdated during bill entry.
         *
         * Values:
         * 			Y - Shipment pickup date was backdated at bill entry
         * 			N - Shipment pickup date = bill entry date
         *
         * Source : Bill Entry/FBES.
         * Required: false
         * Array: false
         */
        this.pickupBackdateInd = undefined;
        /**
         * Expanded Name: FOOD OR POISON CODE
         *
         * Desc; Indicates whether the shipment contains food or posion. Assumption: A shipment will never contain both.
         *
         * Valid Values:
         *
         * 1 - Neither
         * 2 - Food
         * 3 -  Poison
         * Required: false
         * Array: false
         */
        this.foodPoisonCd = undefined;
        /**
         * Expanded Name: Rating Override Code
         *
         * Desc: Indicates if a rating override has been indicated during billing. For example to use a spot quote or lineal feet rating.
         *
         * Valid Values:
         * 1: Spot Quote Number Specified for rating
         * 2: Lineal Feet Rating should be used
         * Required: false
         * Array: false
         */
        this.ratingOverrideCd = undefined;
        /**
         * Expanded Name : Pick-Up Time
         *
         * Desc : Time when the shipment was picked up. This will be required for Late Tender shipments and optional for other shipments.
         *
         * Source : Bill Entry/FBES; can be populated at Pick-up
         * Request create time if a skeleton PRO is available;
         * may be changed in Corrections
         * Required: false
         * Array: false
         */
        this.pickupTime = undefined;
        /**
         * Expanded Name : Service Start Date
         *
         * Desc : Business date on which the service clock starts.  This value will be calculated automatically for all shipments, based on the values of PKUP_DT and LATE_TENDER_CD.  If a shipment is tendered late, the service start date will be set to the next business date after the pick-up date.
         *
         * Source : Will be populated at pkup request create time if a skeleton PRO is available, during bill entry, through the late tender correction screen.
         * Required: false
         * Array: false
         */
        this.serviceStartDate = undefined;
        /**
         * Expanded Name: Inspected Indicator
         *
         * Desc: Indicates whether the shipment has been inspected or not.
         * Required: false
         * Array: false
         */
        this.inspectedInd = undefined;
        /**
         * Expanded Name: Spot Quote ID
         *
         * Desc: The Spot Quote ID that was entered during Billing. This may be different from the one that was used for rating.  For the latest Spot Quote ID that was used for raing, please look at the rating info record.
         * Required: false
         * Array: false
         */
        this.spotQuoteId = undefined;
        /**
         * Expanded Name: Shipper to Consignee Miles
         *
         * Desc: This captures the mileage from shipper to consignee as calculated by the Milemaker package (Rand McNally)
         *
         * Added by C.Doucet 05/06/09 as per Shibashis
         * Required: false
         * Array: false
         */
        this.shipperToConsigneeMiles = undefined;
        /**
         * Expanded name: Shipper loaded trailer code
         *
         * Desc: Identifies whether a shipper loaded a trailer, and if so, where Con-way took possession of trailer.
         *
         * Values:
         * N  Shipper did not load trailer (default)
         * H  Hook loaded; a trailer is loaded by shipper and picked up by Con-way at shipper s site
         * D  Dock Drop; a trailer is loaded by shipper and dropped at Con-way dock by shipper
         * Required: false
         * Array: false
         */
        this.shipperLoadedTrlrCd = undefined;
        /**
         * Expanded Name: Consignee unloaded trailer code
         *
         * Desc: Identifies whether a consignee unloaded a trailer, and if so, where consignee took possession of trailer.
         *
         * Values:
         * N  Consignee did not unload trailer (default)
         * D  Dock Pickup; a loaded trailer is picked up at Con-way dock by consignee to be unloaded by consignee
         * S  Spot loaded; a loaded trailer is left at the consignee site for consignee to unload
         * Required: false
         * Array: false
         */
        this.consigneeUnloadTrlrCd = undefined;
        /**
         * Expanded Name: Lineal Foot Total Number
         *
         * Desc: This is the length in feet that a shipment will take up in a trailer.  This dimension is typically used when the freight is long and narrow and cannot have anything stacked on top of it (e.g. carpet rolls).  For this reason it takes a larger portion of the trailer volume than a shipment of the same weight that is packaged on pallets.  This value can then be used in the planning of trailer loads and to rate shipments that have these characteristics.
         * Required: false
         * Array: false
         */
        this.linealFootTotalNbr = undefined;
        /**
         * Expanded Name: Single Shipment Acquired Indicator                                                                                                                                                Desc:                                                                                                      This indicates whether this was the only Shipment that we acquired from the Customer at the time that we received this one.
         * Values:
         * Y  Yes:  This was the only Shipment Acquired at the time that we received this one.
         * N  No: There were other Shipments Acquired at the time that we received this one.
         * Required: false
         * Array: false
         */
        this.singleShipmentAcquiredInd = undefined;
        /**
         * Desc: This code identifies the method in which Con-way received the freight from the customer.
         * Current values are:
         * PU  Regular Pickup:  This is when the driver goes to the customer dock and loads the Shipment on the trailer to return to the service center.
         * HL  Hook Loaded: This is when the driver goes to the customer site and pickup a loaded trailer that this Shipment was on.
         * DD  Dock Drop:  This is when the customer delivers the Shipment to a Con-way Service Center, so no driver went to the Customer site
         * Required: false
         * Array: false
         */
        this.shipmentAcquiredTypeCd = undefined;
        /**
         * Expanded Name : Loose pieces count
         *
         * Desc : Total loose pieces from all commodities in the shipment.
         *
         * Count of non Motor Move pieces associated with a shipment. This count plus Motor Moves defines the total number of pieces moving within the Con-way system for Operations productivity.  This does not necessarily balance with the total pieces or commodity pieces entered during bill entry.
         * Required: false
         * Array: false
         */
        this.loosePiecesCount = undefined;
        /**
         * Expanded Name: PUP_VOLUME_PERCENTAGE
         *
         * Desc: This is the volume of the shipment expressed as the percentage of a full pup (28 feet long trailer).
         * Required: false
         * Array: false
         */
        this.pupVolumePercentage = undefined;
        /**
         * Desc: Instance id of the Pickup Request that is associated with this Shipment during the Confirm On-Board process.
         * Required: false
         * Array: false
         */
        this.purInstId = undefined;
        /**
         * Expanded Name: Bulk Liquid Indicator
         *
         * Desc: Identifies the shipment as containing bulk liquid.  If volume is known, this applies to shipments >119 gallons.
         *
         * Added 8/5/14 by Paul Bramhall for Tanker Endorsement Project.
         * -------------------------------------------------------------
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cubicFeetProfileMthdCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cubicFeetProfileTypeCd = undefined;
        /**
         * In the event a customer requires a shipment to travel from origin to destination
         * without the loading of any additional shipments on the trailer(s), the shipment is considered Exclusive Use.
         * Required: false
         * Array: false
         */
        this.exclusiveUseInd = undefined;
        /**
         * Indicates that the customer would like to insure the shipment due to excessive value of the freight.
         * Required: false
         * Array: false
         */
        this.excessiveValueInd = undefined;
        /**
         * The value of the freight as declared by the customer.
         * Required: false
         * Array: false
         */
        this.declaredValueAmount = undefined;
        /**
         * Indicates that this shipment is marked as a Mexico Door to Door shipment.
         * Required: false
         * Array: false
         */
        this.mexicoDoorToDoorInd = undefined;
        /**
         * Indicates that this shipment is time date critical and operations needs to call for a delivery appointment.
         * Required: false
         * Array: false
         */
        this.callForAppointmentInd = undefined;
        /**
         * Indicates whether the payment terms were flipped on a bill after biller submit, based on input shipment party data.
         * Required: false
         * Array: false
         */
        this.debtorTermFlipInd = undefined;
        /**
         * Identifier for unit of work on Shipment.  Added 02/13/2020
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Indicates that the PRO is exempted to be tracked via piece level. SLC (Shipper Loaded Count) shipments are generally exempted. Pickups with 15 or more pieces are also allowed to be exempted.    Added 12/10/2020  LISG-9067 Renamed 01/07/2021
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionInd = undefined;
        /**
         * An explanation of why a pro is exempted from piece level tracking.  Added 12/10/2020   LISG-9067 Renamed 01/07/2021
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionReason = undefined;
        /**
         * An appointment should be created for delivering the shipment.  Added 03/11/2021 Cathy Doucet LISG-10771
         * Required: false
         * Array: false
         */
        this.appointmentRequiredInd = undefined;
        /**
         * The consignee should be notified upon arrival of the shipment at destination. During the notification, if the consignee requires an appointment, an appointment will be created, and the shipment will be charged for both services.  Added 03/11/2021  Cathy Doucet LISG-10771.
         * Required: false
         * Array: false
         */
        this.destinationNotifyInd = undefined;
        /**
         * The shipment total weight at the time of reweigh (if a reweigh occurred).  Added 06/28/2021  LISG-13269
         * Required: false
         * Array: false
         */
        this.reweighWeightLbs = undefined;
        /**
         * Indicates if the freight has been poorly packaged or not by the Shipper.  Added 05/04/2022  LISG-16763
         *
         * Valid Values:
         * N - freight is NOT packaged poorly (default)
         * Y - freight IS packaged poorly
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         * Indicates that one or more of the handling units associated with this shipment has become separated from the others.Added 05/16/2022 LISG-16851
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         * Indicates that one or more of the handling units associated with this shipment is missing.Added 05/16/2022 LISG-16851
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.remark = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.movement = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.managementRemark = undefined;
    }
}
/**
 * Holds a snapshot of Shipment. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class ShipmentSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         * Expanded Name : Instance identifier
         *
         * Desc : System generated unique identifier (as per CBD standard).
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class ShipperDimensionsProfile {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationZip = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.profileTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.modeCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mode1Value = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mode2Value = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mode3Value = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mode4Value = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.significantInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.generatorVersionNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inpDataBeginDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.inpDataEndDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.measurementsCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.note = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.avgHeightInches = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmAdvanceBeyondCarrierRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierScacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierProNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierPickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fromTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.toTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmEventLogRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.subTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.programId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalChargeAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.enrouteInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bil21StCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bil22NdCustomerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeCountryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.adminInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeToCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.thirdPartyInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.adminStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billTo2NdRelationshipCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billTo1StRelationshipCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ediSenderId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ediSenderShipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.debtorCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bolInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmExternalStatusRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierShipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionRemark = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredLatitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.occurredLongitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.processStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.processStatusMessage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmHandlingUnitMovementRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementReportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rfsdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.undlvdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmgdCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.remarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bypassScanInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bypassScanReason = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scanDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.splitAuthorizeBy = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.splitAuthorizeDateTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmHandlingUnitRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.childProNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentProNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentTrailerInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentDockLocation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastMovementDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingMovementCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dimensionTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reweighInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.splitInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmMiscLineItemRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lineTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tariffsRate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.unitOfMeasure = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.quantity = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.amount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.prepaidPercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.minimumChargeInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.description = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.paymentMethodCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.checkNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeToCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmMovementExceptionRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.piecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rfsdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.undlvdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmgdCategoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.remarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exceptionReportingSicCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmMovementRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementReportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.rshpCreditInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.obcPickupDlvRouteCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.qualifierReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerLocationArrivalDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.carrierId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmNotification {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scoNtficnInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reschdReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryFromTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeRefNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryToTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callerRacfId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.callDateTime = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of ShmNotification. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class ShmNotificationSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationSequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class ShmServiceOverride {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.remarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideSicCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class ShmShipmentRw {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInstanceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billClassCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.chargeToCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalChargeAmount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.splitInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cashCollectInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.purgeBlockInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastMoveReportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastMovementDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.obcPickupDlvRouteCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDeliveryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryInformationRqdCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliverySignatureName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliverySignatureDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.standardTransitDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualTransitDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transitMovementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.notificationCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routePrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedTransitDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.calculatedServiceDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.calculatedServiceDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.calculatedMovementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.diffCalculatedDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.diffTransitDays = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentServiceStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceCalculatedStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceStartDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipperLoadTrailerCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.consigneeUnloadTrailerCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingUnitExemptionReason = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.appointmentRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationNotifyInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reweighWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pupVolumePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.poorlyPackagedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingUnitSplitInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.handlingUnitPartialInd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class SuppRefExtendedDetail {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * Expanded Name: Packaging Type Text
         *
         * Desc : Type of packaging the commodity was originally
         * picked up in.
         *
         * Examples: Box, Skid, Case, Roll etc.
         *
         * Source: Usually EDI, set 204/211 per VICS bill of lading.
         * Required: false
         * Array: false
         */
        this.packageType = undefined;
        /**
         * Expanded Name : Unit of Measure
         *
         * Desc : A code to identify the units in terms of which
         * the quantity is measured. For
         * eg. weight (in pounds) is the most common unit of
         * measure for commodities.
         *
         * Valid Values :
         * 1 - Weight (in pounds)
         * 2 - Yardage (for carpets)
         * 3 - Count
         * Required: false
         * Array: false
         */
        this.unitOfMeasure = undefined;
        /**
         * Expanded Name : Quantify
         *
         * Desc : Commodity line item quantify associated with this specific supplemental reference number.  Usually expressed in pounds (reference the UOM - Unit of Measure to verify).
         * Required: false
         * Array: false
         */
        this.quantity = undefined;
        /**
         * Expanded Name : Pieces Count
         *
         * Desc : Commodity line pieces associated with this specific supplemental reference number.
         * Required: false
         * Array: false
         */
        this.piecesCount = undefined;
        /**
         * Expanded Name: Pallet or Slip Indicator
         *
         * Desc : Used on a VICS bill of lading to indicate that the freight at time of pickup was loaded on either a pallet or a slip sheet.
         *
         * Valid Values:  Y/N
         *
         * Source: Usually EDI, set 204/211 per VICS bill of lading.
         * Required: false
         * Array: false
         */
        this.palletOrSlipInd = undefined;
        /**
         * Expanded Name: Additional Shipper Information
         *
         * Desc : Contains additional comments supplied from the shipper, usually regarding how the freight should be handled.
         *
         * Source: Usually EDI, set 204/211 per VICS bill of lading.
         * Required: false
         * Array: false
         */
        this.additionalShipperInformation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class SuppRefNbr {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Expanded Name : Type code:
         *
         * Desc : A code describing what the Supplemental Reference is (for eg. PO# for Purchase Order number and GBL for Government Bill of Lading). These are entered by and have a meaning for the customer only. There is no list of valid values.
         *
         * Examples :
         *
         * Purchase orders
         * Shipper numbers
         * GBL numbers
         * SLI numbers
         * Bookclosing numbers
         *
         * Source: E-commerce
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Expanded Name : Sequence number
         *
         * Desc : Sequentially assigned number to preserve the order in which various supplemental reference numbers are to be displayed / printed for a commodity / shipment.
         *
         * Source : Generated
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * Expanded Name : Number text
         *
         * Desc : This is the actual number provided by the customer (sometimes also known as COSTAR - (C)ustomer (O)wned (S)hipment and (T)racking numbers). This valid only when recording supplemental references for a shipment.
         * Required: false
         * Array: false
         */
        this.refNbr = undefined;
        /**
         * Expanded Name : Description
         *
         * Desc : A textual description of what the Supplemental Reference number is (Purchase order number, shipper number, etc.)
         * Required: false
         * Array: false
         */
        this.description = undefined;
        /**
         * Expanded Name : Commodity Sequence Number
         *
         * Desc : Sequence number of the specific Commodity (if any) to which the supplemental reference number applies. Should be 0 if the number applies to the Shipment in general.
         * Required: false
         * Array: false
         */
        this.commoditySequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveControlCd = undefined;
        /**
         * Desc: Source of the Supplemental Reference Number.
         *
         * Values:
         * EDI
         * WEB
         * XML
         * BIL - Billing
         * COR - currently BE36 transaction (Aug.2011)
         * Required: false
         * Array: false
         */
        this.sourceCd = undefined;
        /**
         * Desc: Sender-ID of the EDI Customer.
         * Required: false
         * Array: false
         */
        this.ediSenderId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.suppRefExtendedDetail = undefined;
    }
}
/**
 * Holds a snapshot of SuppRefNbr. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class SuppRefNbrSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Expanded Name : Type code:
         *
         * Desc : A code describing what the Supplemental Reference is (for eg. PO# for Purchase Order number and GBL for Government Bill of Lading). These are entered by and have a meaning for the customer only. There is no list of valid values.
         *
         * Examples :
         *
         * Purchase orders
         * Shipper numbers
         * GBL numbers
         * SLI numbers
         * Bookclosing numbers
         *
         * Source: E-commerce
         * Required: false
         * Array: false
         */
        this.typeCd = undefined;
        /**
         * Expanded Name : Sequence number
         *
         * Desc : Sequentially assigned number to preserve the order in which various supplemental reference numbers are to be displayed / printed for a commodity / shipment.
         *
         * Source : Generated
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class TimeDateCritical {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * Expanded: Time Date Critical Source Code
         *
         * Desc: Where the Time Date Critical information was entered.
         *
         * Valid Values:
         * BIL=Bill Entered
         * PUR=Pick Up Request
         * EDI=EDI
         * WEB=Web
         * COR=Corrections
         * Required: false
         * Array: false
         */
        this.tdcSourceCd = undefined;
        /**
         * Expanded: Time Date Critical Date Type Code
         *
         * Desc: What kind the dates on the TDC shipment is/are.  There are 3 main flavors of date on Time Date Critical shipments: Deliver on a date, Deliver by a date, or a Deliver sometime within a date range
         *
         * Valid Values:
         * ON=On
         * BY=By
         * RNG=Range
         * Required: false
         * Array: false
         */
        this.tdcDateTypeCd = undefined;
        /**
         * Expanded: Time Date Critical Date #1
         *
         * Desc: There are 3 flavors of date for a TDC shipment.  This attribute captures the following flavor of date: ON, BY or FROM
         * Required: false
         * Array: false
         */
        this.tdcDate1 = undefined;
        /**
         * Expanded: Time Date Critical Date #2
         *
         * Desc: There are 3 flavors of date for a TDC shipment.  This attribute captures the TO date if the type is a date range.
         * Required: false
         * Array: false
         */
        this.tdcDate2 = undefined;
        /**
         * Expanded: Time Date Critical Time Type
         *
         * Desc: What kind the times on the TDC shipment is/are.  There are 4 main flavors of time for Time Date Critical shipments: Deliver at a time, Deliver before a time, Deliver after a time, or a Deliver sometime within a time range
         *
         * Valid Values:
         * AT=At
         * BEF=Before
         * AFT=After
         * RNG=Range
         * Required: false
         * Array: false
         */
        this.tdcTimeTypeCd = undefined;
        /**
         * Expanded: Time Date Critical Time #1
         *
         * Desc: There are 4 flavors of time for a TDC shipment.  This attribute captures the following flavor of time: AT, AFTER, BEFORE, FROM
         * Required: false
         * Array: false
         */
        this.tdcTime1 = undefined;
        /**
         * Expanded: Time Date Critical Time #2
         *
         * Desc: There are 4 flavors of time for a TDC shipment.  This attribute captures the TO time if the type is a time range.
         * Required: false
         * Array: false
         */
        this.tdcTime2 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of TimeDateCritical. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class TimeDateCriticalSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 *
 */
class TrailerLoad {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.evntDoor = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvnt = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvntDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLengthFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedDestination = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedWeight = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedCbePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedCbeProfitability = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedReleaseNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.xclvInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closeDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frzblInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.placardInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.garntServiceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityRoute = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.brkOrProprCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDeckCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadedRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadLocation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dfrdShipmentInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedDfrdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealMatchCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.authrzUserid = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodShipmentCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.poisonShipmentCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.evntDoorSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.evntShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInspReqdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedLoadedDestination = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routingRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideRvwRequiredInd = undefined;
        /**
         * Indicates whether the trailer/straight truck has Liftgate installed or not.  Added 03/18/2021 LISG-10718
         *
         * Values: Y/N
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         * Identifies if pro needs RRS=Rapid Remote Service.   Added 03/18/2021 LISG-7488
         * Required: false
         * Array: false
         */
        this.rrsInd = undefined;
        /**
         * Identifies if pro needs G12=Guaranteed by Noon Service.  Added 03/18/2021  LISG-7488
         * Required: false
         * Array: false
         */
        this.g12Ind = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.loadedShipment = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadDetail = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.loadedEquipment = undefined;
    }
}
/**
 *
 */
class TrailerLoadHist {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDoor = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLengthFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedWeight = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedCbePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedCbeProfitability = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedReleaseNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.xclvInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closeDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frzblInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.placardInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedServiceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityRoute = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.brkOrProprCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDeckCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadedRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadLocation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.updatedTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dfrdShipmentInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedDfrdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealMatchCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.authrzUserid = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodShipmentCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.poisonShipmentCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDoorSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInspReqdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedLoadedDestination = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routingRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideRvwRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class TrailerSpecification {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerSubTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.effectiveDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.expiryDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.descr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fullCapacityPercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.fullCapacityVolume = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Length of trailer, in feet.  Added 05/07/2020 for PND Optimizer
         * Required: false
         * Array: false
         */
        this.trailerLengthNbr = undefined;
        /**
         * PND Weight capacity, in pounds, of the trailer.  Added 05/07/2020 for PND Optimizer
         * Required: false
         * Array: false
         */
        this.pndWeightCapacityLbs = undefined;
        /**
         * PND Pallet capacity (number of pallets that can fit on the equipment) of the trailer.  Added 05/07/2020 for PND Optimizer
         * Required: false
         * Array: false
         */
        this.pndPalletCapacityNbr = undefined;
        /**
         * PND HU (Handling Unit) capacity of the trailer.  Added 05/07/2020 for PND Optimizer
         * Required: false
         * Array: false
         */
        this.pndHandlingUnitCapacityNbr = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Trip {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripDsrName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualReturnDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.categoryCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedReturnDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.helperEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lunchTakenTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastStopInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastStopDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastStopTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastStopAreaId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.odometerEndNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.odometerStartNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overtimeStartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripRmrkInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.terminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cashDueCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.timeDateCriticalCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryStopCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryCompletedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.earlyCutoffCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freezeCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.liftgateCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupStopCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupCompletedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopCompletedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentRtndCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headloadCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.warrantyCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripCompletedDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceValidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mobileDeviceStartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.mobileDeviceEndDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedEmptyDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.incompletePickupWeightCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.releaseStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.doNotAutoDisplayFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastStopArrivalDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cmsPullId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hssBookingNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cmsOutForBidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cmsOfferId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scacCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.thirdPartyDriverHazmatCertFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastStopNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUsedNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.directionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.departTimeOfDayCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.arrivalTimeOfDayCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.runTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.createSourceCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.optimizerSourceCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.optimizerRunId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.optimizerTripId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastResequenceDateTime = undefined;
        /**
         * The number of RRS shipments on this
         *
         * Trip.  Added 10/23/2019
         * Required: false
         * Array: false
         */
        this.rrsCount = undefined;
        /**
         * The number of Guaranteed by 12
         *
         * shipments on this Trip.  Added 10/23/2019
         * Required: false
         * Array: false
         */
        this.guaranteedBy12Count = undefined;
        /**
         * Identifies the partner carrier for this cartage trip.  Added 03/24/2021 LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsCarrierId = undefined;
        /**
         * Amount we expect to be charged by the partner carrier for this trip.  Added 03/24/2021 LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsEstimatedChargeAmount = undefined;
        /**
         * Actual amount we were invoiced by the partner carrier for this trip.  Added 03/24/2021  LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsInvoicedChargeAmount = undefined;
        /**
         * Identifying number of the Partner carrier for this trip.  Added 03/24/2021 LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsPartnerTripId = undefined;
        /**
         * Identifies the type of the cartage trip.  Added 10/25/2021  LISG-15411
         *
         * Valid Values:
         * City = Cartage carrier takes the trip directly into city delivery
         * CarrierTerminal - Cartage carrier takes the freight to their terminal for future delivery
         * Required: false
         * Array: false
         */
        this.cmsTripTypeCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * The total number of motor moves associated with the trip.
         * Required: false
         * Array: false
         */
        this.totalMotorMoves = undefined;
        /**
         * The identifier of the first Trip Node that was a Stop on this Trip.
         * Required: false
         * Array: false
         */
        this.firstStopNodeSeqNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dispatchGroupId = undefined;
    }
}
/**
 *
 */
class TripActivity {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activityTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activityDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.odometerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class TripDriver {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dsrEmployeeId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overtimeStartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.domicileSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class TripEquipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripEquipmentSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lengthUOMUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lengthUOMFeetNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.asgmntStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerEmptyFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedTrailerTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originalMoveToSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Identifies the type of trip; denormed from SCO_TRIP.  At present, it is needed solely for Linehaul.  Added 04/15/2020
         *
         * VALID VALUES
         * L=Linehaul
         * (null)
         * Required: false
         * Array: false
         */
        this.tripTypeCd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdFullName = undefined;
    }
}
/**
 *
 */
class TripNode {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.areaInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedArriveDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedArriveDateTimeLocal = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDepartDateTimeLocal = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideOperationsInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideInstructionInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryFromTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.scheduledDeliveryToTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planDistanceFromPrevNode = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.odometerNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentRtndCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.nodeSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.delayReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.estimatedDelayMinimum = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.latitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.longitudeNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUsedActivitySequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.stopBypassedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryWindowCalculatedOffsetMinutes = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryActivityCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupActivityCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentActivityCount = undefined;
        /**
         * The planned distance from the previous node captured with up to 2 places decimal.  Added 10/08/2019
         * Required: false
         * Array: false
         */
        this.plannedDistanceFromNodeMl = undefined;
        /**
         * Identifies the type of trip; denormed from SCO_TRIP.  At present, it is needed solely for Linehaul.  Added 04/15/2020
         *
         * VALID VALUES
         * L=Linehaul
         * (null)
         * Required: false
         * Array: false
         */
        this.tripTypeCd = undefined;
        /**
         * Amount we expect to be charged by the partner carrier for this stop.  Added 03/24/2021 LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsEstimatedChargeAmount = undefined;
        /**
         * Actual amount we were invoiced by the partner carrier for this stop.  Added 03/24/2021  LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsInvoicedChargeAmount = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates if a customer picks up or drops off freight at the dock.
         * Required: false
         * Array: false
         */
        this.dockProfileInd = undefined;
        /**
         * Indicates that a customer has a forklift.
         * Required: false
         * Array: false
         */
        this.forkliftInd = undefined;
        /**
         * The type of customer location; i.e. business/commercial, distribution center, construction site, etc.
         * Required: false
         * Array: false
         */
        this.customerLocationType = undefined;
        /**
         * The employee ID of the DSR.
         * Required: false
         * Array: false
         */
        this.driverEmployeeId = undefined;
    }
}
/**
 *
 */
class TripNodeActivity {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.activityCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.entityTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.parentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freightStatusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryInformationRqdCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.deliveryQualifierCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.movementSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualActivityDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.actualActivityDateTimeLocal = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.swingEquipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.swingTripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cashDueInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.earlyCutoffCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freezeCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headloadCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.liftgateInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.timeDateCriticalInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.appointmentInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalBillCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalPalletCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalWeightCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalVolumeCubicFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalMmCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.archiveInd = undefined;
        /**
         * Same value for CORRE
         * LATION id is assigned to various rows from different tables that form a single b
         * usiness transactions. In other words, this groups multiple rows across various t
         * ables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Defines the specific
         *
         * type of service provided for a shipment.    Added 10/23/2019
         *
         *
         * Values:
         * 1 - Normal LTL
         * 2 - Con-Way Deferred Express (Deprecated)
         * 3 - LTL Expedite Service
         * 4 - Guaranteed by 12
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         * Amount we expect to be charged by the partner carrier for delivery of this shipment.  Added 03/24/2021 LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsEstimatedChargeAmount = undefined;
        /**
         * Actual amount we were invoiced by the partner carrier for delivery of this shipment.  Added 03/24/2021  LISG-10965
         * Required: false
         * Array: false
         */
        this.cmsInvoicedChargeAmount = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Indicates that a shipment has bulk liquid.
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
    }
}
/**
 * Holds a snapshot of TripNodeActivity. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class TripNodeActivitySnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.tripNodeActivitySequenceNbr = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Holds a snapshot of TripNode. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class TripNodeSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.historyActionCd = undefined;
        /**
         * The timestamp of when this snapshot was created.
         * Required: false
         * Array: false
         */
        this.historyCreatedTimestamp = undefined;
        /**
         * Snapshot.
         * Required: false
         * Array: false
         */
        this.tripNode = undefined;
        /**
         * Previous values. Only attributes that are different from snapshot will be populated
         * Required: false
         * Array: false
         */
        this.tripNodeDiff = undefined;
    }
}
/**
 * Holds a snapshot of Trip. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class TripSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.historyActionCd = undefined;
        /**
         * The timestamp of when this snapshot was created.
         * Required: false
         * Array: false
         */
        this.historyCreatedTimestamp = undefined;
        /**
         * Snapshot.
         * Required: false
         * Array: false
         */
        this.trip = undefined;
        /**
         * Previous values. Only attributes that are different from snapshot will be populated
         * Required: false
         * Array: false
         */
        this.tripDiff = undefined;
    }
}
/**
 *
 */
class TrlrLoadSnapshot {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentStatus = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.statusUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEvent = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.currentEventDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDoor = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLengthFeet = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDestinationSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadWeight = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadCbePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadCbeProfitability = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadReleaseNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.xclvInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.billCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closeDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.frzblInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.placardInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedServiceInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.cityRoute = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.brkOrProprCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDeckCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerLoadRemarks = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadLocation = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.customerMadSuffix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.lastUpdateDeviceId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.updatedTimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dfrdShipmentInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadDfrdInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sealMatchCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.authorizationUserid = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.foodShipmentCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.poisonShipmentCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDoorSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.closShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerInspRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationReasonCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.exctdUnloadDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.suggestedLoadDestination = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.routingRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideRvwRequiredInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.equipmentEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class XdockException {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationTerminalSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdPrefix = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.trailerIdSuffixNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.loadedReleaseNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.reportingSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dockName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shortPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmgdPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.allShrtInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.comments = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventDoor = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Holds a snapshot of XdockException. Contains the details at the time on an event. It also provides the previous values if an update occured
 * null
 */
class XdockExceptionSnapshot {
    constructor() {
        /**
         * The action that created this snapshot. Add, Update or Delete
         * Required: false
         * Array: false
         */
        this.eventActionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         * The previous snapshot, before this record was updated.
         * Required: false
         * Array: false
         */
        this.before = undefined;
        /**
         * The current snapshot.
         * Required: false
         * Array: false
         */
        this.after = undefined;
    }
}
/**
 * Contains handling unit
 * 	information related to Shipments in an outbound plan that are indicated
 * 	as incomplete. This should never be the case of pickup requests.
 */
class XdockHandlingUnit {
    constructor() {
        /**
         * Unique ID
         * assigned to the plan. Is random generated
         * Required: false
         * Array: false
         */
        this.planInstanceId = undefined;
        /**
         * Unique
         * sequence number assigned to the shipment within in a plan.
         * Required: false
         * Array: false
         */
        this.xdockShipmentSequenceNbr = undefined;
        /**
         * This is
         * 	the child PRO Number identifying a handling unit of a shipment.
         * Required: false
         * Array: false
         */
        this.childProNbr = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class XdockPlan {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.planInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planDate = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.driverCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.autoInclReshipFlag = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELATION i
         * d is assigned to various rows from different tables that form a single business
         * transactions. In other words, this groups multiple rows across various tables fo
         * r a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is ass
         * igned to various rows from different tables that form a single database transact
         * ions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.planningTrailer = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.xdockShipment = undefined;
    }
}
/**
 *
 */
class XdockShipment {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.xdockPlanInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.xdockShipmentSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.pickupRequestSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.proNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.xdockProgressCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.destinationSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.freezableInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.guaranteedInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.headLoadInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hazmatInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.originSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.planInclInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.serviceTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.shipmentTypeCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalCubePercentage = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.totalMmCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ttlPiecesCount = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.ttlWeightLbs = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.divertSicCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.overrideDoor = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dvrsnNextMoveSic = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dvrsnLoadToShiftCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dvrsnHssCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dvrsnDayInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.runId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dvrsnInstructionSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.bulkLiquidInd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hssScheduledDepartDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.hssLoadedGroupSequenceNbr = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.detailCapxtimestamp = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.replicationLastUpdateDateTime = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.dmlDateTime = undefined;
        /**
         * Same value for CORRELATION
         * id is assigned to various rows from different tables that form a single business
         * 	transactions. In other words, this groups multiple rows across various tables f
         * or a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Same value for event id is as
         * signed to various rows from different tables that form a single database transac
         * tions.
         * Multiple database transactions could make up one business transaction, which is
         * 	indicated by correlation_id
         * In other words, this groups multiple rows across various tables for a single da
         * tabase transaction.
         * Typically this is used to publish a message to a queue for consumption or a wor
         * kflow.
         * Required: false
         * Array: false
         */
        this.scoEventId = undefined;
        /**
         * Indicates if
         * 	the xdock shipment is partial. This means not all handling units are in
         * 	the same xdock plan. XDock Handling Unit records are expected in this
         * 	case, to identify which handling units are part of this xdock
         * 	shipment.
         * Required: false
         * Array: false
         */
        this.completeShipmentInd = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Rule {
    constructor() {
        /**
         * Desc: Unique identifier; generated
         * Required: false
         * Array: false
         */
        this.ruleInstId = undefined;
        /**
         * Desc: Descriptive Name of the rule
         * Required: false
         * Array: false
         */
        this.ruleName = undefined;
        /**
         * Desc: Description of what the rule does
         * Required: false
         * Array: false
         */
        this.ruleDescription = undefined;
        /**
         * Desc: Identifies a rule as being a precondition rule.  Precondition rules are used to determine which rules to run.
         * Required: false
         * Array: false
         */
        this.preconditionInd = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multipple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * You can have multiple rules
         *
         * entered in the system that run from June-Aug, then Aug-Oct, and you want them both to
         *
         * be in the system but not be processed until the date being used for comparison is in
         *
         * that range.   Added 7/19/2019 as per Richard
         * Required: false
         * Array: false
         */
        this.effectiveDateTime = undefined;
        /**
         * You can have multiple rules entered
         *
         * in the system that run from June-Aug, then Aug-Oct, and you want them both to be in
         *
         * the system but not be processed until the date being used for comparison is in that
         *
         * range.. Added 07/19/2019 as per Richard.
         * Required: false
         * Array: false
         */
        this.expiryDateTime = undefined;
        /**
         * Allows a Rule to be in TEST mode
         *
         * before it can run fully. When the rule is ready to run, then it will be set to PROD.
         *
         * If a rule is needed to be turned off, it can be set to INACTIVE.  Added 07/19/2019 as
         *
         * per Richard.
         *
         * VALID VALUES:
         * TEST
         * PROD
         * INACTIVE
         * Required: false
         * Array: false
         */
        this.activeTypeCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.ruleset = undefined;
    }
}
/**
 *
 */
class RuleExpression {
    constructor() {
        /**
         * Generated unique identifier
         * Required: false
         * Array: false
         */
        this.expressionInstId = undefined;
        /**
         * Desc: Name of the attribute to be used in the logical expression.
         * Required: false
         * Array: false
         */
        this.leftOperand = undefined;
        /**
         * Desc: The operand to be used in the logical expression.
         *
         * Valid Values
         * EQUAL_TO
         * NOT_EQUAL_TO
         * GREATER_THAN
         * GREATER_OR_EQUAL_TO
         * LESS_THAN
         * LESS_OR_EQUAL_TO
         * Required: false
         * Array: false
         */
        this.operatorCd = undefined;
        /**
         * Desc: The type of value of the attribute to be used in the logical expression.
         *
         * Valid Values
         * NUMERIC
         * CHAR
         * Required: false
         * Array: false
         */
        this.valueTypeCd = undefined;
        /**
         * Desc: The value of the attribute to be used in the logical expression.
         * Required: false
         * Array: false
         */
        this.value = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multipple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class RuleOutcome {
    constructor() {
        /**
         * Desc: Unique identifier; generated
         * Required: false
         * Array: false
         */
        this.outcomeInstId = undefined;
        /**
         * Added as an additional part of PK so that Outcome name/value pairs can be grouped. 4/15/2019
         * Required: false
         * Array: false
         */
        this.outcomeSeqNbr = undefined;
        /**
         * Desc:
         * Required: false
         * Array: false
         */
        this.outcomeName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.outcomeValue = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multipple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class Ruleset {
    constructor() {
        /**
         * Desc: Unique identifier; generated
         * Required: false
         * Array: false
         */
        this.rulesetInstId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.priorityOrderNbr = undefined;
        /**
         * Desc: Identifies the logical operator value.
         *
         * Valid Values
         * AND
         * OR
         * NOT
         * Required: false
         * Array: false
         */
        this.logicalOperatorCd = undefined;
        /**
         * Desc: The action that should be performed if the logical operator function is true.
         * Required: false
         * Array: false
         */
        this.actionCallBack = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multipple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Descriptive Name of the ruleset.  Added 5/1/2019
         * Required: false
         * Array: false
         */
        this.rulesetName = undefined;
        /**
         * Indicates whether to add, update or delete this entry. This will be used for merging lists.
         *
         * A - Add entry to list
         * D - Delete entry from list
         * U - Update entry
         * Required: false
         * Array: false
         */
        this.listActionCd = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.ruleOutcome = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.ruleExpression = undefined;
    }
}
/**
 *
 */
class EventWait {
    constructor() {
        /**
         * Unique identifier of records in this table. Values populated from sequence
         * Required: false
         * Array: false
         */
        this.waitInstanceId = undefined;
        /**
         * Id of the ensemble that is being put on lock wait
         * Required: false
         * Array: false
         */
        this.ensembleInstanceId = undefined;
        /**
         * Name of the queue that was the source of this ensemble message
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         * The timestamp of when this ensemble was first put on lock wait
         * Required: false
         * Array: false
         */
        this.waitStartTmst = undefined;
        /**
         * The number of times this ensemble was put on lock wait
         * Required: false
         * Array: false
         */
        this.waitCount = undefined;
        /**
         * The duration of wait in milliseconds
         * Required: false
         * Array: false
         */
        this.waitTime = undefined;
        /**
         * The maximum wait time in milliseconds before the lock will be overridden
         * Required: false
         * Array: false
         */
        this.maxWaitTime = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 *
 */
class RetriggerEvent {
    constructor() {
        /**
         *
         * Required: false
         * Array: false
         */
        this.eventRetriggerId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.regionCd = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.sourceTableName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.queueName = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         *
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
    }
}
/**
 * Details of a symphony message
 */
class SymphonyMessage {
    constructor() {
        /**
         * ID of the message
         * Required: false
         * Array: false
         */
        this.messageId = undefined;
        /**
         * Timestamp of when the message was enqueued
         * Required: false
         * Array: false
         */
        this.enqueueTmst = undefined;
        /**
         * Number of time the message was retried
         * Required: false
         * Array: false
         */
        this.retryCount = undefined;
        /**
         * Message payload
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Details about a message queue. E.g. Oracle Advanced Queue
 */
class SymphonyQueue {
    constructor() {
        /**
         * Name of the queue
         * Required: false
         * Array: false
         */
        this.name = undefined;
        /**
         * Maximum number of retries
         * Required: false
         * Array: false
         */
        this.maxRetries = undefined;
        /**
         * Delay for retries
         * Required: false
         * Array: false
         */
        this.retryDelay = undefined;
        /**
         * Indicates if enqueue is enabled
         * Required: false
         * Array: false
         */
        this.enqueueEnabled = undefined;
        /**
         * Indicates if dequeue is enabled
         * Required: false
         * Array: false
         */
        this.dequeueEnabled = undefined;
        /**
         * The retention time
         * Required: false
         * Array: false
         */
        this.retention = undefined;
    }
}
/**
 * Captures transaction events. API writes one record per transaction.  Added to EVT seed model  06/26/2020
 */
class TransactionEvent {
    constructor() {
        /**
         * PK. Unique ID of the transaction. For Oracle, use the transaction id available in the context. Populated by trigger.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Identifies the type of transaction. Could be method name
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
        /**
         * Same value for CORRELATION id is assigned to various rows from different tables that form a single business transactions.
         * In other words, this groups multiple rows across various tables for a single business transaction.
         * Required: false
         * Array: false
         */
        this.correlationId = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Number of seconds to hold event before publishing.  Added 09/21/2020  LISG-7498
         * Required: false
         * Array: false
         */
        this.eventReleaseDelaySeconds = undefined;
        /**
         *
         * Required: false
         * Array: true
         */
        this.transactionDetails = undefined;
    }
}
/**
 * Stores the snapshot of all the tables that were updated as part of the transaction. The snapshot is a json representation of all columns that were changed. The snapshot will be created by insert/update/delete triggers on the source table.   Added to EVT seed model  06/26/2020
 */
class TransactionEventDetail {
    constructor() {
        /**
         * PK. Unique id for the snapshot record. Generated by Sequence.
         * Required: false
         * Array: false
         */
        this.detailId = undefined;
        /**
         * Unique ID of the transaction. For Oracle, use the transaction id available in the context. Populated by trigger.
         * Required: false
         * Array: false
         */
        this.transactionId = undefined;
        /**
         * Name of the table for which the snapshot is stored
         * Required: false
         * Array: false
         */
        this.tableName = undefined;
        /**
         * Json string containing the before and after values for all the columns that were changed by the transaction.
         * Required: false
         * Array: false
         */
        this.snapshot = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * trip_inst_id, route_inst_id, shp_inst_id etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * Audit information such as create and/or update transaction details - who, when and which program/transaction
         * Required: false
         * Array: false
         */
        this.auditInfo = undefined;
        /**
         * Identifies the type of transaction. Could be method name
         * Required: false
         * Array: false
         */
        this.transactionType = undefined;
    }
}
/**
 * Enter comments
 */
class ListEnsembleConfigurationsResp {
    constructor() {
        /**
         * List of message queues - AQ and JMS
         * Required: false
         * Array: true
         */
        this.queues = undefined;
        /**
         * List of subscribers - Pub/Sub
         * Required: false
         * Array: true
         */
        this.subscriptions = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.apiChannels = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.timers = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.emails = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.files = undefined;
        /**
         * List of API channels
         * Required: false
         * Array: true
         */
        this.jmsQueues = undefined;
    }
}
class TimerConfig {
    constructor() {
        /**
         * Name of the timer.
         * Required: true
         * Array: false
         */
        this.name = undefined;
        /**
         * Interval for timer. Specify in seconds. The value is set in Octopus using the property sym.timer.<name>.intervalInSeconds
         * Required: true
         * Array: false
         */
        this.intervalInSeconds = undefined;
        /**
         * Specify the timer behavior. If fixedRate is set to true, the timer will fire an ensemble once every interval regardless of whether previous tasks are executing.
         * 						If fixedRate is set to false, the timer will be scheduled to fire after the specified interval, once the current execution is complete.
         * 						For both settings, it does not matter whether the ensemble execution is successful or not for the next timer event to be fired.
         * Required: true
         * Array: false
         */
        this.fixedRate = undefined;
    }
}
class Timer {
    constructor() {
        /**
         * Configuration of the timer.
         * Required: true
         * Array: false
         */
        this.configuration = undefined;
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: true
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Path parameters for listTimerEnsembles
 */
class ListTimerEnsemblesPath {
    constructor() {
        /**
         * The name of the timer ensemble from which the list of timers is requested
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for listTimerEnsembles
 */
class ListTimerEnsemblesResp {
    constructor() {
        /**
         * List of timers defined in this ensemble.
         * Required: false
         * Array: true
         */
        this.timers = undefined;
    }
}
/**
 * Path parameters for startTimerEnsemble
 */
class StartTimerEnsemblePath {
    constructor() {
        /**
         * The name of the timer to start
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for startTimerEnsemble
 */
class StartTimerEnsembleResp {
    constructor() {
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: false
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Path parameters for stopTimerEnsemble
 */
class StopTimerEnsemblePath {
    constructor() {
        /**
         * The name of the timer to stop
         * Required: false
         * Array: false
         */
        this.name = undefined;
    }
}
/**
 * Response for stopTimerEnsemble
 */
class StopTimerEnsembleResp {
    constructor() {
        /**
         * Indicates whether the timer is running. True indicates timer is running
         * Required: false
         * Array: false
         */
        this.runningInd = undefined;
    }
}
/**
 * Request for startPnDOptimizerEnsemble
 */
class StartPnDOptimizerEnsembleRqst {
    constructor() {
        /**
         * The name of the ensemble to start
         * Required: false
         * Array: false
         */
        this.ensembleName = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The payload for the ensemble process
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Path parameters for startPnDOptimizerEnsemble
 */
class StartPnDOptimizerEnsemblePath {
    constructor() {
        /**
         * The name of the channel in which this ensemble will be executed
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Query parameters for startPnDOptimizerEnsemble
 */
class StartPnDOptimizerEnsembleQuery {
    constructor() {
        /**
         * Send true if a synchronous response is required. Normally, channel execute asynchronously.
         * Required: false
         * Array: false
         */
        this.synchronous = undefined;
    }
}
/**
 * Response for startPnDOptimizerEnsemble
 */
class StartPnDOptimizerEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request for startDriverLocationEnsemble
 */
class StartDriverLocationEnsembleRqst {
    constructor() {
        /**
         * The name of the ensemble to start
         * Required: false
         * Array: false
         */
        this.ensembleName = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The payload for the ensemble process
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Path parameters for startDriverLocationEnsemble
 */
class StartDriverLocationEnsemblePath {
    constructor() {
        /**
         * The name of the channel in which this ensemble will be executed
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Query parameters for startDriverLocationEnsemble
 */
class StartDriverLocationEnsembleQuery {
    constructor() {
        /**
         * Send true if a synchronous response is required. Normally, channel execute asynchronously.
         * Required: false
         * Array: false
         */
        this.synchronous = undefined;
    }
}
/**
 * Response for startDriverLocationEnsemble
 */
class StartDriverLocationEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request for startPrintDocumentEnsemble
 */
class StartPrintDocumentEnsembleRqst {
    constructor() {
        /**
         * The name of the ensemble to start
         * Required: false
         * Array: false
         */
        this.ensembleName = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The payload for the ensemble process
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Path parameters for startPrintDocumentEnsemble
 */
class StartPrintDocumentEnsemblePath {
    constructor() {
        /**
         * The name of the channel in which this ensemble will be executed
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Query parameters for startPrintDocumentEnsemble
 */
class StartPrintDocumentEnsembleQuery {
    constructor() {
        /**
         * Send true if a synchronous response is required. Normally, channel execute asynchronously.
         * Required: false
         * Array: false
         */
        this.synchronous = undefined;
    }
}
/**
 * Response for startPrintDocumentEnsemble
 */
class StartPrintDocumentEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request for startRuleBasedRoutingEnsemble
 */
class StartRuleBasedRoutingEnsembleRqst {
    constructor() {
        /**
         * The name of the ensemble to start
         * Required: false
         * Array: false
         */
        this.ensembleName = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The payload for the ensemble process
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Path parameters for startRuleBasedRoutingEnsemble
 */
class StartRuleBasedRoutingEnsemblePath {
    constructor() {
        /**
         * The name of the channel in which this ensemble will be executed
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Query parameters for startRuleBasedRoutingEnsemble
 */
class StartRuleBasedRoutingEnsembleQuery {
    constructor() {
        /**
         * Send true if a synchronous response is required. Normally, channel execute asynchronously.
         * Required: false
         * Array: false
         */
        this.synchronous = undefined;
    }
}
/**
 * Response for startRuleBasedRoutingEnsemble
 */
class StartRuleBasedRoutingEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
/**
 * Request for startDeviceTripStopEnsemble
 */
class StartDeviceTripStopEnsembleRqst {
    constructor() {
        /**
         * The name of the ensemble to start
         * Required: false
         * Array: false
         */
        this.ensembleName = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey1 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey2 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey3 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey4 = undefined;
        /**
         * Application key value like
         *
         * pro, shipid, pickupdate etc.
         * Required: false
         * Array: false
         */
        this.businessKey5 = undefined;
        /**
         * The payload for the ensemble process
         * Required: false
         * Array: false
         */
        this.payload = undefined;
    }
}
/**
 * Path parameters for startDeviceTripStopEnsemble
 */
class StartDeviceTripStopEnsemblePath {
    constructor() {
        /**
         * The name of the channel in which this ensemble will be executed
         * Required: false
         * Array: false
         */
        this.channelName = undefined;
    }
}
/**
 * Query parameters for startDeviceTripStopEnsemble
 */
class StartDeviceTripStopEnsembleQuery {
    constructor() {
        /**
         * Send true if a synchronous response is required. Normally, channel execute asynchronously.
         * Required: false
         * Array: false
         */
        this.synchronous = undefined;
    }
}
/**
 * Response for startDeviceTripStopEnsemble
 */
class StartDeviceTripStopEnsembleResp {
    constructor() {
        /**
         * Instance ID of the ensemble that was started.
         * Required: false
         * Array: false
         */
        this.ensembleInstId = undefined;
    }
}
class DeliveryShipmentLckSearchRecord extends DeliveryShipmentSearchRecord {
}
/**
 * The payload which is used for processing SCO transaction events.
 */
class CityOperationsEventPayload extends PayloadType {
    constructor() {
        super(...arguments);
        /**
         * The snapshots of the records that have been modified by the transaction event for which this payload was published.
         * Required: true
         * Array: true
         */
        this.snapshots = undefined;
    }
}
/**
 * This type will be used to publish documents targeted for Elastic Search on a Kafka topic. The type includes KafkaMessageHeader.
 */
class DeliveryShipmentSearchRecordMessage extends DeliveryShipmentSearchRecord {
    constructor() {
        super(...arguments);
        /**
         * Additional details about the document
         * Required: false
         * Array: false
         */
        this.messageHeader = undefined;
    }
}
/**
 * This type will be used to publish documents targeted for Elastic Search on a Kafka topic. The type includes KafkaMessageHeader.
 */
class EnsembleStateSearchRecordMessage extends EnsembleStateSearchRecord {
    constructor() {
        super(...arguments);
        /**
         * Additional details about the document
         * Required: false
         * Array: false
         */
        this.messageHeader = undefined;
    }
}
/**
 * Contains basic key information needed to search for a customer.
 */
class CustomerEventPayload extends PayloadType {
    constructor() {
        super(...arguments);
        /**
         * Name Line 1 of the customer Location.
         * Required: false
         * Array: false
         */
        this.name1 = undefined;
        /**
         * Name Line 2 of the customer Location.
         * Required: false
         * Array: false
         */
        this.name2 = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customerAddress = undefined;
        /**
         * Required: false
         * Array: false
         */
        this.customerLocationFunction = undefined;
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */

export { SdkCityOperationsModule, CityOperationsApiService, AssignDispatchGroupToTripPath, AssignDispatchGroupToTripResp, AssignHookEquipmentToTripPath, AssignHookEquipmentToTripRqst, AssignPickupsToDockDropRoutePath, AssignPickupsToDockDropRouteRqst, AssignPickupsToPnDTripPath, AssignPickupsToPnDTripRqst, AssignShipmentsToRoutePath, AssignShipmentsToRouteRqst, AssignShipmentsToRouteResp, AutocompleteDeliveryStopPath, AutocompleteDeliveryStopRqst, BalancePnDRoutesPath, BalancePnDRoutesRqst, BalancePnDRoutesResp, BulkCalculateShipmentCubeRqst, BulkCalculateShipmentCubeResp, CalculatePnDTripMetricsPath, CalculatePnDTripMetricsRqst, CalculatePnDTripMetricsResp, CalculateShipmentCubeRqst, CalculateShipmentCubeResp, CompleteDeliveryStopPath, CompleteDeliveryStopRqst, CompleteDeliveryStopResp, CompleteDockPickupRqst, CompleteEquipmentStopPath, CompleteEquipmentStopQuery, CompleteEquipmentStopRqst, CompleteHookLoadedStopPath, CompleteHookLoadedStopRqst, CompleteHookLoadedStopResp, CompletePickupStopPath, CompletePickupStopRqst, CompletePickupStopResp, CompletePnDDropStopPath, CompletePnDDropStopRqst, CompletePnDDropStopResp, CompleteSpotLoadedStopPath, CompleteSpotLoadedStopRqst, CompleteSpotLoadedStopResp, CompleteTripPath, CompleteTripRqst, ConvertStopActivitiesPath, ConvertStopActivitiesRqst, ConvertRouteRqst, ConvertToCartageStopRqst, CreateDeliveryShipmentForAuditRqst, CreateDockDropPickupRequestRqst, CreateDockDropPickupRequestResp, CreateDsrCheckInDocumentsRqst, CreateDsrCheckInDocumentsResp, CreatePnDManifestDocumentsRqst, CreatePnDManifestDocumentsResp, CreateNewRouteRqst, CreateNewRouteResp, CreateOpsEventRqst, CreateOpsEventResp, CreatePnDDispatchGroupRqst, CreatePnDDispatchGroupResp, CreateDropStopsPath, CreateDropStopsRqst, CreatePnDTripResp, CreatePnDTripRqst, CreatePositioningPointsRqst, DeleteDropLocationPath, DeletePnDDispatchGroupPath, DeletePnDRoutesPath, DeletePnDRoutesQuery, DeletePnDTripPath, DispatchPnDTripPath, DispatchPnDTripRqst, DispatchPnDTripResp, DockActivityDetails, DockStop, EvaluatePnDRouteBalancingRqst, EvaluatePnDRouteBalancingResp, GetActualDriverLocationPath, GetActualDriverLocationResp, GetCarrierLocationTripsQuery, GetCarrierLocationTripsResp, GetCustomerOperationsSchedulesPath, GetCustomerOperationsSchedulesQuery, GetCustomerOperationsSchedulesResp, GetNextAvailableDateForCustomerSchedulePath, GetNextAvailableDateForCustomerScheduleQuery, GetNextAvailableDateForCustomerScheduleResp, GetDeliveryShipmentForAuditQuery, GetDeliveryShipmentForAuditResp, GetDeliveryShipmentsForAuditByProPath, GetDeliveryShipmentsForAuditByProResp, GetDropLocationPath, GetDropLocationResp, GetServiceCenterPrintPreferencesPath, GetServiceCenterPrintPreferencesResp, GetOperationsCustomerProfilePath, GetOperationsCustomerProfileQuery, GetOperationsCustomerProfileResp, GetPickupTripsRqst, GetPickupTripsResp, GetPnDDriverLocationPath, GetPnDDriverLocationResp, GetPnDInboundSelectionProfilePath, GetPnDInboundSelectionProfileResp, GetPnDPickupDockDropIndPath, GetPnDPickupDockDropIndResp, GetPnDPickupDriverPath, GetPnDPickupDriverResp, GetPnDRoutePath, GetPnDRouteQuery, GetPnDRouteResp, GetPnDStopPath, GetPnDStopResp, GetPnDTripPath, GetPnDTripQuery, GetPnDTripResp, GetPnDTripDsrLocationsPath, GetPnDTripDsrLocationsResp, GetTripByEquipmentInstIdPath, GetTripByEquipmentInstIdResp, GetTripDriversPath, GetTripDriversResp, ListCustomerProfileNotesRqst, ListCustomerProfileNotesResp, ListDailyPickupStopCustomerPreferencesRqst, ListDailyPickupStopCustomerPreferencesResp, ListDockRouteStopsRqst, ListDockRouteStopsResp, ListHookEquipmentStopsPath, ListHookEquipmentStopsQuery, ListHookEquipmentStopsResp, ListDropLocationsPath, ListDropLocationsQuery, ListDropLocationsResp, ListPnDDeliveryDensityRqst, ListPnDDeliveryDensityResp, ListPnDDispatchGroupsQuery, ListPnDDispatchGroupsResp, ListPnDDispatchTripsPath, ListPnDDispatchTripsQuery, ListPnDDispatchTripsResp, ListPnDDriverLocationsPath, ListPnDDriverLocationsQuery, ListPnDDriverLocationsResp, ListPnDInboundPlannerTrailersRqst, ListPnDInboundPlannerTrailersResp, ListPnDInboundSelectionProfilesQuery, ListPnDInboundSelectionProfilesResp, ListPnDProximalAddressStopsRqst, ListPnDProximalAddressStopsResp, ListPnDRoutesPath, ListPnDRoutesQuery, ListPnDRoutesResp, ListPnDRoutesByGeofenceRqst, ListPnDRoutesByGeofenceResp, ListPnDServiceCenterMetricsPath, ListPnDServiceCenterMetricsQuery, ListPnDServiceCenterMetricsResp, ListPnDStopsPath, ListPnDStopsQuery, ListPnDStopsResp, ListPnDSuggestedTripsForPickupPath, ListPnDSuggestedTripsForPickupQuery, ListPnDSuggestedTripsForPickupResp, ListPnDSuggestedRouteNamesPath, ListPnDSuggestedRouteNamesQuery, ListPnDSuggestedRouteNamesResp, ListPnDTripsPath, ListPnDTripsQuery, ListPnDTripsResp, ListPnDTripStopsPath, ListPnDTripStopsResp, ListPnDUnassignedPickupsRqst, ListPnDUnassignedPickupsResp, ListPnDUnassignedStopsRqst, ListPnDUnassignedStopsResp, ListShipmentsOnTrailerPath, ListShipmentsOnTrailerResp, MergePnDStopsPath, MergePnDStopsRqst, MergePnDStopsResp, ReassignPnDPickupStopsPath, ReassignPnDPickupStopsRqst, ReassignPnDPickupStopsResp, ReassignPnDShipmentsPath, ReassignPnDShipmentsRqst, ReassignStopsRqst, ReassignStopsResp, ResequenceFreightByTripRqst, SplitPnDTripStopPath, SplitPnDTripStopRqst, SplitPnDTripStopResp, StartDriverLocationPath, StopSequenceModelRqst, StopSequenceModelResp, TransferShipmentsRqst, UnassignPnDShipmentsFromRoutePath, UnassignPnDShipmentsFromRouteRqst, UnassignStopsRqst, UpdateCurrentDriverLocationPath, UpdateCurrentDriverLocationRqst, UpdateCustomerGeoCoordinatesPath, UpdateCustomerGeoCoordinatesRqst, UpdateDeliveryShipmentAuditStatusRqst, UpdateDeliveryShipmentExcludedTrailerIndPath, UpdateDeliveryShipmentExcludedTrailerIndRqst, UpdateDeliveryShipmentFromAuditRqst, UpdateOperationsCustomerProfilePath, UpdateOperationsCustomerProfileRqst, UpdateOperationsCustomerProfileResp, UpdatePnDDeliveryStatusForShipmentsRqst, UpdatePnDDeliveryStatusForShipmentsResp, UpdatePnDDispatchGroupRqst, UpdateDropStopsPath, UpdateDropStopsRqst, UpdatePnDEquipmentHookStopPath, UpdatePnDEquipmentHookStopRqst, UpdatePnDEquipmentHookStopResp, UpdatePnDRoutesRqst, UpdatePnDRoutesResp, UpdatePnDRouteStatusForShipmentsRqst, UpdatePnDTripRqst, UpdatePnDTripResp, UpdateRouteStopSequenceRqst, UpdateTripStatusPath, UpdateTripStatusRqst, ValidatePnDRoutePath, ValidatePnDRouteRqst, ValidatePnDRouteResp, BuildPnDOptimizerRequestPath, BuildPnDOptimizerRequestResp, CalculateCustomerDwellTimesRqst, CalculateCustomerDwellTimesResp, CalculatePnDOptimizerMetricsRqst, CalculatePnDOptimizerMetricsResp, CreatePnDOptimizerRqst, CreatePnDOptimizerResp, CreatePnDOptimizerCommodityDimensionsPath, CreatePnDOptimizerDriversRqst, CreatePnDOptimizerDriversResp, CreatePnDOptimizerEquipmentRqst, CreatePnDOptimizerEquipmentResp, CreatePnDOptimizerFailureResponseRqst, CreatePnDOptimizerFailureResponseResp, CreatePnDOptimizerPickupRequestsRqst, CreatePnDOptimizerPickupRequestsResp, CreatePnDOptimizerRoutedShipmentsPath, CreatePnDOptimizerRoutedShipmentsRqst, CreatePnDOptimizerRoutesPath, CreatePnDOptimizerRoutesRqst, CreatePnDOptimizerRoutesResp, CreatePnDOptimizerShipmentsRqst, CreatePnDOptimizerShipmentsResp, CreateRoutesFromOptimizerPath, CreateRoutesFromOptimizerRqst, CreateRoutesFromOptimizerResp, DeterminePnDOptimizerSuccessfulRunPath, DeterminePnDOptimizerSuccessfulRunResp, ExecutePnDOptimizerRequestPath, ExecutePnDOptimizerRequestRqst, ExecutePnDOptimizerRequestResp, GetPnDOptimizerStatusQuery, GetPnDOptimizerStatusResp, GetPnDOptimizerSummaryPath, GetPnDOptimizerSummaryQuery, GetPnDOptimizerSummaryResp, ListDeliveryShipmentsByProfilePath, ListDeliveryShipmentsByProfileQuery, ListDeliveryShipmentsByProfileRqst, ListDeliveryShipmentsByProfileResp, ListDeliveryShipmentsByProRqst, ListDeliveryShipmentsByProResp, ListOperationsSchedulesForCustomersPath, ListOperationsSchedulesForCustomersRqst, ListOperationsSchedulesForCustomersResp, ListOptimizerSameAddressShipmentsPath, ListOptimizerSameAddressShipmentsResp, ListPnDDriversPath, ListPnDDriversQuery, ListPnDDriversResp, ListSpotOrDropEquipmentPath, ListSpotOrDropEquipmentQuery, ListSpotOrDropEquipmentResp, ListTripDriversPath, ListTripDriversQuery, ListTripDriversResp, ListTripNodeActivitiesQuery, ListTripNodeActivitiesResp, ListPnDEquipmentPath, ListPnDEquipmentResp, ListPnDOptimizerCommodityDimensionsPath, ListPnDOptimizerCommodityDimensionsResp, ListPnDOptimizerDriversPath, ListPnDOptimizerDriversResp, ListPnDOptimizerEquipmentPath, ListPnDOptimizerEquipmentResp, ListPnDOptimizerParametersQuery, ListPnDOptimizerParametersResp, ListPnDOptimizerPickupStopsPath, ListPnDOptimizerPickupStopsResp, ListPnDOptimizerResultsPath, ListPnDOptimizerResultsResp, ListPnDOptimizerRoutesPath, ListPnDOptimizerRoutesResp, ListPnDOptimizerRoutesByRouteInstIdsRqst, ListPnDOptimizerRoutesByRouteInstIdsResp, ListPnDOptimizerShipmentsPath, ListPnDOptimizerShipmentsResp, ListPnDPositioningPointsPath, ListPnDPositioningPointsQuery, ListPnDPositioningPointsResp, StartPnDOptimizerPath, UpdatePnDOptimizerDriversPath, UpdatePnDOptimizerDriversRqst, UpdatePnDOptimizerEquipmentRqst, UpdatePnDOptimizerEquipmentPath, UpdatePnDOptimizerRouteRqst, UpdatePnDOptimizerRouteResp, UpdatePnDOptimizerShipmentsPath, UpdatePnDOptimizerShipmentsRqst, UpdatePnDOptimizerStatusRqst, UpdatePnDOptimizerStatusResp, UpsertDropLocationRqst, UpsertDropLocationResp, UpsertPnDRouteResultsRqst, AssignNextShipmentExceptionSequenceNbrPath, AssignNextShipmentExceptionSequenceNbrResp, CompleteDeliveryActivitiesFromDevicePath, CompleteDeliveryActivitiesFromDeviceRqst, CompleteDeliveryStopFromDevicePath, CompleteDeliveryStopFromDeviceRqst, CompleteDeliveryStopFromDeviceResp, CompleteEquipmentStopFromDevicePath, CompleteEquipmentStopFromDeviceQuery, CompleteEquipmentStopFromDeviceRqst, CompleteEquipmentStopFromDeviceResp, CompleteHookLoadedStopFromDevicePath, CompleteHookLoadedStopFromDeviceRqst, CompleteHookLoadedStopFromDeviceResp, CompletePickupStopFromDevicePath, CompletePickupStopFromDeviceRqst, CompletePickupStopFromDeviceResp, CompletePnDDropStopFromDevicePath, CompletePnDDropStopFromDeviceRqst, CompletePnDDropStopFromDeviceResp, CompletePnDTripFromDevicePath, CompletePnDTripFromDeviceRqst, CompletePnDTripFromDeviceResp, CompleteSpotLoadActivitiesFromDevicePath, CompleteSpotLoadActivitiesFromDeviceRqst, CompleteSpotLoadedStopFromDevicePath, CompleteSpotLoadedStopFromDeviceRqst, CompleteSpotLoadedStopFromDeviceResp, CompleteTripStopFromDevicePath, CompleteTripStopFromDeviceRqst, CreateDsrMessageLogFromDeviceRqst, CreatePnDDropStopFromDevicePath, CreatePnDDropStopFromDeviceRqst, CreatePnDDropStopFromDeviceResp, CreatePnDTripActivityFromDevicePath, CreatePnDTripActivityFromDeviceRqst, CreatePnDTripActivityFromDeviceResp, CreateTripEventFromDeviceRqst, GetAutoDispatchTripForDsrFromDevicePath, GetAutoDispatchTripForDsrFromDeviceResp, GetTripExpectationPath, GetTripExpectationResp, GetTripStopActivityFromDevicePath, GetTripStopActivityFromDeviceResp, GetTripStopFromDevicePath, GetTripStopFromDeviceQuery, GetTripStopFromDeviceResp, RecordPnDTripMessageFromDeviceRqst, RecordPnDTripMessageFromDeviceResp, RescheduleOrCancelPickupsRqst, RescheduleOrCancelPickupsResp, SearchDriversQuery, SearchDriversResp, SplitTripStopActivitiesToNewStopFromDevicePath, SplitTripStopActivitiesToNewStopFromDeviceRqst, UnassignPickupsRqst, UnassignStopActivityRqst, UpdateActivityLineItemsPath, UpdateActivityLineItemsRqst, UpdateActivityLineItemsResp, UpdateActivityLineItemsFromDevicePath, UpdateActivityLineItemsFromDeviceRqst, UpdateActivityLineItemsFromDeviceResp, UpdateAndUnassignHookLoadsFromDevicePath, UpdateAndUnassignHookLoadsFromDeviceRqst, UpdateHookLoadActivityFromDevicePath, UpdateHookLoadActivityFromDeviceRqst, UpdateHookLoadActivityFromDeviceResp, UpdateOperationsCustomerProfileFromDevicePath, UpdateOperationsCustomerProfileFromDeviceRqst, UpdateOperationsCustomerProfileFromDeviceResp, UpdatePickupActivityFromDevicePath, UpdatePickupActivityFromDeviceRqst, UpdatePickupActivityFromDeviceResp, UpdatePnDDropStopFromDevicePath, UpdatePnDDropStopFromDeviceRqst, UpdatePnDDropStopFromDeviceResp, UpdatePnDEquipmentHookStopFromDevicePath, UpdatePnDEquipmentHookStopFromDeviceRqst, UpdatePnDEquipmentHookStopFromDeviceResp, UpdatePnDStopActualArrivalDepartureFromDevicePath, UpdatePnDStopActualArrivalDepartureFromDeviceRqst, UpdatePnDStopActualArrivalDepartureFromDeviceResp, UpsertServiceCenterPrintPreferencesPath, UpsertServiceCenterPrintPreferencesRqst, UpsertServiceCenterPrintPreferencesResp, GetFinalConsigneeDestinationArrivalTmstByProPath, GetFinalConsigneeDestinationArrivalTmstByProResp, PrintOutboundBreakoutManifestReportPath, PrintOutboundBreakoutManifestReportRqst, PrintOutboundBreakoutManifestReportResp, PrintPnDReportPath, PrintPnDReportRqst, DeliveryShipmentSearchRecord, Activity, ActivityEquipment, ActivityInfoBasic, ActivityPickup, ActivityShipment, ActivityShipmentStatus, BeaconConversation, CalculatedShipmentCube, CustomerDwellTime, CustomerProfileNote, CustomerStop, DailyPickupStopCustomerPreference, DeliveryActivity, DeliveryDensity, DeliveryHandlingUnit, DeliveryException, DeliveryLocationDensity, DeliveryNotificationSignup, DeliveryPayment, DeliveryQualifierShipmentStatusChange, DeliveryShipmentAtStop, DeliveryShipmentDimension, DeliverySpecialServiceAuditMetric, DeliverySetPrint, DeviceTripStop, DeviceTripStopActivity, DeviceTripStopPayload, DispatchDriver, DispatchEquipment, DispatchRoute, DispatchTrip, DriverLocation, DropStop, DropStopEquipment, DsrCheckinPrint, DwellTimeTripNode, ExistingRouteSummary, GeoArea, HookEquipmentStop, InboundTrailer, ManifestPrint, NodeDwellTime, OptimizerCommodityDimension, OptimizerDriverSummary, OptimizerEquipmentSummary, OptimizerParameter, OptimizerResult, OptimizerResultRoute, OptimizerResultShipment, OptimizerRouteStop, OptimizerShipment, OptimizerShipmentSummary, OptimizerStop, OptimizerSummary, PickupLineItem, PickupTrip, PnDDispatchGroupRegion, PnDEquipment, PnDTrip, PrintDocumentPayload, PrintRequest, ProximalStop, RemainingFeetUpdate, RouteBalancingActivity, RouteBalancingRoute, RouteBalancingStop, RouteBalancingTrip, RouteDetail, RouteEquipment, RouteIdentifier, RouteSequenceAdjustment, RouteStop, RouteSummary, RuleBasedRoutingPayload, ShipmentCube, ShipmentRuleEvaluation, SplitTripStopActivity, SpotOrDropEquipment, StartDriverLocationPayload, StartPnDOptimizerPayload, Stop, StopProximityCheck, StopSequenceModelConflict, StopSequenceModelData, StopSequenceModelPickupPoint, StopSequenceModelStop, StopSequenceModelTrip, StopSequenceTrip, SuggestedRouteName, Trailer, TrailerContent, TripDetail, TripExpectation, TripExpectationNode, TripId, TripNodeActivityId, TripNodeId, TripStop, TripSummary, TripTrailerContentSummary, UnassignedPickup, UnassignedStop, XdockTypeCdMap, DoNotUse, CityOperationsEventSnapshot, RouteFilter, RouteChangeRecord, RegisterFilterRoutesRqst, RegisterFilterRoutesResp, UnregisterFilterRoutesRqst, ChangesFeedRoutesRqst, TripDetailFilter, TripDetailChangeRecord, RegisterFilterTripsRqst, RegisterFilterTripsResp, UnregisterFilterTripsRqst, ChangesFeedTripsRqst, MetricFilter, MetricChangeRecord, RegisterFilterServiceCenterMetricsRqst, RegisterFilterServiceCenterMetricsResp, UnregisterFilterServiceCenterMetricsRqst, ChangesFeedServiceCenterMetricsRqst, PdoControlFilter, PdoControlChangeRecord, RegisterFilterPndOptimizerRqst, RegisterFilterPndOptimizerResp, UnregisterFilterPndOptimizerRqst, ChangesFeedPndOptimizerRqst, DsrCurrentLocationFilter, DsrCurrentLocationChangeRecord, RegisterFilterCurrentDsrLocationRqst, RegisterFilterCurrentDsrLocationResp, UnregisterFilterCurrentDsrLocationRqst, ChangesFeedCurrentDsrLocationRqst, DeleteRulePath, ExecuteRuleRqst, ExecuteRuleResp, GetRulePath, GetRuleResp, GetRuleByNamePath, GetRuleByNameResp, ListRulesQuery, ListRulesResp, ListRulesByExpressionsRqst, ListRulesByExpressionsResp, ListRulesByRulesetNameQuery, ListRulesByRulesetNameResp, ListRulesForExecutionQuery, ListRulesForExecutionResp, RuleForExecution, RulesetForExecution, RuleOutcomeForExecution, RuleExpressionForExecution, RulesetOutcomeResp, UpsertRuleRqst, UpsertRuleResp, GetEnsembleStatusCountsPath, GetEnsembleStatusCountsResp, GetQueuePendingCountPath, GetQueuePendingCountResp, RestartEnsemblePath, RestartEnsembleRqst, RestartEnsembleResp, ResumeEnsemblePath, ResumeEnsembleRqst, ResumeEnsembleResp, StartMessageDrivenEnsembleRqst, StartMessageDrivenEnsembleResp, KillEnsembleRqst, KillEnsembleResp, StopQueuePath, StopQueueWebhookPath, StopQueueWebhookQuery, StartQueuePath, ListQueuesResp, ListExpiredMessagesPath, ListExpiredMessagesResp, DeleteExpiredMessagesPath, DeleteExpiredMessagesQuery, DeleteExpiredMessagesResp, ReplayMessagePath, GetPubSubStatusCountsPath, GetPubSubStatusCountsResp, StartSubscriptionPath, StopSubscriptionPath, GetUndeliveredMessagesPath, GetUndeliveredMessagesResp, ListSubscriptionsResp, GetSubscriptionPath, GetSubscriptionResp, MessageDrivenEnsemblePayload, DynamicMessageDrivenPayload, TransactionEventEnsemblePayload, DeleteExpiredMessageStatus, KillEnsembleStatus, EnsembleStateSearchRecord, Payload, EnsembleState, NestedEnsembleState, EnsembleExecutionContext, MinuetState, EnsembleMetrics, PubSubMetrics, QueueMetrics, AuxMessagePublishers, EnsembleConfiguration, Subscription, ScoDlvrShmMessageDrivenEnsemblePayload, ScoEventMessageDrivenEnsemblePayload, IntrfcAcctMessageDrivenEnsemblePayload, LTLEventCityOperations, LTLEventCustomer, BulkUpdateResp, BulkUpdateItemError, BulkApiRespItem, EsAggregationFieldRqst, EsDynamicPivotRqst, EsAggregationRqst, EsAggregationFieldResp, EsAggregationResp, DeliveryShipmentSearchFilterNot, DeliveryShipmentSearchFilterOr, DeliveryShipmentSearchFilterAnd, DeliveryShipmentSearchFilter, GetElasticDocumentDeliveryShipmentPath, GetElasticDocumentDeliveryShipmentQuery, GetElasticDocumentDeliveryShipmentResp, GetElasticDocumentDeliveryShipmentCountQuery, GetElasticDocumentDeliveryShipmentCountResp, UpdateElasticDocumentDeliveryShipmentRqst, UpdateElasticDocumentDeliveryShipmentPath, UpdateElasticDocumentDeliveryShipmentResp, BulkUpdateElasticDocumentDeliveryShipmentResp, DeleteElasticDocumentDeliveryShipmentPath, DeleteElasticDocumentDeliveryShipmentResp, SearchUnassignedDeliveriesRqst, SearchUnassignedDeliveriesResp, ExportUnassignedDeliveriesRqst, RegisterFilterUnassignedDeliveriesRqst, RegisterFilterUnassignedDeliveriesResp, UnregisterFilterUnassignedDeliveriesRqst, ChangesFeedUnassignedDeliveriesRqst, ListAggregationsUnassignedDeliveriesRqst, ListAggregationsUnassignedDeliveriesResp, SearchUnassignedDeliveriesWithUnmappedRqst, SearchUnassignedDeliveriesWithUnmappedResp, ExportUnassignedDeliveriesWithUnmappedRqst, RegisterFilterUnassignedDeliveriesWithUnmappedRqst, RegisterFilterUnassignedDeliveriesWithUnmappedResp, UnregisterFilterUnassignedDeliveriesWithUnmappedRqst, ChangesFeedUnassignedDeliveriesWithUnmappedRqst, ListAggregationsUnassignedDeliveriesWithUnmappedRqst, ListAggregationsUnassignedDeliveriesWithUnmappedResp, SearchUnassignedDeliveriesPlanByTrailerRqst, SearchUnassignedDeliveriesPlanByTrailerResp, ExportUnassignedDeliveriesPlanByTrailerRqst, RegisterFilterUnassignedDeliveriesPlanByTrailerRqst, RegisterFilterUnassignedDeliveriesPlanByTrailerResp, UnregisterFilterUnassignedDeliveriesPlanByTrailerRqst, ChangesFeedUnassignedDeliveriesPlanByTrailerRqst, ListAggregationsUnassignedDeliveriesPlanByTrailerRqst, ListAggregationsUnassignedDeliveriesPlanByTrailerResp, SearchYetToBeDispatchedDeliveriesRqst, SearchYetToBeDispatchedDeliveriesResp, ExportYetToBeDispatchedDeliveriesRqst, RegisterFilterYetToBeDispatchedDeliveriesRqst, RegisterFilterYetToBeDispatchedDeliveriesResp, UnregisterFilterYetToBeDispatchedDeliveriesRqst, ChangesFeedYetToBeDispatchedDeliveriesRqst, ListAggregationsYetToBeDispatchedDeliveriesRqst, ListAggregationsYetToBeDispatchedDeliveriesResp, SearchUnbilledShipmentsRqst, SearchUnbilledShipmentsResp, ExportUnbilledShipmentsRqst, RegisterFilterUnbilledShipmentsRqst, RegisterFilterUnbilledShipmentsResp, UnregisterFilterUnbilledShipmentsRqst, ChangesFeedUnbilledShipmentsRqst, ListAggregationsUnbilledShipmentsRqst, ListAggregationsUnbilledShipmentsResp, SearchDeliveryShipmentsRqst, SearchDeliveryShipmentsResp, ExportDeliveryShipmentsRqst, RegisterFilterDeliveryShipmentsRqst, RegisterFilterDeliveryShipmentsResp, UnregisterFilterDeliveryShipmentsRqst, ChangesFeedDeliveryShipmentsRqst, ListAggregationsDeliveryShipmentsRqst, ListAggregationsDeliveryShipmentsResp, SearchUnmappedShipmentsRqst, SearchUnmappedShipmentsResp, ExportUnmappedShipmentsRqst, RegisterFilterUnmappedShipmentsRqst, RegisterFilterUnmappedShipmentsResp, UnregisterFilterUnmappedShipmentsRqst, ChangesFeedUnmappedShipmentsRqst, ListAggregationsUnmappedShipmentsRqst, ListAggregationsUnmappedShipmentsResp, SearchAdminRouteShipmentsRqst, SearchAdminRouteShipmentsResp, ExportAdminRouteShipmentsRqst, RegisterFilterAdminRouteShipmentsRqst, RegisterFilterAdminRouteShipmentsResp, UnregisterFilterAdminRouteShipmentsRqst, ChangesFeedAdminRouteShipmentsRqst, ListAggregationsAdminRouteShipmentsRqst, ListAggregationsAdminRouteShipmentsResp, SearchUndeletedShipmentsRqst, SearchUndeletedShipmentsResp, ExportUndeletedShipmentsRqst, RegisterFilterUndeletedShipmentsRqst, RegisterFilterUndeletedShipmentsResp, UnregisterFilterUndeletedShipmentsRqst, ChangesFeedUndeletedShipmentsRqst, ListAggregationsUndeletedShipmentsRqst, ListAggregationsUndeletedShipmentsResp, SearchPastDueShipmentsRqst, SearchPastDueShipmentsResp, ExportPastDueShipmentsRqst, RegisterFilterPastDueShipmentsRqst, RegisterFilterPastDueShipmentsResp, UnregisterFilterPastDueShipmentsRqst, ChangesFeedPastDueShipmentsRqst, ListAggregationsPastDueShipmentsRqst, ListAggregationsPastDueShipmentsResp, SearchUnpurgedShipmentsRqst, SearchUnpurgedShipmentsResp, ExportUnpurgedShipmentsRqst, RegisterFilterUnpurgedShipmentsRqst, RegisterFilterUnpurgedShipmentsResp, UnregisterFilterUnpurgedShipmentsRqst, ChangesFeedUnpurgedShipmentsRqst, ListAggregationsUnpurgedShipmentsRqst, ListAggregationsUnpurgedShipmentsResp, SearchCrossDockTrailerShipmentsRqst, SearchCrossDockTrailerShipmentsResp, ExportCrossDockTrailerShipmentsRqst, RegisterFilterCrossDockTrailerShipmentsRqst, RegisterFilterCrossDockTrailerShipmentsResp, UnregisterFilterCrossDockTrailerShipmentsRqst, ChangesFeedCrossDockTrailerShipmentsRqst, ListAggregationsCrossDockTrailerShipmentsRqst, ListAggregationsCrossDockTrailerShipmentsResp, SearchDeliveryShipmentUnfilteredRqst, SearchDeliveryShipmentUnfilteredResp, ExportDeliveryShipmentUnfilteredRqst, RegisterFilterDeliveryShipmentUnfilteredRqst, RegisterFilterDeliveryShipmentUnfilteredResp, UnregisterFilterDeliveryShipmentUnfilteredRqst, ChangesFeedDeliveryShipmentUnfilteredRqst, ListAggregationsDeliveryShipmentUnfilteredRqst, ListAggregationsDeliveryShipmentUnfilteredResp, EnsembleStateSearchFilterNot, EnsembleStateSearchFilterOr, EnsembleStateSearchFilterAnd, EnsembleStateSearchFilter, GetElasticDocumentEnsembleStatePath, GetElasticDocumentEnsembleStateQuery, GetElasticDocumentEnsembleStateResp, GetElasticDocumentEnsembleStateCountQuery, GetElasticDocumentEnsembleStateCountResp, UpdateElasticDocumentEnsembleStateRqst, UpdateElasticDocumentEnsembleStatePath, UpdateElasticDocumentEnsembleStateResp, BulkUpdateElasticDocumentEnsembleStateResp, DeleteElasticDocumentEnsembleStatePath, DeleteElasticDocumentEnsembleStateResp, SearchEnsembleStateUnfilteredRqst, SearchEnsembleStateUnfilteredResp, ExportEnsembleStateUnfilteredRqst, RegisterFilterEnsembleStateUnfilteredRqst, RegisterFilterEnsembleStateUnfilteredResp, UnregisterFilterEnsembleStateUnfilteredRqst, ChangesFeedEnsembleStateUnfilteredRqst, ListAggregationsEnsembleStateUnfilteredRqst, ListAggregationsEnsembleStateUnfilteredResp, Contact, CustomerAddress, CustomerAddressSnapshot, CustomerLocationFunction, CustomerLocationFunctionSnapshot, CustomerOperationsNote, CustomerOperationsProfile, CustomerOperationsProfileSnapshot, CustomerOperationsSchedule, CustomerOperationsScheduleSnapshot, CustomerProfile, DeliveryNotification, DeliveryShipment, DeliveryShipmentEta, DeliveryShipmentEtaSnapshot, DeliveryShipmentSnapshot, DeliverySpecialService, DeliverySpecialServiceAudit, DeliverySpecialServiceHist, DimensionsCheckDsrRecheck, DimensionsCheckLog, DimensionsCheckTrailer, DispatchArea, DispatchGroup, DispatchGroupRegion, DockActivity, DockLoadedHandlingUnit, DockLoadedShipment, DockLocation, DockVisit, DropLocation, DropLocationSnapshot, DsrCurrentLocation, DsrCurrentLocationSnapshot, DsrLocation, EmployeeDevice, Equipment, EquipmentHist, EquipmentIdXref, EquipmentSnapshot, Event, EventLog, EventLogDetail, EventLogSnapshot, EventSnapshot, HeadLoadDetail, HookRequest, InterfaceAcct, InterfaceAcctSnapshot, InterfaceEmployee, InterfaceEmployeeSnapshot, LoadDeck, LoadedEquipment, LoadedHandlingUnit, LoadedShipment, LocationExceptionCtl, ManagementRemark, ManagementRemarkSnapshot, MessageLog, Metric, Movement, MovementException, MovementExceptionAction, MovementExceptionActionSnapshot, MovementExceptionSnapshot, MovementSnapshot, Note, NotificationShipment, OperationsDetail, OperationsEquipmentSnapshot, OperationsTrailerLoadSnapshot, PdoControl, PdoEnrouteSameAddressShipment, PdoException, PdoParameter, PdoParameterSetting, PdoRequestAnchorPoint, PdoRequestCommodity, PdoRequestDriver, PdoRequestEquipment, PdoRequestHandlingUnit, PdoRequestLnhDimension, PdoRequestPickupRequest, PdoRequestShipment, PdoResponseHeader, PdoResponseRoute, PdoResponseRouteStop, PdoResponseRouteStopAcvty, Placard, PlanningTrailer, PndDriver, PndLiftgateTrailer, PndOverrideDaySchedule, PositioningNode, PositioningPoint, PrintOption, ProMovementHist, ProMovementHistSnapshot, ProfileExcludeTrailer, ProfileSic, Projection, PurDeliveryLocationRw, PurEventLogRw, PurPickupRequestRw, PurRequestorRw, RefEquipmentIdXrefRw, RefScacCodeRo, RefStandardCodeRo, Remark, RemarkSnapshot, Route, RouteShipment, RouteShipmentSnapshot, RouteSnapshot, RouteTrip, RouteTripSnapshot, Schedule, ScoCollectedPayment, ScoEvent, ScoEventSnapshot, SelectionProfile, Shipment, ShipmentSnapshot, ShipperDimensionsProfile, ShmAdvanceBeyondCarrierRw, ShmEventLogRw, ShmExternalStatusRw, ShmHandlingUnitMovementRw, ShmHandlingUnitRw, ShmMiscLineItemRw, ShmMovementExceptionRw, ShmMovementRw, ShmNotification, ShmNotificationSnapshot, ShmServiceOverride, ShmShipmentRw, SuppRefExtendedDetail, SuppRefNbr, SuppRefNbrSnapshot, TimeDateCritical, TimeDateCriticalSnapshot, TrailerLoad, TrailerLoadHist, TrailerSpecification, Trip, TripActivity, TripDriver, TripEquipment, TripNode, TripNodeActivity, TripNodeActivitySnapshot, TripNodeSnapshot, TripSnapshot, TrlrLoadSnapshot, XdockException, XdockExceptionSnapshot, XdockHandlingUnit, XdockPlan, XdockShipment, Rule, RuleExpression, RuleOutcome, Ruleset, EventWait, RetriggerEvent, SymphonyMessage, SymphonyQueue, TransactionEvent, TransactionEventDetail, ListEnsembleConfigurationsResp, TimerConfig, Timer, ListTimerEnsemblesPath, ListTimerEnsemblesResp, StartTimerEnsemblePath, StartTimerEnsembleResp, StopTimerEnsemblePath, StopTimerEnsembleResp, StartPnDOptimizerEnsembleRqst, StartPnDOptimizerEnsemblePath, StartPnDOptimizerEnsembleQuery, StartPnDOptimizerEnsembleResp, StartDriverLocationEnsembleRqst, StartDriverLocationEnsemblePath, StartDriverLocationEnsembleQuery, StartDriverLocationEnsembleResp, StartPrintDocumentEnsembleRqst, StartPrintDocumentEnsemblePath, StartPrintDocumentEnsembleQuery, StartPrintDocumentEnsembleResp, StartRuleBasedRoutingEnsembleRqst, StartRuleBasedRoutingEnsemblePath, StartRuleBasedRoutingEnsembleQuery, StartRuleBasedRoutingEnsembleResp, StartDeviceTripStopEnsembleRqst, StartDeviceTripStopEnsemblePath, StartDeviceTripStopEnsembleQuery, StartDeviceTripStopEnsembleResp, DeliveryShipmentLckSearchRecord, CityOperationsEventPayload, DeliveryShipmentSearchRecordMessage, EnsembleStateSearchRecordMessage, CustomerEventPayload };

